import * as i0 from '@angular/core';
import { isDevMode, NgModule, Directive, Injectable, Optional, SkipSelf, Injector, ApplicationRef, ComponentFactoryResolver, Input, HostBinding, HostListener, Component, ChangeDetectionStrategy, EventEmitter, Output, forwardRef, ViewChild, ContentChild, InjectionToken, APP_INITIALIZER, ElementRef, Pipe, Inject, ContentChildren, NgZone, TemplateRef, ViewContainerRef, QueryList, ViewChildren } from '@angular/core';
import Popper from 'popper.js';
import * as i1 from '@angular/common';
import { CommonModule, Location } from '@angular/common';
import * as i1$3 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormGroup, FormArray, Validators } from '@angular/forms';
import { __awaiter, __rest, __decorate } from 'tslib';
import * as i1$1 from '@angular/common/http';
import { HttpHeaders, HttpClientModule, HttpClient, HttpResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BehaviorSubject, throwError, Subject, from, fromEvent, Subscription, of, merge } from 'rxjs';
import { catchError, takeUntil, debounceTime, throttleTime, tap, distinctUntilChanged, finalize, map, filter, skip, auditTime, merge as merge$1 } from 'rxjs/operators';
import flatpickr from 'flatpickr';
import { German } from 'flatpickr/dist/l10n/de';
import { english } from 'flatpickr/dist/l10n/default';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import SignaturePad from 'signature_pad';
import ResizeObserver from 'resize-observer-polyfill';
import * as i1$2 from '@angular/router';
import { NavigationEnd, RouterModule, Router } from '@angular/router';
import { parse, differenceInYears, addMonths, isFuture, isBefore, isAfter, isSameDay, isSameMonth, format } from 'date-fns';

/**
 * DB Lib Module
 */
class DBCoreModule {
    /**
     * Constructor
     */
    constructor() {
        // If this module is being used (imported and thus present in the application bundle) log out a warning for developers
        if (isDevMode()) {
            console.warn([
                '[@db/db-core]',
                'It seems that you are using the "DBCoreModule" within your application.',
                'This module is deprecated and wont work anymore. Please import required components by their respective module.',
            ].join(' '));
        }
    }
}
DBCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBCoreModule });
DBCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCoreModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [],
                    imports: [],
                }]
        }], ctorParameters: function () { return []; } });

/**
 * DB Abstract Overlay Component
 */
class DBOverlayComponentDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     * @param renderer       - Renderer
     * @param element        - Element
     */
    constructor(changeDetector, renderer, element) {
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.element = element;
        this.positionEngine = null;
    }
    /**
     * On Init Lifecycle Hook
     */
    ngOnInit() {
        this.renderer.addClass(this.element.nativeElement, 'db-overlay');
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        this.destroyPopper();
    }
    /**
     * Initialize the overlay
     *
     * @param triggerElement - Trigger element
     * @param options       - Overlay options for position, size
     */
    initOverlay(triggerElement, options) {
        // Add size class
        this.renderer.addClass(this.element.nativeElement, `db-overlay__overlay--${options.size}`);
        // Add arrow element
        const arrowElement = this.renderer.createElement('div');
        this.renderer.setAttribute(arrowElement, 'x-arrow', 'x-arrow');
        this.renderer.appendChild(this.element.nativeElement, arrowElement);
        // Setup
        this.positionEngine = new Popper(triggerElement.nativeElement, this.element.nativeElement, {
            modifiers: {
                arrow: {
                    element: '[x-arrow]',
                },
                computeStyle: {
                    gpuAcceleration: false, // Allows us to use 'transform' for animations
                },
                hide: {
                    enabled: true,
                },
                offset: {
                    offset: '0,10',
                },
                offsets: {
                    arrow: '50'
                },
                preventOverflow: {
                    enabled: true,
                    boundariesElement: 'window',
                    padding: 15
                },
            },
            placement: options.position
        });
        // Change detection
        this.changeDetector.detectChanges();
    }
    /**
     * Destroy the popper
     */
    destroyPopper() {
        if (this.positionEngine) {
            this.positionEngine.destroy();
        }
    }
    /**
     * Show the overlay
     *
     * @returns - Promise, resolves once the animation finished
     */
    showOverlay() {
        const animationInDuration = 200;
        this.renderer.setStyle(this.element.nativeElement, 'transition-duration', `${animationInDuration}ms`);
        this.renderer.addClass(this.element.nativeElement, 'is-visible');
        return this.awaitTime(animationInDuration);
    }
    /**
     * Hide the Overlay
     *
     * @returns - Promise, resolves once the animation finished
     */
    hideOverlay() {
        const animationOutDuration = 100;
        this.renderer.setStyle(this.element.nativeElement, 'transition-duration', `${animationOutDuration}ms`);
        this.renderer.removeClass(this.element.nativeElement, 'is-visible');
        return this.awaitTime(animationOutDuration);
    }
    /**
     * Await time, and resolve promise once the time has passed
     *
     * @param   time - Time in ms
     * @returns      - Promise, resolving after the time has passed
     */
    awaitTime(time) {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    }
}
DBOverlayComponentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBOverlayComponentDirective, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
DBOverlayComponentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBOverlayComponentDirective, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBOverlayComponentDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; } });

/**
 * DB Window Ref (Service)
 */
class DBWindowRef {
    /**
     * Constructor
     */
    constructor() {
        this.nativeWindow = window;
        this.nativeDocument = this.nativeWindow.document;
        this.nativeBody = this.nativeDocument.body;
        this.isIframe = window.parent !== window;
    }
}
DBWindowRef.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBWindowRef, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBWindowRef.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBWindowRef });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBWindowRef, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * Window Ref Service Factory
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function windowRefServiceFactory(parentWindowRef) {
    return parentWindowRef || new DBWindowRef();
}
/**
 * DB Window Ref Module
 */
class DBWindowRefModule {
}
DBWindowRefModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBWindowRefModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBWindowRefModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBWindowRefModule });
DBWindowRefModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBWindowRefModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBWindowRef],
            ],
            provide: DBWindowRef,
            useFactory: windowRefServiceFactory,
        },
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBWindowRefModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBWindowRef],
                            ],
                            provide: DBWindowRef,
                            useFactory: windowRefServiceFactory,
                        },
                    ],
                }]
        }] });

/**
 * DB Renderer
 *
 * This service can be used to dynamically create and render components outside the application component. It also includes methods for
 * cleanup actions. This functionality is especially interesting for components that have to act as an overlay, such as tooltips, popovers,
 * modals, or similar.
 *
 * Technical note:
 * Using the Angular Renderer(2) class is not possible within services. Trying to inject to "root" renderer from the root application
 * module was also not successful. Thus, we're using native DOM operations here.
 */
class DBRenderer {
    /**
     * Constructor
     *
     * @param windowRef                - Window Ref
     * @param injector                 - Injector
     * @param applicationRef           - Application Ref
     * @param componentFactoryResolver - Component Factory Resolver
     */
    constructor(windowRef, injector, applicationRef, componentFactoryResolver) {
        this.windowRef = windowRef;
        this.injector = injector;
        this.applicationRef = applicationRef;
        this.componentFactoryResolver = componentFactoryResolver;
    }
    /**
     * Create the given component, then append it to the application component
     *
     * @param   componentType - Component type of the component to create and append
     * @returns               - Component reference
     */
    createAndAppendComponent(componentType) {
        // Create component
        const componentRef = this.componentFactoryResolver
            .resolveComponentFactory(componentType)
            .create(this.injector);
        // Setup cleanup on component destroy (#perfmatters)
        componentRef.onDestroy(() => {
            componentRef.changeDetectorRef.detach();
        });
        // Render component in the DOM, next to the root component
        const componentElement = componentRef.hostView.rootNodes[0];
        this.windowRef.nativeBody.appendChild(componentElement);
        // Attach component view to application, and run global change detection once
        this.applicationRef.attachView(componentRef.hostView);
        this.applicationRef.tick();
        return componentRef;
    }
    /**
     * Destroy and remove a component
     *
     * @param componentRef - Component reference
     */
    destroyAndRemoveComponent(componentRef) {
        // Detach component view from application
        this.applicationRef.detachView(componentRef.hostView);
        // Destroy component (triggers ngOnDestroy lifecycle)
        componentRef.destroy();
    }
}
DBRenderer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRenderer, deps: [{ token: DBWindowRef }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Injectable });
DBRenderer.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRenderer });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRenderer, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DBWindowRef }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: i0.ComponentFactoryResolver }]; } });

/**
 * Renderer Factory
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function rendererFactory(parentRenderer, windowRef, injector, applicationRef, componentFactoryResolver) {
    return parentRenderer || new DBRenderer(windowRef, injector, applicationRef, componentFactoryResolver);
}
/**
 * DB Renderer Module
 */
class DBRendererModule {
}
DBRendererModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRendererModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBRendererModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBRendererModule, imports: [DBWindowRefModule] });
DBRendererModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRendererModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBRenderer],
                DBWindowRef,
                Injector,
                ApplicationRef,
                ComponentFactoryResolver,
            ],
            provide: DBRenderer,
            useFactory: rendererFactory,
        },
    ], imports: [DBWindowRefModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRendererModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DBWindowRefModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBRenderer],
                                DBWindowRef,
                                Injector,
                                ApplicationRef,
                                ComponentFactoryResolver,
                            ],
                            provide: DBRenderer,
                            useFactory: rendererFactory,
                        },
                    ],
                }]
        }] });

/**
 * Code from deepmerge 4.3.1
 *
 * Transformed into typescript compatible Angular Service.
 *
 * Source:
 * https://github.com/TehShrike/deepmerge/blob/v4.3.1/index.js
 * https://github.com/TehShrike/is-mergeable-object/v1.1.1/master/index.js
 */
// Original JS Source: https://github.com/TehShrike/deepmerge/blob/v4.3.1/index.js
class DeepmergeService {
    // Original JS Source: https://github.com/TehShrike/is-mergeable-object/blob/v1.1.1/index.js
    // Needed to transform into a function from original class, otherwise failed
    isMergeableObject(value) {
        const isSpecial = () => {
            const stringValue = Object.prototype.toString.call(value);
            return stringValue === '[object RegExp]'
                || stringValue === '[object Date]'
                || isReactElement();
        };
        const isNonNullObject = () => !!value && typeof value === 'object';
        const canUseSymbol = () => typeof Symbol === 'function' && Symbol.for;
        const isReactElement = () => value.$$typeof === reactElementType;
        const reactElementType = canUseSymbol() ? Symbol.for('react.element') : 0xeac7;
        return isNonNullObject() && !isSpecial();
    }
    emptyTarget(val) {
        return Array.isArray(val) ? [] : {};
    }
    cloneUnlessOtherwiseSpecified(value, options = {}) {
        return (options.clone !== false && options.isMergeableObject(value))
            ? this.deepmerge(this.emptyTarget(value), value, options)
            : value;
    }
    defaultArrayMerge(target, source, options) {
        return target.concat(source).map((element) => this.cloneUnlessOtherwiseSpecified(element, options));
    }
    getEnumerableOwnPropertySymbols(target) {
        return Object.getOwnPropertySymbols
            ? Object.getOwnPropertySymbols(target).filter((symbol) => Object.propertyIsEnumerable.call(target, symbol))
            : [];
    }
    getKeys(target) {
        return Object.keys(target).concat(this.getEnumerableOwnPropertySymbols(target));
    }
    propertyIsOnObject(object, property) {
        try {
            return property in object;
        }
        catch (_) {
            return false;
        }
    }
    propertyIsUnsafe(target, key) {
        return this.propertyIsOnObject(target, key) // Properties are safe to merge if they don't exist in the target yet,
            && !(Object.hasOwnProperty.call(target, key) // unsafe if they exist up the prototype chain,
                && Object.propertyIsEnumerable.call(target, key)); // and also unsafe if they're nonenumerable.
    }
    mergeObject(target, source, options) {
        const destination = {};
        if (options.isMergeableObject(target)) {
            this.getKeys(target).forEach((key) => {
                destination[key] = this.cloneUnlessOtherwiseSpecified(target[key], options);
            });
        }
        this.getKeys(source).forEach((key) => {
            if (this.propertyIsUnsafe(target, key)) {
                return;
            }
            if (this.propertyIsOnObject(target, key) && options.isMergeableObject(source[key])) {
                destination[key] = this.getMergeFunction(key, options)(target[key], source[key], options);
            }
            else {
                destination[key] = this.cloneUnlessOtherwiseSpecified(source[key], options);
            }
        });
        return destination;
    }
    // From Original code -> used in mergeObject function, see comment
    // Please let it stay to ensure original code base is given for bug fixing
    getMergeFunction(key, options) {
        if (!options.customMerge) {
            return this.merge.bind(this);
        }
        const customMerge = options.customMerge(key);
        return typeof customMerge === 'function' ? customMerge.bind(this) : this.merge.bind(this);
    }
    deepmerge(target, source, options = {}) {
        options.arrayMerge = options.arrayMerge || this.defaultArrayMerge.bind(this);
        options.isMergeableObject = options.isMergeableObject || this.isMergeableObject.bind(this);
        // cloneUnlessOtherwiseSpecified is added to `options` so that custom arrayMerge()
        // implementations can use it. The caller may not replace it.
        options.cloneUnlessOtherwiseSpecified = this.cloneUnlessOtherwiseSpecified.bind(this);
        const sourceIsArray = Array.isArray(source);
        const targetIsArray = Array.isArray(target);
        const sourceAndTargetTypesMatch = sourceIsArray === targetIsArray;
        if (!sourceAndTargetTypesMatch) {
            return this.cloneUnlessOtherwiseSpecified(source, options);
        }
        else if (sourceIsArray) {
            return options.arrayMerge(target, source, options);
        }
        else {
            return this.mergeObject(target, source, options);
        }
    }
    deepmergeAll(array, options) {
        if (!Array.isArray(array)) {
            throw new Error('first argument should be an array');
        }
        return array.reduce((prev, next) => this.deepmerge(prev, next, options), {});
    }
    // New shordhand methods
    merge(target, source, options = {}) {
        return this.deepmerge(target, source, options);
    }
    all(array, options) {
        return this.deepmergeAll(array, options);
    }
}
DeepmergeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DeepmergeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DeepmergeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DeepmergeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DeepmergeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * DB Form Component
 *
 * Base form component class, enabling compatibility with the Angular Forms module. All form components should inherit from this class.
 */
class DBCoreBaseDirective {
    /**
     * Constructor
     */
    constructor() {
        this._disabled = false;
        this.readonly = false;
    }
    /**
     * Disabled flag
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = value;
    }
    /**
     * Click event handler, intercepting all click events and stops them if the component is disabled
     *
     * @param event - Event
     */
    onClick(event) {
        if (this.disabled) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
        }
        else {
            return true;
        }
    }
    /**
     * Keyboard event handler, intercepting keyboard events with backspace and stops them if the component is readonly.
     * It prevents the browserback action on a readonly element.
     * @param Event event - Event
     */
    onKeyDown(event) {
        // Hint: no type for Event, because error keyCode not exists on type Event, but it exists
        const keyCode = 'keyCode';
        if (this.readonly && event[keyCode] === 8) {
            event.preventDefault();
        }
    }
}
DBCoreBaseDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCoreBaseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DBCoreBaseDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBCoreBaseDirective, inputs: { disabled: "disabled", readonly: "readonly" }, host: { listeners: { "click": "onClick($event)", "keydown": "onKeyDown($event)" }, properties: { "class.is-disabled": "this.disabled", "class.is-readonly": "this.readonly" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCoreBaseDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return []; }, propDecorators: { disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.is-disabled']
            }], readonly: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.is-readonly']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

/**
 * DB Button Component
 */
class DBButtonComponent extends DBCoreBaseDirective {
    /*
     * Constructor
     */
    constructor(element, renderer) {
        super();
        this.element = element;
        this.renderer = renderer;
        this.type = 'button';
        this.design = 'secondary';
        this.size = 'md';
        this.arrow = null;
        this.loading = false;
        this.iconOnly = false;
        this.removeDuplicateFocus();
    }
    /**
     * Handle Click Event
     * @param $event - Event
     */
    onButtonClick($event) {
        if (this.disabled || this.loading) {
            $event.stopPropagation();
        }
    }
    /**
     * Remove potentially duplicate focusable elements
     *
     * This happens when people assign a tabindex manually, or use the Angular 'routerLink' directive (which, internally, also sets a tabindex
     * to make its host element focusable)
     */
    removeDuplicateFocus() {
        this.renderer.removeAttribute(this.element.nativeElement, 'tabindex');
    }
}
DBButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBButtonComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
DBButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBButtonComponent, selector: "db-button", inputs: { type: "type", design: "design", size: "size", arrow: "arrow", loading: "loading", iconOnly: "iconOnly" }, usesInheritance: true, ngImport: i0, template: "<button\r\n    class=\"db-button\"\r\n    [class.db-button--icon-left]=\"arrow === 'left'\"\r\n    [class.db-button--icon-right]=\"arrow === 'right'\"\r\n    [class.db-button--disabled]=\"disabled\"\r\n    [class.db-button--loading]=\"loading\"\r\n    [class.db-button--icon-only]=\"iconOnly\"\r\n    [ngClass]=\"[ 'db-button--' + design, 'db-button--' + size ]\"\r\n    [type]=\"type\"\r\n    [attr.tabindex]=\"disabled ? '-1': '0'\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n\r\n    (click)=\"onButtonClick($event)\">\r\n\r\n    <svg class=\"db-button__icon\" viewBox=\"0 0 6.5 12\" *ngIf=\"arrow === 'left'\">\r\n      <g>\r\n        <path d=\"M6.3,5.5L1.3,0.2c-0.3-0.3-0.7-0.3-1,0c-0.3,0.3-0.3,0.7,0,1L4.8,6l-4.6,4.7c-0.3,0.3-0.3,0.7,0,1C0.4,11.9,0.6,12,0.7,12\r\n          c0.1,0,0.4-0.1,0.5-0.2l5.1-5.3C6.6,6.2,6.6,5.8,6.3,5.5\" transform=\"rotate(180 3.25 6)\"></path>\r\n      </g>\r\n    </svg>\r\n\r\n    <span class=\"db-button__content\" [class.d-flex]=\"loading\" [class.justify-content-center]=\"loading\">\r\n      <span [class.invisible]=\"loading\">\r\n        <ng-content>\r\n        </ng-content>\r\n      </span>\r\n      <svg class=\"db-button__icon-loading\" viewBox=\"0 0 24 24\" *ngIf=\"loading\">\r\n        <g>\r\n          <path d=\"M3.25,12 C3.25,16.8324916 7.16750844,20.75 12,20.75 C16.8324916,20.75 20.75,16.8324916 20.75,12 C20.75,7.16750844 16.8324916,3.25\r\n            12,3.25 L12,3.25 L12,2 C17.5228475,2 22,6.4771525 22,12 C22,17.5228475 17.5228475,22 12,22 C6.4771525,22 2,17.5228475 2,12 L2,12 Z\">\r\n            <animateTransform attributeName=\"transform\" attributeType=\"XML\" type=\"rotate\" from=\"0 12 12\" to=\"360 12 12\" dur=\"1.5s\" repeatCount=\"indefinite\"/>\r\n          </path>\r\n        </g>\r\n      </svg>\r\n    </span>\r\n\r\n    <svg class=\"db-button__icon\" viewBox=\"0 0 6.5 12\" *ngIf=\"arrow === 'right'\">\r\n      <g>\r\n        <path d=\"M6.3,5.5L1.3,0.2c-0.3-0.3-0.7-0.3-1,0c-0.3,0.3-0.3,0.7,0,1L4.8,6l-4.6,4.7c-0.3,0.3-0.3,0.7,0,1C0.4,11.9,0.6,12,0.7,12\r\n          c0.1,0,0.4-0.1,0.5-0.2l5.1-5.3C6.6,6.2,6.6,5.8,6.3,5.5\"></path>\r\n      </g>\r\n    </svg>\r\n\r\n</button>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBButtonComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-button', template: "<button\r\n    class=\"db-button\"\r\n    [class.db-button--icon-left]=\"arrow === 'left'\"\r\n    [class.db-button--icon-right]=\"arrow === 'right'\"\r\n    [class.db-button--disabled]=\"disabled\"\r\n    [class.db-button--loading]=\"loading\"\r\n    [class.db-button--icon-only]=\"iconOnly\"\r\n    [ngClass]=\"[ 'db-button--' + design, 'db-button--' + size ]\"\r\n    [type]=\"type\"\r\n    [attr.tabindex]=\"disabled ? '-1': '0'\"\r\n    [attr.aria-disabled]=\"disabled\"\r\n\r\n    (click)=\"onButtonClick($event)\">\r\n\r\n    <svg class=\"db-button__icon\" viewBox=\"0 0 6.5 12\" *ngIf=\"arrow === 'left'\">\r\n      <g>\r\n        <path d=\"M6.3,5.5L1.3,0.2c-0.3-0.3-0.7-0.3-1,0c-0.3,0.3-0.3,0.7,0,1L4.8,6l-4.6,4.7c-0.3,0.3-0.3,0.7,0,1C0.4,11.9,0.6,12,0.7,12\r\n          c0.1,0,0.4-0.1,0.5-0.2l5.1-5.3C6.6,6.2,6.6,5.8,6.3,5.5\" transform=\"rotate(180 3.25 6)\"></path>\r\n      </g>\r\n    </svg>\r\n\r\n    <span class=\"db-button__content\" [class.d-flex]=\"loading\" [class.justify-content-center]=\"loading\">\r\n      <span [class.invisible]=\"loading\">\r\n        <ng-content>\r\n        </ng-content>\r\n      </span>\r\n      <svg class=\"db-button__icon-loading\" viewBox=\"0 0 24 24\" *ngIf=\"loading\">\r\n        <g>\r\n          <path d=\"M3.25,12 C3.25,16.8324916 7.16750844,20.75 12,20.75 C16.8324916,20.75 20.75,16.8324916 20.75,12 C20.75,7.16750844 16.8324916,3.25\r\n            12,3.25 L12,3.25 L12,2 C17.5228475,2 22,6.4771525 22,12 C22,17.5228475 17.5228475,22 12,22 C6.4771525,22 2,17.5228475 2,12 L2,12 Z\">\r\n            <animateTransform attributeName=\"transform\" attributeType=\"XML\" type=\"rotate\" from=\"0 12 12\" to=\"360 12 12\" dur=\"1.5s\" repeatCount=\"indefinite\"/>\r\n          </path>\r\n        </g>\r\n      </svg>\r\n    </span>\r\n\r\n    <svg class=\"db-button__icon\" viewBox=\"0 0 6.5 12\" *ngIf=\"arrow === 'right'\">\r\n      <g>\r\n        <path d=\"M6.3,5.5L1.3,0.2c-0.3-0.3-0.7-0.3-1,0c-0.3,0.3-0.3,0.7,0,1L4.8,6l-4.6,4.7c-0.3,0.3-0.3,0.7,0,1C0.4,11.9,0.6,12,0.7,12\r\n          c0.1,0,0.4-0.1,0.5-0.2l5.1-5.3C6.6,6.2,6.6,5.8,6.3,5.5\"></path>\r\n      </g>\r\n    </svg>\r\n\r\n</button>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { type: [{
                type: Input
            }], design: [{
                type: Input
            }], size: [{
                type: Input
            }], arrow: [{
                type: Input
            }], loading: [{
                type: Input
            }], iconOnly: [{
                type: Input
            }] } });

/**
 * DB Button Module
 */
class DBButtonModule {
}
DBButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBButtonModule, declarations: [DBButtonComponent], imports: [CommonModule], exports: [DBButtonComponent] });
DBButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBButtonModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBButtonComponent,
                    ],
                    exports: [
                        DBButtonComponent,
                    ],
                    imports: [
                        CommonModule
                    ],
                }]
        }] });

/**
 * DB Form Model Component
 */
class DBCoreFormBaseDirective extends DBCoreBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     */
    constructor(changeDetector) {
        super();
        this.propagateChange = (value) => {
            // Nothing to implement
        };
        this.propagateTouched = (value) => {
            // Nothing to implement
        };
        this.modelChange = new EventEmitter();
        this.changeDetector = changeDetector;
    }
    /**
     * Model, implemented as getter / setter to execute side effects
     */
    set model(value) {
        this.setInternalModel(value, true, false, true);
        this.propagateTouched(this.internalModel);
    }
    get model() {
        return this.internalModel;
    }
    /**
     * Write the value to the form component (model -> view)
     *
     * @param value - Value
     */
    writeValue(value) {
        this.setInternalModel(value, true, true, false);
    }
    /**
     * Register the change event callback function (view -> model)
     *
     * @param onChangeCallback - Change event callback function
     */
    registerOnChange(onChangeCallback) {
        this.propagateChange = onChangeCallback;
    }
    /**
     * Register the touched event callback function (view -> model)
     *
     * @param onTouchedCallback - Touch event callback function
     */
    registerOnTouched(onTouchedCallback) {
        this.propagateTouched = onTouchedCallback;
    }
    /**
     * Set (update) the disabled state (model -> view)
     *
     * @param disabled - Disabled State
     */
    setDisabledState(disabled) {
        this.disabled = disabled;
        // Run change detection to update the visual state
        this.triggerChangeDetection();
    }
    /**
     * Set internal model, probably with normalization, and return a flag describing whether the value has actually changed
     *
     * @param value      - Value
     * @param doRender   - Flag, describing whether the rendered value has to be updated
     * @param emitEvent  - Flag, describing whether the model change event has to be emitted
     * @param emitChange - Flag, describing whether the Angular Forms propagate change method has to be called
     */
    setInternalModel(value, doRender, emitEvent, emitChange) {
        // Normalize the value
        const normalizedValue = this.doNormalize(value);
        // Only take action if the value has actually changed (#perfmatters)
        if (normalizedValue !== this.internalModel) {
            // Update the model (always)
            this.internalModel = normalizedValue;
            // Update the value rendered in HTML
            if (doRender) {
                this.doRender(normalizedValue, false);
            }
            // Emit model change event
            if (emitEvent) {
                this.modelChange.emit(normalizedValue);
            }
            // Propagate change (for Angular Forms)
            if (emitChange) {
                this.propagateChange(normalizedValue);
            }
            // Run change detection to update the rendered values
            this.triggerChangeDetection();
        }
    }
    /**
     * Usually, rendering happens by simple Angular data binding. In special use cases, however, this won't work properly. For those cases,
     * components extending from this class can provide an addition / alternative way of rendering things by overwriting this method. The
     * custom rendering will happen during model updates (setInternalModel, in particular). Usually, components will need to inject the
     * Angular renderer service for implementing custom rendering.
     *
     * @param value - Value to render
     * @param boolean setCursor  - Flag, for cursor position
     */
    doRender(value, setCursor) {
        // No action by default
    }
    /**
     * If necessary, components can decide to normalize the model when it gets updated. For example, most often values such as "null" or
     * "undefined" might lead to issues later on -- in those cases, they (for example) might be normalized to an empty string.
     *
     * @param value - Value to normalize
     */
    doNormalize(value) {
        // No action by default
        return value;
    }
    /**
     * Run change detection
     *
     * Note:
     * The change detection will only be triggered if the component (and thus the view) has not yet been destroyed.
     * Workaround inspired by <https://stackoverflow.com/questions/37849453/attempt-to-use-a-destroyed-view-detectchanges>
     */
    triggerChangeDetection() {
        if (!this.changeDetector.destroyed) {
            this.changeDetector.markForCheck();
        }
    }
}
DBCoreFormBaseDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCoreFormBaseDirective, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
DBCoreFormBaseDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBCoreFormBaseDirective, inputs: { model: "model" }, outputs: { modelChange: "modelChange" }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCoreFormBaseDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { model: [{
                type: Input
            }], modelChange: [{
                type: Output
            }] } });

/**
 * DB Checkbox Group Component
 */
class DBCheckboxGroupComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Angular change detector
     */
    constructor(changeDetector) {
        super(changeDetector);
        this.design = 'default';
        this.internalModel = [];
        this.checkboxComponentsRefs = [];
        this.internalDisabled = null;
        this.legend = null;
        // Each checkbox need the same unique name to get grouped together correctly
        this.name = `checkbox-group-${DBCheckboxGroupComponent.groupCount}`;
        DBCheckboxGroupComponent.groupCount++;
    }
    /**
     * Property for disabling/enabling a whole checkbox group
     * if disabled state is changed at the group, all childen
     * get also changed
     */
    set disabled(value) {
        if (this.internalDisabled !== value) {
            this.internalDisabled = value;
        }
    }
    get disabled() {
        return this.internalDisabled;
    }
    /**
     * Default design component class
     */
    get defaultDesign() {
        return this.design === 'default';
    }
    /**
     * Cards design component class
     */
    get cardDesign() {
        return this.design === 'cards';
    }
    /**
     * After Content Init lifecycle hook, setting up the checkbox child components
     */
    ngAfterContentInit() {
        this.updateDisabledState();
    }
    /**
     * Register checkbox child component
     *
     * @param checkboxComponent - checkbox component
     */
    registerCheckbox(checkboxComponent) {
        // Add child component to group (bottom up)
        this.checkboxComponentsRefs.push(checkboxComponent);
        // Early exit if the checkbox model has already a value
        const doesCheckboxAlreadyExist = !!this.model
            .find((checkBox) => checkBox.name === checkboxComponent.name);
        if (doesCheckboxAlreadyExist) {
            return;
        }
        // Update checkbox group model
        this.setInternalModel([
            ...this.model,
            {
                model: checkboxComponent.model,
                name: checkboxComponent.name,
            },
        ], false, true, true);
    }
    /**
     * Unregister checkbox child component, remove it from ref list and group model
     *
     * @param checkboxComponent - checkbox component
     */
    unregisterCheckbox(checkboxComponent) {
        this.checkboxComponentsRefs = this.checkboxComponentsRefs
            .filter((checkboxComponentRef) => checkboxComponentRef !== checkboxComponent);
        this.setInternalModel([
            ...this.model.filter((checkboxModel) => checkboxModel.name !== checkboxComponent.name)
        ], false, true, true);
        this.changeDetector.markForCheck();
    }
    /**
     * Update Checkbox Group Form State
     *
     * @param changedCheckboxModel - new checkbox form value
     */
    updateModelItem(changedCheckboxModel) {
        const updatedModel = this.model
            .map((checkboxModel) => checkboxModel.name === changedCheckboxModel.name
            ? {
                model: changedCheckboxModel.model,
                name: changedCheckboxModel.name,
            }
            : checkboxModel);
        this.setInternalModel(updatedModel, false, true, true);
        this.changeDetector.markForCheck();
    }
    /**
     * Do render the new checkbox value
     */
    doRender() {
        this.cleanupCheckboxRefOrphans();
        this.updateDisabledState();
        this.updateView();
    }
    /**
     * Update all checkbox child components to reflect the new disabled state (needed forcard design)
     */
    updateDisabledState() {
        if (this.checkboxComponentsRefs) {
            this.checkboxComponentsRefs
                .forEach((checkboxComponent) => {
                checkboxComponent.disabled = this.disabled;
            });
        }
    }
    /**
     * Trigger change detection (including child checkbox components)
     */
    updateView() {
        if (!this.checkboxComponentsRefs) {
            return;
        }
        this.checkboxComponentsRefs
            .forEach((checkboxComponent) => {
            checkboxComponent.model = this.model
                .find((checkboxModel) => checkboxModel.name === checkboxComponent.name)
                .model;
            checkboxComponent.updateView();
        });
    }
    /**
     * Remove any checkbox ref orphans not reflected in the model due to recent internal model changes.
     */
    cleanupCheckboxRefOrphans() {
        this.checkboxComponentsRefs = this.checkboxComponentsRefs
            .filter((checkboxComponentRef) => this.model.some((entry) => checkboxComponentRef.name === entry.name));
    }
}
/**
 * Number, shared between checkbox group components, for calculating unique checkbox group names
 */
DBCheckboxGroupComponent.groupCount = 1;
DBCheckboxGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxGroupComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBCheckboxGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBCheckboxGroupComponent, selector: "db-checkbox-group", inputs: { legend: "legend", disabled: "disabled", design: "design" }, host: { properties: { "class.is-disabled": "this.disabled", "class.db-checkbox-group--default": "this.defaultDesign", "class.db-checkbox-group--cards": "this.cardDesign" } }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBCheckboxGroupComponent),
        },
    ], usesInheritance: true, ngImport: i0, template: "<fieldset class=\"db-checkbox-group__fieldset m-0 p-0 border-0\">\r\n  <legend class=\"db-checkbox-group__legend\" *ngIf=\"legend\">\r\n    {{ legend }}\r\n  </legend>\r\n  <ng-content>\r\n  </ng-content>\r\n</fieldset>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxGroupComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBCheckboxGroupComponent),
                        },
                    ], selector: 'db-checkbox-group', template: "<fieldset class=\"db-checkbox-group__fieldset m-0 p-0 border-0\">\r\n  <legend class=\"db-checkbox-group__legend\" *ngIf=\"legend\">\r\n    {{ legend }}\r\n  </legend>\r\n  <ng-content>\r\n  </ng-content>\r\n</fieldset>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { legend: [{
                type: Input
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.is-disabled']
            }], design: [{
                type: Input
            }], defaultDesign: [{
                type: HostBinding,
                args: ['class.db-checkbox-group--default']
            }], cardDesign: [{
                type: HostBinding,
                args: ['class.db-checkbox-group--cards']
            }] } });

/**
 * DB Icon Resources Component
 */
class DBIconResourcesComponent {
    /**
     * Constructor
     *
     * @param elementRef     - Element Ref
     * @param changeDetector - Change detector
     * @param renderer       - Renderer
     */
    constructor(elementRef, changeDetector, renderer) {
        this.internalSvgElement = null;
        this.elementRef = elementRef;
        this.renderer = renderer;
        changeDetector.detach(); // Detach this component from the rest of the application
    }
    /**
     * SVG element
     */
    set svgElement(svgElement) {
        // Remove old SVG element first
        if (this.internalSvgElement) {
            this.renderer.removeChild(this.elementRef.nativeElement, this.internalSvgElement);
        }
        // Then, append new SVG element
        this.internalSvgElement = svgElement;
        this.renderer.appendChild(this.elementRef.nativeElement, this.internalSvgElement);
    }
    get svgElement() {
        return this.internalSvgElement;
    }
    /**
     * Setup icon resources
     *
     * @param svgSymbols - List of SVG symbols
     */
    setupIconResources(svgSymbols) {
        // Create SVG element with definitions
        const svgNamespace = 'http://www.w3.org/2000/svg';
        const svgElement = this.renderer.createElement('svg', svgNamespace);
        this.renderer.setAttribute(svgElement, 'focusable', 'false'); // Just to be sure ...
        const defsElement = this.renderer.createElement('defs', svgNamespace);
        this.renderer.appendChild(svgElement, defsElement);
        // Add SVG symbols
        svgSymbols.forEach((svgSymbol) => {
            this.renderer.appendChild(defsElement, svgSymbol);
        });
        // Render view
        this.svgElement = svgElement;
    }
}
DBIconResourcesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconResourcesComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
DBIconResourcesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBIconResourcesComponent, selector: "db-icon-resources", ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconResourcesComponent, decorators: [{
            type: Component,
            args: [{
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    selector: 'db-icon-resources',
                    template: '',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }]; } });

/**
 * DB Icon Service
 */
class DBIconService {
    /**
     * Constructor
     */
    constructor(windowRef, http, injector, applicationRef, componentFactoryResolver) {
        this.http = http;
        this.icons = [];
        this.iconChangesSource = new BehaviorSubject([]);
        this.iconChanges = this.iconChangesSource.asObservable();
        this.iconResourcesComponent = this.createIconResourcesComponent(componentFactoryResolver, applicationRef, injector, windowRef);
    }
    /**
     * Fetch and setup icons (e.g. SVG sprites)
     *
     * @param   [urlOrUrls='./assets/icons/db-icons.svg'] - List of URLs or URL to svg file(s)
     * @returns                                             Promise, resolved with list of icon names
     */
    use(urlOrUrls = './assets/icons/db-icons.svg') {
        return __awaiter(this, void 0, void 0, function* () {
            // Get SVG urls
            const svgUrls = urlOrUrls instanceof Array ? urlOrUrls : [urlOrUrls];
            // Fetch SVG files, async and in parallel (#perfmatters)
            let svgFiles;
            try {
                svgFiles = yield Promise.all(svgUrls.map((svgUrl) => this.fetchSvgFile(svgUrl)));
            }
            catch (error) {
                throw new Error(error); // Re-throw error
            }
            // Get list of svg symbols
            const domParser = new DOMParser();
            const svgSymbols = svgFiles
                .map((svgFile) => domParser.parseFromString(svgFile, 'image/svg+xml'))
                .reduce((svgFileSymbols, svgFileDocument) => [...svgFileSymbols, ...[].slice.call(svgFileDocument.querySelectorAll('symbol'))], []);
            // Get list of icon names
            // TODO: Check for duplicate icon names
            this.icons = svgSymbols.map((svgSymbol) => svgSymbol.id);
            // Setup icon resources
            this.iconResourcesComponent.setupIconResources(svgSymbols);
            // Return results
            this.iconChangesSource.next(this.icons);
            return this.icons;
        });
    }
    /**
     * Check if an icon with the given name (id) exists
     *
     * @param   iconName - Icon name
     * @returns          - Flag, describing whether the given icons exists or not
     */
    doesIconExist(iconName) {
        return iconName === '' || this.icons.length === 0 || this.icons.indexOf(iconName) !== -1;
    }
    /**
     * Create icon resources component
     *
     * @param componentFactoryResolver - Component factory resolver
     * @param           applicationRef           - Application ref
     * @param                 injector                 - Injector
     * @param              windowRef                - Window Ref
     * @returns                          - Creates icon resources component
     */
    createIconResourcesComponent(componentFactoryResolver, applicationRef, injector, windowRef) {
        // Resolve and create component
        const iconResourcesComponentRef = componentFactoryResolver
            .resolveComponentFactory(DBIconResourcesComponent)
            .create(injector);
        // Attach component to the application
        applicationRef.attachView(iconResourcesComponentRef.hostView);
        // Render component in the DOM
        windowRef.nativeBody.insertBefore(iconResourcesComponentRef.hostView.rootNodes[0], windowRef.nativeBody.firstChild);
        // Return component instance
        return iconResourcesComponentRef.instance;
    }
    /**
     * Fetch an SVG file
     *
     * @param   url - SVG file resource URL
     * @returns     - Promise, resolved with svg file content
     */
    fetchSvgFile(url) {
        return this.http
            .get(url, {
            headers: new HttpHeaders({
                Accept: 'image/svg+xml',
            }),
            responseType: 'text',
        })
            .pipe(catchError((error) => throwError([
            '[DB ICON SERVICE]',
            `An error occured while trying to fetch the SVG file from "${url}".`,
            'Make sure that the URL is correct, and the file exists.',
        ].join(' '))))
            .toPromise();
    }
}
DBIconService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconService, deps: [{ token: DBWindowRef }, { token: i1$1.HttpClient }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Injectable });
DBIconService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: DBWindowRef }, { type: i1$1.HttpClient }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: i0.ComponentFactoryResolver }]; } });

class DevModeService {
    isDevMode() {
        return isDevMode();
    }
}
DevModeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DevModeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DevModeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DevModeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DevModeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * DB Icon Component
 */
class DBIconComponent {
    /**
     * Constructor
     */
    constructor(iconService, elementRef, renderer, changeDetector, devModeService) {
        this.iconService = iconService;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.changeDetector = changeDetector;
        this.devModeService = devModeService;
        // Set aria hidden attribute to true
        renderer.setAttribute(elementRef.nativeElement, 'aria-hidden', 'true');
        this.size = '';
        this.iconName = '';
        this.subscription = null;
    }
    /**
     * On Init Lifecycle Hook
     */
    ngOnInit() {
        // Check that the icon actually exists (DEV-MODE ONLY)
        this.subscription = this.iconService.iconChanges.subscribe((availableIcons) => {
            this.updateIcon();
        });
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        // Cleanup
        if (this.subscription !== null) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }
    /**
     * After View Checked Lifecycle Hook
     */
    ngAfterViewChecked() {
        this.updateIcon();
    }
    /**
     * Get icon URL
     */
    getIconUrl() {
        return `#${this.iconName}`;
    }
    /**
     * Get icon class (for size)
     */
    getIconSizeClass() {
        return this.size !== '' ? `db-icon__icon--${this.size}` : '';
    }
    /**
     * Get icon size
     */
    getCustomSize() {
        return this.size !== 'sm' && this.size !== 'md' && this.size !== 'lg' ? this.size : '';
    }
    /**
     * Update icon
     */
    updateIcon() {
        let currentIconName = '';
        if (this.iconNameElement && this.iconNameElement.nativeElement && this.iconNameElement.nativeElement.textContent) {
            currentIconName = this.iconNameElement.nativeElement.textContent.trim();
        }
        // Update icon (if necessary)
        if (currentIconName !== this.iconName && this.verifyThatIconExists(currentIconName)) {
            // Set classes
            if (this.iconName !== '') {
                this.renderer.removeClass(this.elementRef.nativeElement, `db-icon--${this.iconName}`);
            }
            if (currentIconName !== '') {
                this.renderer.addClass(this.elementRef.nativeElement, `db-icon--${currentIconName}`);
            }
            // Update icons
            this.iconName = currentIconName;
            this.changeDetector.detectChanges();
        }
    }
    /**
     * Verifies that the given icon actually exists.
     * @param iconName name of the icon
     * @returns Boolean value indicating whether the icon exists. Throws in devMode.
     */
    verifyThatIconExists(iconName) {
        if (!this.iconService.doesIconExist(iconName)) {
            if (this.devModeService.isDevMode()) {
                console.warn([
                    '[DB ICON COMPONENT]',
                    `An icon with the name "${iconName}" does not exist.`,
                    'Make sure that the name is correct, or add the missing icon to the SVG sprite.',
                ].join(' '));
            }
            return false;
        }
        return true;
    }
}
DBIconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconComponent, deps: [{ token: DBIconService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: DevModeService }], target: i0.ɵɵFactoryTarget.Component });
DBIconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBIconComponent, selector: "db-icon", inputs: { color: "color", size: "size" }, viewQueries: [{ propertyName: "iconNameElement", first: true, predicate: ["iconName"], descendants: true, static: true }], ngImport: i0, template: "<span #iconName class=\"sr-only\" translate=\"no\">\r\n  <ng-content></ng-content>\r\n</span>\r\n\r\n<!--\r\n  NOTE:\r\n  In IE, SVG elements are focusable by default - thus, we disable this 'feature' here.\r\n  See <https://stackoverflow.com/questions/18646111/disable-onfocus-event-for-svg-element> for details.\r\n-->\r\n<svg\r\n  focusable=\"false\"\r\n  class=\"db-icon__icon\"\r\n  [ngClass]=\"getIconSizeClass()\"\r\n  [style.fill]=\"color\"\r\n  [style.height]=\"getCustomSize()\"\r\n  [style.width]=\"getCustomSize()\">\r\n\r\n  <!--\r\n    NOTE:\r\n    We still use the old 'xlink:href' here because Safari does not support the (much simpler) 'href' attribute yet.\r\n    See <https://stackoverflow.com/questions/43961807/svg-use-not-working-in-safari>) for details.\r\n    Also, we do not use both the 'href' and 'xlink:href' attributes (in order to achieve backwards compatibility) as the W3C HTML5 validator\r\n    will throw an error.\r\n  -->\r\n  <use [attr.xlink:href]=\"getIconUrl()\">\r\n  </use>\r\n\r\n</svg>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-icon', template: "<span #iconName class=\"sr-only\" translate=\"no\">\r\n  <ng-content></ng-content>\r\n</span>\r\n\r\n<!--\r\n  NOTE:\r\n  In IE, SVG elements are focusable by default - thus, we disable this 'feature' here.\r\n  See <https://stackoverflow.com/questions/18646111/disable-onfocus-event-for-svg-element> for details.\r\n-->\r\n<svg\r\n  focusable=\"false\"\r\n  class=\"db-icon__icon\"\r\n  [ngClass]=\"getIconSizeClass()\"\r\n  [style.fill]=\"color\"\r\n  [style.height]=\"getCustomSize()\"\r\n  [style.width]=\"getCustomSize()\">\r\n\r\n  <!--\r\n    NOTE:\r\n    We still use the old 'xlink:href' here because Safari does not support the (much simpler) 'href' attribute yet.\r\n    See <https://stackoverflow.com/questions/43961807/svg-use-not-working-in-safari>) for details.\r\n    Also, we do not use both the 'href' and 'xlink:href' attributes (in order to achieve backwards compatibility) as the W3C HTML5 validator\r\n    will throw an error.\r\n  -->\r\n  <use [attr.xlink:href]=\"getIconUrl()\">\r\n  </use>\r\n\r\n</svg>\r\n" }]
        }], ctorParameters: function () { return [{ type: DBIconService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: DevModeService }]; }, propDecorators: { color: [{
                type: Input
            }], size: [{
                type: Input
            }], iconNameElement: [{
                type: ViewChild,
                args: ['iconName', { static: true }]
            }] } });

/**
 * DB Checkbox Component
 */
class DBCheckboxComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     */
    constructor(changeDetector, checkboxGroupComponent) {
        super(changeDetector);
        this.internalModel = false;
        this.design = 'default';
        this.checkboxGroupComponentRef = checkboxGroupComponent;
    }
    /**
     * On Init Lifecycle Hook - Connect to parent checkbox group if exists
     */
    ngOnInit() {
        if (this.checkboxGroupComponentRef) {
            this.checkboxGroupComponentRef.registerCheckbox(this);
            this.groupName = this.checkboxGroupComponentRef.name;
            this.disabled = this.checkboxGroupComponentRef.disabled;
        }
    }
    /**
     * After View Init Lifecycle Hook - Initialize popover if db-icon exists
     */
    ngAfterViewInit() {
        this.popoverShow = this.popoverDiv != null;
        this.updateView();
    }
    /**
     * On Destroy - Disconnect from parent checkbox group if exists
     */
    ngOnDestroy() {
        if (this.checkboxGroupComponentRef) {
            this.checkboxGroupComponentRef.unregisterCheckbox(this);
        }
    }
    /**
     * Cards design component class
     */
    get cardDesign() {
        return this.design === 'cards';
    }
    /**
     * Handle checkbox click event
     *
     * @param $event - Event object
     */
    onCheckboxClick($event) {
        $event.stopPropagation(); // Prevent the click event from being fired twice
    }
    /**
     * Handle checkbox change event
     */
    onCheckboxChange() {
        this.setInternalModel(!this.model, false, true, true);
        if (this.checkboxGroupComponentRef) {
            this.checkboxGroupComponentRef.updateModelItem({
                model: this.model,
                name: this.name,
            });
        }
    }
    /**
     * Trigger change detection (used by parent checkbox group)
     */
    updateView() {
        this.changeDetector.detectChanges();
    }
}
DBCheckboxComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: DBCheckboxGroupComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component });
DBCheckboxComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBCheckboxComponent, selector: "db-checkbox", inputs: { name: "name", design: "design" }, host: { properties: { "class.db-checkbox-group--cards": "this.cardDesign", "class.db-checkbox-group--single-card": "this.cardDesign" } }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBCheckboxComponent),
        },
    ], queries: [{ propertyName: "popoverDiv", first: true, predicate: DBIconComponent, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<label\r\n  class=\"db-checkbox__label\"\r\n  [class.db-input__label--disabled]=\"disabled\">\r\n\r\n  <input\r\n    #checkBoxRef\r\n    class=\"db-checkbox__input\"\r\n    type=\"checkbox\"\r\n    [disabled]=\"disabled\"\r\n    [checked]=\"model\"\r\n    [value]=\"model\"\r\n    (click)=\"onCheckboxClick( $event )\"\r\n    (change)=\"onCheckboxChange()\">\r\n\r\n  <div class=\"db-checkbox__content\">\r\n\r\n    <svg class=\"db-checkbox__svg\" viewBox=\"0 0 64 64\" focusable=\"false\">\r\n      <path d=\"M56.5 8.68c-1.33.04-2.6.62-3.52 1.6L22.1 42.46 9.03 28.82c-1.3-1.4-3.2-1.94-4.98-1.45-1.8.48-3.2 1.94-3.66 3.8-.47\r\n      1.87.07 3.85 1.4 5.2l16.7 17.4c.96 1 2.27 1.56 3.63 1.56 1.36 0 2.67-.56 3.63-1.57l34.5-35.93c1.5-1.53 1.96-3.87\r\n      1.14-5.9-.83-2.02-2.76-3.32-4.87-3.25z\">\r\n      </path>\r\n    </svg>\r\n\r\n    <span class=\"db-checkbox__content-container\">\r\n      <ng-content>\r\n      </ng-content>\r\n    </span>\r\n\r\n  </div>\r\n</label>\r\n\r\n<div [class.extension--info]=\"extension.children.length\" [class.mx-2]=\"extension.children.length\"  #extension *ngIf=\"popoverShow\">\r\n  <ng-content select=\"[dbPopover][dbPopoverTitle]\">\r\n\r\n  </ng-content>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBCheckboxComponent),
                        },
                    ], selector: 'db-checkbox', template: "<label\r\n  class=\"db-checkbox__label\"\r\n  [class.db-input__label--disabled]=\"disabled\">\r\n\r\n  <input\r\n    #checkBoxRef\r\n    class=\"db-checkbox__input\"\r\n    type=\"checkbox\"\r\n    [disabled]=\"disabled\"\r\n    [checked]=\"model\"\r\n    [value]=\"model\"\r\n    (click)=\"onCheckboxClick( $event )\"\r\n    (change)=\"onCheckboxChange()\">\r\n\r\n  <div class=\"db-checkbox__content\">\r\n\r\n    <svg class=\"db-checkbox__svg\" viewBox=\"0 0 64 64\" focusable=\"false\">\r\n      <path d=\"M56.5 8.68c-1.33.04-2.6.62-3.52 1.6L22.1 42.46 9.03 28.82c-1.3-1.4-3.2-1.94-4.98-1.45-1.8.48-3.2 1.94-3.66 3.8-.47\r\n      1.87.07 3.85 1.4 5.2l16.7 17.4c.96 1 2.27 1.56 3.63 1.56 1.36 0 2.67-.56 3.63-1.57l34.5-35.93c1.5-1.53 1.96-3.87\r\n      1.14-5.9-.83-2.02-2.76-3.32-4.87-3.25z\">\r\n      </path>\r\n    </svg>\r\n\r\n    <span class=\"db-checkbox__content-container\">\r\n      <ng-content>\r\n      </ng-content>\r\n    </span>\r\n\r\n  </div>\r\n</label>\r\n\r\n<div [class.extension--info]=\"extension.children.length\" [class.mx-2]=\"extension.children.length\"  #extension *ngIf=\"popoverShow\">\r\n  <ng-content select=\"[dbPopover][dbPopoverTitle]\">\r\n\r\n  </ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: function () {
        return [{ type: i0.ChangeDetectorRef }, { type: DBCheckboxGroupComponent, decorators: [{
                        type: Optional
                    }] }];
    }, propDecorators: { popoverDiv: [{
                type: ContentChild,
                args: [DBIconComponent, /* static flag */ { static: true }]
            }], name: [{
                type: Input
            }], design: [{
                type: Input
            }], cardDesign: [{
                type: HostBinding,
                args: ['class.db-checkbox-group--cards']
            }, {
                type: HostBinding,
                args: ['class.db-checkbox-group--single-card']
            }] } });

/**
 * DB Checkbox Module
 */
class DBCheckboxModule {
}
DBCheckboxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBCheckboxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxModule, declarations: [DBCheckboxComponent,
        DBCheckboxGroupComponent], imports: [CommonModule], exports: [DBCheckboxComponent,
        DBCheckboxGroupComponent] });
DBCheckboxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBCheckboxComponent,
                        DBCheckboxGroupComponent,
                    ],
                    exports: [
                        DBCheckboxComponent,
                        DBCheckboxGroupComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

/**
 * DB Spinner Component
 */
class DBSpinnerComponent {
    constructor() {
        this.design = 'default';
    }
}
DBSpinnerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBSpinnerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBSpinnerComponent, selector: "db-spinner", inputs: { design: "design" }, ngImport: i0, template: "<ng-container *ngIf=\"design === 'rotating'; else defaultTpl\">\r\n    <svg class=\"db-spinner__rotate\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\" >\r\n        <circle class=\"db-spinner__rotate__path\" fill=\"none\" stroke-linecap=\"butt\" cx=\"50\" cy=\"50\" r=\"40\"></circle>\r\n    </svg>\r\n</ng-container>\r\n<ng-template #defaultTpl>\r\n    <div class=\"db-spinner__item db-spinner__item--1\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--2\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--3\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--4\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--5\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--6\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--7\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--8\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--9\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--10\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--11\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--12\"></div>\r\n</ng-template>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSpinnerComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-spinner', template: "<ng-container *ngIf=\"design === 'rotating'; else defaultTpl\">\r\n    <svg class=\"db-spinner__rotate\" viewBox=\"0 0 100 100\" xmlns=\"http://www.w3.org/2000/svg\" >\r\n        <circle class=\"db-spinner__rotate__path\" fill=\"none\" stroke-linecap=\"butt\" cx=\"50\" cy=\"50\" r=\"40\"></circle>\r\n    </svg>\r\n</ng-container>\r\n<ng-template #defaultTpl>\r\n    <div class=\"db-spinner__item db-spinner__item--1\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--2\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--3\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--4\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--5\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--6\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--7\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--8\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--9\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--10\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--11\"></div>\r\n    <div class=\"db-spinner__item db-spinner__item--12\"></div>\r\n</ng-template>\r\n" }]
        }], propDecorators: { design: [{
                type: Input
            }] } });

/**
 * DB Download Button Component
 */
class DBDownloadButtonComponent {
    /**
     * Constructor
     * @param windowRef - DBWindowRef
     * @param renderer - Renderer2
     * @param changeDetector - ChangeDetectorRef
     */
    constructor(windowRef, renderer, changeDetector) {
        this.source = null;
        this.loadingText = 'Downloading ...';
        this.disabled = false;
        this.downloadStart = new EventEmitter();
        this.downloadSuccess = new EventEmitter();
        this.downloadError = new EventEmitter();
        this.isDownloading = false;
        this.isDownloadSuccess = false;
        this.showDownloadSuccessIcon = false;
        this.registeredFormControlValue = false;
        this.hideDownloadIcon = false;
        this.design = 'secondary';
        this.size = 'md';
        this.windowRef = windowRef;
        this.renderer = renderer;
        this.changeDetector = changeDetector;
    }
    /**
     * Name of the file
     */
    get name() {
        return this.nameInternal;
    }
    set name(value) {
        this.nameInternal = value;
    }
    ngOnInit() {
        this.platform = this.getUserAgent();
    }
    /**
     * Handle click event
     *
     * @param event - Event object
     */
    onClick(event) {
        // Ignore click even when we are still in progress
        if (this.isDownloading) {
            event.stopPropagation();
            event.stopImmediatePropagation();
            return;
        }
        this.downloadFile();
    }
    /**
     * Download the source file
     */
    downloadFile() {
        return __awaiter(this, void 0, void 0, function* () {
            // Skip if no download source has been defined
            if (!this.source) {
                if (isDevMode()) {
                    console.error('[DB DOWNLOAD DIRECTIVE] No download source has been defined.');
                }
                return;
            }
            // Try to download the file
            this.downloadStart.emit(this.source);
            this.isDownloading = true;
            let file;
            try {
                if (typeof this.source === 'string') {
                    // Removes any file extension that starts with a dot (.),
                    // Removes characters that are not alphanumeric, hyphen and underscore,
                    // Finally removes whitespaces
                    let fileSource = '';
                    if (!this.name) {
                        fileSource = this.source;
                    }
                    else {
                        this.name = this.name.replace(/\.[a-zA-Z0-9]+/g, '').replace(/[^a-zA-Z0-9-_]/g, '')
                            .replace(/\s/g, '');
                        // source as string
                        if (this.source.indexOf('?') !== -1) {
                            fileSource = this.source.concat('&name=').concat(this.name);
                        }
                        else {
                            fileSource = this.source.concat('?name=').concat(this.name);
                        }
                    }
                    this.windowRef.nativeWindow.open(fileSource, '_blank');
                    this.isDownloading = false;
                    this.changeDetector.markForCheck();
                    this.downloadSuccess.emit(fileSource);
                    this.isDownloadSuccess = true;
                    return;
                }
                else if (this.source instanceof Blob) {
                    // source as blob
                    file = this.source;
                }
                else {
                    // source as function
                    file = yield this.source();
                }
            }
            catch (error) {
                this.downloadError.emit(error);
                this.isDownloading = false;
                this.isDownloadSuccess = false;
                this.changeDetector.markForCheck();
                if (isDevMode()) {
                    console.error('[DB DOWNLOAD DIRECTIVE] An error occurred during the file download.', error);
                }
                return;
            }
            // Download PDF file (different handling for IE - obviously ...)
            if (this.windowRef.nativeWindow.navigator && this.windowRef.nativeWindow.navigator.msSaveOrOpenBlob) {
                /* istanbul ignore next */
                this.windowRef.nativeWindow.navigator.msSaveOrOpenBlob(file, this.name);
            }
            else if ((!this.platform.isChromeIOS
                && !this.platform.isChromeAndroid
                && this.platform.isMobile
                && this.platform.isSafari)
                || this.platform.isMacintosh) {
                this.windowRef.nativeWindow.open(window.URL.createObjectURL(file));
            }
            else if (this.platform.isChromeIOS) {
                // Chrome iOS
                const reader = new FileReader();
                reader.addEventListener('loadend', () => {
                    this.windowRef.nativeWindow.open(reader.result);
                });
                reader.readAsDataURL(file);
            }
            else {
                const url = window.URL.createObjectURL(file);
                this.renderer.setAttribute(this.linkElement.nativeElement, 'href', url);
                this.linkElement.nativeElement.click(); // Dispatch click event
                this.renderer.removeAttribute(this.linkElement.nativeElement, 'href');
                window.URL.revokeObjectURL(url);
            }
            this.downloadSuccess.emit(file);
            this.isDownloading = false;
            this.isDownloadSuccess = true;
            this.changeDetector.markForCheck();
        });
    }
    /**
     * checks user agent string for mobile/chrome/safari patterns and ios version
     */
    getUserAgent() {
        const userAgent = window.navigator.userAgent;
        const userAgentOptions = userAgent.match(/(Safari|Mobile|CriOS|Chrome|Macintosh)/g);
        const platform = {
            isChromeAndroid: false,
            isChromeIOS: false,
            isMacintosh: false,
            isMobile: false,
            isSafari: false
        };
        if (userAgentOptions) {
            platform.isMacintosh = (userAgentOptions.indexOf('Macintosh') > -1);
            platform.isSafari = (userAgentOptions.indexOf('Safari') > -1);
            platform.isMobile = (userAgentOptions.indexOf('Mobile') > -1);
            platform.isChromeIOS = (userAgentOptions.indexOf('CriOS') > -1);
            platform.isChromeAndroid = (userAgentOptions.indexOf('Chrome') > -1);
        }
        return platform;
    }
}
DBDownloadButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDownloadButtonComponent, deps: [{ token: DBWindowRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBDownloadButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDownloadButtonComponent, selector: "db-download-button", inputs: { source: "source", name: "name", loadingText: "loadingText", disabled: "disabled", showDownloadSuccessIcon: "showDownloadSuccessIcon", registeredFormControlValue: "registeredFormControlValue", design: "design", size: "size", hideDownloadIcon: "hideDownloadIcon", isDownloading: "isDownloading" }, outputs: { downloadStart: "downloadStart", downloadSuccess: "downloadSuccess", downloadError: "downloadError" }, host: { properties: { "class.is-downloading": "this.isDownloading" } }, viewQueries: [{ propertyName: "linkElement", first: true, predicate: ["linkElement"], descendants: true, static: true }], ngImport: i0, template: "<a\r\n  #linkElement\r\n  class=\"db-button db-download-button__link\"\r\n  [ngClass]=\"[ 'db-button--' + design, 'db-button--' + size ]\"\r\n  [download]=\"name\"\r\n  target=\"_blank\"\r\n  rel=\"noopener\"\r\n  [class.disabled]=\"disabled\"\r\n  [class.db-button--disabled]=\"disabled\"\r\n  (click)=\"!disabled && onClick( $event )\">\r\n\r\n  <ng-container *ngIf=\"!isDownloading\">\r\n    <span *ngIf=\"!hideDownloadIcon\">\r\n      <db-icon *ngIf=\"showDownloadSuccessIcon && (isDownloadSuccess || registeredFormControlValue); else downloadIcon\" class=\"db-download-button__icon\" size=\"24px\">check</db-icon>\r\n    </span>\r\n    <span [class.db-download-button__text]=\"!hideDownloadIcon\" class=\"d-flex justify-content-center\"><ng-content></ng-content></span>\r\n  </ng-container>\r\n\r\n  <ng-container *ngIf=\"isDownloading\">\r\n    <db-spinner class=\"db-download-button__spinner\"></db-spinner>\r\n    <span class=\"db-download-button__text d-flex\">{{ loadingText }}</span>\r\n  </ng-container>\r\n\r\n  <ng-template #downloadIcon>\r\n    <db-icon class=\"db-download-button__icon\">download</db-icon>\r\n  </ng-template>\r\n\r\n</a>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBSpinnerComponent, selector: "db-spinner", inputs: ["design"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDownloadButtonComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-download-button', template: "<a\r\n  #linkElement\r\n  class=\"db-button db-download-button__link\"\r\n  [ngClass]=\"[ 'db-button--' + design, 'db-button--' + size ]\"\r\n  [download]=\"name\"\r\n  target=\"_blank\"\r\n  rel=\"noopener\"\r\n  [class.disabled]=\"disabled\"\r\n  [class.db-button--disabled]=\"disabled\"\r\n  (click)=\"!disabled && onClick( $event )\">\r\n\r\n  <ng-container *ngIf=\"!isDownloading\">\r\n    <span *ngIf=\"!hideDownloadIcon\">\r\n      <db-icon *ngIf=\"showDownloadSuccessIcon && (isDownloadSuccess || registeredFormControlValue); else downloadIcon\" class=\"db-download-button__icon\" size=\"24px\">check</db-icon>\r\n    </span>\r\n    <span [class.db-download-button__text]=\"!hideDownloadIcon\" class=\"d-flex justify-content-center\"><ng-content></ng-content></span>\r\n  </ng-container>\r\n\r\n  <ng-container *ngIf=\"isDownloading\">\r\n    <db-spinner class=\"db-download-button__spinner\"></db-spinner>\r\n    <span class=\"db-download-button__text d-flex\">{{ loadingText }}</span>\r\n  </ng-container>\r\n\r\n  <ng-template #downloadIcon>\r\n    <db-icon class=\"db-download-button__icon\">download</db-icon>\r\n  </ng-template>\r\n\r\n</a>\r\n" }]
        }], ctorParameters: function () { return [{ type: DBWindowRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { source: [{
                type: Input
            }], name: [{
                type: Input
            }], loadingText: [{
                type: Input
            }], disabled: [{
                type: Input
            }], showDownloadSuccessIcon: [{
                type: Input
            }], registeredFormControlValue: [{
                type: Input
            }], design: [{
                type: Input
            }], size: [{
                type: Input
            }], hideDownloadIcon: [{
                type: Input
            }], downloadStart: [{
                type: Output
            }], downloadSuccess: [{
                type: Output
            }], downloadError: [{
                type: Output
            }], linkElement: [{
                type: ViewChild,
                args: ['linkElement', { static: true }]
            }], isDownloading: [{
                type: HostBinding,
                args: ['class.is-downloading']
            }, {
                type: Input
            }] } });

/**
 * Icon Initializer Factory
 *
 * @param   injector - Injector
 * @param   iconUrls - Icon URLs
 * @returns          - Factory, resolving asynchronously
 */
function iconInitializerFactory(injector, iconUrls) {
    return () => __awaiter(this, void 0, void 0, function* () {
        const iconService = injector.get(DBIconService); // Prevent cyclic dependencies issues
        yield iconService.use(iconUrls);
    });
}

/**
 * Icon Service Factory
 *
 * @param   parentIconService         - Parent icon service (if exists)
 * @param   windowRef                 - Window Ref dependency
 * @param   http                      - Http dependency
 * @param   injector                  - Injector dependency
 * @param   applicationRef            - Application Ref dependency
 * @param   componentFactoryResolver  - Component Factory Resolver dependency
 * @returns                           - Icon service instance
 */
function iconServiceFactory(parentIconService, windowRef, http, injector, applicationRef, componentFactoryResolver) {
    return parentIconService || new DBIconService(windowRef, http, injector, applicationRef, componentFactoryResolver);
}

// eslint-disable  @typescript-eslint/naming-convention,no-underscore-dangle,id-blacklist,id-match
const DB_ICON_URLS = new InjectionToken('[db-icon-service] urls');
// eslint-enable  @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match

/**
 * DB Icon Module
 */
class DBIconModule {
    /**
     * Activate initializer
     *
     * @param iconUrls - One or multiple icon urls
     */
    static withInitializer(iconUrls) {
        // Push urls
        this.providers.push({
            provide: DB_ICON_URLS,
            useValue: iconUrls,
        });
        // Activate app initializer
        this.providers.push({
            deps: [
                Injector,
                DB_ICON_URLS,
            ],
            multi: true,
            provide: APP_INITIALIZER,
            useFactory: iconInitializerFactory,
        });
    }
}
/**
 * Custom providers
 */
DBIconModule.providers = [];
DBIconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBIconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBIconModule, declarations: [DBIconComponent,
        DBIconResourcesComponent], imports: [CommonModule,
        HttpClientModule,
        DBWindowRefModule], exports: [DBIconComponent] });
DBIconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBIconService],
                DBWindowRef,
                HttpClient,
                Injector,
                ApplicationRef,
                ComponentFactoryResolver,
            ],
            provide: DBIconService,
            useFactory: iconServiceFactory,
        },
    ], imports: [CommonModule,
        HttpClientModule,
        DBWindowRefModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBIconComponent,
                        DBIconResourcesComponent,
                    ],
                    exports: [
                        DBIconComponent,
                    ],
                    imports: [
                        CommonModule,
                        HttpClientModule,
                        DBWindowRefModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBIconService],
                                DBWindowRef,
                                HttpClient,
                                Injector,
                                ApplicationRef,
                                ComponentFactoryResolver,
                            ],
                            provide: DBIconService,
                            useFactory: iconServiceFactory,
                        },
                    ]
                }]
        }] });

/**
 * DB Spinner Module
 */
class DBSpinnerModule {
}
DBSpinnerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBSpinnerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBSpinnerModule, declarations: [DBSpinnerComponent], imports: [CommonModule], exports: [DBSpinnerComponent] });
DBSpinnerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSpinnerModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBSpinnerComponent,
                    ],
                    exports: [
                        DBSpinnerComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

/**
 * DB Download Button Module
 */
class DBDownloadButtonModule {
}
DBDownloadButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDownloadButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDownloadButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDownloadButtonModule, declarations: [DBDownloadButtonComponent], imports: [CommonModule,
        DBSpinnerModule,
        DBIconModule], exports: [DBDownloadButtonComponent] });
DBDownloadButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDownloadButtonModule, imports: [CommonModule,
        DBSpinnerModule,
        DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDownloadButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBDownloadButtonComponent,
                    ],
                    exports: [
                        DBDownloadButtonComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBSpinnerModule,
                        DBIconModule,
                    ],
                }]
        }] });

/**
 * Credit Card Number Formatter
 */
const creditCardFormatter = {
    /**
     * Primary delimiter character
     */
    primaryDelimiter: ' ',
    /**
     * Format CreditCard
     *
     * @param value - Parsed CreditCard
     * @returns       - Formatted CreditCard
     */
    format: (value) => value.replace(new RegExp('(.{4})', 'g'), `$1${creditCardFormatter.primaryDelimiter}`).trim(),
    /**
     * Parse CreditCard
     *
     * @param value - Formatted CreditCard
     * @returns       - Parsed CreditCard
     */
    parse: (value) => value.replace(new RegExp(creditCardFormatter.primaryDelimiter, 'g'), ''),
};

/**
 * DATE Formatter
 */
const dateFormatter = {
    /**
     * Primary delimiter character
     */
    primaryDelimiter: '.',
    /**
     * Format DATE
     *
     * @param value - Parsed DATE
     * @returns      - Formatted DATE
     */
    format: (value) => {
        let tempValue = value.replace(new RegExp(`\\${dateFormatter.primaryDelimiter}`, 'g'), '');
        tempValue = tempValue.substr(0, 4)
            .replace(new RegExp(`(\\d{2})`, 'g'), `$1.`) + tempValue.substr(4);
        return tempValue;
    },
    /**
     * Parse DATE
     *
     * @param value - Formatted DATE
     * @returns      - Parsed DATE
     */
    parse: (value) => 
    // for date formatter, model value should be displayed (formatted) value, example: 10.12.1990
    dateFormatter.format(value),
};

/**
 * DebitCardNumber Formatter
 */
const debitCardFormatter = {
    /**
     * Primary delimiter character
     */
    primaryDelimiter: '/',
    /**
     * Format DebitCardNumber
     * DARSTLLUNG
     * @param value - Parsed DebitCardNumber
     * @returns       - Formatted DebitCardNumber
     */
    format: (value) => value.replace(new RegExp('(.{7})', 'g'), `$1${debitCardFormatter.primaryDelimiter}`),
    /**
     * Parse DebitCardNumber
     *
     * @param value - Formatted DebitCardNumber
     * @returns       - Parsed DebitCardNumber
     */
    parse: (value) => value.replace(new RegExp(debitCardFormatter.primaryDelimiter, 'g'), ''),
};

/**
 * IBAN Formatter
 */
const ibanFormatter = {
    /**
     * Primary delimiter character
     */
    primaryDelimiter: ' ',
    /**
     * Format IBAN
     *
     * @param value - Parsed IBAN
     * @returns       - Formatted IBAN
     */
    format: (value) => value.replace(new RegExp('(.{4})', 'g'), `$1${ibanFormatter.primaryDelimiter}`).toUpperCase(),
    /**
     * Parse IBAN
     *
     * @param value - Fomratted IBAN
     * @returns       - Parsed IBAN
     */
    parse: (value) => value.replace(new RegExp(ibanFormatter.primaryDelimiter, 'g'), '').toUpperCase(),
};

/**
 * Integer Formatter
 */
const integerFormatter = {
    /**
     * Format integer
     *
     * @param value - Parsed number
     * @returns       - Formatted number
     */
    format: (value) => value.replace(/\D+/g, ''),
    /**
     * Parse integer
     *
     * @param value - Formatted number
     * @returns       - Parsed number
     */
    parse: (value) => value.replace(/\D+/g, ''),
};

/**
 * MONTH YEAR Formatter
 */
const monthYearFormatter = {
    /**
     * Primary delimiter character
     */
    primaryDelimiter: '.',
    /**
     * Format MONTH YEAR
     *
     * @param value - Parsed MONTH YEAR
     * @returns      - Formatted MONTH YEAR
     */
    format: (value) => {
        let tempValue = value.replace(new RegExp(`\\${monthYearFormatter.primaryDelimiter}`, 'g'), '');
        tempValue = tempValue.substr(0, 2)
            .replace(new RegExp(`(\\d{2})`, 'g'), `$1.`) + tempValue.substr(2);
        return tempValue;
    },
    /**
     * Parse MONTH YEAR
     *
     * @param value - Formatted DATE
     * @returns      - Parsed DATE
     */
    parse: (value) => 
    // for monthYear formatter, model value should be displayed (formatted) value, example: 12.1990
    monthYearFormatter.format(value),
};

/**
 * Number Formatter
 */
const numberFormatter = {
    /**
     * Delimiters for en and de are defined
     */
    delimiterNumberFormat: {
        en: {
            primaryDelimiter: ',',
            secondaryDelimiter: '.'
        },
        de: {
            primaryDelimiter: '.',
            secondaryDelimiter: ','
        }
    },
    /**
     * Default Primary delimiter character ( for THOUSAND )
     */
    primaryDelimiter: '.',
    /**
     * Default Secondary delimiter character (for DECIMAL )
     */
    secondaryDelimiter: ',',
    /**
     * Negative sign to be used when negatove values are allowed
     */
    negativeSign: '-',
    /**
     * User entered value to be used for formatting
     */
    savedValueToFormat: '',
    /**
     * Set delimiters based on language code
     * @param lang - language code de or en
     */
    setDelimiters: (lang = 'de') => {
        numberFormatter.primaryDelimiter = numberFormatter.delimiterNumberFormat[lang].primaryDelimiter;
        numberFormatter.secondaryDelimiter = numberFormatter.delimiterNumberFormat[lang].secondaryDelimiter;
    },
    /**
     * Format number
     *
     * @param value - Parsed number
     * @param limitDecimalDigits - limits the digits after the comma
     * @returns       - Formatted number
     */
    format: (value, limitDecimalDigits, preventAutomaticDecimals = false, isFocusedOut = false) => {
        let valueAsString = value.toString();
        // Return only negative sign or empty string, if the entered value contains only - or field not in focus to prevent formatting issues
        const enteredValue = (numberFormatter.savedValueToFormat.length !== 0) ? numberFormatter.savedValueToFormat : valueAsString;
        const isNegativeValue = new RegExp(numberFormatter.negativeSign).test(enteredValue);
        if (isNegativeValue) {
            const checkEmptyNegative = enteredValue.match(new RegExp(`^-$`));
            if (checkEmptyNegative) {
                return (isFocusedOut) ? '' : numberFormatter.negativeSign;
            }
        }
        if (isFocusedOut) {
            numberFormatter.savedValueToFormat = '';
        }
        // Early return to prevent formatting issues
        if (valueAsString.length === 0) {
            return valueAsString;
        }
        // Split the enteredValue if preventAutomaticDecimals to find whether secondary delimiter is entered
        if (preventAutomaticDecimals) {
            const splittedValue = enteredValue.split(numberFormatter.secondaryDelimiter);
            valueAsString = (splittedValue.length > 1) ? enteredValue : splittedValue[0];
        }
        // Replace the english decimal separator with german decimal separator and cut multiple occurences (just use the first one)
        const commaSeparator = valueAsString.match(new RegExp(`[^-\\d]`));
        if (!commaSeparator && (!limitDecimalDigits || limitDecimalDigits < 1)) {
            return valueAsString.replace(new RegExp('(\\d)(?=(\\d\\d\\d)+(?!\\d))', 'g'), `$1${numberFormatter.primaryDelimiter}`);
        }
        const commaSeparatorIndex = commaSeparator ? commaSeparator.index : valueAsString.length;
        if (limitDecimalDigits !== null && +limitDecimalDigits === 0) {
            return `${parseInt((valueAsString.substr(0, commaSeparatorIndex) || '0'), 10)}`.replace(new RegExp('(\\d)(?=(\\d\\d\\d)+(?!\\d))', 'g'), `$1${numberFormatter.primaryDelimiter}`);
        }
        let preCommaNumber = `${parseInt((valueAsString.substr(0, commaSeparatorIndex) || '0'), 10)}${numberFormatter.secondaryDelimiter}`;
        // ParseInt truncates - sign when the value is 0, prepend negative sign for this case.
        preCommaNumber = (isNegativeValue && preCommaNumber.indexOf(numberFormatter.negativeSign) === -1)
            ? numberFormatter.negativeSign + preCommaNumber
            : preCommaNumber;
        preCommaNumber = preCommaNumber.replace(new RegExp(`(\\d)(?=(\\d\\d\\d)+([${numberFormatter.secondaryDelimiter}]))`, 'g'), `$1${numberFormatter.primaryDelimiter}`);
        const isDelimiterEntered = valueAsString.indexOf(numberFormatter.secondaryDelimiter) !== -1;
        if (preventAutomaticDecimals && !isDelimiterEntered) {
            preCommaNumber = preCommaNumber.replace(new RegExp(`\\${numberFormatter.secondaryDelimiter}`, 'g'), '');
        }
        let postCommaNumber = valueAsString.substr(commaSeparatorIndex + 1).replace(new RegExp(`[^-\\d]`, 'g'), '');
        if (!preventAutomaticDecimals) {
            if (limitDecimalDigits && limitDecimalDigits < postCommaNumber.length) {
                postCommaNumber = postCommaNumber.substr(0, limitDecimalDigits);
            }
            else if ((limitDecimalDigits > postCommaNumber.length)) {
                while (limitDecimalDigits > postCommaNumber.length) {
                    postCommaNumber += '0';
                }
            }
        }
        else if (preventAutomaticDecimals && limitDecimalDigits && limitDecimalDigits < postCommaNumber.length) {
            postCommaNumber = postCommaNumber.substr(0, limitDecimalDigits);
        }
        return `${preCommaNumber}${postCommaNumber}`;
    },
    /**
     * Parse number
     *
     * @param value - Formatted number
     * @param limitDecimalDigits
     * @returns       - Parsed number
     */
    parse: (value, limitDecimalDigits, allowNegative, delimiterNumberFormat = 'de') => {
        let valueAsString = value.toString();
        let finalString = '';
        const isNegativeValue = (allowNegative && valueAsString.indexOf(numberFormatter.negativeSign) !== -1);
        // Add zero before delimiter if there is no number
        valueAsString = (valueAsString[0] === numberFormatter.secondaryDelimiter
            || (isNegativeValue && valueAsString[1] === numberFormatter.secondaryDelimiter))
            ? checkNumberAfterDecimalPoint(valueAsString)
            : valueAsString;
        // Allow negative sign if allowNegative property is true
        const removeUnwantedChar = (allowNegative)
            ? `[^-?0-9${numberFormatter.secondaryDelimiter}]+`
            : `[^?0-9${numberFormatter.secondaryDelimiter}]+`;
        const secondCharacter = valueAsString.charAt(1);
        let formattedValue = valueAsString
            // Remove unwanted characters (non numerical) in form value
            .replace(new RegExp(`${removeUnwantedChar}`, 'g'), '')
            // Do not allow negative sign to be entered apart from the beginning of the string
            .replace(new RegExp(`\\-`, 'g'), (negativeChar, index) => index === 0 ? negativeChar : '')
            // Do not allow the user to enter the thoudans separator, it should be auto-formatted
            .replace(new RegExp(`\\${numberFormatter.primaryDelimiter}`, 'g'), '')
            // Remove leading zero from two digit string
            .replace(new RegExp('^0+([0-9])'), secondCharacter)
            // Remove leading zero from negative two digit string
            .replace(new RegExp('^-0+([0-9])'), secondCharacter);
        numberFormatter.savedValueToFormat = formattedValue;
        // Return empty if the entered value is only '-'
        if (isNegativeValue && valueAsString.length === 1) {
            return '';
        }
        // Replace the german decimal separator with english decimal separator to keep the model correct if delimiterNumberFormat is de
        if (delimiterNumberFormat !== 'en') {
            formattedValue = formattedValue.replace(new RegExp(`\\${numberFormatter.secondaryDelimiter}`, 'g'), `${numberFormatter.primaryDelimiter}`);
        }
        // Store delimiter based on delimiterNumberFormat for formatting decimal points
        const modelDelimiter = (delimiterNumberFormat === 'de')
            ? numberFormatter.primaryDelimiter
            : numberFormatter.secondaryDelimiter;
        if (limitDecimalDigits !== null) {
            const split = formattedValue.split(modelDelimiter);
            if (+limitDecimalDigits === 0) {
                return split[0];
            }
            if (split.length >= 2) {
                const formattedDecimals = split[1].substr(0, limitDecimalDigits)
                    .replace(new RegExp(`[^-\\d]`, 'g'), '');
                finalString = `${split[0]}${modelDelimiter}${formattedDecimals}`;
                return formatDecimalDigits(finalString, limitDecimalDigits, modelDelimiter);
            }
            else if (formattedValue !== '') {
                return `${split[0]}${modelDelimiter}${appendMissingZeros(limitDecimalDigits)}`;
            }
        }
        return formattedValue;
    },
};
const appendMissingZeros = (limitDecimalDigits) => {
    let fillingZeros = '';
    for (let i = 0; i < limitDecimalDigits; i++) {
        fillingZeros += '0';
    }
    return fillingZeros;
};
/**
 * Check if number after decimal point is greater than zero otherwise
 * return empty string
 * @param valueAsString
 */
const checkNumberAfterDecimalPoint = (valueAsString) => parseInt(valueAsString.split(',')[1], 10) > 0 ? `0${valueAsString}` : '';
/**
 * Format digits after decimal points, Adds number of zeros
 * equal to decimal digit limit(eg: 12345,0 => 12345,00  if limitDecimal digit is 2)
 * @param finalString
 * @param limitDecimalDigits
 */
const formatDecimalDigits = (finalString, limitDecimalDigits, modelDelimiter) => {
    let postCommaNumber = finalString.split(modelDelimiter)[1];
    if (limitDecimalDigits && limitDecimalDigits < postCommaNumber.length) {
        postCommaNumber = postCommaNumber.substr(0, limitDecimalDigits);
        finalString =
            `${finalString.split(modelDelimiter)[0]}${modelDelimiter}${postCommaNumber}`;
    }
    else if (limitDecimalDigits && limitDecimalDigits > postCommaNumber.length) {
        while (limitDecimalDigits > postCommaNumber.length) {
            postCommaNumber += '0';
        }
        finalString =
            `${finalString.split(modelDelimiter)[0]}${modelDelimiter}${postCommaNumber}`;
    }
    return finalString;
};

/**
 * Phone Number Formatter
 */
const phoneNumberFormatter = {
    /**
     * Primary delimiter character
     */
    primaryDelimiter: ' ',
    /**
     * Format Phone Number
     *
     * @param value - Parsed Phone Number
     * @returns      - Formatted Phone Number
     */
    format: (value) => value.indexOf('+') > -1 ?
        value.replace(/^(\+\d{1,2})(\d{0,3})(\d{0,})/, `$1${phoneNumberFormatter.primaryDelimiter}$2${phoneNumberFormatter.primaryDelimiter}$3`) :
        value.replace(/^(\d{1,2})(\d{0,3})(\d{0,})/, `+$1${phoneNumberFormatter.primaryDelimiter}$2${phoneNumberFormatter.primaryDelimiter}$3`),
    /**
     * Parse Phone Number
     *
     * @param value - Formatted Phone Number
     * @returns      - Parsed Phone Number
     */
    parse: (value) => value.replace(/[^+\d]/g, ''),
};

/**
 * SealOne ID Formatter
 */
const sealOneIdFormatter = {
    /**
     * Primary delimiter character
     */
    primaryDelimiter: '-',
    /**
     * Format SealOne ID
     *
     * @param value - Parsed SealOne ID
     * @returns       - Formatted SealOne ID
     */
    format: (value) => value.replace(new RegExp('(.{5})', 'g'), `$1${sealOneIdFormatter.primaryDelimiter}`).toUpperCase().substr(0, 17),
    /**
     * Parse SealOne ID
     *
     * @param value - Fomratted SealOne ID
     * @returns       - Parsed SealOne ID
     */
    parse: (value) => value.replace(new RegExp('[^0-9ABCDEFGHJKLNPQRSTUVXYZ]', 'gi'), '').toUpperCase(),
};

/**
 * DB Formatters
 */
class DBFormatters {
}
DBFormatters.iban = ibanFormatter;
DBFormatters.integer = integerFormatter;
DBFormatters.number = numberFormatter;
DBFormatters.date = dateFormatter;
DBFormatters.monthYear = monthYearFormatter;
DBFormatters.debitCardNumber = debitCardFormatter;
DBFormatters.phoneNumber = phoneNumberFormatter;
DBFormatters.creditCardNumber = creditCardFormatter;
DBFormatters.sealOneId = sealOneIdFormatter;

/**
 * DB Input Formatter Directive
 */
class DBInputFormatDirective {
    /**
     * Constructor
     */
    constructor() {
        /**
         * Limits the digits after the comma
         */
        this.limitDecimalDigits = null;
        /**
         * Allows negative value
         */
        this.allowNegative = false;
        /**
         * Restrict adding zeroes automatically
         */
        this.preventAutomaticDecimals = false;
        /**
         * Delimiter format for formatting
         */
        this.delimiterNumberFormat = 'de';
        this.format = null;
        this.currentModel = '';
        this.currentDeleteCorrection = 0;
        this.currentKey = null;
        this.currentCursorPosition = 0;
        this.currentDelimitersBeforeCursorPosition = 0;
        this.inputComponent = null;
    }
    /**
     * Format rule
     */
    set format(format) {
        if (format) {
            // Verify that formatter actually exists
            if (!DBFormatters.hasOwnProperty(format)) {
                throw new Error([
                    '[DB INPUT FORMAT DIRECTIVE]',
                    `A formatter named "${format}" does not exist.`,
                    'Formatters must be defined in the "DBFormatters" class.',
                ].join(' '));
            }
            // Save format rule and generate the regexp for delimiters (only once, #perfmatters)
            this.internalFormat = format;
            const delimiter = DBFormatters[format].primaryDelimiter
                ? DBFormatters[format].primaryDelimiter.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&')
                : '';
            this.delimiterRegex = new RegExp(`[${delimiter}]`, 'g');
        }
    }
    get format() {
        return this.internalFormat;
    }
    ngOnInit() {
        if (this.internalFormat && this.internalFormat === 'number') {
            DBFormatters[this.internalFormat].setDelimiters(this.delimiterNumberFormat);
            const delimiter = DBFormatters[this.internalFormat].primaryDelimiter
                ? DBFormatters[this.internalFormat].primaryDelimiter.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&')
                : '';
            this.delimiterRegex = new RegExp(`[${delimiter}]`, 'g');
        }
    }
    /**
     * Mark which key we are using for the next change
     *
     * @param key - Key type
     */
    markKey(key) {
        this.currentKey = key;
    }
    /**
     * Prepare for future parsing; we correct the user interaction and save values necessary for re-positioning the cursor later on.
     *
     * @param value          - Original (rendered) input value
     * @param cursorPosition - Current cursor (caret) position
     * @returns                - Prepared (possibly corrected) value, ready to be parsed
     */
    prepareParse(value, cursorPosition) {
        // Save the current cursor position (needed later on when calculating the new cursor position)
        this.currentCursorPosition = cursorPosition;
        // Save the number of delimiters occuring before the cursor (needed later on when calculating the new cursor position)
        this.currentDelimitersBeforeCursorPosition =
            (value
                .substring(0, this.currentCursorPosition)
                .match(this.delimiterRegex) || []).length;
        // Correct the delete action if the deleted character was a delimiter instead of an actual value
        // If the deleted character was 'just' a delimiter, delete the actual character to be deleted as well
        let preparedValue = value;
        if (this.currentModel[this.currentCursorPosition] === DBFormatters[this.internalFormat].secondaryDelimiter
            && !this.preventAutomaticDecimals) {
            preparedValue = this.prepareSecondaryDelimiterDelete(value, cursorPosition);
        }
        else if (this.currentModel[this.currentCursorPosition] === DBFormatters[this.internalFormat].primaryDelimiter) {
            preparedValue = this.preparePrimaryDelimiterDelete(value, cursorPosition);
        }
        // Update current model and model length diff (needed later on when calculating the new cursor position)
        this.currentModel = value;
        this.currentDeleteCorrection = preparedValue.length - value.length;
        return preparedValue;
    }
    /**
     * Complete the rendering; we re-position the carret and reset the previously saved values.
     *
     * @param value - Final value
     * @returns       - New cursor (caret) position
     */
    completeRender(value) {
        // Calculate the new cursor position (depending on the key action)
        const newCursorPosition = this.currentKey === 'Delete'
            ? this.currentCursorPosition
            : this.currentCursorPosition
                // Get the difference of delimiters before and after formatting (always before the cursor position) - except we deleted
                + (this.currentKey !== 'Backspace'
                    ? (value
                        .substring(0, this.currentCursorPosition + value.length - this.currentModel.length)
                        .match(this.delimiterRegex) || []).length - this.currentDelimitersBeforeCursorPosition
                    : 0)
                // Add additional correction resulting from the preparation phase (e.g. deletion correction)
                + this.currentDeleteCorrection;
        // Reset / update values
        this.currentKey = null;
        this.currentDeleteCorrection = 0;
        this.currentDelimitersBeforeCursorPosition = 0;
        this.currentModel = value;
        return newCursorPosition;
    }
    /**
     * Do format a value
     *
     * @param value - Parsed value
     * @param limitDecimalDigits
     * @returns       - Formatted value
     */
    doFormat(value) {
        return this.format === 'number'
            ? DBFormatters[this.format].format(value, this.limitDecimalDigits, (this.inputComponent.isFocused) ? this.preventAutomaticDecimals : false, !this.inputComponent.isFocused)
            : DBFormatters[this.format].format(value);
    }
    /**
     * Do parse a value
     *
     * @param value - Formatted value
     * @param limitDecimalDigits
     * @returns       - Parsed value
     */
    doParse(value) {
        return DBFormatters[this.format].parse(value, this.limitDecimalDigits, this.allowNegative, this.delimiterNumberFormat, !this.inputComponent.isFocused);
    }
    /**
     * Correct the delete action if the deleted character was a Secondary delimiter instead of an actual value
     * If the deleted character was 'just' a delimiter, delete the actual character to be deleted as well
     * @param value - Formatted value
     * @param cursorPosition
     * @returns     - Parsed string
     */
    prepareSecondaryDelimiterDelete(value, cursorPosition) {
        let parsedValue = value;
        if (this.currentKey === 'Backspace') {
            parsedValue =
                `${value.slice(0, cursorPosition - 1)}${DBFormatters[this.internalFormat].secondaryDelimiter}${value.slice(cursorPosition)}`;
            // to position cursor on removal of delimiter
        }
        else if (this.currentKey === 'Delete') {
            parsedValue =
                `${value.slice(0, cursorPosition)}${DBFormatters[this.internalFormat].secondaryDelimiter}${value.slice(this.currentCursorPosition + 1)}`;
        }
        return parsedValue;
    }
    /**
     * Correct the delete action if the deleted character was a Primary delimiter instead of an actual value
     * If the deleted character was 'just' a delimiter, delete the actual character to be deleted as well
     *
     * @param value - Formatted value
     * @param cursorPosition
     * @returns     - Parsed string
     */
    preparePrimaryDelimiterDelete(value, cursorPosition) {
        let parsedValue = value;
        if (this.currentKey === 'Backspace') {
            parsedValue = `${value.slice(0, cursorPosition - 1)}${value.slice(cursorPosition)}`;
        }
        else if (this.currentKey === 'Delete') {
            parsedValue = `${value.slice(0, cursorPosition)}${value.slice(cursorPosition + 1)}`;
        }
        return parsedValue;
    }
}
DBInputFormatDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputFormatDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DBInputFormatDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBInputFormatDirective, selector: "db-input[format]", inputs: { format: "format", limitDecimalDigits: "limitDecimalDigits", allowNegative: "allowNegative", preventAutomaticDecimals: "preventAutomaticDecimals", delimiterNumberFormat: "delimiterNumberFormat" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputFormatDirective, decorators: [{
            type: Directive,
            args: [{
                    /* eslint-disable @angular-eslint/directive-selector */
                    selector: 'db-input[format]',
                    /* eslint-enable @angular-eslint/directive-selector */
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { format: [{
                type: Input
            }], limitDecimalDigits: [{
                type: Input
            }], allowNegative: [{
                type: Input
            }], preventAutomaticDecimals: [{
                type: Input
            }], delimiterNumberFormat: [{
                type: Input
            }] } });

/**
 * DB Input Component
 */
class DBInputComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param renderer        - Renderer
     * @param changeDetector  - Change detector
     * @param formatDirective - Format directive (optional)
     */
    constructor(renderer, changeDetector, formatDirective) {
        super(changeDetector);
        /**
         * Decides if a text get displayed in extension instead of icon
         */
        this.showExtensionText = false;
        /**
         * Value to calculate the counter length when formatter is used
         */
        this.formattedValue = '';
        this.autocomplete = null; // Not used by default
        this.focusOut = new EventEmitter();
        this.internalModel = '';
        this.isFocused = false;
        this.label = '';
        this.loading = false;
        this.max = null; // Not used by default
        this.maxlength = null; // Not used by default
        this.min = null; // Not used by default
        this.name = null; // Not used by default
        this.placeholder = null; // Not used by default
        this.renderer = renderer;
        this.showCounter = false;
        this.showStepper = true;
        this.step = null; // Not used by default
        this.type = 'text';
        this.inputmode = '';
        this.spellcheck = false;
        // Setup formatter
        if (formatDirective) {
            this.formatter = formatDirective;
            formatDirective.inputComponent = this;
        }
    }
    /**
     * Cursor position (used by formatting functionality)
     */
    set cursorPosition(cursorPosition) {
        if (this.inputElement) {
            this.inputElement.nativeElement.setSelectionRange(cursorPosition, cursorPosition);
        }
    }
    get cursorPosition() {
        return this.inputElement
            ? this.inputElement.nativeElement.selectionStart
            : 0;
    }
    /**
     * After View Init
     */
    ngAfterViewInit() {
        // We assign each label-input combination a unique ID - in addition to the implicit relationship those elements already have due to the
        // nesting. This way, we can ensure the highest #accessibility compatibility.
        // See: https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/label-has-for.md
        const id = `db-input--${DBInputComponent.id}`;
        // We use the renderer here to achieve a one-time binding (#perfmatters)
        this.renderer.setAttribute(this.inputElement.nativeElement, 'id', id);
        this.renderer.setAttribute(this.labelElement.nativeElement, 'for', id);
        DBInputComponent.id++;
    }
    /**
     * Handle input keydown event
     *
     * @param key - Key code
     */
    onInputKeydown(key) {
        if (this.formatter && !!this.formatter.format) {
            // We use the "KeyboardEvent.key" property to obtain the pressed key; it seems to be the most browser-compatible solution
            // See <https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key>
            this.formatter.markKey(key);
        }
    }
    /**
     * Handle input change event
     *
     * @param value - Model value
     */
    onInputInput(value) {
        if (!this.isValidInputEvent(value)) { // --> Breaks unit tests if they don't call focus first !
            return;
        }
        if (this.formatter && !!this.formatter.format) {
            // Prepare, then parse the model value
            const preparedValue = this.formatter.prepareParse(value, this.cursorPosition);
            const parsedValue = this.formatter.doParse(preparedValue);
            // Update the model with the trimmed & parsed value, but render the non-trimmed formatted version
            this.setInternalModel(parsedValue, false, true, true);
            this.doRender(parsedValue);
        }
        else {
            this.setInternalModel(value, false, true, true);
        }
    }
    /**
     * Handle input focus event
     */
    onInputFocus() {
        this.isFocused = true;
    }
    /**
     * Handle input blur event
     */
    onInputBlur() {
        this.isFocused = false;
        this.doRender(this.internalModel, false); // Re-render normalized model
        this.focusOut.emit(this.model);
        this.propagateTouched(this.model);
    }
    /**
     * Check if input is currently being used (by the user, by the implementation)
     */
    isUsed() {
        return this.isFocused || !!this.placeholder || !!this.internalModel;
    }
    /**
     * Get current model length (for the counter).
     * Returns internalModel / formattedValue based on whether a formatter is used or not
     * @returns - Model length
     */
    getModelLength() {
        if (this.formattedValue) {
            return this.formattedValue.length;
        }
        return !!this.internalModel ? this.internalModel.length : 0;
    }
    /**
     * Apply formatter manually (used in dynamic forms)
     *
     * @param format - Formatter to apply
     */
    setFormatter(format, options) {
        // Set formatter
        this.formatter = new DBInputFormatDirective();
        this.formatter.inputComponent = this;
        this.formatter.format = format;
        // Apply options
        if (options && options.limitDecimalDigits !== void 0) {
            this.formatter.limitDecimalDigits = options.limitDecimalDigits;
        }
    }
    /**
     * Do render the new input value
     *
     * @param value - New value to render into the input field
     */
    doRender(value, setCursor = true) {
        if (this.formatter && !!this.formatter.format) {
            const formattedValue = this.formatter.doFormat(value);
            this.renderer.setProperty(this.inputElement.nativeElement, 'value', formattedValue);
            if (setCursor) {
                this.cursorPosition = this.formatter.completeRender(formattedValue);
            }
            else {
                this.formatter.completeRender(formattedValue);
            }
            this.formattedValue = formattedValue;
        }
        else if (this.inputElement) {
            this.renderer.setProperty(this.inputElement.nativeElement, 'value', value);
        }
    }
    /**
     * Normalizes the input value
     *
     * @param value - Value to normalize
     */
    doNormalize(value) {
        return (value === null || value === undefined) ? '' : value.toString().trim(); // Always convert to string and trim
    }
    /**
     * Set internal model, probably with normalization, and return a flag describing whether the value has actually changed
     *
     * @param value      - Value
     * @param doRender   - Flag, describing whether the rendered value has to be updated
     * @param emitEvent  - Flag, describing whether the model change event has to be emitted
     * @param emitChange - Flag, describing whether the Angular Forms propagate change method has to be called
     */
    setInternalModel(value, doRender, emitEvent, emitChange) {
        super.setInternalModel(value, doRender, emitEvent, emitChange);
    }
    /**
     * This function prevents bugs only caused in Internet Explorer 11.
     * Checks if input event was caused by an actual change or placeholder change.
     * @param value - event value
     */
    isValidInputEvent(value) {
        // Bad event since input change was fired but input was not focused yet.
        // Caused by setting placeholder.
        // Except flatpicker-input since the value is set from an overlay without focus on the input field.
        if (!this.isFocused && !this.inputElement.nativeElement.classList.contains('flatpickr-input')) {
            return false;
        }
        // Bad event since input had empty / invalid value before and after event.
        // Caused by focusing and blurring input when placeholder is set.
        if (!value && !this.internalModel) {
            return false;
        }
        return true;
    }
}
/**
 * Unique ID
 */
DBInputComponent.id = 0;
DBInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: DBInputFormatDirective, optional: true }], target: i0.ɵɵFactoryTarget.Component });
DBInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBInputComponent, selector: "db-input", inputs: { type: "type", name: "name", autocomplete: "autocomplete", spellcheck: "spellcheck", label: "label", placeholder: "placeholder", maxlength: "maxlength", min: "min", max: "max", step: "step", inputmode: "inputmode", loading: "loading", showExtensionText: "showExtensionText", showStepper: "showStepper", showCounter: "showCounter" }, outputs: { focusOut: "focusOut" }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBInputComponent),
        },
    ], viewQueries: [{ propertyName: "inputElement", first: true, predicate: ["inputElement"], descendants: true, static: true }, { propertyName: "extension", first: true, predicate: ["extension"], descendants: true, static: true }, { propertyName: "labelElement", first: true, predicate: ["labelElement"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<label\r\n  #labelElement\r\n  class=\"db-input__label\"\r\n  [style.margin.px]=\"type === 'hidden' ? 0: undefined\"\r\n  [class.db-input__label--disabled]=\"disabled\"\r\n  [class.db-input__label--focus]=\"isFocused\"\r\n  [class.db-input__label--readonly]=\"readonly\">\r\n\r\n  <span class=\"db-input__label-text\" [class.db-input__label-text--small]=\"isUsed()\" [class.d-none]=\"type === 'hidden'\">{{ label }}</span>\r\n\r\n  <!--\r\n    NOTE:\r\n    For native HTML elements, we always prefer bindings using the '.attr' prefix. Doing so leads to attributes which are binded to the value\r\n    'null' getting removed (instead getting set to the value 'null' or an empty string). Eventually, this also prevents W3C HTML validation\r\n    issues (some attributes must not have an empty value).\r\n  -->\r\n  <input\r\n    #inputElement\r\n    class=\"db-input__field\"\r\n    [class.db-input__field--extension]=\"extension.children.length\"\r\n    [class.db-input__field--no-stepper]=\"!showStepper\"\r\n    [attr.autocomplete]=\"autocomplete\"\r\n    [attr.spellcheck]=\"spellcheck\"\r\n    [attr.max]=\"max\"\r\n    [attr.maxlength]=\"maxlength\"\r\n    [attr.min]=\"min\"\r\n    [attr.name]=\"name\"\r\n    [attr.placeholder]=\"placeholder\"\r\n    [attr.step]=\"step\"\r\n    [attr.type]=\"type\"\r\n    [attr.inputmode]=\"inputmode\"\r\n    [disabled]=\"disabled\"\r\n    [readonly]=\"readonly\"\r\n    (focus)=\"onInputFocus()\"\r\n    (blur)=\"onInputBlur()\"\r\n    (input)=\"onInputInput( $event.target.value )\"\r\n    (keydown)=\"onInputKeydown( $event.key )\">\r\n\r\n  <span class=\"db-input__label-counter\" *ngIf=\"showCounter && !readonly\">{{ getModelLength() }} / {{ maxlength }}</span>\r\n\r\n</label>\r\n\r\n<div [class.db-input__extension]=\"extension.children.length || loading\"\r\n     [class.db-input__extension--text]=\"showExtensionText && extension.children.length\"\r\n     #extension>\r\n  <db-spinner class=\"db-input__extension__loading\" *ngIf=\"loading\"></db-spinner>\r\n  <ng-content></ng-content>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBSpinnerComponent, selector: "db-spinner", inputs: ["design"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBInputComponent),
                        },
                    ], selector: 'db-input', template: "<label\r\n  #labelElement\r\n  class=\"db-input__label\"\r\n  [style.margin.px]=\"type === 'hidden' ? 0: undefined\"\r\n  [class.db-input__label--disabled]=\"disabled\"\r\n  [class.db-input__label--focus]=\"isFocused\"\r\n  [class.db-input__label--readonly]=\"readonly\">\r\n\r\n  <span class=\"db-input__label-text\" [class.db-input__label-text--small]=\"isUsed()\" [class.d-none]=\"type === 'hidden'\">{{ label }}</span>\r\n\r\n  <!--\r\n    NOTE:\r\n    For native HTML elements, we always prefer bindings using the '.attr' prefix. Doing so leads to attributes which are binded to the value\r\n    'null' getting removed (instead getting set to the value 'null' or an empty string). Eventually, this also prevents W3C HTML validation\r\n    issues (some attributes must not have an empty value).\r\n  -->\r\n  <input\r\n    #inputElement\r\n    class=\"db-input__field\"\r\n    [class.db-input__field--extension]=\"extension.children.length\"\r\n    [class.db-input__field--no-stepper]=\"!showStepper\"\r\n    [attr.autocomplete]=\"autocomplete\"\r\n    [attr.spellcheck]=\"spellcheck\"\r\n    [attr.max]=\"max\"\r\n    [attr.maxlength]=\"maxlength\"\r\n    [attr.min]=\"min\"\r\n    [attr.name]=\"name\"\r\n    [attr.placeholder]=\"placeholder\"\r\n    [attr.step]=\"step\"\r\n    [attr.type]=\"type\"\r\n    [attr.inputmode]=\"inputmode\"\r\n    [disabled]=\"disabled\"\r\n    [readonly]=\"readonly\"\r\n    (focus)=\"onInputFocus()\"\r\n    (blur)=\"onInputBlur()\"\r\n    (input)=\"onInputInput( $event.target.value )\"\r\n    (keydown)=\"onInputKeydown( $event.key )\">\r\n\r\n  <span class=\"db-input__label-counter\" *ngIf=\"showCounter && !readonly\">{{ getModelLength() }} / {{ maxlength }}</span>\r\n\r\n</label>\r\n\r\n<div [class.db-input__extension]=\"extension.children.length || loading\"\r\n     [class.db-input__extension--text]=\"showExtensionText && extension.children.length\"\r\n     #extension>\r\n  <db-spinner class=\"db-input__extension__loading\" *ngIf=\"loading\"></db-spinner>\r\n  <ng-content></ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: function () {
        return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: DBInputFormatDirective, decorators: [{
                        type: Optional
                    }] }];
    }, propDecorators: { type: [{
                type: Input
            }], name: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], spellcheck: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], step: [{
                type: Input
            }], inputmode: [{
                type: Input
            }], loading: [{
                type: Input
            }], showExtensionText: [{
                type: Input
            }], showStepper: [{
                type: Input
            }], showCounter: [{
                type: Input
            }], focusOut: [{
                type: Output
            }], inputElement: [{
                type: ViewChild,
                args: ['inputElement', { static: true }]
            }], extension: [{
                type: ViewChild,
                args: ['extension', { static: true }]
            }], labelElement: [{
                type: ViewChild,
                args: ['labelElement', { static: true }]
            }] } });

/**
 * DB Input Date Picker Directive
 */
class DBInputDateDirective {
    /**
     * Constructor
     * @param element - The element reference
     * @param renderer - Angular Renderer2 reference
     */
    constructor(element, renderer, deepmerge) {
        this.element = element;
        this.renderer = renderer;
        this.deepmerge = deepmerge;
        this.calendarDiv = null;
        /**
         * Directive Configuration
         * Show Month Year Selection for flatpicker
         */
        this.showMonthYearSelection = false;
        /**
         * Set default language for flatpicker
         */
        this.flatpickrLanguage = German;
        /**
         * Check for key press event
         */
        this.isKeyDown = false;
        // Default config for date picker
        this.internalDateConfig = {
            allowInput: true,
            dateFormat: ''
        };
        // Default config for Month Year picker
        this.internalMonthYearConfig = {
            altInput: true,
            altFormat: 'F Y',
            disableMobile: true
        };
    }
    /**
     * Directive Configuration
     * Allow to merge the partial date configuration ( if passed ) with the main configuration content
     */
    set dbDateTimeConfig(partialConfig) {
        if (partialConfig) {
            this.internalDateConfig = this.deepmerge.merge(this.internalDateConfig || [], partialConfig);
        }
    }
    get dbDateTimeConfig() {
        return this.internalDateConfig;
    }
    /**
     * Directive Configuration
     * Allow to merge the partial month and year configuration ( if passed ) with the main configuration content
     */
    set dbMonthYearConfig(partialConfig) {
        if (partialConfig) {
            this.internalMonthYearConfig = this.deepmerge.merge(this.internalMonthYearConfig, partialConfig);
        }
    }
    get dbMonthYearConfig() {
        return this.internalMonthYearConfig;
    }
    /**
     * Directive Configuration
     * Set language to customize flatpickr instance
     */
    set dbDatePickerLanguage(lang) {
        this.flatpickrLanguage = lang === 'en' ? english : German;
    }
    /**
     * Called when value changes of input field
     * @param event
     */
    onValueChange(event) {
        if (this.isKeyDown && !this.flatPicker.isMobile) {
            this.setDatePickerValue(event.target.value);
        }
    }
    /**
     * Listens for the modelChange event and compares the model value with the calendar value.
     * Updates the flatpickr calendar with the new selected value to show the proper date.
     * This is needed when the date is used inside a form and the value is set using setValue or patchValue.
     * @param event
     */
    onModelChange(value) {
        if (!this.isKeyDown && this.flatPicker && value) {
            const selectedDate = (this.flatPicker.latestSelectedDateObj) ?
                this.flatPicker.formatDate(this.flatPicker.latestSelectedDateObj, this.dbDateTimeConfig.dateFormat) :
                null;
            if (selectedDate && selectedDate !== value) {
                this.setDatePickerValue(value);
            }
        }
    }
    /**
     * Called on key down of key board
     * @param event - keyboard event
     */
    onKeyDown(event) {
        // close popup when user manually enters value but restricting to close on arrow keys to keep keyboard accessibility
        if (event && (event.key !== 'ArrowUp' && event.key !== 'ArrowDown' && event.key !== 'ArrowRight' && event.key !== 'ArrowLeft')) {
            this.flatPicker.close();
        }
        if (event && (event.key !== 'Backspace' && event.key !== 'Delete')) {
            this.isKeyDown = true;
        }
    }
    /**
     * contextmenu
     * @param event - Mouse event
     */
    onMouseRightClick(event) {
        // do not show date picker on right click
        this.flatPicker.close();
    }
    /**
     * After View Init Lifecycle Hook
     *
     * Sets the date picker localization via language input
     * Register flatpickr on the db-input element
     */
    ngAfterViewInit() {
        if (this.calendarDiv != null) {
            this.dbDateTimeConfig = { wrap: true };
        }
        /* Setting format based on language, add check for dateFormat to avoid overriding
        existing dateFormat set in input through configuration for time and hours */
        if (!this.dbDateTimeConfig.dateFormat) {
            if (this.dbDateTimeConfig.enableTime) {
                this.dbDateTimeConfig.dateFormat = this.flatpickrLanguage === German ? 'd.m.Y H:i' : 'm/d/Y H:i';
            }
            else {
                this.dbDateTimeConfig.dateFormat = this.flatpickrLanguage === German ? 'd.m.Y' : 'm/d/Y';
            }
        }
        flatpickr.localize(this.flatpickrLanguage);
        // Add monthSelectPlugin to monthYearConfig with the required date format based on language selected
        if (this.dbDateTimeConfig.wrap || this.dbMonthYearConfig.wrap) {
            this.renderer.addClass(this.element.nativeElement, 'flatpickr');
        }
        if (this.showMonthYearSelection) {
            const defaultDateFormat = (this.flatpickrLanguage === German) ? 'm.Y' : 'm/Y';
            const monthYearConfigDateFormat = (this.dbMonthYearConfig.dateFormat) ? this.dbMonthYearConfig.dateFormat : defaultDateFormat;
            this.dbMonthYearConfig.plugins = [
                monthSelectPlugin({ dateFormat: monthYearConfigDateFormat, altFormat: this.dbMonthYearConfig.altFormat })
            ];
            // Create flatPicker instance by passing monthYearConfig
            const element = (this.dbMonthYearConfig.wrap) ? this.element.nativeElement :
                this.element.nativeElement.firstElementChild.childNodes[1];
            this.flatPicker = flatpickr(element, this.dbMonthYearConfig);
        }
        else {
            // Create flatPicker instance by passing dateTimeConfig if showMonthYearSelection is false
            const element = (this.dbDateTimeConfig.wrap) ? this.element.nativeElement :
                this.element.nativeElement.firstElementChild.childNodes[1];
            this.flatPicker = flatpickr(element, this.dbDateTimeConfig);
        }
        // Add class to show flatpickr library default placeholder on mobile device devices
        if (this.flatPicker.isMobile || this.flatPicker.config.defaultDate) {
            this.renderer.addClass(this.element.nativeElement.firstElementChild.childNodes[0], 'db-input__label-text--small');
        }
        // Add open-event listener to focus the date picker by default whenever opened. (e.g. IE would require 2 clicks to select a date)
        this.flatPicker.config.onOpen.push(() => {
            setTimeout(() => {
                (this.flatPicker.selectedDateElem ||
                    this.flatPicker.todayDateElem ||
                    this.flatPicker.calendarContainer).focus();
            }, 0);
        });
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        if (this.flatPicker) {
            this.flatPicker.destroy();
        }
    }
    /**
     * Handle change value of date picker
     * @param value
     */
    setDatePickerValue(value) {
        this.flatPicker.setDate(value, false, this.flatPicker.config.dateFormat);
    }
}
DBInputDateDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: DeepmergeService }], target: i0.ɵɵFactoryTarget.Directive });
DBInputDateDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBInputDateDirective, selector: "db-input-date[dbDateTimePicker]", inputs: { dbDateTimeConfig: "dbDateTimeConfig", dbMonthYearConfig: "dbMonthYearConfig", dbDatePickerLanguage: "dbDatePickerLanguage", showMonthYearSelection: "showMonthYearSelection" }, host: { listeners: { "change": "onValueChange($event)", "modelChange": "onModelChange($event)", "keydown": "onKeyDown($event)", "contextmenu": "onMouseRightClick($event)" } }, queries: [{ propertyName: "calendarDiv", first: true, predicate: DBIconComponent, descendants: true, read: ElementRef }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateDirective, decorators: [{
            type: Directive,
            args: [{
                    /* eslint-disable @angular-eslint/directive-selector */
                    selector: 'db-input-date[dbDateTimePicker]',
                    /* eslint-enable @angular-eslint/directive-selector */
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: DeepmergeService }]; }, propDecorators: { calendarDiv: [{
                type: ContentChild,
                args: [DBIconComponent, { read: ElementRef }]
            }], dbDateTimeConfig: [{
                type: Input
            }], dbMonthYearConfig: [{
                type: Input
            }], dbDatePickerLanguage: [{
                type: Input
            }], showMonthYearSelection: [{
                type: Input
            }], onValueChange: [{
                type: HostListener,
                args: ['change', ['$event']]
            }], onModelChange: [{
                type: HostListener,
                args: ['modelChange', ['$event']]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onMouseRightClick: [{
                type: HostListener,
                args: ['contextmenu', ['$event']]
            }] } });

/**
 * DB Input Module
 */
class DBInputModule {
}
DBInputModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBInputModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBInputModule, declarations: [DBInputComponent], imports: [CommonModule,
        DBSpinnerModule], exports: [DBInputComponent] });
DBInputModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputModule, imports: [CommonModule,
        DBSpinnerModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBInputComponent,
                    ],
                    exports: [
                        DBInputComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBSpinnerModule
                    ],
                }]
        }] });

/**
 * DB Input Date Component
 */
class DBInputDateComponent extends DBInputComponent {
    constructor(renderer, changeDetector) {
        super(renderer, changeDetector, null);
    }
    /**
     * After View Init
     */
    ngAfterViewInit() {
        // We assign each label-input combination a unique ID -
        // in addition to the implicit relationship those elements already have due to the
        // nesting. This way, we can ensure the highest #accessibility compatibility.
        // See: https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/label-has-for.md
        const id = `db-input-date--${DBInputDateComponent.dateId}`;
        // // We use the renderer here to achieve a one-time binding (#perfmatters)
        this.renderer.setAttribute(this.inputElement.nativeElement, 'id', id);
        this.renderer.setAttribute(this.labelElement.nativeElement, 'for', id);
        this.renderer.setAttribute(this.inputElement.nativeElement, 'data-input', 'true');
        this.renderer.setAttribute(this.extension.nativeElement, 'data-toggle', 'true');
        DBInputDateComponent.dateId++;
    }
    /**
     * Add datePicker manually (used in dynamic forms)
     *
     * @param element
     * @param renderer
     */
    setDatePicker(elementInput, rendrer, deepmerge) {
        // Set datePicker
        this.datePicker = new DBInputDateDirective(elementInput, rendrer, deepmerge);
        this.datePicker.ngAfterViewInit();
    }
}
/**
 * Unique ID
 */
DBInputDateComponent.dateId = 0;
DBInputDateComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBInputDateComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBInputDateComponent, selector: "db-input-date", providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBInputDateComponent)
        },
    ], usesInheritance: true, ngImport: i0, template: "<label\r\n  #labelElement\r\n  class=\"db-input__label\"\r\n  [style.margin.px]=\"type === 'hidden' ? 0: undefined\"\r\n  [class.db-input__label--disabled]=\"disabled\"\r\n  [class.db-input__label--focus]=\"isFocused\"\r\n  [class.db-input__label--readonly]=\"readonly\">\r\n\r\n  <span class=\"db-input__label-text\" [class.db-input__label-text--small]=\"isUsed()\" [class.d-none]=\"type === 'hidden'\">{{ label }}</span>\r\n\r\n  <!--\r\n    NOTE:\r\n    For native HTML elements, we always prefer bindings using the '.attr' prefix. Doing so leads to attributes which are binded to the value\r\n    'null' getting removed (instead getting set to the value 'null' or an empty string). Eventually, this also prevents W3C HTML validation\r\n    issues (some attributes must not have an empty value).\r\n  -->\r\n  <input\r\n    #inputElement\r\n    class=\"db-input__field\"\r\n    [class.db-input__field--extension]=\"extension.children.length\"\r\n    [class.db-input__field--no-stepper]=\"!showStepper\"\r\n    [attr.autocomplete]=\"autocomplete\"\r\n    [attr.spellcheck]=\"spellcheck\"\r\n    [attr.max]=\"max\"\r\n    [attr.maxlength]=\"maxlength\"\r\n    [attr.min]=\"min\"\r\n    [attr.name]=\"name\"\r\n    [attr.placeholder]=\"placeholder\"\r\n    [attr.step]=\"step\"\r\n    [attr.type]=\"type\"\r\n    [attr.inputmode]=\"inputmode\"\r\n    [disabled]=\"disabled\"\r\n    [readonly]=\"readonly\"\r\n    (focus)=\"onInputFocus()\"\r\n    (blur)=\"onInputBlur()\"\r\n    (input)=\"onInputInput( $event.target.value )\"\r\n    (keydown)=\"onInputKeydown( $event.key )\">\r\n\r\n  <span class=\"db-input__label-counter\" *ngIf=\"showCounter && !readonly\">{{ getModelLength() }} / {{ maxlength }}</span>\r\n\r\n</label>\r\n\r\n<div [class.db-input__extension]=\"extension.children.length || loading\"\r\n     [class.db-input__extension--text]=\"showExtensionText && extension.children.length\"\r\n     #extension>\r\n  <db-spinner class=\"db-input__extension__loading\" *ngIf=\"loading\"></db-spinner>\r\n  <ng-content></ng-content>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBSpinnerComponent, selector: "db-spinner", inputs: ["design"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBInputDateComponent)
                        },
                    ], selector: 'db-input-date', template: "<label\r\n  #labelElement\r\n  class=\"db-input__label\"\r\n  [style.margin.px]=\"type === 'hidden' ? 0: undefined\"\r\n  [class.db-input__label--disabled]=\"disabled\"\r\n  [class.db-input__label--focus]=\"isFocused\"\r\n  [class.db-input__label--readonly]=\"readonly\">\r\n\r\n  <span class=\"db-input__label-text\" [class.db-input__label-text--small]=\"isUsed()\" [class.d-none]=\"type === 'hidden'\">{{ label }}</span>\r\n\r\n  <!--\r\n    NOTE:\r\n    For native HTML elements, we always prefer bindings using the '.attr' prefix. Doing so leads to attributes which are binded to the value\r\n    'null' getting removed (instead getting set to the value 'null' or an empty string). Eventually, this also prevents W3C HTML validation\r\n    issues (some attributes must not have an empty value).\r\n  -->\r\n  <input\r\n    #inputElement\r\n    class=\"db-input__field\"\r\n    [class.db-input__field--extension]=\"extension.children.length\"\r\n    [class.db-input__field--no-stepper]=\"!showStepper\"\r\n    [attr.autocomplete]=\"autocomplete\"\r\n    [attr.spellcheck]=\"spellcheck\"\r\n    [attr.max]=\"max\"\r\n    [attr.maxlength]=\"maxlength\"\r\n    [attr.min]=\"min\"\r\n    [attr.name]=\"name\"\r\n    [attr.placeholder]=\"placeholder\"\r\n    [attr.step]=\"step\"\r\n    [attr.type]=\"type\"\r\n    [attr.inputmode]=\"inputmode\"\r\n    [disabled]=\"disabled\"\r\n    [readonly]=\"readonly\"\r\n    (focus)=\"onInputFocus()\"\r\n    (blur)=\"onInputBlur()\"\r\n    (input)=\"onInputInput( $event.target.value )\"\r\n    (keydown)=\"onInputKeydown( $event.key )\">\r\n\r\n  <span class=\"db-input__label-counter\" *ngIf=\"showCounter && !readonly\">{{ getModelLength() }} / {{ maxlength }}</span>\r\n\r\n</label>\r\n\r\n<div [class.db-input__extension]=\"extension.children.length || loading\"\r\n     [class.db-input__extension--text]=\"showExtensionText && extension.children.length\"\r\n     #extension>\r\n  <db-spinner class=\"db-input__extension__loading\" *ngIf=\"loading\"></db-spinner>\r\n  <ng-content></ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; } });

/**
 * DB Input Date Module
 */
class DBInputDateModule {
}
DBInputDateModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBInputDateModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateModule, declarations: [DBInputDateComponent,
        DBInputDateDirective], imports: [CommonModule,
        DBSpinnerModule], exports: [DBInputDateComponent,
        DBInputDateDirective] });
DBInputDateModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateModule, imports: [CommonModule,
        DBSpinnerModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBInputDateModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBInputDateComponent,
                        DBInputDateDirective
                    ],
                    imports: [
                        CommonModule,
                        DBSpinnerModule
                    ],
                    exports: [
                        DBInputDateComponent,
                        DBInputDateDirective
                    ]
                }]
        }] });

const pluginStack = {
    webrtc: {
        ie: 'Tem.TemWebRTCPlugin',
        others: 'application/x-temwebrtcplugin',
    },
};
/* eslint-disable  */
const browserStack = {
    edge: /Edge?/,
    chrome: /Chrome|CriOS/,
    firefox: /Firefox/,
    ie: /MSIE|Trident/,
    opera: /Opera/,
    safari: /Safari/,
};
/* eslint-enable */
const osStack = {
    android: /Android/,
    ios: /like Mac/,
    mac: /Mac/,
    ubuntu: /Ubuntu/,
    windows: /Windows/,
};

/**
 * DB System Detection
 */
class DBSystemDetectionService {
    /**
     *  Constructor for service.
     * Intialises the values
     */
    constructor(windowRef) {
        this.windowRef = windowRef;
        this.ua = navigator.userAgent;
        this.os = this.getOs();
        this.browser = this.getBrowser();
    }
    /**
     * Gets the operating system being used.
     * @returns - returns the OS name
     */
    getOs() {
        return Object
            .keys(osStack)
            .find((e) => !!this.ua.match(osStack[e])) || 'unknown';
    }
    /**
     * Gets the name of browser being used.
     * @returns - returns the browser name
     */
    getBrowser() {
        return Object
            .keys(browserStack)
            .find((e) => !!this.ua.match(browserStack[e])) || 'unknown';
    }
    /**
     * This function checks if the plugins requested by user are available or not
     * @params Array<string> - list of all requested plugins
     * @returns Array<string> - list of all supported plugins
     */
    detectPlugins(pluginArr) {
        const hasPlugin = [];
        if (this.checkPluginsSupport()) {
            const browser = this.getBrowser();
            for (const plugin of pluginArr) {
                if (pluginStack[plugin]) {
                    switch (browser) {
                        case 'ie':
                            try {
                                if (!!new this.windowRef.nativeWindow.ActiveXObject(pluginStack[plugin].ie)) {
                                    hasPlugin.push(plugin);
                                }
                            }
                            catch (err) {
                                // do nothing. catch err for any unsupported plugin
                                // try-catch is absolutely necessary because new nativeWindow() throws error
                            }
                            break;
                        default:
                            if (!!navigator.mimeTypes[pluginStack[plugin].others]) {
                                hasPlugin.push(plugin);
                            }
                            break;
                    }
                }
            }
        }
        return hasPlugin;
    }
    /**
     * Check if the current device is a mobile device.
     *
     * @returns - Flag, describing whether the given / current user agent implies a mobile device
     */
    isMobileDevice() {
        const ua = navigator.userAgent;
        return !!ua.match(/Mobile/);
    }
    /**
     * This function check if browser supports checking for plugins ( ActiveX and Navigator )
     * ActiveXObject is no longer a property of window ( https://msdn.microsoft.com/library/dn423948(v=vs.85).aspx )
     * @returns boolean
     */
    checkPluginsSupport() {
        return this.getBrowser() === 'ie' ?
            /* https://stackoverflow.com/questions/19638981/window-activexobject-difference-in-ie11 */
            ((Object.getOwnPropertyDescriptor && Object.getOwnPropertyDescriptor(this.windowRef.nativeWindow, 'ActiveXObject'))
                || ('ActiveXObject' in this.windowRef.nativeWindow)) : !!Object.keys(navigator.mimeTypes).length;
    }
}
DBSystemDetectionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSystemDetectionService, deps: [{ token: DBWindowRef }], target: i0.ɵɵFactoryTarget.Injectable });
DBSystemDetectionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSystemDetectionService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSystemDetectionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DBWindowRef }]; } });

// issue #191 in codelyzr - model property present in baseclass
/**
 * DB Radio Group Component
 */
class DBRadioGroupComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Angular change detector
     */
    constructor(changeDetector) {
        super(changeDetector);
        this.legend = null;
        this.design = 'default';
        this.internalModel = null;
        this.radioButtonComponentRefs = [];
        this.internalDisabled = false;
        // Each radio group and its buttons need the same unique name to get grouped together correctly
        this.name = `radio-group-${DBRadioGroupComponent.radioGroupCounter}`;
        DBRadioGroupComponent.radioGroupCounter++;
    }
    /**
     * Flag, describing the disabled state; we overwrite this here due to further side-effects on other local state
     */
    set disabled(value) {
        if (this.internalDisabled !== value) {
            this.internalDisabled = value;
            this.updateDisabledState();
            this.updateView();
        }
    }
    get disabled() {
        return this.internalDisabled;
    }
    /**
     * Default design component class
     */
    get defaultDesign() {
        return this.design === 'default';
    }
    /**
     * Cards design component class
     */
    get cardDesign() {
        return this.design === 'cards';
    }
    /**
     * After Content Init lifecycle hook, setting up the radio button child components
     */
    ngAfterContentInit() {
        this.updateRadioButtonSelection();
        this.updateView();
    }
    /**
     * Register radio button component
     *
     * @param radioButtonComponent - Radio button component
     */
    registerRadioButton(radioButtonComponent) {
        this.radioButtonComponentRefs.push(radioButtonComponent);
    }
    /**
     * Unregister radio button component
     *
     * @param radioButtonComponent - Radio button component
     */
    unregisterRadioButton(radioButtonComponent) {
        this.radioButtonComponentRefs = this.radioButtonComponentRefs.filter((radioButtonComponentRef) => radioButtonComponentRef !== radioButtonComponent);
    }
    /**
     * Do render the new radio value
     *
     * @param value - New value to select
     */
    doRender(value) {
        this.updateRadioButtonSelection();
        this.updateView();
    }
    /**
     * Update all radio button child components to reflect the new selection state
     */
    updateRadioButtonSelection() {
        if (this.radioButtonComponentRefs) {
            this.radioButtonComponentRefs.forEach((radioButtonComponent) => {
                radioButtonComponent.isSelected = radioButtonComponent.model === this.internalModel;
            });
        }
    }
    /**
     * Update all radio button child components to reflect the new disabled state
     */
    updateDisabledState() {
        if (this.radioButtonComponentRefs) {
            this.radioButtonComponentRefs.forEach((radioButtonComponent) => {
                radioButtonComponent.disabled = this.disabled;
            });
        }
    }
    /**
     * Trigger change detection (including child radio button components)
     */
    updateView() {
        if (this.radioButtonComponentRefs) {
            this.radioButtonComponentRefs.forEach((radioButtonComponent) => {
                radioButtonComponent.updateView();
            });
        }
    }
}
/**
 * Radio group counter, shared between radio group components for calculating unique radio group names
 */
DBRadioGroupComponent.radioGroupCounter = 1;
DBRadioGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRadioGroupComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBRadioGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBRadioGroupComponent, selector: "db-radio-group", inputs: { legend: "legend", design: "design", disabled: "disabled" }, host: { properties: { "class.db-radio-group-legend": "this.legend", "class.is-disabled": "this.disabled", "class.db-radio-group--default": "this.defaultDesign", "class.db-radio-group--cards": "this.cardDesign" } }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBRadioGroupComponent),
        },
    ], usesInheritance: true, ngImport: i0, template: "<fieldset class=\"db-radio-group__fieldset m-0 p-0 border-0\">\r\n  <div [class.db-radio-group__container]=\"legend\">\r\n    <legend class=\"db-radio-group__legend d-flex\" *ngIf=\"legend\">\r\n      {{ legend }}\r\n    </legend>\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n\r\n</fieldset>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRadioGroupComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBRadioGroupComponent),
                        },
                    ], selector: 'db-radio-group', template: "<fieldset class=\"db-radio-group__fieldset m-0 p-0 border-0\">\r\n  <div [class.db-radio-group__container]=\"legend\">\r\n    <legend class=\"db-radio-group__legend d-flex\" *ngIf=\"legend\">\r\n      {{ legend }}\r\n    </legend>\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n\r\n</fieldset>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { legend: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.db-radio-group-legend']
            }], design: [{
                type: Input
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.is-disabled']
            }], defaultDesign: [{
                type: HostBinding,
                args: ['class.db-radio-group--default']
            }], cardDesign: [{
                type: HostBinding,
                args: ['class.db-radio-group--cards']
            }] } });

/**
 * DB Radio Button Component
 */
class DBRadioButtonComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector         - Change detector
     * @param systemDetectionService - System detection service
     * @param radioGroupComponent    - Radio Group Component (next parent in the dependency hierarchy)
     */
    constructor(changeDetector, systemDetectionService, radioGroupComponent) {
        super(changeDetector);
        // Check for correct usage
        if (!radioGroupComponent) {
            throw new Error([
                '[DB RADIO BUTTON COMPONENT]',
                'You can use the "db-radio-button" component only in combination with the "db-radio-group" component.',
                'Make sure that every "db-radio-button" is nested within a "db-radio-group".',
            ].join(' '));
        }
        this.withMouse = !systemDetectionService.isMobileDevice();
        this.isSelected = false;
        // Connect to parent radio group
        this.radioGroupComponentRef = radioGroupComponent;
        this.radioGroupComponentRef.registerRadioButton(this);
        this.name = this.radioGroupComponentRef.name;
    }
    ngOnInit() {
        if (this.radioGroupComponentRef.disabled) {
            this.disabled = this.radioGroupComponentRef.disabled;
        }
        if (this.radioGroupComponentRef.readonly) {
            this.readonly = this.radioGroupComponentRef.readonly;
        }
    }
    ngOnDestroy() {
        this.radioGroupComponentRef.unregisterRadioButton(this);
    }
    /**
     * Handle radio button click event
     *
     * @param $event - Event object
     */
    onRadioButtonClick($event) {
        $event.stopPropagation(); // Prevent the click event from being fired twice
    }
    /**
     * Handle radio button change event
     */
    onRadioButtonChange() {
        this.radioGroupComponentRef.model = this.model;
        this.radioGroupComponentRef.modelChange.next(this.model);
        this.setInternalModel(this.model, false, true, true);
    }
    /**
     * Trigger change detection (used by parent radio group)
     */
    updateView() {
        this.changeDetector.detectChanges();
    }
}
DBRadioButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRadioButtonComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: DBSystemDetectionService }, { token: DBRadioGroupComponent, optional: true }], target: i0.ɵɵFactoryTarget.Component });
DBRadioButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBRadioButtonComponent, selector: "db-radio-button", host: { properties: { "class.db-radio-button--mouse": "this.withMouse" } }, usesInheritance: true, ngImport: i0, template: "<label class=\"db-radio-button__label\" [class.db-input__label--disabled]=\"disabled\">\r\n\r\n  <input\r\n    class=\"db-radio-button__input\"\r\n    type=\"radio\"\r\n    [disabled]=\"disabled || readonly\"\r\n    [checked]=\"isSelected\"\r\n    [name]=\"name\"\r\n    [value]=\"model\"\r\n    (click)=\"onRadioButtonClick( $event )\"\r\n    (change)=\"onRadioButtonChange()\">\r\n\r\n  <div class=\"db-radio-button__content\">\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n</label>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRadioButtonComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-radio-button', template: "<label class=\"db-radio-button__label\" [class.db-input__label--disabled]=\"disabled\">\r\n\r\n  <input\r\n    class=\"db-radio-button__input\"\r\n    type=\"radio\"\r\n    [disabled]=\"disabled || readonly\"\r\n    [checked]=\"isSelected\"\r\n    [name]=\"name\"\r\n    [value]=\"model\"\r\n    (click)=\"onRadioButtonClick( $event )\"\r\n    (change)=\"onRadioButtonChange()\">\r\n\r\n  <div class=\"db-radio-button__content\">\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n</label>\r\n" }]
        }], ctorParameters: function () {
        return [{ type: i0.ChangeDetectorRef }, { type: DBSystemDetectionService }, { type: DBRadioGroupComponent, decorators: [{
                        type: Optional
                    }] }];
    }, propDecorators: { withMouse: [{
                type: HostBinding,
                args: ['class.db-radio-button--mouse']
            }] } });

/**
 * System Detection Service Factory
 *
 * @param parentSystemDetectionService - Parent system detection service (if exists)
 * @param windowRef - Window Ref
 * @returns - System detection service instance ( from parent if exists, otherwise new )
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function systemDetectionServiceFactory(parentSystemDetectionService, windowRef) {
    return parentSystemDetectionService || new DBSystemDetectionService(windowRef);
}
/**
 * DB System Detection Module
 */
class DBSystemDetectionModule {
}
DBSystemDetectionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSystemDetectionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBSystemDetectionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBSystemDetectionModule, imports: [DBWindowRefModule] });
DBSystemDetectionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSystemDetectionModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBSystemDetectionService],
                DBWindowRef,
            ],
            provide: DBSystemDetectionService,
            useFactory: systemDetectionServiceFactory,
        },
    ], imports: [DBWindowRefModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSystemDetectionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DBWindowRefModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBSystemDetectionService],
                                DBWindowRef,
                            ],
                            provide: DBSystemDetectionService,
                            useFactory: systemDetectionServiceFactory,
                        },
                    ],
                }]
        }] });

/**
 * DB Radio Button Module
 */
class DBRadioButtonModule {
}
DBRadioButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRadioButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBRadioButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBRadioButtonModule, declarations: [DBRadioButtonComponent,
        DBRadioGroupComponent], imports: [CommonModule,
        DBSystemDetectionModule], exports: [DBRadioButtonComponent,
        DBRadioGroupComponent] });
DBRadioButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRadioButtonModule, imports: [CommonModule,
        DBSystemDetectionModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBRadioButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBRadioButtonComponent,
                        DBRadioGroupComponent,
                    ],
                    exports: [
                        DBRadioButtonComponent,
                        DBRadioGroupComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBSystemDetectionModule,
                    ],
                }]
        }] });

/**
 * DB Select Component
 */
class DBSelectComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     */
    constructor(changeDetector) {
        super(changeDetector);
        /**
         * Provides label to use while input is loading
         */
        this.loadingLabel = 'loading ...';
        this.options = [];
        this.label = '';
        this.loading = false;
        this.placeholder = '';
        this.internalModel = '';
        this.isOpened = false;
    }
    /**
     * Option identifier (used as trackby function) (#perfmatters)
     *
     * @param index - Index of the element
     * @param item  - Element itself
     * @returns - Unique value, in this case the option value
     */
    identifyOptions(index, item) {
        return item.value;
    }
    /**
     * OptionGroup identifier (used as trackby function) (#perfmatters)
     *
     * @param index - Index of the element
     * @param item  - Element itself
     * @returns - Unique value, in this case the concatted option values
     */
    identifyOptGroups(index, item) {
        return Array.isArray(item.value)
            ? item.value.map((val) => val.value).join('-')
            : '';
    }
    /**
     * Handle select value change event
     *
     * @param value - Value
     */
    onSelectChange(value) {
        this.setInternalModel(value, false, true, true);
    }
    /**
     * Handle select click event
     *
     * @param value - Value
     */
    onSelectClick(value) {
        if (!this.disabled) {
            this.isOpened = !this.isOpened;
        }
    }
    /**
     * Handle select blur event
     *
     * @param value - Value
     */
    onSelectBlur(value) {
        this.isOpened = false;
        this.propagateTouched(this.model);
    }
    /**
     * get label from value for readonly state
     *
     * @param value - model value
     */
    getLabel(value) {
        let foundOption;
        if (this.hasOptGroup()) {
            for (const option of this.options) {
                foundOption = this.getLabelFromOptionsArray(option.value, value);
                if (foundOption) {
                    break;
                }
            }
        }
        else {
            foundOption = this.getLabelFromOptionsArray(this.options, value);
        }
        return foundOption && foundOption.label || '';
    }
    /**
     * Determine whether the select has optgroups
     */
    hasOptGroup() {
        return this.options && this.options.length > 0 && Array.isArray(this.options[0].value);
    }
    /**
     * Life cycle method overridden to detect if any content child is passed conditional
     */
    ngAfterViewInit() {
        this.changeDetector.detectChanges();
    }
    /**
     * Normalize
     *
     * @param value - Value
     * @returns       - Normalized value
     */
    doNormalize(value) {
        return !value ? '' : value;
    }
    /**
     * Find option with given value
     *
     * @param value - model value
     */
    getLabelFromOptionsArray(options, value) {
        return options.find((opt) => (opt.value === value));
    }
}
DBSelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSelectComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBSelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBSelectComponent, selector: "db-select", inputs: { options: "options", label: "label", loading: "loading", loadingLabel: "loadingLabel", placeholder: "placeholder" }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBSelectComponent),
        },
    ], usesInheritance: true, ngImport: i0, template: "<label\r\n  class=\"db-select__label\"\r\n  [class.mt-0]=\"!label\"\r\n>\r\n  <span class=\"db-select__label-text\">\r\n    {{ label }}\r\n  </span>\r\n\r\n  <input *ngIf=\"readonly\" class=\"db-select__dropdown\" type=\"text\" [value]=\"getLabel(model)\" readonly>\r\n  <select\r\n    [hidden]=\"readonly\"\r\n    class=\"db-select__dropdown\"\r\n    [class.db-select__dropdown--extension]=\"extension.children.length\"\r\n    [disabled]=\"disabled || readonly || loading\"\r\n    [class.is-opened]=\"isOpened\"\r\n    (click)=\"onSelectClick( $event.target.value )\"\r\n    (blur)=\"onSelectBlur( $event.target.value )\"\r\n    (change)=\"onSelectChange( $event.target.value )\">\r\n\r\n    <option\r\n      *ngIf=\"placeholder !== ''\"\r\n      class=\"db-select__dropdown-item--placeholder\"\r\n      value=\"\"\r\n      [selected]=\"model === ''\">\r\n      {{ placeholder }}\r\n    </option>\r\n\r\n    <ng-container *ngIf=\"!hasOptGroup(); else optionGroups\">\r\n      <ng-container\r\n        *ngTemplateOutlet=\"optionsTemplate; context: { $implicit: { options: options } }\">\r\n      </ng-container>\r\n    </ng-container>\r\n\r\n    <ng-template #optionGroups>\r\n      <optgroup\r\n        [label]=\"optionGroup.label\"\r\n        class=\"db-select__dropdown-opt-group\"\r\n        *ngFor=\"let optionGroup of options; trackBy: identifyOptGroups\">\r\n\r\n        <ng-container\r\n          *ngTemplateOutlet=\"optionsTemplate; context: { $implicit: { options: optionGroup.value } }\">\r\n        </ng-container>\r\n\r\n      </optgroup>\r\n    </ng-template>\r\n\r\n  </select>\r\n\r\n  <svg *ngIf=\"!loading\" class=\"db-select__dropdown-icon\" viewBox=\"0 0 64 64\" focusable=\"false\">\r\n    <path\r\n      d=\"M5.3 13.25c-2.15 0-4.1 1.37-4.9 3.45-.82 2.1-.34 4.48 1.2 6.05l26.67 27.77c2.08 2.16 5.44 2.16 7.5 0l26.68-27.77c1.35-1.4\r\n        1.85-3.45 1.37-5.35-.5-1.9-1.93-3.4-3.76-3.9-1.83-.52-3.8.03-5.13 1.42l-22.9 23.86-22.9-23.86c-1-1.07-2.38-1.67-3.82-1.67z\">\r\n    </path>\r\n  </svg>\r\n\r\n  <db-spinner *ngIf=\"loading\" class=\"db-select__dropdown__loading\">\r\n  </db-spinner>\r\n\r\n</label>\r\n\r\n<div [class.db-select__extension]=\"extension.children.length\" #extension>\r\n  <ng-content></ng-content>\r\n</div>\r\n\r\n<ng-template #optionsTemplate let-context>\r\n  <option\r\n    class=\"db-select__dropdown-item\"\r\n    [value]=\"option.value\"\r\n    [selected]=\"model === option.value\"\r\n    *ngFor=\"let option of context.options; trackBy: identifyOptions\">\r\n    {{ loading ? loadingLabel : option.label }}\r\n  </option>\r\n</ng-template>\r\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: DBSpinnerComponent, selector: "db-spinner", inputs: ["design"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSelectComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBSelectComponent),
                        },
                    ], selector: 'db-select', template: "<label\r\n  class=\"db-select__label\"\r\n  [class.mt-0]=\"!label\"\r\n>\r\n  <span class=\"db-select__label-text\">\r\n    {{ label }}\r\n  </span>\r\n\r\n  <input *ngIf=\"readonly\" class=\"db-select__dropdown\" type=\"text\" [value]=\"getLabel(model)\" readonly>\r\n  <select\r\n    [hidden]=\"readonly\"\r\n    class=\"db-select__dropdown\"\r\n    [class.db-select__dropdown--extension]=\"extension.children.length\"\r\n    [disabled]=\"disabled || readonly || loading\"\r\n    [class.is-opened]=\"isOpened\"\r\n    (click)=\"onSelectClick( $event.target.value )\"\r\n    (blur)=\"onSelectBlur( $event.target.value )\"\r\n    (change)=\"onSelectChange( $event.target.value )\">\r\n\r\n    <option\r\n      *ngIf=\"placeholder !== ''\"\r\n      class=\"db-select__dropdown-item--placeholder\"\r\n      value=\"\"\r\n      [selected]=\"model === ''\">\r\n      {{ placeholder }}\r\n    </option>\r\n\r\n    <ng-container *ngIf=\"!hasOptGroup(); else optionGroups\">\r\n      <ng-container\r\n        *ngTemplateOutlet=\"optionsTemplate; context: { $implicit: { options: options } }\">\r\n      </ng-container>\r\n    </ng-container>\r\n\r\n    <ng-template #optionGroups>\r\n      <optgroup\r\n        [label]=\"optionGroup.label\"\r\n        class=\"db-select__dropdown-opt-group\"\r\n        *ngFor=\"let optionGroup of options; trackBy: identifyOptGroups\">\r\n\r\n        <ng-container\r\n          *ngTemplateOutlet=\"optionsTemplate; context: { $implicit: { options: optionGroup.value } }\">\r\n        </ng-container>\r\n\r\n      </optgroup>\r\n    </ng-template>\r\n\r\n  </select>\r\n\r\n  <svg *ngIf=\"!loading\" class=\"db-select__dropdown-icon\" viewBox=\"0 0 64 64\" focusable=\"false\">\r\n    <path\r\n      d=\"M5.3 13.25c-2.15 0-4.1 1.37-4.9 3.45-.82 2.1-.34 4.48 1.2 6.05l26.67 27.77c2.08 2.16 5.44 2.16 7.5 0l26.68-27.77c1.35-1.4\r\n        1.85-3.45 1.37-5.35-.5-1.9-1.93-3.4-3.76-3.9-1.83-.52-3.8.03-5.13 1.42l-22.9 23.86-22.9-23.86c-1-1.07-2.38-1.67-3.82-1.67z\">\r\n    </path>\r\n  </svg>\r\n\r\n  <db-spinner *ngIf=\"loading\" class=\"db-select__dropdown__loading\">\r\n  </db-spinner>\r\n\r\n</label>\r\n\r\n<div [class.db-select__extension]=\"extension.children.length\" #extension>\r\n  <ng-content></ng-content>\r\n</div>\r\n\r\n<ng-template #optionsTemplate let-context>\r\n  <option\r\n    class=\"db-select__dropdown-item\"\r\n    [value]=\"option.value\"\r\n    [selected]=\"model === option.value\"\r\n    *ngFor=\"let option of context.options; trackBy: identifyOptions\">\r\n    {{ loading ? loadingLabel : option.label }}\r\n  </option>\r\n</ng-template>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { options: [{
                type: Input
            }], label: [{
                type: Input
            }], loading: [{
                type: Input
            }], loadingLabel: [{
                type: Input
            }], placeholder: [{
                type: Input
            }] } });

/**
 * Returns corresponding label to given value.
 * @param value - 'value' for selected 'label' from DBSelectOptions.
 * @param dbSelectOptions -  array of DBSelectOption.
 * @returns - returns the label for given value.
 */
function getLabelByValue(value, dbSelectOptions) {
    const dbSelectOption = dbSelectOptions.find((option) => option.value === value);
    return dbSelectOption ? dbSelectOption.label : null;
}

/**
 * DB Select Module
 */
class DBSelectModule {
}
DBSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBSelectModule, declarations: [DBSelectComponent], imports: [CommonModule,
        DBSpinnerModule], exports: [DBSelectComponent] });
DBSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSelectModule, imports: [CommonModule,
        DBSpinnerModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBSelectComponent,
                    ],
                    exports: [
                        DBSelectComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBSpinnerModule
                    ],
                }]
        }] });

/**
 * Signature Pad Factory
 *
 * A factory wrapper to provide signature_pad as injectable dependency.
 */
class SignaturePadFactory {
    create(canvas, options) {
        return new SignaturePad(canvas, options);
    }
}

/**
 * DB Signature Component
 */
class DBSignatureComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector       - Change detector
     * @param renderer             - Renderer
     * @param signaturePadFactory  - SignaturePad factory
     */
    constructor(changeDetector, renderer, signaturePadFactory, deepmerge) {
        super(changeDetector);
        this.signaturePadFactory = signaturePadFactory;
        this.deepmerge = deepmerge;
        /**
         * Signature Data Regex
         */
        this.signatureDataRegex = /data:image\/(?:jpeg|png|svg\+xml);base64/;
        /**
         * Signature Pad Export Formats (internal)
         */
        this.internalSignaturePadExportTypes = {
            jpg: 'image/jpeg',
            png: 'image/png',
            svg: 'image/svg+xml'
        };
        this.renderer = renderer;
        this.internalModel = '';
        this.label = '';
        this.saveButtonLabel = '';
        this.clearButtonLabel = '';
        this.isFocused = false;
        this.saveOnStrokeEnd = false;
        this.exportValueType = 'svg';
        this.internalSignaturePadConfig = {};
        this.focusOut = new EventEmitter();
        this.ngUnsubscribe = new Subject();
        this.resizeEventSubject = new Subject();
    }
    /**
     * Signature Pad Configuration (internal only)
     */
    set signaturePadConfig(partialConfig) {
        if (partialConfig) {
            this.internalSignaturePadConfig = this.deepmerge.merge(this.internalSignaturePadConfig, partialConfig);
        }
    }
    /**
     * Window resize handler
     */
    onWindowResize() {
        this.resizeEventSubject.next();
    }
    /**
     * On Changes
     */
    ngOnChanges(changes) {
        // Treat disabled and readonly flags equally internally and disable signature input if either is enabled.
        if ((changes.hasOwnProperty('disabled') && !changes.disabled.isFirstChange())
            || (changes.hasOwnProperty('readonly') && !changes.readonly.isFirstChange())) {
            this.setSignatureDisabledState(this.disabled || this.readonly);
        }
    }
    /**
     * After View Init
     */
    ngAfterViewInit() {
        this.resizeEventSubject.pipe(takeUntil(this.ngUnsubscribe), debounceTime(100)).subscribe(() => this.resizeSignaturePad());
        this.alignCanvasSize();
        this.signaturePad = this.signaturePadFactory.create(this.canvasElement.nativeElement, this.internalSignaturePadConfig);
        this.signaturePad.addEventListener('beginStroke', () => {
            this.onStrokeBegin();
        });
        this.signaturePad.addEventListener('endStroke', () => {
            this.onStrokeEnd();
        });
        if (this.internalModel) {
            // Prevent value prefilling for SVG signatures due to possible value inconsistencies caused by resize events.
            // While internal model value may still be valid the rendered signature is lost during resize events
            // due to missing internal data points for signature_pad which is not initialized by "fromDataURL".
            if (this.exportValueType === 'svg') {
                if (isDevMode()) {
                    console.warn('[DB SIGNATURE COMPONENT] Value prefilling is not available for exportValueType "svg".');
                }
                this.setInternalModel('', true, true, true);
            }
            else {
                this.renderSignature(this.internalModel);
            }
        }
        this.setSignatureDisabledState(this.disabled || this.readonly);
        this.previousSignaturePadDimensions = this.getCurrentSignaturePadDimensions();
    }
    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        this.resizeEventSubject.complete();
        this.signaturePad.removeEventListener('beginStroke', () => {
            this.onStrokeBegin();
        });
        this.signaturePad.removeEventListener('endStroke', () => {
            this.onStrokeEnd();
        });
    }
    /**
     * Store current signature value.
     */
    storeSignature() {
        if (this.signaturePad.isEmpty()) {
            return;
        }
        this.setInternalModel(this.signaturePad.toDataURL(this.internalSignaturePadExportTypes[this.exportValueType]), false, true, true);
        this.isFocused = false;
        this.focusOut.emit(this.model);
        this.propagateTouched(this.model);
    }
    /**
     * Clear current signature value.
     */
    clearSignature() {
        this.signaturePad.clear();
        this.setInternalModel('', false, true, true);
        this.isFocused = false;
        this.focusOut.emit(this.model);
    }
    /**
     * Check if signature is currently being used or already filled (by the user, by the implementation).
     */
    isUsed() {
        return this.isFocused || !!this.internalModel;
    }
    /**
     * Do render the new signature value.
     *
     * @param value - New value to render into the signature field
     */
    doRender(value) {
        if (this.signaturePad) {
            this.renderSignature(value);
        }
    }
    /**
     * Resize internal signature pad to match current canvas element size for drawing.
     */
    resizeSignaturePad() {
        // Store value temporarily if signature is not empty.
        let tempValue;
        if (!this.signaturePad.isEmpty()) {
            if (this.exportValueType === 'svg') {
                tempValue = this.signaturePad.toData();
            }
            else {
                tempValue = this.signaturePad.toDataURL(this.internalSignaturePadExportTypes[this.exportValueType]);
            }
        }
        this.alignCanvasSize();
        // Clear pad, otherwise isEmpty() might return incorrect value.
        this.signaturePad.clear();
        // Set signature data again after resizing to show it in the UI.
        if (tempValue) {
            if (typeof tempValue === 'object') {
                this.signaturePad.fromData(this.scaleSignaturePadData(tempValue, this.previousSignaturePadDimensions, this.getCurrentSignaturePadDimensions()));
            }
            else {
                this.signaturePad.fromDataURL(tempValue);
            }
        }
    }
    /**
     * Align canvas element size attributes to new rendered size.
     */
    alignCanvasSize() {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        this.renderer.setProperty(this.canvasElement.nativeElement, 'width', this.canvasElement.nativeElement.offsetWidth * ratio);
        this.renderer.setProperty(this.canvasElement.nativeElement, 'height', this.canvasElement.nativeElement.offsetHeight * ratio);
        this.canvasElement.nativeElement.getContext('2d').scale(ratio, ratio);
    }
    /**
     * Render signature value in signature pad.
     */
    renderSignature(value) {
        if (!this.signatureDataRegex.test(value)) {
            return;
        }
        this.signaturePad.clear();
        this.signaturePad.fromDataURL(value);
        this.changeDetector.detectChanges();
    }
    /**
     * On stroke begin handler
     */
    onStrokeBegin() {
        this.isFocused = true;
        this.changeDetector.detectChanges();
    }
    /**
     * On stroke end handler
     */
    onStrokeEnd() {
        if (this.saveOnStrokeEnd) {
            this.storeSignature();
        }
    }
    /**
     * Set signature pad on/off state based on disabled flag.
     * @param isDisabled - The new disabled state.
     */
    setSignatureDisabledState(isDisabled) {
        if (this.signaturePad && isDisabled) {
            this.signaturePad.off();
        }
        else {
            this.signaturePad.on();
        }
    }
    /**
     * Provides the current signature pad canvas element dimensions.
     */
    getCurrentSignaturePadDimensions() {
        return { x: this.canvasElement.nativeElement.offsetWidth, y: this.canvasElement.nativeElement.offsetHeight };
    }
    /**
     * Scales signature pad data points to new canvas element dimensions.
     * Calculates horizontal and vertical scale factors based on element dimension changes and returns scaled point positions.
     * @param pointGroups - Signature pad point groups.
     * @param previous - The previous canvas element dimensions.
     * @param current - The current canvas element dimensions.
     * @returns - The scaled point positions.
     */
    scaleSignaturePadData(pointGroups, previous, current) {
        const horizontalScale = current.x / previous.x;
        const verticalScale = current.y / previous.y;
        pointGroups.forEach((pointGroup) => {
            pointGroup.points.forEach((point) => {
                point.x = Math.floor(point.x * horizontalScale);
                point.y = Math.floor(point.y * verticalScale);
            });
        });
        // Store current dimensions as previous for next scaling.
        this.previousSignaturePadDimensions = current;
        return pointGroups;
    }
}
DBSignatureComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSignatureComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: SignaturePadFactory }, { token: DeepmergeService }], target: i0.ɵɵFactoryTarget.Component });
DBSignatureComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBSignatureComponent, selector: "db-signature", inputs: { signaturePadConfig: "signaturePadConfig", label: "label", saveButtonLabel: "saveButtonLabel", clearButtonLabel: "clearButtonLabel", saveOnStrokeEnd: "saveOnStrokeEnd", exportValueType: "exportValueType" }, outputs: { focusOut: "focusOut" }, host: { listeners: { "window:resize": "onWindowResize()" } }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBSignatureComponent)
        }
    ], viewQueries: [{ propertyName: "canvasElement", first: true, predicate: ["canvasElement"], descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<label class=\"db-signature__label\"\r\n       [class.db-signature__label--disabled]=\"disabled\"\r\n       [class.db-signature__label--focus]=\"isFocused\">\r\n\r\n  <span class=\"db-signature__label-text\"\r\n        [class.db-signature__label-text--small]=\"isUsed()\">{{ label }}</span>\r\n\r\n  <canvas #canvasElement class=\"db-signature__field\"></canvas>\r\n</label>\r\n\r\n<ng-content></ng-content>\r\n\r\n<div *ngIf=\"!disabled && !readonly\" class=\"db-signature__controls mt-3 d-md-flex\">\r\n  <db-button *ngIf=\"!saveOnStrokeEnd\"\r\n             class=\"d-flex mr-md-3 mb-3 mb-md-0\"\r\n             (click)=\"storeSignature()\">\r\n    {{ saveButtonLabel }}\r\n  </db-button>\r\n  <db-button class=\"d-flex\"\r\n             (click)=\"clearSignature()\">\r\n    {{ clearButtonLabel }}\r\n  </db-button>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBButtonComponent, selector: "db-button", inputs: ["type", "design", "size", "arrow", "loading", "iconOnly"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSignatureComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBSignatureComponent)
                        }
                    ], selector: 'db-signature', template: "<label class=\"db-signature__label\"\r\n       [class.db-signature__label--disabled]=\"disabled\"\r\n       [class.db-signature__label--focus]=\"isFocused\">\r\n\r\n  <span class=\"db-signature__label-text\"\r\n        [class.db-signature__label-text--small]=\"isUsed()\">{{ label }}</span>\r\n\r\n  <canvas #canvasElement class=\"db-signature__field\"></canvas>\r\n</label>\r\n\r\n<ng-content></ng-content>\r\n\r\n<div *ngIf=\"!disabled && !readonly\" class=\"db-signature__controls mt-3 d-md-flex\">\r\n  <db-button *ngIf=\"!saveOnStrokeEnd\"\r\n             class=\"d-flex mr-md-3 mb-3 mb-md-0\"\r\n             (click)=\"storeSignature()\">\r\n    {{ saveButtonLabel }}\r\n  </db-button>\r\n  <db-button class=\"d-flex\"\r\n             (click)=\"clearSignature()\">\r\n    {{ clearButtonLabel }}\r\n  </db-button>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: SignaturePadFactory }, { type: DeepmergeService }]; }, propDecorators: { signaturePadConfig: [{
                type: Input
            }], label: [{
                type: Input
            }], saveButtonLabel: [{
                type: Input
            }], clearButtonLabel: [{
                type: Input
            }], saveOnStrokeEnd: [{
                type: Input
            }], exportValueType: [{
                type: Input
            }], focusOut: [{
                type: Output
            }], canvasElement: [{
                type: ViewChild,
                args: ['canvasElement', { static: true }]
            }], onWindowResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

/**
 * DB Signature Module
 */
class DBSignatureModule {
}
DBSignatureModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSignatureModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBSignatureModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBSignatureModule, declarations: [DBSignatureComponent], imports: [CommonModule,
        DBButtonModule], exports: [DBSignatureComponent] });
DBSignatureModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSignatureModule, providers: [
        SignaturePadFactory
    ], imports: [CommonModule,
        DBButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSignatureModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBSignatureComponent
                    ],
                    exports: [
                        DBSignatureComponent
                    ],
                    imports: [
                        CommonModule,
                        DBButtonModule
                    ],
                    providers: [
                        SignaturePadFactory
                    ]
                }]
        }] });

var SliderPositionReference;
(function (SliderPositionReference) {
    SliderPositionReference["DEFAULT"] = "defaultHandlerPosition";
    SliderPositionReference["SECOND"] = "secondHandlerPosition";
})(SliderPositionReference || (SliderPositionReference = {}));

class DBSliderService {
    /**
     * set the default values of min, max and step
     */
    constructor() {
        this.minValue = DBSliderService.defaultMinValue;
        this.maxValue = DBSliderService.defaultMaxValue;
        this.stepValue = DBSliderService.defaultStepValue;
    }
    /**
     * set step value
     */
    set step(value) {
        this.stepValue = value;
        this.stepSetExternally = true;
    }
    /**
     * set step value
     */
    get step() {
        return this.stepValue;
    }
    /**
     * Maximum allowed value for slider
     */
    set max(value) {
        this.maxValue = value;
    }
    /**
     * set step value
     */
    set min(value) {
        this.minValue = value;
    }
    /**
     * get the position related of the value
     * @param value
     * @returns
     */
    getHandlerPositionFromValue(value) {
        const position = (value - this.minValue) / (this.maxValue - this.minValue);
        return (!Number.isNaN(position)) ? position * this.maxHandlePos : 0;
    }
    /**
     * set necessary values based on slider elements
     * function has to be called after elements are already initializes otherwise wrong values are used
     * @param sliderOffsetWidth the offset width of the slider native element
     * @param handleOffsetWidth the offset width of the handle native element
     */
    setElementSpecificValues(sliderOffsetWidth, handleOffsetWidth) {
        this.grabPos = handleOffsetWidth / 2;
        this.maxHandlePos = sliderOffsetWidth - handleOffsetWidth;
    }
    /**
     * get the relative position of the mouse / touch subtracted with the half of the handle element.
     * @param event
     * @param sliderBoundingRectLeft
     * @returns
     */
    getCurrentPointerPosition(event, sliderBoundingRectLeft) {
        return this.getRelativePosition(event, sliderBoundingRectLeft) - this.grabPos;
    }
    /**
     * get the value related of the current slider handle position
     * snap the position to a step
     * @param posValue
     * @returns
     */
    getModelValueFromPosition(posValue) {
        const pos = Math.min(Math.max(posValue, 0), this.maxHandlePos);
        const toFixed = String(this.stepValue).replace('.', '').length - 1;
        const percentage = ((pos) / (this.maxHandlePos || 1));
        const value = this.stepValue * Math.round(percentage * (this.maxValue - this.minValue) / this.stepValue) + this.minValue;
        return Number((value).toFixed(toFixed));
    }
    /**
     * If the value is too big and the viewport is too small it is possible that the display value is not completely visible.
     * Calculate the distance between the slider border and the display value element,
     * so that the display value is not overstepping the border.
     * @param sliderBoundingRectLeft left slider position relative to the viewport
     * @param sliderBoundingRectRight right slider position relative to the viewport
     * @param displayValueBoundLeft the left display value element position relative to the viewport
     * @param displayValueBoundRight the right display value element position relative to the viewport
     * @returns calced new left postion of the display value
     */
    getDisplayValueGap(sliderBoundingRectLeft, sliderBoundingRectRight, displayValueBoundLeft, displayValueBoundRight) {
        const rangeSliderRelativePosLeft = +sliderBoundingRectLeft.toFixed();
        const rangeSliderRelativePosRight = +sliderBoundingRectRight.toFixed();
        const displayValueRelativePosLeft = +displayValueBoundLeft.toFixed();
        const displayValueRelativePosRight = +displayValueBoundRight.toFixed();
        if (displayValueRelativePosLeft > rangeSliderRelativePosLeft &&
            displayValueRelativePosRight < rangeSliderRelativePosRight) {
            // IF 'display value' element between the broders then the gap is 0
            return 0;
        }
        else if (displayValueRelativePosLeft <= rangeSliderRelativePosLeft &&
            displayValueRelativePosRight < rangeSliderRelativePosRight) {
            // 'display value' element stepped over the left border of the rangeslider
            // IF
            // the left relative position of 'display value' element is smaller equal then the left relative position of the rangeslider
            // AND
            // the right relative position of 'display value' element is smaller then the right relative position of the rangeslder
            return Math.floor(rangeSliderRelativePosLeft - displayValueRelativePosLeft);
        }
        else {
            // 'display value' element stepped over the right border of the rangeslider
            return Math.floor(-(displayValueRelativePosRight - rangeSliderRelativePosRight));
        }
    }
    /**
     * add the grab pos to the position value for filling the whole slider bar
     * @param currentValue
     * @returns
     */
    getFillWidth(currentValue) {
        return currentValue + this.grabPos;
    }
    /**
     * checks whether value is between min and max otherwise return either min or max
     * @param value
     * @returns
     */
    getValueLimitedByModel(value) {
        let newValue = value;
        if (newValue === undefined || newValue < this.minValue) {
            newValue = this.minValue;
        }
        if (newValue > this.maxValue) {
            newValue = this.maxValue;
        }
        return newValue;
    }
    /**
     * Get the horizontal position relative to the rangeslider and the viewport.
     * @param event
     * @param sliderBoundingRectLeft the current left slider position relative to the viewport
     * @returns
     */
    getRelativePosition(event, sliderBoundingRectLeft) {
        let pageCoordinate = 0;
        // get the correct X coordinate value depending on the event type
        if (event instanceof MouseEvent) {
            pageCoordinate = event.clientX;
        }
        else if (event.touches &&
            event.touches[0] &&
            event.touches[0].clientX !== undefined) {
            pageCoordinate = event.touches[0].clientX;
        }
        return pageCoordinate - sliderBoundingRectLeft;
    }
}
/**
 * the default min value of the slider
 */
DBSliderService.defaultMinValue = 0;
/**
 * the default max value of the slider
 */
DBSliderService.defaultMaxValue = 100;
/**
 * the default step value of the slider
 */
DBSliderService.defaultStepValue = 1;
DBSliderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBSliderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * Service to handle a list with values to be represented by steppers on DBSliderComponent.
 * Wrap steppersList Input parameter from DBSliderComponent and simplify access to it.
 */
class DBSliderStepValuesService {
    constructor() {
        this.values = [];
    }
    get stepValues() {
        return this.values;
    }
    set stepValues(array) {
        this.values = array;
    }
    get minValue() {
        return this.values[0] || 0;
    }
    get maxValue() {
        return this.values[this.values.length - 1] || 100;
    }
    getValueOf(index) {
        return this.values[index];
    }
    getStepperForValue(value) {
        return this.values.indexOf(this.closestStepperFrom(value));
    }
    getValueLimitedByModel(value) {
        let newValue = value;
        if (newValue === undefined || newValue < this.minValue) {
            newValue = this.minValue;
        }
        if (newValue > this.maxValue) {
            newValue = this.maxValue;
        }
        return newValue;
    }
    closestStepperFrom(value) {
        return this.values.reduce((previous, current) => (Math.abs(current - value) < Math.abs(previous - value) ? current : previous));
    }
}
DBSliderStepValuesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderStepValuesService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBSliderStepValuesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderStepValuesService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderStepValuesService, decorators: [{
            type: Injectable
        }] });

/**
 * DB Number Pipe
 */
class DBNumberPipe {
    /**
     * Format number
     *
     * @param value - Value
     * @returns       - Formatted value
     */
    transform(value, limitDecimalDigits = null, preventAutomaticDecimals = false) {
        return DBFormatters.number.format(value.toString(), limitDecimalDigits, preventAutomaticDecimals);
    }
}
DBNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBNumberPipe, name: "dbNumber" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNumberPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbNumber',
                    pure: true,
                }]
        }] });

/* eslint-disable @typescript-eslint/member-ordering */
/**
 * DBSliderComponent component. Based on and thanks to http://rangeslider.js.org/.
 */
class DBSliderComponent extends DBCoreFormBaseDirective {
    /**
     * constructor
     * @param el
     * @param sliderService
     * @param stepValuesService
     * @param changeDetectorRef
     *
     */
    constructor(el, sliderService, stepValuesService, changeDetector) {
        super(changeDetector);
        this.el = el;
        this.sliderService = sliderService;
        this.stepValuesService = stepValuesService;
        /**
         * the value of the internal model except it is out of range then min / max is displayed
         */
        this.displayValueNumber = '';
        /**
         * the second value of the internal model except it is out of range then min / max is displayed
         */
        this.secondDisplayValueNumber = '';
        this.hasStepValues = false;
        this.isSingleSlider = true;
        this.ngUnsubscribe = new Subject();
        this.resizeSubject = new Subject();
        this.resizeObservable = this.resizeSubject.asObservable().pipe(throttleTime(10));
        this.enableDragging = false;
        this.showValue = true;
        this.labelStepValue = 0;
        this.minValue = DBSliderService.defaultMinValue;
        this.maxValue = DBSliderService.defaultMaxValue;
        this.steppersListToDisplay = [];
        this.isViewInitialized = false;
    }
    /**
     * set model if passed value is a number, min/max object or it is not disabled
     */
    set model(value) {
        this.setInternalModel(Number(`${value}`) || value || 0, true, false, true);
        this.propagateTouched(this.internalModel);
    }
    get model() {
        return this.internalModel;
    }
    get numericModel() {
        if (this.hasStepValues) {
            return this.stepValuesService.getStepperForValue(this.internalModel);
        }
        return this.internalModel;
    }
    get rangeModel() {
        if (this.hasStepValues) {
            return {
                minValue: this.stepValuesService.getStepperForValue(this.internalModel.minValue),
                maxValue: this.stepValuesService.getStepperForValue(this.internalModel.maxValue)
            };
        }
        return this.internalModel;
    }
    /**
     * Minimal allowed value for slider range.
     */
    set min(value) {
        this.minValue = Number(`${value}`) || 0;
        this.sliderService.min = this.minValue;
    }
    get min() {
        return this.hasStepValues ? this.stepValuesService.minValue : this.minValue;
    }
    /**
     * Maximum allowed value for slider range.
     */
    set max(value) {
        this.maxValue = Number(`${value}`) || 0;
        this.sliderService.max = this.maxValue;
    }
    get max() {
        return this.hasStepValues ? this.stepValuesService.maxValue : this.maxValue;
    }
    /**
     * Step value for slider
     */
    set step(value) {
        this.sliderService.step = Number(`${value}`) || 0;
    }
    get step() {
        return this.sliderService.step;
    }
    /**
     * set class when showValue is true
     */
    get isShowValue() {
        return this.showValue;
    }
    /**
     * this is for showing labels for intermediate values
     */
    set labelStep(value) {
        this.labelStepValue = Number(`${value}`) || 0;
    }
    /**
     * If true and step size < 0, all decimal points in step size will applied to labels, even if 0.
     */
    set forceStepDecimals(value) {
        this.stepDecimals = value ? (1 / this.sliderService.step).toString().length - 1 : null;
    }
    /**
     * this is for showing a stepper's list with non regular sequences
     */
    set steppersList(value) {
        this.stepValuesService.stepValues = value;
        this.hasStepValues = true;
        this.min = 0;
        this.max = value.length - 1;
    }
    /**
     * subscribe to resize observable
     */
    ngOnInit() {
        this.resizeObservable.pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.updateSpecificValuesAndChangeSliderPos());
    }
    /**
     * unsubscribe resize observable
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        this.resizeSubject.unsubscribe();
    }
    /**
     * initialize specfic values for slider service and set slider position
     */
    ngAfterViewInit() {
        this.isSingleSlider = !this.model || typeof this.model === 'number';
        this.isViewInitialized = true;
        this.updateSpecificValuesAndChangeSliderPos();
        this.changeDetector.detectChanges();
    }
    /**
     * Life cycle method to update the value when any of the inputs changes
     */
    ngOnChanges() {
        this.updateSpecificValuesAndChangeSliderPos();
    }
    /**
     * emit window resize with resize subject
     */
    onWindowResize() {
        this.resizeSubject.next();
    }
    /**
     * Called when mousedown or touchstart event is fired.
     * set the new slider position
     * @param event
     * @returns
     */
    onHandleDown(event) {
        // avoids unwanted snapping on iOS devices
        event.preventDefault();
        // if disabled is true don't change slider
        if (this.disabled) {
            return;
        }
        this.enableDragging = true;
        // if the handle elements is clicked then don't set new position otherwise the handle will jump the whole time
        if (event.target === this.handleElement.nativeElement
            || (this.secondHandlerElement && event.target === this.secondHandlerElement.nativeElement)) {
            return;
        }
        this.updateHandlerPosition(event);
        // set focus on current host to enable keyboard navigation
        if (this.el.nativeElement.contains(event.target)) {
            this.el.nativeElement.firstElementChild.focus();
        }
    }
    /**
     * Called when a keyup event is fired.
     * set the new slider position and update the model
     * @param KeyboardEvent event
     * @returns void
     */
    onHandleKeypress(event) {
        if (this.isSingleSlider && this.el.nativeElement.contains(event.target) && !this.disabled) {
            switch (event.keyCode) {
                case 35: /*end key*/
                    this.setInternalModel(this.maxValue, true, true, true);
                    event.preventDefault();
                    break;
                case 36: /*home key*/
                    this.setInternalModel(this.minValue, true, true, true);
                    event.preventDefault();
                    break;
                case 37:
                case 39: /*arror keys*/
                    /* Jump by step value when step value set by user ( twice as fast in shft mode )
                    *  Jump by default step value when step value not set by user ( ten times as fast in shft mode )
                    */
                    const stepValue = event.shiftKey ? (this.sliderService.stepSetExternally ? this.sliderService.step * 2
                        : this.sliderService.step * 10) : this.sliderService.step;
                    const currentVal = this.sliderService.getValueLimitedByModel(this.internalModel);
                    const newValue = event.keyCode === 37 ? (currentVal - stepValue) : (currentVal + stepValue);
                    this.setInternalModel(this.sliderService.getValueLimitedByModel(newValue), true, true, true);
                    event.preventDefault();
                    break;
                default:
                    break;
            }
        }
    }
    /**
     * on pointerup, mouseup, touchcancel and touchend disable dragging
     */
    onMouseUp() {
        this.enableDragging = false;
        // set focus on current host to enable keyboard navigation
        if (this.el.nativeElement.contains(event.target)) {
            this.el.nativeElement.firstElementChild.focus();
        }
    }
    /**
     * if dragging is enabled set the correct position for the slider
     * @param event
     */
    onMouseMove(event) {
        if (this.enableDragging) {
            // update handle position and internal model
            this.updateHandlerPosition(event);
        }
    }
    /**
     * pluralize the unit when values are less than -1 and greater than 1
     */
    getUnit(defaultValue, secondValue = 0) {
        if (!this.unitPlural || (Math.abs(Number(defaultValue) || 0) <= 1 && Math.abs(Number(secondValue) || 0) <= 1)) {
            return this.unit;
        }
        return this.unitPlural;
    }
    /**
     * doRender is called on external model @Input / change
     * slider can be just set if view is initialized
     * internal model is not updated by handleMinPosition function, was already done by external change
     * @param value
     */
    doRender(value) {
        // before setting slider position
        // check @Input model is a number
        // and view is initialized
        if ((!Number.isNaN(value) && this.isViewInitialized)) {
            if (this.isSingleSlider) {
                this.moveSingleHandler(this.sliderService.getHandlerPositionFromValue(value));
            }
            else {
                this.moveLeftHandler(this.sliderService.getHandlerPositionFromValue(value));
                this.moveRightHandler(this.sliderService.getHandlerPositionFromValue(value));
            }
        }
    }
    updateHandlerPosition(event) {
        let clickedPoint = this.sliderService.getCurrentPointerPosition(event, this.getSliderBoundingRectLeft());
        if (this.isSingleSlider) {
            this.moveSingleHandler(clickedPoint, true);
            return;
        }
        const halfStepSize = this.sliderService.getHandlerPositionFromValue(this.minValue + Number(this.step)) * 0.60;
        if (this.isClickedPointNearestToRightRandler(clickedPoint)) {
            if (this.isDraggingToLeftHandler(clickedPoint, halfStepSize)) {
                clickedPoint -= halfStepSize;
                this.moveLeftHandler(clickedPoint, true);
            }
            this.moveRightHandler(clickedPoint, true);
        }
        else {
            if (this.isDraggingToRightHandler(clickedPoint, halfStepSize)) {
                clickedPoint += halfStepSize;
                this.moveRightHandler(clickedPoint, true);
            }
            this.moveLeftHandler(clickedPoint, true);
        }
    }
    isDraggingToRightHandler(clickedPoint, halfStepSize) {
        return this.secondHandlerPosition - clickedPoint < halfStepSize;
    }
    isDraggingToLeftHandler(clickedPoint, halfStepSize) {
        return clickedPoint - this.defaultHandlerPosition < halfStepSize;
    }
    isClickedPointNearestToRightRandler(clickedPoint) {
        return (this.secondHandlerPosition - clickedPoint) < (clickedPoint - this.defaultHandlerPosition);
    }
    /**
     * update specific values for slider service
     * set slider position on the basis of 'internalModel'
     */
    updateSpecificValuesAndChangeSliderPos() {
        this.updateElementSpecificValues();
        // get a either the value of the model back or min / max depending on the value of the model
        if (this.isSingleSlider) {
            const value = this.sliderService.getValueLimitedByModel(this.numericModel);
            this.moveSingleHandler(this.sliderService.getHandlerPositionFromValue(value));
        }
        else {
            const minValue = this.sliderService.getValueLimitedByModel(this.rangeModel.minValue);
            this.moveLeftHandler(this.sliderService.getHandlerPositionFromValue(minValue));
            const maxValue = this.sliderService.getValueLimitedByModel(this.rangeModel.maxValue);
            this.moveRightHandler(this.sliderService.getHandlerPositionFromValue(maxValue));
        }
        if (this.showSteps) {
            const values = this.hasStepValues ? this.stepValuesService.stepValues : this.createRegularStepValues();
            this.setDisplayStepperValues(values);
        }
    }
    createRegularStepValues() {
        const steppersQuantity = (this.maxValue - this.minValue) / this.labelStepValue + 1;
        return Array.from(Array(steppersQuantity), (_, index) => this.minValue + (index * this.labelStepValue));
    }
    setDisplayStepperValues(values) {
        this.steppersListToDisplay = values.map((stepper, index) => {
            const stepperValue = this.hasStepValues ? index : stepper;
            return {
                label: stepper.toString(),
                leftPosition: this.sliderService.getHandlerPositionFromValue(stepperValue)
            };
        });
    }
    /**
     * set values depending on html DOM elements
     */
    updateElementSpecificValues() {
        this.sliderService.setElementSpecificValues(this.sliderElement.nativeElement.offsetWidth, this.handleElement.nativeElement.offsetWidth);
    }
    /**
     * Set the hadler position based based on model value
     * @param value model value to be represented by the handler.
     * @param handlerPositionParameter handler position parameter name to be moved: defaultHandlerPosition or secondHandlerPosition
     */
    setHandlerPositionByValue(value, handlerPositionParameter) {
        const newPos = this.sliderService.getHandlerPositionFromValue(value);
        this[handlerPositionParameter] = newPos;
        this.fillSelectedInterval();
    }
    /**
     * Set the slider position and fill the bar.
     * Update the internal model and controls as well whether model is not out of range of min and max.
     * @param pos slider position to be set.
     * @param [updateInternalModel=false]
     */
    moveSingleHandler(pos, updateInternalModel = false) {
        const newValue = this.sliderService.getModelValueFromPosition(pos);
        this.setHandlerPositionByValue(newValue, SliderPositionReference.DEFAULT);
        // update internal model and emit event and change if model is not changed externally
        if (updateInternalModel) {
            this.setInternalModel(this.hasStepValues ? this.stepValuesService.getValueOf(newValue) : newValue, false, true, true);
        }
        if (this.showValue) {
            this.updateDisplayValue();
        }
    }
    /**
     * Set the min range slider position and fill the bar.
     * Update the internal model and controls as well whether model is not out of range of min and max.
     * @param pos slider position to be set.
     * @param [updateInternalModel=false]
     */
    moveLeftHandler(pos, updateInternalModel = false) {
        const newValue = this.sliderService.getModelValueFromPosition(pos);
        this.setHandlerPositionByValue(newValue, SliderPositionReference.DEFAULT);
        // update internal model and emit event and change if model is not changed externally
        if (updateInternalModel) {
            const newModelValue = {
                minValue: this.hasStepValues ? this.stepValuesService.getValueOf(newValue) : newValue,
                maxValue: this.model.maxValue,
            };
            this.setInternalModel(newModelValue, false, true, true);
        }
        if (this.showValue) {
            this.updateDisplayValue();
        }
    }
    /**
     * Set the max range slider position and fill the bar.
     * Update the internal model and controls as well whether model is not out of range of min and max.
     * @param pos slider position to be set.
     * @param [updateInternalModel=false]
     */
    moveRightHandler(pos, updateInternalModel = false) {
        const newValue = this.sliderService.getModelValueFromPosition(pos);
        this.setHandlerPositionByValue(newValue, SliderPositionReference.SECOND);
        // update internal model and emit event and change if model is not changed externally
        if (updateInternalModel) {
            const newModelValue = {
                minValue: this.model.minValue,
                maxValue: this.hasStepValues ? this.stepValuesService.getValueOf(newValue) : newValue,
            };
            this.setInternalModel(newModelValue, false, true, true);
        }
        if (this.showValue) {
            this.updateDisplayValue();
        }
    }
    fillSelectedInterval() {
        if (this.isSingleSlider) {
            this.fillWidth = this.sliderService.getFillWidth(this.defaultHandlerPosition);
        }
        else {
            this.startFillPosition = this.defaultHandlerPosition;
            this.fillWidth = this.secondHandlerPosition - this.defaultHandlerPosition;
        }
    }
    /**
     * update the value or range displayed above the slider according to the model type and if a step list is provided
     */
    updateDisplayValue() {
        const service = this.hasStepValues ? this.stepValuesService : this.sliderService;
        if (this.isSingleSlider) {
            const modelValue = this.model || this.stepValuesService.minValue;
            this.displayValueNumber = service.getValueLimitedByModel(modelValue).toString();
        }
        else {
            const minModelValue = this.model.minValue;
            this.displayValueNumber = service.getValueLimitedByModel(minModelValue).toString();
            const maxModelValue = this.model.maxValue;
            this.secondDisplayValueNumber = service.getValueLimitedByModel(maxModelValue).toString();
        }
        // tell Anuglar an update has happened
        this.changeDetector.detectChanges();
        // set the distance between the rangeslider and the display value element
        // so that the display value element is not overstepping the rangeslider's borders
        this.displayValueGap = this.sliderService.getDisplayValueGap(this.getSliderBoundingRectLeft(), this.getSliderBoundingRectRight(), this.getDisplayValueBoundingRectLeft(), this.getDisplayValueBoundingRectRight());
    }
    /**
     * get the left slider position relative to the viewport
     * @returns
     */
    getSliderBoundingRectLeft() {
        return this.sliderElement.nativeElement.getBoundingClientRect().left;
    }
    /**
     * get the right slider position relative to the viewport
     * @returns
     */
    getSliderBoundingRectRight() {
        return this.sliderElement.nativeElement.getBoundingClientRect().right;
    }
    /**
     * get the left display value element position relative to the viewport
     * @returns
     */
    getDisplayValueBoundingRectLeft() {
        return this.displayValueElement.nativeElement.getBoundingClientRect().left;
    }
    /**
     * get the right display value element position relative to the viewport
     * @returns
     */
    getDisplayValueBoundingRectRight() {
        return this.displayValueElement.nativeElement.getBoundingClientRect().right;
    }
}
DBSliderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderComponent, deps: [{ token: i0.ElementRef }, { token: DBSliderService }, { token: DBSliderStepValuesService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBSliderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBSliderComponent, selector: "db-slider", inputs: { model: "model", min: "min", max: "max", step: "step", labelStep: "labelStep", forceStepDecimals: "forceStepDecimals", steppersList: "steppersList", unit: "unit", unitPlural: "unitPlural", hideUnitOnTop: "hideUnitOnTop", labelMin: "labelMin", labelMax: "labelMax", showValue: "showValue", showSteps: "showSteps" }, host: { listeners: { "window:resize": "onWindowResize()", "document:keydown": "onHandleKeypress($event)", "document:pointerup": "onMouseUp()", "document:mouseup": "onMouseUp()", "document:touchcancel": "onMouseUp()", "document:touchend": "onMouseUp()", "document:pointermove": "onMouseMove($event)", "document:touchmove": "onMouseMove($event)", "document:mousemove": "onMouseMove($event)" }, properties: { "class.db-slider--padding-top": "this.isShowValue" } }, providers: [
        DBSliderService,
        DBSliderStepValuesService,
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBSliderComponent),
        },
    ], viewQueries: [{ propertyName: "sliderElement", first: true, predicate: ["slider"], descendants: true, static: true }, { propertyName: "handleElement", first: true, predicate: ["defaultHandler"], descendants: true, static: true }, { propertyName: "secondHandlerElement", first: true, predicate: ["secondHandler"], descendants: true, static: true }, { propertyName: "displayValueElement", first: true, predicate: ["displayValue"], descendants: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"db-slider\" #slider [ngClass]=\"{\r\n        'is-dragged': enableDragging,\r\n        'is-disabled': disabled\r\n    }\" (touchstart)=\"onHandleDown($event)\" (pointerdown)=\"onHandleDown($event)\" (mousedown)=\"onHandleDown($event)\"\r\n    tabindex=\"0\">\r\n  <span class=\"db-slider__stepper-label-container\">\r\n    <span class=\"db-slider__stepper-label\" *ngFor=\"let stepper of steppersListToDisplay\"\r\n    [style.left.px]=\"stepper.leftPosition\" [style.transform]=\"stepper.transform\">\r\n      {{ stepper.label | dbNumber }}\r\n      <p class=\"db-slider__stepper-label-unit\" *ngIf=\"unit\"> {{ getUnit(stepper.label) }}</p>\r\n    </span>\r\n  </span>\r\n  <span class=\"db-slider__stepper-points-container\">\r\n    <span class=\"db-slider__stepper-point\" *ngFor=\"let stepper of steppersListToDisplay\" [style.left.px]=\"stepper.leftPosition\">\r\n    </span>\r\n  </span>\r\n  <div class=\"db-slider__fill\" [style.margin-left.px]=\"startFillPosition\" [style.width.px]=\"fillWidth\"></div>\r\n  <div #defaultHandler class=\"db-slider__handle\" [style.left.px]=\"defaultHandlerPosition\">\r\n    <span class=\"db-slider__display-value-container\" #displayValue *ngIf=\"showValue\">\r\n      <span class=\"db-slider__display-value\" [style.left.px]=\"displayValueGap\">\r\n        {{ displayValueNumber | dbNumber:stepDecimals }}\r\n        <span *ngIf=\"!hideUnitOnTop\">\r\n          <span class=\"db-slider__unit\" *ngIf=\"unit\"> {{ getUnit(displayValueNumber) }}</span>\r\n        </span>\r\n      </span>\r\n    </span>\r\n  </div>\r\n  <div #secondHandler class=\"db-slider__handle\" [style.left.px]=\"secondHandlerPosition\" *ngIf=\"secondHandlerPosition\">\r\n    <span class=\"db-slider__display-value-container\" #displayValue *ngIf=\"showValue\">\r\n      <span class=\"db-slider__display-value\" [style.left.px]=\"displayValueGap\">\r\n        {{ secondDisplayValueNumber | dbNumber:stepDecimals }}\r\n        <span *ngIf=\"!hideUnitOnTop\">\r\n          <span class=\"db-slider__unit\" *ngIf=\"unit\"> {{ getUnit(secondDisplayValueNumber) }}</span>\r\n        </span>\r\n      </span>\r\n    </span>\r\n  </div>\r\n  <span class=\"db-slider__range-values db-slider__range-values--min\" *ngIf=\"!steppersListToDisplay.length\">\r\n    <span *ngIf=\"labelMin\">{{ labelMin }} </span>\r\n    <span>{{ min | dbNumber }}</span>\r\n    <span *ngIf=\"unit\"> {{ getUnit(min) }}</span>\r\n  </span>\r\n  <span class=\"db-slider__range-values db-slider__range-values--max\" *ngIf=\"!steppersListToDisplay.length\">\r\n    <span *ngIf=\"labelMax\">{{ labelMax }} </span>\r\n    <span>{{ max | dbNumber }}</span>\r\n    <span *ngIf=\"unit\"> {{ getUnit(max) }}</span>\r\n  </span>\r\n</div>\r\n<span class=\"db-slider-right-panel\" *ngIf=\"showValue\">\r\n  {{ displayValueNumber | dbNumber:stepDecimals }}\r\n  <span *ngIf=\"secondDisplayValueNumber !== '' && displayValueNumber !== secondDisplayValueNumber\">\r\n  {{ ' - ' + (secondDisplayValueNumber | dbNumber:stepDecimals) }}\r\n  </span>\r\n  <span *ngIf=\"!hideUnitOnTop\">\r\n    <span *ngIf=\"unit\"> {{ getUnit(displayValueNumber, secondDisplayValueNumber) }}</span>\r\n  </span>\r\n</span>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: DBNumberPipe, name: "dbNumber" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        DBSliderService,
                        DBSliderStepValuesService,
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBSliderComponent),
                        },
                    ], selector: 'db-slider', template: "<div class=\"db-slider\" #slider [ngClass]=\"{\r\n        'is-dragged': enableDragging,\r\n        'is-disabled': disabled\r\n    }\" (touchstart)=\"onHandleDown($event)\" (pointerdown)=\"onHandleDown($event)\" (mousedown)=\"onHandleDown($event)\"\r\n    tabindex=\"0\">\r\n  <span class=\"db-slider__stepper-label-container\">\r\n    <span class=\"db-slider__stepper-label\" *ngFor=\"let stepper of steppersListToDisplay\"\r\n    [style.left.px]=\"stepper.leftPosition\" [style.transform]=\"stepper.transform\">\r\n      {{ stepper.label | dbNumber }}\r\n      <p class=\"db-slider__stepper-label-unit\" *ngIf=\"unit\"> {{ getUnit(stepper.label) }}</p>\r\n    </span>\r\n  </span>\r\n  <span class=\"db-slider__stepper-points-container\">\r\n    <span class=\"db-slider__stepper-point\" *ngFor=\"let stepper of steppersListToDisplay\" [style.left.px]=\"stepper.leftPosition\">\r\n    </span>\r\n  </span>\r\n  <div class=\"db-slider__fill\" [style.margin-left.px]=\"startFillPosition\" [style.width.px]=\"fillWidth\"></div>\r\n  <div #defaultHandler class=\"db-slider__handle\" [style.left.px]=\"defaultHandlerPosition\">\r\n    <span class=\"db-slider__display-value-container\" #displayValue *ngIf=\"showValue\">\r\n      <span class=\"db-slider__display-value\" [style.left.px]=\"displayValueGap\">\r\n        {{ displayValueNumber | dbNumber:stepDecimals }}\r\n        <span *ngIf=\"!hideUnitOnTop\">\r\n          <span class=\"db-slider__unit\" *ngIf=\"unit\"> {{ getUnit(displayValueNumber) }}</span>\r\n        </span>\r\n      </span>\r\n    </span>\r\n  </div>\r\n  <div #secondHandler class=\"db-slider__handle\" [style.left.px]=\"secondHandlerPosition\" *ngIf=\"secondHandlerPosition\">\r\n    <span class=\"db-slider__display-value-container\" #displayValue *ngIf=\"showValue\">\r\n      <span class=\"db-slider__display-value\" [style.left.px]=\"displayValueGap\">\r\n        {{ secondDisplayValueNumber | dbNumber:stepDecimals }}\r\n        <span *ngIf=\"!hideUnitOnTop\">\r\n          <span class=\"db-slider__unit\" *ngIf=\"unit\"> {{ getUnit(secondDisplayValueNumber) }}</span>\r\n        </span>\r\n      </span>\r\n    </span>\r\n  </div>\r\n  <span class=\"db-slider__range-values db-slider__range-values--min\" *ngIf=\"!steppersListToDisplay.length\">\r\n    <span *ngIf=\"labelMin\">{{ labelMin }} </span>\r\n    <span>{{ min | dbNumber }}</span>\r\n    <span *ngIf=\"unit\"> {{ getUnit(min) }}</span>\r\n  </span>\r\n  <span class=\"db-slider__range-values db-slider__range-values--max\" *ngIf=\"!steppersListToDisplay.length\">\r\n    <span *ngIf=\"labelMax\">{{ labelMax }} </span>\r\n    <span>{{ max | dbNumber }}</span>\r\n    <span *ngIf=\"unit\"> {{ getUnit(max) }}</span>\r\n  </span>\r\n</div>\r\n<span class=\"db-slider-right-panel\" *ngIf=\"showValue\">\r\n  {{ displayValueNumber | dbNumber:stepDecimals }}\r\n  <span *ngIf=\"secondDisplayValueNumber !== '' && displayValueNumber !== secondDisplayValueNumber\">\r\n  {{ ' - ' + (secondDisplayValueNumber | dbNumber:stepDecimals) }}\r\n  </span>\r\n  <span *ngIf=\"!hideUnitOnTop\">\r\n    <span *ngIf=\"unit\"> {{ getUnit(displayValueNumber, secondDisplayValueNumber) }}</span>\r\n  </span>\r\n</span>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: DBSliderService }, { type: DBSliderStepValuesService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { model: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], step: [{
                type: Input
            }], isShowValue: [{
                type: HostBinding,
                args: ['class.db-slider--padding-top']
            }], labelStep: [{
                type: Input
            }], forceStepDecimals: [{
                type: Input
            }], steppersList: [{
                type: Input
            }], sliderElement: [{
                type: ViewChild,
                args: ['slider', { static: true }]
            }], handleElement: [{
                type: ViewChild,
                args: ['defaultHandler', { static: true }]
            }], secondHandlerElement: [{
                type: ViewChild,
                args: ['secondHandler', { static: true }]
            }], displayValueElement: [{
                type: ViewChild,
                args: ['displayValue']
            }], unit: [{
                type: Input
            }], unitPlural: [{
                type: Input
            }], hideUnitOnTop: [{
                type: Input
            }], labelMin: [{
                type: Input
            }], labelMax: [{
                type: Input
            }], showValue: [{
                type: Input
            }], showSteps: [{
                type: Input
            }], onWindowResize: [{
                type: HostListener,
                args: ['window:resize']
            }], onHandleKeypress: [{
                type: HostListener,
                args: ['document:keydown', ['$event']]
            }], onMouseUp: [{
                type: HostListener,
                args: ['document:pointerup']
            }, {
                type: HostListener,
                args: ['document:mouseup']
            }, {
                type: HostListener,
                args: ['document:touchcancel']
            }, {
                type: HostListener,
                args: ['document:touchend']
            }], onMouseMove: [{
                type: HostListener,
                args: ['document:pointermove', ['$event']]
            }, {
                type: HostListener,
                args: ['document:touchmove', ['$event']]
            }, {
                type: HostListener,
                args: ['document:mousemove', ['$event']]
            }] } });

/**
 * DBCreditCardNumber Pipe
 */
class DBCreditCardPipe {
    /**
     * Format DBCreditCardNumber
     *
     * @param value - Value
     * @returns       - Formatted value
     */
    transform(value) {
        return DBFormatters.debitCardNumber.format(value.toString());
    }
}
DBCreditCardPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCreditCardPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBCreditCardPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBCreditCardPipe, name: "dbCreditCard" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCreditCardPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbCreditCard',
                    pure: true,
                }]
        }] });

/**
 * DB Credit Card Pipe
 */
class DBCreditCardNumberPipe {
    /**
     * Format Credit Card Number
     *
     * @param value - Value
     * @returns       - Formatted value
     */
    transform(value) {
        return DBFormatters.creditCardNumber.format(value);
    }
}
DBCreditCardNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCreditCardNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBCreditCardNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBCreditCardNumberPipe, name: "dbCreditCardNumber" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCreditCardNumberPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbCreditCardNumber',
                    pure: true,
                }]
        }] });

/**
 * DB DATE Pipe
 */
class DBDatePipe {
    /**
     * Format Date
     *
     * @param value - Value
     * @returns       - Formatted value
     */
    transform(value) {
        return DBFormatters.date.format(value);
    }
}
DBDatePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBDatePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDatePipe, name: "dbDate" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbDate',
                    pure: true,
                }]
        }] });

/**
 * DB IBAN Pipe
 */
class DBIbanPipe {
    /**
     * Format IBAN
     *
     * @param value - Value
     * @returns       - Formatted value
     */
    transform(value) {
        return DBFormatters.iban.format(value);
    }
}
DBIbanPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIbanPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBIbanPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBIbanPipe, name: "dbIban" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIbanPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbIban',
                    pure: true,
                }]
        }] });

/**
 * DB MONTH YEAR Pipe
 */
class DBMonthYearPipe {
    /**
     * Format MONTH YEAR
     *
     * @param value - Value
     * @returns       - Formatted value
     */
    transform(value) {
        return DBFormatters.monthYear.format(value);
    }
}
DBMonthYearPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMonthYearPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBMonthYearPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBMonthYearPipe, name: "dbMonthYear" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMonthYearPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbMonthYear',
                    pure: true,
                }]
        }] });

/**
 * DB Phone Number Pipe
 */
class DBPhoneNumberPipe {
    /**
     * Format Phone Number
     *
     * @param value - Value
     * @returns       - Formatted value
     */
    transform(value) {
        return DBFormatters.phoneNumber.format(value);
    }
}
DBPhoneNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPhoneNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBPhoneNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBPhoneNumberPipe, name: "dbPhoneNumber" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPhoneNumberPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbPhoneNumber',
                    pure: true,
                }]
        }] });

/**
 * DB Form Validation Module
 */
class DBFormattingModule {
}
DBFormattingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFormattingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBFormattingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBFormattingModule, declarations: [DBInputFormatDirective,
        DBIbanPipe,
        DBMonthYearPipe,
        DBNumberPipe,
        DBDatePipe,
        DBCreditCardPipe,
        DBPhoneNumberPipe,
        DBCreditCardNumberPipe], imports: [CommonModule], exports: [DBInputFormatDirective,
        DBIbanPipe,
        DBMonthYearPipe,
        DBNumberPipe,
        DBDatePipe,
        DBCreditCardPipe,
        DBPhoneNumberPipe,
        DBCreditCardNumberPipe] });
DBFormattingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFormattingModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFormattingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBInputFormatDirective,
                        DBIbanPipe,
                        DBMonthYearPipe,
                        DBNumberPipe,
                        DBDatePipe,
                        DBCreditCardPipe,
                        DBPhoneNumberPipe,
                        DBCreditCardNumberPipe
                    ],
                    exports: [
                        DBInputFormatDirective,
                        DBIbanPipe,
                        DBMonthYearPipe,
                        DBNumberPipe,
                        DBDatePipe,
                        DBCreditCardPipe,
                        DBPhoneNumberPipe,
                        DBCreditCardNumberPipe
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

/**
 * DB Slider Module
 */
class DBSliderModule {
}
DBSliderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBSliderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBSliderModule, declarations: [DBSliderComponent], imports: [CommonModule,
        DBFormattingModule], exports: [DBSliderComponent] });
DBSliderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderModule, imports: [CommonModule,
        DBFormattingModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBSliderComponent,
                    ],
                    exports: [
                        DBSliderComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBFormattingModule,
                    ],
                }]
        }] });

/**
 * DB Switch Component
 */
class DBSwitchComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     */
    constructor(changeDetector) {
        super(changeDetector);
        this.onLabel = '';
        this.offLabel = '';
        this.hideLabels = false;
        this.internalModel = false;
    }
    /**
     * Handle checkbox click event
     *
     * @param $event - Event object
     */
    onCheckboxClick($event) {
        $event.stopPropagation(); // Prevent the click event from being fired twice
    }
    /**
     * Handle checkbox change event
     */
    onCheckboxChange() {
        this.setInternalModel(!this.internalModel, false, true, true);
    }
    /**
     * Handle checkbox text label click event
     *
     * @param value - New model value
     */
    onCheckboxLabelClick(value) {
        if (this.disabled === false) {
            this.setInternalModel(value, false, true, true);
        }
    }
}
DBSwitchComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSwitchComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBSwitchComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBSwitchComponent, selector: "db-switch", inputs: { onLabel: "onLabel", offLabel: "offLabel", hideLabels: "hideLabels" }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBSwitchComponent),
        },
    ], usesInheritance: true, ngImport: i0, template: "<div\r\n  class=\"db-switch__label-text\" [class.db-switch__label-padding]=\"offLabel\"\r\n  *ngIf=\"!hideLabels\"\r\n  (click)=\"onCheckboxLabelClick( false )\">\r\n\r\n  {{ offLabel }}\r\n\r\n</div>\r\n<span *ngIf=\"hideLabels || !offLabel\" class=\"sr-only\">{{ offLabel }}</span>\r\n\r\n<label\r\n  class=\"db-switch__label\">\r\n\r\n  <input\r\n    class=\"db-switch__input\"\r\n    type=\"checkbox\"\r\n    [disabled]=\"disabled\"\r\n    [checked]=\"model\"\r\n    [value]=\"model\"\r\n    (click)=\"onCheckboxClick( $event )\"\r\n    (change)=\"onCheckboxChange()\">\r\n\r\n  <div\r\n    [class.db-switch__slider--hide-labels]=\"hideLabels\"\r\n    class=\"db-switch__slider\">\r\n  </div>\r\n\r\n</label>\r\n\r\n<span *ngIf=\"hideLabels || !onLabel\" class=\"sr-only\">{{ onLabel }}</span>\r\n<div\r\n  class=\"db-switch__label-text\" [class.db-switch__label-padding]=\"onLabel\"\r\n  *ngIf=\"!hideLabels\"\r\n  (click)=\"onCheckboxLabelClick( true )\">\r\n\r\n  {{ onLabel }}\r\n\r\n</div>\r\n\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSwitchComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBSwitchComponent),
                        },
                    ], selector: 'db-switch', template: "<div\r\n  class=\"db-switch__label-text\" [class.db-switch__label-padding]=\"offLabel\"\r\n  *ngIf=\"!hideLabels\"\r\n  (click)=\"onCheckboxLabelClick( false )\">\r\n\r\n  {{ offLabel }}\r\n\r\n</div>\r\n<span *ngIf=\"hideLabels || !offLabel\" class=\"sr-only\">{{ offLabel }}</span>\r\n\r\n<label\r\n  class=\"db-switch__label\">\r\n\r\n  <input\r\n    class=\"db-switch__input\"\r\n    type=\"checkbox\"\r\n    [disabled]=\"disabled\"\r\n    [checked]=\"model\"\r\n    [value]=\"model\"\r\n    (click)=\"onCheckboxClick( $event )\"\r\n    (change)=\"onCheckboxChange()\">\r\n\r\n  <div\r\n    [class.db-switch__slider--hide-labels]=\"hideLabels\"\r\n    class=\"db-switch__slider\">\r\n  </div>\r\n\r\n</label>\r\n\r\n<span *ngIf=\"hideLabels || !onLabel\" class=\"sr-only\">{{ onLabel }}</span>\r\n<div\r\n  class=\"db-switch__label-text\" [class.db-switch__label-padding]=\"onLabel\"\r\n  *ngIf=\"!hideLabels\"\r\n  (click)=\"onCheckboxLabelClick( true )\">\r\n\r\n  {{ onLabel }}\r\n\r\n</div>\r\n\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { onLabel: [{
                type: Input
            }], offLabel: [{
                type: Input
            }], hideLabels: [{
                type: Input
            }] } });

/**
 * DB Switch Module
 */
class DBSwitchModule {
}
DBSwitchModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSwitchModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBSwitchModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBSwitchModule, declarations: [DBSwitchComponent], imports: [CommonModule], exports: [DBSwitchComponent] });
DBSwitchModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSwitchModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSwitchModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBSwitchComponent,
                    ],
                    exports: [
                        DBSwitchComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

/**
 * DB Textarea Component
 */
class DBTextareaComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param         renderer       - Renderer
     * @param changeDetector - Change detector
     */
    constructor(renderer, changeDetector) {
        super(changeDetector);
        this.name = null; // Not used by default
        this.label = '';
        this.placeholder = null; // Not used by default
        this.maxlength = null; // Not used by default
        this.showCounter = false;
        this.required = false;
        this.focusOut = new EventEmitter();
        this.isFocused = false;
        this.internalModel = '';
        this.renderer = renderer;
        this.resizable = false;
    }
    /**
     * After View Init
     */
    ngAfterViewInit() {
        // We assign each label-textarea combination a unique ID - in addition to the implicit relationship those elements already have due to
        // the nesting. This way, we can ensure the highest #accessibility compatibility.
        // See: https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/label-has-for.md
        const id = `db-textarea--${DBTextareaComponent.id}`;
        // We use the renderer here to achieve a one-time binding (#perfmatters)
        this.renderer.setAttribute(this.textareaElement.nativeElement, 'id', id);
        this.renderer.setAttribute(this.labelElement.nativeElement, 'for', id);
        DBTextareaComponent.id++;
    }
    /**
     * Handle textarea input change event
     *
     * @param value - current value of the text area
     */
    onTextareaInput(value) {
        this.setInternalModel(value, false, true, true);
    }
    /**
     * Handle textarea focus event
     *
     */
    onTextareaFocus() {
        this.isFocused = true;
    }
    /**
     * Handle textarea blur event
     */
    onTextareaBlur() {
        this.doRender(this.internalModel); // Re-render normalized model
        this.isFocused = false;
        this.focusOut.emit(this.model);
        this.propagateTouched(this.model);
    }
    /**
     * Check if textarea is empty, has no focus, and no placeholder exists
     *
     * @returns - Flag, describing the result
     */
    isUsed() {
        return this.isFocused || !!this.placeholder || !!this.internalModel;
    }
    /**
     * Get current model length (for the counter)
     *
     * @returns - Model length
     */
    getModelLength() {
        return !!this.internalModel ? this.internalModel.length : 0;
    }
    /**
     * Do render the new textarea value
     *
     * @param value - New value to render into the textarea field
     */
    doRender(value) {
        this.renderer.setProperty(this.textareaElement.nativeElement, 'value', value);
    }
    /**
     * normalizes to emtpystring if null
     *
     * @param value
     */
    doNormalize(value) {
        return !value ? '' : value.trim(); // Trim the model
    }
    /**
     * Set internal model, probably with normalization, and return a flag describing whether the value has actually changed
     *
     * @param  value      - Value
     * @param doRender   - Flag, describing whether the rendered value has to be updated
     * @param emitEvent  - Flag, describing whether the model change event has to be emitted
     * @param emitChange - Flag, describing whether the Angular Forms propagate change method has to be called
     */
    setInternalModel(value, doRender, emitEvent, emitChange) {
        super.setInternalModel(value, doRender, emitEvent, emitChange);
    }
}
/**
 * Unique ID
 */
DBTextareaComponent.id = 0;
DBTextareaComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTextareaComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBTextareaComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBTextareaComponent, selector: "db-textarea", inputs: { name: "name", label: "label", placeholder: "placeholder", maxlength: "maxlength", showCounter: "showCounter", required: "required", resizable: "resizable" }, outputs: { focusOut: "focusOut" }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBTextareaComponent),
        },
    ], viewQueries: [{ propertyName: "textareaElement", first: true, predicate: ["textareaElement"], descendants: true, static: true }, { propertyName: "labelElement", first: true, predicate: ["labelElement"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<label\r\n  #labelElement\r\n  class=\"db-textarea__label\"\r\n  [class.db-textarea__label--disabled]=\"disabled\"\r\n  [class.db-textarea__label--readonly]=\"readonly\"\r\n  [class.db-textarea__label--focus]=\"isFocused\">\r\n\r\n  <span class=\"db-textarea__label-text\" [class.db-textarea__label-text--small]=\"isUsed()\">{{ label }}</span>\r\n\r\n  <span class=\"db-textarea__label-counter\" *ngIf=\"showCounter\">{{ getModelLength() }} / {{ maxlength }}</span>\r\n\r\n  <!--\r\n    NOTE:\r\n    For native HTML elements, we always prefer bindings using the '.attr' prefix. Doing so leads to attributes which are binded to the value\r\n    'null' getting removed (instead getting set to the value 'null' or an empty string). Eventually, this also prevents W3C HTML validation\r\n    issues (some attributes must not have an empty value).\r\n  -->\r\n  <textarea\r\n    #textareaElement\r\n    class=\"db-textarea__textarea\"\r\n    [ngClass]=\"resizable ? 'db-textarea__textarea-resize' : ''\"\r\n    [attr.name]=\"name\"\r\n    [attr.placeholder]=\"placeholder\"\r\n    [attr.maxlength]=\"maxlength\"\r\n    [disabled]=\"disabled\"\r\n    [readonly]=\"readonly\"\r\n    [required]=\"required\"\r\n    (focus)=\"onTextareaFocus()\"\r\n    (blur)=\"onTextareaBlur()\"\r\n    (input)=\"onTextareaInput( $event.target.value )\"></textarea>\r\n\r\n</label>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTextareaComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBTextareaComponent),
                        },
                    ], selector: 'db-textarea', template: "<label\r\n  #labelElement\r\n  class=\"db-textarea__label\"\r\n  [class.db-textarea__label--disabled]=\"disabled\"\r\n  [class.db-textarea__label--readonly]=\"readonly\"\r\n  [class.db-textarea__label--focus]=\"isFocused\">\r\n\r\n  <span class=\"db-textarea__label-text\" [class.db-textarea__label-text--small]=\"isUsed()\">{{ label }}</span>\r\n\r\n  <span class=\"db-textarea__label-counter\" *ngIf=\"showCounter\">{{ getModelLength() }} / {{ maxlength }}</span>\r\n\r\n  <!--\r\n    NOTE:\r\n    For native HTML elements, we always prefer bindings using the '.attr' prefix. Doing so leads to attributes which are binded to the value\r\n    'null' getting removed (instead getting set to the value 'null' or an empty string). Eventually, this also prevents W3C HTML validation\r\n    issues (some attributes must not have an empty value).\r\n  -->\r\n  <textarea\r\n    #textareaElement\r\n    class=\"db-textarea__textarea\"\r\n    [ngClass]=\"resizable ? 'db-textarea__textarea-resize' : ''\"\r\n    [attr.name]=\"name\"\r\n    [attr.placeholder]=\"placeholder\"\r\n    [attr.maxlength]=\"maxlength\"\r\n    [disabled]=\"disabled\"\r\n    [readonly]=\"readonly\"\r\n    [required]=\"required\"\r\n    (focus)=\"onTextareaFocus()\"\r\n    (blur)=\"onTextareaBlur()\"\r\n    (input)=\"onTextareaInput( $event.target.value )\"></textarea>\r\n\r\n</label>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input
            }], label: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], showCounter: [{
                type: Input
            }], required: [{
                type: Input
            }], resizable: [{
                type: Input
            }], focusOut: [{
                type: Output
            }], textareaElement: [{
                type: ViewChild,
                args: ['textareaElement', { static: true }]
            }], labelElement: [{
                type: ViewChild,
                args: ['labelElement', { static: true }]
            }] } });

/**
 * DB Textarea Module
 */
class DBTextareaModule {
}
DBTextareaModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTextareaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBTextareaModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBTextareaModule, declarations: [DBTextareaComponent], imports: [CommonModule], exports: [DBTextareaComponent] });
DBTextareaModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTextareaModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTextareaModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBTextareaComponent,
                    ],
                    exports: [
                        DBTextareaComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

/**
 * DB Detect Click Outside Directive
 */
class DBDetectClickOutsideDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        /**
         * Click outside event emitter
         */
        this.clickOutside = new EventEmitter();
    }
    /**
     * Listen to document click event to find if clicked outside
     * @param targetElement
     */
    onClick(targetElement) {
        if (!this.elementRef.nativeElement.contains(targetElement)) {
            this.clickOutside.emit(!!this.currentClickEvent);
        }
    }
    /**
     * Listen to host element click event to find if clicked outside
     * @param event
     */
    componentClick(event) {
        this.currentClickEvent = event;
    }
}
DBDetectClickOutsideDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDetectClickOutsideDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
DBDetectClickOutsideDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBDetectClickOutsideDirective, selector: "[dbDetectClickOutside]", outputs: { clickOutside: "clickOutside" }, host: { listeners: { "document:click": "onClick($event.target)", "click": "componentClick($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDetectClickOutsideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dbDetectClickOutside]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { clickOutside: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }], componentClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

// THIS FILE WAS AUTOMATICALLY GENERATED, DO NOT EDIT IT
/* eslint-disable*/
const config = {
    DB_TYPE_AHEAD: {
        NEXT: 'Weiter',
        BACK: 'Zurück',
        NO_RESULT: 'Es wurden keine Einträge für Ihre Suche gefunden.',
        MIN_LENGTH_WARNING: 'Bitte geben Sie mindestens drei Buchstaben ein.'
    }
};

/**
 * DB Type Ahead Data Service
 */
class DBTypeAheadDataService {
    /**
     * Constructor
     * @param basePath
     * @param apiConfig
     * @param http
     */
    constructor(basePath, http) {
        this.basePath = basePath;
        this.http = http;
    }
    /**
     * Get type ahead options from url provided
     * @param url
     * @returns - The Observable<HttpResponse<TypeAheadOptionsResponse>> response including http headers
     */
    getTypeAheadOptions(url) {
        return this.http.get(this.basePath.concat(url), { observe: 'response' });
    }
}
DBTypeAheadDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadDataService, deps: [{ token: 'BASE_URL' }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
DBTypeAheadDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: ['BASE_URL']
                    }] }, { type: i1$1.HttpClient }];
    } });

/**
 * DB Type Ahead Component
 */
class DBTypeAheadComponent extends DBCoreFormBaseDirective {
    /**
     * Constructor
     * @param typeAheadDataService
     * @param detectChange
     * @param elementRef
     */
    constructor(typeAheadDataService, detectChange, elementRef) {
        super(detectChange);
        this.typeAheadDataService = typeAheadDataService;
        this.detectChange = detectChange;
        this.elementRef = elementRef;
        this.showScrollBar = false;
        this.typeAheadOptions = [];
        this.localDataIndexOffset = 0;
        this.config = config.DB_TYPE_AHEAD;
        this.pageSize = 4;
        this.keyAttribute = 'value';
        this.valueAttribute = 'key';
        this.descAttribute = 'description';
        this.minLengthForTypeAhead = 3;
        this.selectOption = new EventEmitter();
        // Each checkbox need the same unique name to get grouped together correctly
        this.name = `type-ahead-${DBTypeAheadComponent.typeAheadCounter}`;
        this.label = '';
        this.restrictedTerms = [];
        this.componentDestroyEvent = new Subject();
        this.inputChangesEvent = new Subject();
        DBTypeAheadComponent.typeAheadCounter++;
    }
    /**
     * After View Init Lifecycle Hook
     */
    ngAfterViewInit() {
        this.setupInputChangesListener();
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        this.componentDestroyEvent.next();
        this.componentDestroyEvent.complete();
        this.inputChangesEvent.complete();
    }
    /**
     * Updates input model and form value on option selection.
     * @param selectedOption
     */
    onOptionSelected(selectedOption) {
        this.setInternalModel({ key: selectedOption[this.keyAttribute], value: selectedOption[this.valueAttribute] }, false, true, true);
        this.inputComponentRef.model = selectedOption[this.keyAttribute];
        this.showTypeAheadOptions = false;
        this.selectOption.emit(selectedOption[this.valueAttribute]);
    }
    /**
     * Reset the type ahead results on input change
     */
    resetOptionResults() {
        this.setInternalModel(null, false, true, true);
        this.showTypeAheadOptions = false;
        this.typeAheadOptions = [];
        this.localDataIndexOffset = 0;
        this.selectOption.emit('');
        this.inputChangesEvent.next();
    }
    /**
     * To handle navigation event of pagination
     * @param event
     * @param direction
     */
    onNavigate(event, direction) {
        event.stopImmediatePropagation();
        if (this.url) {
            const navUrl = (direction === 'next' ? this.nextUrl : this.previousUrl);
            this.getSearchResultFromRemote(`${this.url}${navUrl}`);
        }
        else {
            this.localDataIndexOffset =
                (direction === 'next' ? this.localDataIndexOffset + this.pageSize : this.localDataIndexOffset - this.pageSize);
            this.getSearchResultFromLocal();
        }
    }
    /**
     * Manipulate the json object to populate the type ahead search list.
     * @Param searchResult
     */
    showRecords(searchResult) {
        this.typeAheadOptions = this.filterRestrictedTerms(searchResult.sort((a, b) => this.compare(a, b)));
        this.showTypeAheadOptions = true;
        this.showScrollBar = false;
        this.detectChange.markForCheck();
        this.calculateModuleHeight();
    }
    /**
     * close on click outside
     */
    hideTypeAheadOptions(isTouched) {
        if (isTouched) {
            this.propagateTouched(this.model);
        }
        this.showTypeAheadOptions = false;
    }
    /**
     * Helper function to avoid access to null or undefined when trying to access any deeply nested property of an unsafe object.
     * Example: safeGet(objectRef, 'path.to.nested.property')
     * @param obj - object to fetch data from.
     * @param path - path to the nested property inside the given object.
     * @returns - the nested value if available else null or undefined.
     */
    safeGet(obj, path) {
        return path.split('.').reduce((accValue, currValue) => (typeof accValue === 'undefined' || accValue === null) ? accValue : accValue[currValue], obj);
    }
    /**
     * Is next data set exists, its used to display next button
     * @returns boolean
     */
    showNextButton() {
        return !!this.nextUrl || (this.filteredLocalData && this.localDataIndexOffset + this.pageSize < this.filteredLocalData.length);
    }
    /**
     * If previous data set exists, it is used to display previous button
     * @returns boolean
     */
    showPreviousButton() {
        return !!this.previousUrl || this.localDataIndexOffset > 0;
    }
    /**
     * Get results on click, if minLengthForTypeAhead is 0
     */
    getSearchResultsOnClick() {
        if (this.minLengthForTypeAhead === 0) {
            if (this.url) {
                this.getSearchResultFromRemote(`${this.url}?searchKey=${btoa('')}`);
            }
            else {
                this.getSearchResultFromLocal();
            }
        }
    }
    /**
     * Do render the type ahead value
     * @param value - New value to render
     */
    doRender(value) {
        this.updateInputComponent();
    }
    /**
     * Custom compare function for the search results
     * @param a - type ahead option to be compared
     * @param b - type ahead option to be compared
     * @returns {number}
     */
    compare(a, b) {
        const valueA = (a[this.keyAttribute] || '').toLowerCase();
        const valueB = (b[this.keyAttribute] || '').toLowerCase();
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
    }
    filterRestrictedTerms(searchResults) {
        if (this.restrictedTerms.length === 0) {
            return searchResults;
        }
        return searchResults.filter((searchResult) => !this.restrictedTerms.some(restrictedTerm => searchResult[this.keyAttribute].indexOf(restrictedTerm) > -1));
    }
    /**
     * Get search results from remote using url
     * @param url
     */
    getSearchResultFromRemote(url) {
        this.typeAheadDataService.getTypeAheadOptions(url)
            .pipe(takeUntil(this.componentDestroyEvent), takeUntil(this.inputChangesEvent))
            .subscribe((response) => {
            this.handleSearchResult(response);
        });
    }
    /**
     * Get search result from locally provided data
     */
    getSearchResultFromLocal() {
        this.filteredLocalData = this.localData.filter((option) => option[this.keyAttribute].toLowerCase().indexOf(this.inputComponentRef.model.toLowerCase()) > -1);
        this.showRecords(this.filteredLocalData.slice(this.localDataIndexOffset, this.localDataIndexOffset + this.pageSize));
    }
    /**
     * Update all input child components to reflect the new selection state
     */
    updateInputComponent() {
        if (this.inputComponentRef) {
            this.inputComponentRef.model = this.internalModel ? this.internalModel.key : null;
        }
    }
    /**
     * Calculate the height of the search list box to be shown according to records
     */
    calculateModuleHeight() {
        if (this.searchList && this.searchList.nativeElement && this.searchList.nativeElement.offsetParent) {
            this.showScrollBar = this.searchList.nativeElement.offsetParent.offsetHeight > 220;
        }
        this.detectChange.detectChanges();
    }
    /**
     * Handle search results
     * @param response
     */
    handleSearchResult(response) {
        this.parseNavigationLink(response.headers.get('link') || '');
        const responseOptions = this.responseBodyOptionsPath
            ? this.safeGet(response.body, this.responseBodyOptionsPath) || []
            : response.body;
        if (responseOptions.length === 0) {
            this.showTypeAheadOptions = false;
        }
        else {
            this.showRecords(responseOptions);
        }
    }
    /**
     * Parse link for next and previous navigation buttons.
     * @param link
     */
    parseNavigationLink(link) {
        const result = {};
        const urls = link.split(',');
        urls.forEach((value) => {
            const urlRegex = /<(.*?)>/;
            const relationRegex = /rel="(.*?)\"/;
            result[value.match(relationRegex) ? value.match(relationRegex)[1] : null] =
                value.match(urlRegex) ? value.match(urlRegex)[1] : null;
        });
        this.nextUrl = result['next'];
        this.previousUrl = result['prev'];
    }
    /**
     * Initializes subscription to input changes.
     */
    setupInputChangesListener() {
        from(this.inputComponentRef.modelChange)
            .pipe(tap(() => this.resetOptionResults()), debounceTime(1000), distinctUntilChanged(), takeUntil(this.componentDestroyEvent)).subscribe((text) => {
            if (text.length >= this.minLengthForTypeAhead) {
                if (this.url) {
                    this.getSearchResultFromRemote(`${this.url}?searchKey=${btoa(text)}`);
                }
                else {
                    this.getSearchResultFromLocal();
                }
            }
        });
    }
}
/**
 * Unique ID
 */
DBTypeAheadComponent.typeAheadCounter = 0;
DBTypeAheadComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadComponent, deps: [{ token: DBTypeAheadDataService }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DBTypeAheadComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBTypeAheadComponent, selector: "db-type-ahead", inputs: { config: "config", label: "label", localData: "localData", url: "url", pageSize: "pageSize", keyAttribute: "keyAttribute", valueAttribute: "valueAttribute", descAttribute: "descAttribute", minLengthForTypeAhead: "minLengthForTypeAhead", restrictedTerms: "restrictedTerms", responseBodyOptionsPath: "responseBodyOptionsPath" }, outputs: { selectOption: "selectOption" }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBTypeAheadComponent),
        },
    ], viewQueries: [{ propertyName: "inputComponentRef", first: true, predicate: i0.forwardRef(function () { return DBInputComponent; }), descendants: true, static: true }, { propertyName: "searchList", first: true, predicate: ["searchList"], descendants: true, read: ElementRef, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"db-type-ahead\" dbDetectClickOutside (clickOutside)=\"hideTypeAheadOptions( $event )\">\r\n  <db-input [ngClass]=\"{'remove-border-radius': showTypeAheadOptions ||\r\n  ( inputComponentRef.isFocused && inputComponentRef.model.length < minLengthForTypeAhead ) }\"\r\n            [label]=\"label\"\r\n            [readonly]=\"readonly\"\r\n            (click)=\"getSearchResultsOnClick()\"\r\n            autocomplete=\"off\"\r\n            >\r\n    <ng-content></ng-content>\r\n  </db-input>\r\n  <div class=\"db-type-ahead__drop-down\" *ngIf=\"showTypeAheadOptions\">\r\n    <div *ngIf=\"typeAheadOptions.length > 0\">\r\n      <div class=\"db-type-ahead__record-panel\" [class.db-type-ahead-input__record-panel--scrollable]=\"showScrollBar\" #searchList>\r\n        <div class=\"db-type-ahead__record-panel__entry\" *ngFor=\"let option of typeAheadOptions\"\r\n             (click)=\"onOptionSelected(option)\">\r\n          <p class=\"m-0 db-type-ahead__record-panel__title\">{{ safeGet(option, keyAttribute) }}</p>\r\n          <p class=\"m-0 db-type-ahead__record-panel__text\">{{ safeGet(option, descAttribute) }}</p>\r\n        </div>\r\n      </div>\r\n      <div class=\"d-md-flex justify-content-md-end text-right py-4 pr-md-4 db-type-ahead__navigation-buttons\" *ngIf=\"showPreviousButton() || showNextButton()\">\r\n        <db-button class=\"mx-2\"\r\n                   size=\"sm\"\r\n                   design=\"secondary\"\r\n                   arrow=\"left\"\r\n                   *ngIf=\"showPreviousButton()\"\r\n                   (click)=\"onNavigate( $event, 'previous' )\">\r\n          {{ config?.BACK }}\r\n        </db-button>\r\n        <db-button class=\"mx-2\"\r\n                   size=\"sm\"\r\n                   design=\"secondary\"\r\n                   arrow=\"right\"\r\n                   *ngIf=\"showNextButton()\"\r\n                   (click)=\"onNavigate( $event, 'next' )\">\r\n          {{ config?.NEXT }}\r\n        </db-button>\r\n      </div>\r\n    </div>\r\n    <p *ngIf=\"typeAheadOptions.length === 0\" class=\"p-3 db-type-ahead__information\">\r\n      {{ config?.NO_RESULT }}\r\n    </p>\r\n  </div>\r\n  <div *ngIf=\"inputComponentRef.isFocused && inputComponentRef.model.length < minLengthForTypeAhead\" class=\"db-type-ahead__drop-down\">\r\n    <p class=\"pl-3 db-type-ahead__information db-text-5\">{{ config?.MIN_LENGTH_WARNING }}</p>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBButtonComponent, selector: "db-button", inputs: ["type", "design", "size", "arrow", "loading", "iconOnly"] }, { kind: "component", type: DBInputComponent, selector: "db-input", inputs: ["type", "name", "autocomplete", "spellcheck", "label", "placeholder", "maxlength", "min", "max", "step", "inputmode", "loading", "showExtensionText", "showStepper", "showCounter"], outputs: ["focusOut"] }, { kind: "directive", type: DBDetectClickOutsideDirective, selector: "[dbDetectClickOutside]", outputs: ["clickOutside"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBTypeAheadComponent),
                        },
                    ], selector: 'db-type-ahead', template: "<div class=\"db-type-ahead\" dbDetectClickOutside (clickOutside)=\"hideTypeAheadOptions( $event )\">\r\n  <db-input [ngClass]=\"{'remove-border-radius': showTypeAheadOptions ||\r\n  ( inputComponentRef.isFocused && inputComponentRef.model.length < minLengthForTypeAhead ) }\"\r\n            [label]=\"label\"\r\n            [readonly]=\"readonly\"\r\n            (click)=\"getSearchResultsOnClick()\"\r\n            autocomplete=\"off\"\r\n            >\r\n    <ng-content></ng-content>\r\n  </db-input>\r\n  <div class=\"db-type-ahead__drop-down\" *ngIf=\"showTypeAheadOptions\">\r\n    <div *ngIf=\"typeAheadOptions.length > 0\">\r\n      <div class=\"db-type-ahead__record-panel\" [class.db-type-ahead-input__record-panel--scrollable]=\"showScrollBar\" #searchList>\r\n        <div class=\"db-type-ahead__record-panel__entry\" *ngFor=\"let option of typeAheadOptions\"\r\n             (click)=\"onOptionSelected(option)\">\r\n          <p class=\"m-0 db-type-ahead__record-panel__title\">{{ safeGet(option, keyAttribute) }}</p>\r\n          <p class=\"m-0 db-type-ahead__record-panel__text\">{{ safeGet(option, descAttribute) }}</p>\r\n        </div>\r\n      </div>\r\n      <div class=\"d-md-flex justify-content-md-end text-right py-4 pr-md-4 db-type-ahead__navigation-buttons\" *ngIf=\"showPreviousButton() || showNextButton()\">\r\n        <db-button class=\"mx-2\"\r\n                   size=\"sm\"\r\n                   design=\"secondary\"\r\n                   arrow=\"left\"\r\n                   *ngIf=\"showPreviousButton()\"\r\n                   (click)=\"onNavigate( $event, 'previous' )\">\r\n          {{ config?.BACK }}\r\n        </db-button>\r\n        <db-button class=\"mx-2\"\r\n                   size=\"sm\"\r\n                   design=\"secondary\"\r\n                   arrow=\"right\"\r\n                   *ngIf=\"showNextButton()\"\r\n                   (click)=\"onNavigate( $event, 'next' )\">\r\n          {{ config?.NEXT }}\r\n        </db-button>\r\n      </div>\r\n    </div>\r\n    <p *ngIf=\"typeAheadOptions.length === 0\" class=\"p-3 db-type-ahead__information\">\r\n      {{ config?.NO_RESULT }}\r\n    </p>\r\n  </div>\r\n  <div *ngIf=\"inputComponentRef.isFocused && inputComponentRef.model.length < minLengthForTypeAhead\" class=\"db-type-ahead__drop-down\">\r\n    <p class=\"pl-3 db-type-ahead__information db-text-5\">{{ config?.MIN_LENGTH_WARNING }}</p>\r\n  </div>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: DBTypeAheadDataService }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }]; }, propDecorators: { config: [{
                type: Input
            }], label: [{
                type: Input
            }], localData: [{
                type: Input
            }], url: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], keyAttribute: [{
                type: Input
            }], valueAttribute: [{
                type: Input
            }], descAttribute: [{
                type: Input
            }], minLengthForTypeAhead: [{
                type: Input
            }], restrictedTerms: [{
                type: Input
            }], responseBodyOptionsPath: [{
                type: Input
            }], selectOption: [{
                type: Output
            }], inputComponentRef: [{
                type: ViewChild,
                args: [forwardRef(() => DBInputComponent), { static: true }]
            }], searchList: [{
                type: ViewChild,
                args: ['searchList', { read: ElementRef, static: true }]
            }] } });

/**
 * DB Type Ahead Module
 */
class DBTypeAheadModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('DBTypeAheadModule is already loaded. Import it in the AppModule only');
        }
    }
}
DBTypeAheadModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadModule, deps: [{ token: DBTypeAheadModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
DBTypeAheadModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadModule, declarations: [DBTypeAheadComponent,
        DBDetectClickOutsideDirective], imports: [CommonModule,
        // DB Platform
        DBButtonModule,
        DBInputModule], exports: [DBTypeAheadComponent] });
DBTypeAheadModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadModule, providers: [
        DBTypeAheadDataService
    ], imports: [CommonModule,
        // DB Platform
        DBButtonModule,
        DBInputModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTypeAheadModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBTypeAheadComponent,
                        DBDetectClickOutsideDirective,
                    ],
                    exports: [
                        DBTypeAheadComponent,
                    ],
                    imports: [
                        CommonModule,
                        // DB Platform
                        DBButtonModule,
                        DBInputModule,
                    ],
                    providers: [
                        DBTypeAheadDataService
                    ],
                }]
        }], ctorParameters: function () {
        return [{ type: DBTypeAheadModule, decorators: [{
                        type: Optional
                    }, {
                        type: SkipSelf
                    }] }];
    } });

/* eslint-disable @typescript-eslint/member-ordering */
class DBVolumeSliderComponent extends DBCoreFormBaseDirective {
    constructor(changeDetector, el) {
        super(changeDetector);
        this.el = el;
        /**
         * The title of the slider
         */
        this.title = '';
        /**
         * The container height percent relative to the default value (for custom heights)
         */
        this.heightPercent = 100;
        /**
         * Emitted when the user interacts (click / drag) with the slider
         */
        this.thumbMoved = new EventEmitter();
        this._thumbLeft = false;
        this.internalModel = '';
    }
    /**
     * The physical limits of the slider
     */
    set minLimit(limit) {
        this._minLimit = Number(limit) || 0;
    }
    get minLimit() {
        return this._minLimit;
    }
    set maxLimit(limit) {
        this._maxLimit = Number(limit) || 100;
    }
    get maxLimit() {
        return this._maxLimit;
    }
    /**
     * The value increments
     */
    set step(increment) {
        this._step = Number(increment) || 5;
    }
    get step() {
        return this._step;
    }
    /**
     * The slider value (two-way binding)
     */
    set model(value) {
        let newValue = Number(value) || 0;
        if (this.sliderValue === newValue) {
            return;
        }
        if (newValue < this.minLimit) {
            newValue = Number(this.minLimit);
        }
        if (newValue > this.maxLimit) {
            newValue = Number(this.maxLimit);
        }
        this.updateFilling(newValue);
        this.setInternalModel(newValue.toString(), false, false, true);
    }
    get model() {
        return this.internalModel;
    }
    /**
     * Used to display the thumb on the left side
     */
    set thumbLeft(isLeft) {
        this._thumbLeft = String(isLeft) === 'true';
    }
    get thumbLeft() {
        return this._thumbLeft;
    }
    ngOnInit() {
        this.performInputChecks();
        this.checkValueLimits();
        this.updateFilling(Number(this.model));
    }
    /**
     * Read HTML containers data to help in click calculations when sliding
     */
    ngAfterViewInit() {
        this.changeDetector.detectChanges();
    }
    /**
     * Called when a keyboard event is fired.
     * Updates the model
     */
    onHandleKeypress(event) {
        let valueToChange = Number(this.model);
        const stepsNr = (this.maxValue - this.minValue) / this.step;
        const fastIncrement = stepsNr >= 100 ? this.step * 10 : this.step * 2;
        const increment = event.shiftKey ? fastIncrement : this.step;
        if (!this.el.nativeElement.contains(event.target)) {
            return;
        }
        switch (event.keyCode) {
            case 35: /* End key*/
                valueToChange = this.maxValue;
                event.preventDefault();
                break;
            case 36: /* Home key*/
                valueToChange = this.minValue;
                event.preventDefault();
                break;
            case 38: /* Up key */
                valueToChange += increment;
                event.preventDefault();
                break;
            case 40: /* Down key */
                valueToChange -= increment;
                event.preventDefault();
                break;
            default:
                return;
        }
        if (valueToChange.toString() === this.model) {
            return;
        }
        if (valueToChange < this.minValue) {
            valueToChange = this.minValue;
        }
        if (valueToChange > this.maxValue) {
            valueToChange = this.maxValue;
        }
        this.setInternalModel(valueToChange.toString(), false, true, true);
        this.thumbMoved.emit(valueToChange);
    }
    /**
     * Formats the slider value to be displayed in HTML
     */
    formatLabelValue(sliderValue) {
        return sliderValue === null || sliderValue === void 0 ? void 0 : sliderValue.toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    /**
     * Left-click handler which enables the dragging functionality
     */
    onContainerMouseDown(event) {
        document.onmousemove = this.onContainerDrag.bind(this);
        document.onmouseup = this.closeDragElement;
        this.onContainerDrag(event);
    }
    /**
     * Touch handler which enables dragging on the slider
     */
    onContainerTouch(event) {
        document.ontouchmove = this.onContainerDrag.bind(this);
        document.ontouchcancel = this.closeDragElement;
        document.ontouchend = this.closeDragElement;
        this.onContainerDrag(event);
    }
    /**
     * Container drag handler
     */
    onContainerDrag(event) {
        const percent = this.calculatePercent(this.getClickPoint(event));
        let selectedValue = this.getValueByPercent(percent);
        this.setFocus(event);
        /* Disables text selection while dragging */
        event.preventDefault();
        /* Safety check if the value exceeds the limits */
        selectedValue = Math.min(selectedValue, this.maxValue);
        selectedValue = Math.max(selectedValue, this.minValue);
        /* Blocks clicking on the same area or dragging over the limit */
        if (this.sliderValue === selectedValue) {
            return;
        }
        this.setInternalModel(selectedValue.toString(), false, true, true);
        this.thumbMoved.emit(selectedValue);
    }
    /**
     * Adjusts the container height (visible area) of the slider
     */
    updateContainerHeight() {
        this.containerHeight = this.container.nativeElement.parentElement.clientHeight * 0.01 * this.heightPercent;
        this.changeDetector.detectChanges();
    }
    /**
     * Manually setting the focus on the selected element
     */
    setFocus(event) {
        if (this.el.nativeElement.contains(event.target)) {
            this.el.nativeElement.querySelector('.db-volume-slider__thumb').focus();
        }
    }
    /**
     * Retrieve the vertical coordinate of the click / touch
     */
    getClickPoint(event) {
        return event.clientY || event.touches[0].clientY;
    }
    /**
     * Fills the slider to the selected value
     */
    updateFilling(value) {
        this.sliderValue = value;
        this.fillingPercent = this.getPercentByValue(value);
    }
    /**
     * Uses the distance from the sliding area to the top of the page
     * to determine the filling percent of the slider based on the clickY coordinate
     */
    calculatePercent(clickY) {
        const containerTopOffset = this.container.nativeElement.getBoundingClientRect().top;
        const pixelsFromContainerTop = clickY - containerTopOffset;
        const percent = 100 - (pixelsFromContainerTop * 100) / this.container.nativeElement.clientHeight;
        if (percent < 0) {
            return 0;
        }
        if (percent > 100) {
            return 100;
        }
        return percent;
    }
    /**
     * Retrieve the selected value based on the limits and the filling percent
     */
    getValueByPercent(percent) {
        const value = this.minLimit +
            Math.round((this.maxLimit - this.minLimit) * percent * 0.01);
        if (this.step && value % this.step !== 0) {
            return value + (this.step - (value % this.step));
        }
        return value;
    }
    /**
     * Retrieve the percent based on the selected value
     */
    getPercentByValue(value) {
        return (value - this.minLimit) / (0.01 * (this.maxLimit - this.minLimit));
    }
    /**
     * Stop the dragging when the left mouse button / touch is released
     */
    closeDragElement() {
        document.onmouseup = null;
        document.onmousemove = null;
        document.ontouchmove = null;
        document.ontouchend = null;
        document.ontouchcancel = null;
    }
    /**
     * Set the logical restrictions as the limits, if not specified
     */
    checkValueLimits() {
        if (this.minValue === undefined || this.minValue < this.minLimit) {
            this.minValue = this.minLimit;
        }
        if (this.maxValue === undefined || this.maxValue > this.maxLimit) {
            this.maxValue = this.maxLimit;
        }
    }
    /**
     * Checks that the input values provided are correct
     */
    performInputChecks() {
        if (this.maxLimit % this.step !== 0 || this.minLimit % this.step !== 0) {
            throw new Error('Please input a step value to divide to the slider limits.');
        }
        if (this.maxLimit <= this.minLimit || Number(this.model) > this.maxLimit || Number(this.model) < this.minLimit) {
            throw new Error('Slider limits or value incorrectly set');
        }
    }
}
DBVolumeSliderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBVolumeSliderComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DBVolumeSliderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBVolumeSliderComponent, selector: "db-volume-slider", inputs: { minLimit: "minLimit", maxLimit: "maxLimit", step: "step", model: "model", minValue: "minValue", maxValue: "maxValue", thumbLeft: "thumbLeft", title: "title", heightPercent: "heightPercent" }, outputs: { thumbMoved: "thumbMoved" }, host: { listeners: { "document:keydown": "onHandleKeypress($event)" } }, providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBVolumeSliderComponent),
        },
    ], viewQueries: [{ propertyName: "container", first: true, predicate: ["container"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"db-volume-slider__wrapper\" [class.db-volume-slider__wrapper--left]=\"thumbLeft === true\">\r\n  <div #container class=\"db-volume-slider__container\" [style.height.%]=\"heightPercent\"\r\n  (mousedown)=\"onContainerMouseDown($event)\" (touchstart)=\"onContainerTouch($event)\">\r\n    <p class=\"db-volume-slider__title\">{{title}}</p>\r\n    <div #filling class=\"db-volume-slider__container__filling\" [style.height.%]=\"fillingPercent\">\r\n      <div class=\"db-volume-slider__line-marker\" [class.db-volume-slider__line-marker--left]=\"thumbLeft === true\">\r\n        <div class=\"db-volume-slider__drag-container\"\r\n          [class.db-volume-slider__drag-container--left]=\"thumbLeft === true\">\r\n          <div class=\"db-volume-slider__thumb\" tabindex='0'></div>\r\n        </div>\r\n      </div>\r\n      <div #valueLabel class=\"db-volume-slider__value-label\"\r\n        [class.db-volume-slider__value-label--above]=\"valueLabel.clientHeight > filling.clientHeight\">\r\n        {{ formatLabelValue(sliderValue) }}\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBVolumeSliderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-volume-slider', providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBVolumeSliderComponent),
                        },
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"db-volume-slider__wrapper\" [class.db-volume-slider__wrapper--left]=\"thumbLeft === true\">\r\n  <div #container class=\"db-volume-slider__container\" [style.height.%]=\"heightPercent\"\r\n  (mousedown)=\"onContainerMouseDown($event)\" (touchstart)=\"onContainerTouch($event)\">\r\n    <p class=\"db-volume-slider__title\">{{title}}</p>\r\n    <div #filling class=\"db-volume-slider__container__filling\" [style.height.%]=\"fillingPercent\">\r\n      <div class=\"db-volume-slider__line-marker\" [class.db-volume-slider__line-marker--left]=\"thumbLeft === true\">\r\n        <div class=\"db-volume-slider__drag-container\"\r\n          [class.db-volume-slider__drag-container--left]=\"thumbLeft === true\">\r\n          <div class=\"db-volume-slider__thumb\" tabindex='0'></div>\r\n        </div>\r\n      </div>\r\n      <div #valueLabel class=\"db-volume-slider__value-label\"\r\n        [class.db-volume-slider__value-label--above]=\"valueLabel.clientHeight > filling.clientHeight\">\r\n        {{ formatLabelValue(sliderValue) }}\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }]; }, propDecorators: { minLimit: [{
                type: Input
            }], maxLimit: [{
                type: Input
            }], step: [{
                type: Input
            }], model: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], thumbLeft: [{
                type: Input
            }], title: [{
                type: Input
            }], heightPercent: [{
                type: Input
            }], thumbMoved: [{
                type: Output
            }], container: [{
                type: ViewChild,
                args: ['container', { static: true }]
            }], onHandleKeypress: [{
                type: HostListener,
                args: ['document:keydown', ['$event']]
            }] } });

/**
 * DB Volume Slider Module
 */
class DBVolumeSliderModule {
}
DBVolumeSliderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBVolumeSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBVolumeSliderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBVolumeSliderModule, declarations: [DBVolumeSliderComponent], imports: [CommonModule], exports: [DBVolumeSliderComponent] });
DBVolumeSliderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBVolumeSliderModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBVolumeSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBVolumeSliderComponent,
                    ],
                    exports: [
                        DBVolumeSliderComponent,
                    ],
                    imports: [
                        CommonModule
                    ],
                }]
        }] });

class DBAlertComponent {
    constructor() {
        this.status = 'success';
        this.title = '';
        this.contact = '';
        this.contactIcon = null;
        this.contactSubText = '';
        this.design = 'panel';
        this.size = 'lg';
    }
}
DBAlertComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBAlertComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBAlertComponent, selector: "db-alert", inputs: { status: "status", title: "title", contact: "contact", contactIcon: "contactIcon", contactSubText: "contactSubText", design: "design", size: "size" }, ngImport: i0, template: "<div\r\n  [ngClass]=\"{\r\n    'db-alert__notification--sm': design === 'notification' && size === 'sm',\r\n    'db-alert__notification mt-5 mt-md-6': design === 'notification',\r\n    'db-alert__panel': design === 'panel'\r\n  }\"\r\n>\r\n  <div\r\n    class=\"db-alert d-flex\"\r\n    [ngClass]=\"{\r\n      'justify-content-center': design === 'panel',\r\n      'db-alert--success': status === 'success',\r\n      'db-alert--warning': status === 'warning',\r\n      'db-alert--error': status === 'error',\r\n      'db-alert--info': status === 'info'\r\n    }\"\r\n  >\r\n    <div\r\n      [ngClass]=\"{\r\n        'align-items-center flex-column d-flex db-alert__container':\r\n          design === 'panel'\r\n      }\"\r\n    >\r\n      <div class=\"db-alert__status-container\">\r\n        <db-icon\r\n          size=\"100%\"\r\n          class=\"db-alert__status\"\r\n          [ngClass]=\"{\r\n            'db-alert__status--success': status === 'success',\r\n            'db-alert__status--warning': status === 'warning',\r\n            'db-alert__status--error': status === 'error',\r\n            'db-alert__status--info': status === 'info',\r\n            'db-alert__title--empty': !title\r\n          }\"\r\n        >\r\n          {{\r\n            this.status === \"success\"\r\n              ? \"success-filled\"\r\n              : this.status === \"error\"\r\n              ? \"error-filled\"\r\n              : this.status === \"warning\"\r\n              ? \"warning-filled\"\r\n              : this.status === \"info\"\r\n              ? \"info-filled\"\r\n              : \"\"\r\n          }}\r\n        </db-icon>\r\n      </div>\r\n\r\n      <div\r\n        *ngIf=\"title\"\r\n        role=\"heading\"\r\n        aria-level=\"3\"\r\n        [ngClass]=\"{\r\n          'db-heading-4 mt-md-5 mb-1': design === 'notification',\r\n          'db-heading-3 my-4': design === 'panel'\r\n        }\"\r\n        class=\"db-alert__title\"\r\n      >\r\n        {{ title }}\r\n      </div>\r\n\r\n      <div\r\n        [ngClass]=\"{\r\n          'db-text-4': design === 'panel' || (design === 'notification' && size !=='sm'),\r\n          'no-title': design === 'notification' && !title,\r\n          'db-text-6': design === 'notification' && size === 'sm',\r\n          'mb-5 mb-md-6':\r\n            contact\r\n        }\"\r\n        class=\"db-alert__content\"\r\n      >\r\n        <ng-content></ng-content>\r\n      </div>\r\n\r\n      <a\r\n        *ngIf=\"contact && design === 'panel'\"\r\n        [href]=\"\r\n          contactIcon === 'phone' ? 'tel:' + contact : 'mailto:' + contact\r\n        \"\r\n        class=\"db-text-3 db-text-bold db-alert__contact\"\r\n      >\r\n        <db-icon\r\n          *ngIf=\"contactIcon\"\r\n          class=\"mr-2 db-alert__contact-icon\"\r\n          size=\"100%\"\r\n          >{{ contactIcon }}</db-icon\r\n        >{{ contact }}\r\n      </a>\r\n      <div\r\n        *ngIf=\"contactSubText && design === 'panel'\"\r\n        class=\"db-text-4 mt-1 db-alert__content-subtext\"\r\n      >\r\n        {{ contactSubText }}\r\n      </div>\r\n      <span *ngIf=\"links.children.length > 0\" class=\"mt-4\"></span>\r\n      <div #links>\r\n        <ng-content select=\"[slot=links]\"></ng-content>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAlertComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-alert', template: "<div\r\n  [ngClass]=\"{\r\n    'db-alert__notification--sm': design === 'notification' && size === 'sm',\r\n    'db-alert__notification mt-5 mt-md-6': design === 'notification',\r\n    'db-alert__panel': design === 'panel'\r\n  }\"\r\n>\r\n  <div\r\n    class=\"db-alert d-flex\"\r\n    [ngClass]=\"{\r\n      'justify-content-center': design === 'panel',\r\n      'db-alert--success': status === 'success',\r\n      'db-alert--warning': status === 'warning',\r\n      'db-alert--error': status === 'error',\r\n      'db-alert--info': status === 'info'\r\n    }\"\r\n  >\r\n    <div\r\n      [ngClass]=\"{\r\n        'align-items-center flex-column d-flex db-alert__container':\r\n          design === 'panel'\r\n      }\"\r\n    >\r\n      <div class=\"db-alert__status-container\">\r\n        <db-icon\r\n          size=\"100%\"\r\n          class=\"db-alert__status\"\r\n          [ngClass]=\"{\r\n            'db-alert__status--success': status === 'success',\r\n            'db-alert__status--warning': status === 'warning',\r\n            'db-alert__status--error': status === 'error',\r\n            'db-alert__status--info': status === 'info',\r\n            'db-alert__title--empty': !title\r\n          }\"\r\n        >\r\n          {{\r\n            this.status === \"success\"\r\n              ? \"success-filled\"\r\n              : this.status === \"error\"\r\n              ? \"error-filled\"\r\n              : this.status === \"warning\"\r\n              ? \"warning-filled\"\r\n              : this.status === \"info\"\r\n              ? \"info-filled\"\r\n              : \"\"\r\n          }}\r\n        </db-icon>\r\n      </div>\r\n\r\n      <div\r\n        *ngIf=\"title\"\r\n        role=\"heading\"\r\n        aria-level=\"3\"\r\n        [ngClass]=\"{\r\n          'db-heading-4 mt-md-5 mb-1': design === 'notification',\r\n          'db-heading-3 my-4': design === 'panel'\r\n        }\"\r\n        class=\"db-alert__title\"\r\n      >\r\n        {{ title }}\r\n      </div>\r\n\r\n      <div\r\n        [ngClass]=\"{\r\n          'db-text-4': design === 'panel' || (design === 'notification' && size !=='sm'),\r\n          'no-title': design === 'notification' && !title,\r\n          'db-text-6': design === 'notification' && size === 'sm',\r\n          'mb-5 mb-md-6':\r\n            contact\r\n        }\"\r\n        class=\"db-alert__content\"\r\n      >\r\n        <ng-content></ng-content>\r\n      </div>\r\n\r\n      <a\r\n        *ngIf=\"contact && design === 'panel'\"\r\n        [href]=\"\r\n          contactIcon === 'phone' ? 'tel:' + contact : 'mailto:' + contact\r\n        \"\r\n        class=\"db-text-3 db-text-bold db-alert__contact\"\r\n      >\r\n        <db-icon\r\n          *ngIf=\"contactIcon\"\r\n          class=\"mr-2 db-alert__contact-icon\"\r\n          size=\"100%\"\r\n          >{{ contactIcon }}</db-icon\r\n        >{{ contact }}\r\n      </a>\r\n      <div\r\n        *ngIf=\"contactSubText && design === 'panel'\"\r\n        class=\"db-text-4 mt-1 db-alert__content-subtext\"\r\n      >\r\n        {{ contactSubText }}\r\n      </div>\r\n      <span *ngIf=\"links.children.length > 0\" class=\"mt-4\"></span>\r\n      <div #links>\r\n        <ng-content select=\"[slot=links]\"></ng-content>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { status: [{
                type: Input
            }], title: [{
                type: Input
            }], contact: [{
                type: Input
            }], contactIcon: [{
                type: Input
            }], contactSubText: [{
                type: Input
            }], design: [{
                type: Input
            }], size: [{
                type: Input
            }] } });

class DBAlertModule {
}
DBAlertModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBAlertModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBAlertModule, declarations: [DBAlertComponent], imports: [CommonModule, DBIconModule], exports: [DBAlertComponent] });
DBAlertModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAlertModule, imports: [CommonModule, DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DBAlertComponent],
                    exports: [DBAlertComponent],
                    imports: [CommonModule, DBIconModule],
                }]
        }] });

const denyList = [
    'gmbh',
    'sarl',
    'inc',
    'von',
    'van',
    'zu',
    'dr',
    'prof',
    'co',
    'kg',
    'ag',
    'se',
];
class AvatarInitialsPipe {
    transform(name) {
        let initials = '';
        if (name) {
            const pieces = name
                .trim()
                .toLowerCase()
                .replace(/[.,]/g, '')
                .split(' ')
                .filter((result) => !denyList.includes(result));
            if (pieces.length > 0) {
                const firstLetter = pieces[0].charAt(0).toUpperCase();
                initials = firstLetter;
                if (pieces.length > 1) {
                    const lastLetter = pieces[pieces.length - 1];
                    initials += lastLetter.charAt(0).toUpperCase();
                }
            }
        }
        return initials;
    }
}
AvatarInitialsPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AvatarInitialsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
AvatarInitialsPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: AvatarInitialsPipe, name: "avatarInitials" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AvatarInitialsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'avatarInitials',
                }]
        }] });

class DBAvatarComponent {
    constructor() {
        this.size = 'md';
        this.icon = null;
        this.name = null;
        this.src = null;
        this.disabled = false;
        this.hideBorder = false;
        this.transparent = false;
        this.alt = '';
    }
}
DBAvatarComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAvatarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBAvatarComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBAvatarComponent, selector: "db-avatar", inputs: { size: "size", icon: "icon", name: "name", src: "src", disabled: "disabled", hideBorder: "hideBorder", transparent: "transparent", alt: "alt" }, ngImport: i0, template: "<div\r\n  class=\"db-avatar\"\r\n  [ngClass]=\"['db-avatar-size--' + size]\"\r\n  [class.db-avatar-type--icon]=\"icon\"\r\n  [class.db-avatar-type--initials]=\"name\"\r\n  [class.db-avatar-type--image]=\"src\"\r\n  [class.db-avatar-type--icon-disabled]=\"disabled\"\r\n  [class.db-avatar--hide-border]=\"hideBorder\"\r\n  [class.db-avatar--transparent]=\"transparent\"\r\n>\r\n  <db-icon *ngIf=\"icon; else srcTpl\">{{ icon }}</db-icon>\r\n  <ng-template #srcTpl>\r\n    <ng-container *ngIf=\"src; else initialsTpl\">\r\n      <img [src]=\"src\" [attr.alt]=\"alt\" [attr.title]=\"alt\" />\r\n    </ng-container>\r\n  </ng-template>\r\n  <ng-template #initialsTpl>\r\n    <span translate=\"no\" *ngIf=\"name\">{{ name | avatarInitials }}</span>\r\n  </ng-template>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }, { kind: "pipe", type: AvatarInitialsPipe, name: "avatarInitials" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAvatarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-avatar', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\r\n  class=\"db-avatar\"\r\n  [ngClass]=\"['db-avatar-size--' + size]\"\r\n  [class.db-avatar-type--icon]=\"icon\"\r\n  [class.db-avatar-type--initials]=\"name\"\r\n  [class.db-avatar-type--image]=\"src\"\r\n  [class.db-avatar-type--icon-disabled]=\"disabled\"\r\n  [class.db-avatar--hide-border]=\"hideBorder\"\r\n  [class.db-avatar--transparent]=\"transparent\"\r\n>\r\n  <db-icon *ngIf=\"icon; else srcTpl\">{{ icon }}</db-icon>\r\n  <ng-template #srcTpl>\r\n    <ng-container *ngIf=\"src; else initialsTpl\">\r\n      <img [src]=\"src\" [attr.alt]=\"alt\" [attr.title]=\"alt\" />\r\n    </ng-container>\r\n  </ng-template>\r\n  <ng-template #initialsTpl>\r\n    <span translate=\"no\" *ngIf=\"name\">{{ name | avatarInitials }}</span>\r\n  </ng-template>\r\n</div>\r\n" }]
        }], propDecorators: { size: [{
                type: Input
            }], icon: [{
                type: Input
            }], name: [{
                type: Input
            }], src: [{
                type: Input
            }], disabled: [{
                type: Input
            }], hideBorder: [{
                type: Input
            }], transparent: [{
                type: Input
            }], alt: [{
                type: Input
            }] } });

class DBAvatarModule {
}
DBAvatarModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAvatarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBAvatarModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBAvatarModule, declarations: [DBAvatarComponent, AvatarInitialsPipe], imports: [CommonModule, DBIconModule], exports: [DBAvatarComponent] });
DBAvatarModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAvatarModule, imports: [CommonModule, DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAvatarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DBAvatarComponent, AvatarInitialsPipe],
                    imports: [CommonModule, DBIconModule],
                    exports: [DBAvatarComponent],
                }]
        }] });

class DBBadgeComponent {
    get formattedContent() {
        if (this.content) {
            if (typeof this.content === 'number' && this.content > 99) {
                return '99+';
            }
            return this.content.toString();
        }
    }
}
DBBadgeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBBadgeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBBadgeComponent, selector: "db-badge", inputs: { content: "content" }, ngImport: i0, template: "<div class=\"db-badge\" *ngIf=\"content\">\r\n  {{ formattedContent }}\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-badge', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"db-badge\" *ngIf=\"content\">\r\n  {{ formattedContent }}\r\n</div>\r\n" }]
        }], propDecorators: { content: [{
                type: Input
            }] } });

class DBBadgedContentComponent {
    constructor(cdRef) {
        this.cdRef = cdRef;
        this.hoverActive = false;
    }
    set hover(hover) {
        this.hoverActive = hover;
        this.cdRef.markForCheck();
    }
}
DBBadgedContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgedContentComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBBadgedContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBBadgedContentComponent, selector: "db-badged-content", inputs: { value: "value", hover: "hover" }, ngImport: i0, template: "<span\r\n  class=\"db-badged-content\"\r\n  [class.has-value]=\"value\"\r\n  [class.hover]=\"hoverActive\"\r\n>\r\n  <span class=\"db-badged-content__content\">\r\n    <ng-content select=\"db-icon\"></ng-content>\r\n  </span>\r\n\r\n  <db-badge class=\"db-badged-content__badge\" [content]=\"value\"></db-badge>\r\n</span>\r\n", dependencies: [{ kind: "component", type: DBBadgeComponent, selector: "db-badge", inputs: ["content"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgedContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-badged-content', changeDetection: ChangeDetectionStrategy.OnPush, template: "<span\r\n  class=\"db-badged-content\"\r\n  [class.has-value]=\"value\"\r\n  [class.hover]=\"hoverActive\"\r\n>\r\n  <span class=\"db-badged-content__content\">\r\n    <ng-content select=\"db-icon\"></ng-content>\r\n  </span>\r\n\r\n  <db-badge class=\"db-badged-content__badge\" [content]=\"value\"></db-badge>\r\n</span>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { value: [{
                type: Input
            }], hover: [{
                type: Input
            }] } });

class DBBadgedContentTriggerDirective {
    constructor() {
        this.contentChildren = null;
    }
    onMouseOver() {
        var _a;
        (_a = this.contentChildren) === null || _a === void 0 ? void 0 : _a.forEach((badgedContent) => {
            badgedContent.hover = true;
        });
    }
    onMouseOut() {
        var _a;
        (_a = this.contentChildren) === null || _a === void 0 ? void 0 : _a.forEach((badgedContent) => {
            badgedContent.hover = false;
        });
    }
}
DBBadgedContentTriggerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgedContentTriggerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DBBadgedContentTriggerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBBadgedContentTriggerDirective, selector: "[dbBadgedContentTrigger]", host: { listeners: { "mouseover": "onMouseOver()", "mouseout": "onMouseOut()" } }, queries: [{ propertyName: "contentChildren", predicate: DBBadgedContentComponent, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgedContentTriggerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dbBadgedContentTrigger]',
                }]
        }], propDecorators: { contentChildren: [{
                type: ContentChildren,
                args: [DBBadgedContentComponent, { descendants: true }]
            }], onMouseOver: [{
                type: HostListener,
                args: ['mouseover']
            }], onMouseOut: [{
                type: HostListener,
                args: ['mouseout']
            }] } });

class DBBadgeModule {
}
DBBadgeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBBadgeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBBadgeModule, declarations: [DBBadgeComponent, DBBadgedContentComponent, DBBadgedContentTriggerDirective], imports: [CommonModule], exports: [DBBadgeComponent, DBBadgedContentComponent, DBBadgedContentTriggerDirective] });
DBBadgeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgeModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBBadgeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DBBadgeComponent, DBBadgedContentComponent, DBBadgedContentTriggerDirective],
                    imports: [CommonModule],
                    exports: [DBBadgeComponent, DBBadgedContentComponent, DBBadgedContentTriggerDirective],
                }]
        }] });

/**
 * DB Carousel Slide Component
 */
class DBCarouselSlideComponent {
    /**
     * Constructor
     *
     * @param elementRef - Reference to the element
     */
    constructor(elementRef) {
        this.isVisible = true;
        this.internalElement = elementRef.nativeElement;
    }
    /**
     * Get the carousel slide component's element
     */
    get element() {
        return this.internalElement;
    }
}
DBCarouselSlideComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselSlideComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DBCarouselSlideComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBCarouselSlideComponent, selector: "db-carousel-slide", ngImport: i0, template: "<ng-content></ng-content>\r\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselSlideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-carousel-slide', template: "<ng-content></ng-content>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

/**
 * DB Viewport Service
 */
class DBViewportService {
    /**
     * Constructor
     *
     * @param windowRef - Window Ref
     * @param ngZone    - Angular zone
     */
    constructor(windowRef, ngZone) {
        this.windowRef = windowRef;
        this.ngZone = ngZone;
        // Setup media query matchers
        this.mediaQueryMatchers = this.setupMediaQueryMatchers({
            widescreen: '(min-width: 1200px)',
            desktop: '(max-width: 1199px) and (min-width: 992px)',
            tablet: '(max-width: 991px) and (min-width: 768px)',
            phonelarge: '(max-width: 767px) and (min-width: 576px)',
            phone: '(max-width: 575px)',
        });
        this.mediaQueryChangesSource = null;
        // Escape the Angular change detection (#perfmatters)
        this.resizeChangesSource = new Subject();
        this.ngZone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(this.onResizeChange.bind(this));
        });
    }
    /**
     * Get visible area of the given element
     *
     * @param   elementRef - Element
     * @returns            - Intersection Observer Enry
     */
    getVisibleArea(elementRef) {
        // One-time intersection observer usage
        return new Promise((resolve) => {
            const observer = new IntersectionObserver((entries) => {
                // One-time intersection observer usage, thus cleanup here (#perfmatters)
                observer.unobserve(elementRef.nativeElement);
                observer.disconnect();
                // Resolve
                resolve(entries[0]);
            });
            // Get intersections
            observer.observe(elementRef.nativeElement);
        });
    }
    /**
     * Observable for media query changes, getting setup only if necessary (#perfmatters)
     */
    mediaQueryChanges() {
        // Setup first (if necessary)
        // istanbul ignore else
        if (!this.mediaQueryChangesSource) {
            this.setupMediaQueryEventListener();
        }
        // Return observable
        return this.mediaQueryChangesSource
            .asObservable()
            .pipe(finalize(() => {
            // Cleanup when the last substriber wants to go (#perfmatters)
            // istanbul ignore else
            if (this.mediaQueryChangesSource.observers.length === 1) { // 1 right now, yet after 'unsubscribe' it will be 0
                this.cleanupMediaQueryEventListener();
            }
        }));
    }
    /**
     * Listen to resize changes of the given element
     *
     * @param element - Element to listen for size changes for
     */
    resizeChanges(element = this.windowRef.nativeBody) {
        // Observe new element for resize changes
        this.resizeObserver.observe(element);
        // Return observable stream of events (an initial event gets emitted by default)
        return this.resizeChangesSource
            .asObservable()
            .pipe(
        // Filter out changes not related to this element
        map((entries) => entries.find((entry) => entry.target === element)), 
        // Do not update the observable if not related changes exist
        filter((entry) => !!entry), 
        // Cleanup before unsubsribing (#perfmatters)
        finalize(() => {
            this.resizeObserver.unobserve(element);
        }));
    }
    /**
     * Handle resize change events
     *
     * @param entries - List of elements which changed their size, including the new size
     */
    onResizeChange(entries) {
        this.resizeChangesSource.next(entries);
    }
    /**
     * Shared event handler for match media changes
     *
     * @param MediaQueryList event - Event details
     */
    onMediaQueryChange(event) {
        // Only forward state transitions from non-matching -> matching
        // istanbul ignore else
        if (event.matches) {
            // Find the media query name
            const name = Object
                .keys(this.mediaQueryMatchers)
                .find((mediaQueryName) => 
            // Compare the 'normalized' media query rules (to make it work cross-browser)
            // For details about browser-specifics, visit <http://www.javascriptkit.com/dhtmltutors/cssmediaqueries4.shtml>
            this.mediaQueryMatchers[mediaQueryName].media === event.media);
            // Update observable stream
            this.mediaQueryChangesSource.next(name);
        }
    }
    /**
     * Setup media query matchers
     *
     * @param mediaQueryRules - Media query rules
     */
    setupMediaQueryMatchers(mediaQueryRules) {
        return Object
            .keys(mediaQueryRules)
            .reduce((mediaQueryMatchers, mediaQueryName) => {
            mediaQueryMatchers[mediaQueryName] = this.windowRef.nativeWindow.matchMedia(mediaQueryRules[mediaQueryName]);
            return mediaQueryMatchers;
        }, {});
    }
    /**
     * Setup the media query event listener (for media-query based viewport / window changes)
     */
    setupMediaQueryEventListener() {
        // Escape the Angular change detection (#perfmatters)
        let initialMediaQueryName = null;
        Object
            .keys(this.mediaQueryMatchers)
            .forEach((mediaQueryName) => {
            // Attach event listener
            this.ngZone.runOutsideAngular(() => {
                this.mediaQueryMatchers[mediaQueryName].addListener(this.onMediaQueryChange.bind(this));
            });
            // Get initial media query name
            if (this.mediaQueryMatchers[mediaQueryName].matches) {
                initialMediaQueryName = mediaQueryName;
            }
        });
        // Setup observable source
        this.mediaQueryChangesSource = new BehaviorSubject(initialMediaQueryName);
    }
    /**
     * Cleanup the media query event listener
     */
    cleanupMediaQueryEventListener() {
        // De-register all media query event listeners
        Object
            .keys(this.mediaQueryMatchers)
            .forEach((mediaQueryName) => {
            this.mediaQueryMatchers[mediaQueryName].removeListener(this.onMediaQueryChange.bind(this));
        });
        // Complete and destroy observables
        this.mediaQueryChangesSource.complete();
        this.mediaQueryChangesSource = null;
    }
}
DBViewportService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBViewportService, deps: [{ token: DBWindowRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
DBViewportService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBViewportService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBViewportService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: DBWindowRef }, { type: i0.NgZone }]; } });

/**
 * DB Carousel Component
 */
class DBCarouselComponent {
    /**
     * Constructor (initial setup)
     *
     * @param renderer        - Angular Renderer
     * @param changeDetector  - Angular Change Detector
     * @param viewportService - Viewport service
     */
    constructor(renderer, changeDetector, viewportService) {
        /**
         * For hiding the carousel arrows
         */
        this.showCarouselArrow = true;
        this.hasCarouselSlides = false;
        this.slidesToShow = 5;
        this.slidesToScroll = 1;
        this.currentSlidesToShow = this.slidesToShow;
        this.currentSlidesToScroll = this.slidesToScroll;
        this.currentVisibleIndex = 0;
        this.isScrollableToLeft = true;
        this.isScrollableToRight = true;
        this.currentMediaQueryName = 'widescreen';
        this.isAnimationInProgress = false;
        this.visibleModifierClassName = 'db-carousel__slide--visible';
        this.hiddenModifierClassName = 'db-carousel__slide--hidden';
        this.toBeVisibleModifierClassName = 'db-carousel__slide--to-be-visible';
        this.subscriptions = [];
        this.renderer = renderer;
        this.changeDetector = changeDetector;
        this.viewportService = viewportService;
        this.visibleSlideIndexChange = new EventEmitter();
    }
    /**
     * After view init component lifecycle hook (called after all carousel slides have been created)
     */
    ngAfterContentInit() {
        // Render (and update) the carousel and all its slides depending on the active media query
        const mediaQueryChangesSubscription = this.viewportService.mediaQueryChanges().subscribe((mediaQueryName) => {
            this.currentMediaQueryName = mediaQueryName;
            this.setupProperties();
            this.renderView();
        });
        this.subscriptions.push(mediaQueryChangesSubscription);
        // Render the view if any slides change
        const carouselSlideComponentsChangesSubscription = this.carouselSlideComponents.changes.subscribe((changes) => {
            this.currentVisibleIndex = 0; // Reset carousel state when the children change
            this.renderView();
        });
        this.subscriptions.push(carouselSlideComponentsChangesSubscription);
        // Render view initially
        this.setupProperties();
        this.renderView();
    }
    /**
     * Respond after Angular checks the content projected into the component
     * called after the ngAfterContentInit() and every subsequent ngDoCheck()
     */
    ngAfterViewInit() {
        if (this.visibleItem !== undefined) { // '0' is a valid value here, thus we compare with 'undefined' directly
            this.scrollToSlide(this.visibleItem);
        }
    }
    /**
     * Destroy component lifecycle hook (called when this component gets destroyed)
     */
    ngOnDestroy() {
        // Cleanup to free ressources
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
        this.subscriptions = null;
    }
    /**
     * On changes component lifecycle hook
     *
     * @param changes - Changes
     */
    ngOnChanges(changes) {
        // Check if the 'slidesToShow' value has changed
        if (changes.hasOwnProperty('slidesToShow') && !changes.slidesToShow.isFirstChange() &&
            this.hasPropertyChanged(changes.slidesToShow.previousValue, changes.slidesToShow.currentValue)) {
            this.setupProperties();
            this.renderView();
        }
        // Check if the 'slidesToScroll' value has changed
        if (changes.hasOwnProperty('slidesToScroll') && !changes.slidesToScroll.isFirstChange() &&
            this.hasPropertyChanged(changes.slidesToScroll.previousValue, changes.slidesToScroll.currentValue)) {
            this.setupProperties();
        }
        // Check if the 'visibleItem' value has changed
        if (changes.hasOwnProperty('visibleItem') && !changes.visibleItem.isFirstChange() &&
            this.hasPropertyChanged(changes.visibleItem.previousValue, changes.visibleItem.currentValue)) {
            let slideToItemIndex = changes.visibleItem.currentValue;
            slideToItemIndex = Math.min(slideToItemIndex, this.carouselSlideComponents.length);
            slideToItemIndex = Math.max(slideToItemIndex, 0);
            this.scrollToSlide(slideToItemIndex);
        }
    }
    /**
     * Handle click on 'previous' button
     *
     * @param $event - Event
     */
    onPrevButtonClick($event) {
        // Exist early if the carousel is currently transitioning from one state to another
        if (this.isAnimationInProgress) {
            return;
        }
        this.isAnimationInProgress = true;
        // Check if there are enough slides left to scroll the given numberof slides
        const slidesToScroll = this.currentVisibleIndex < this.currentSlidesToScroll
            ? this.currentVisibleIndex
            : this.currentSlidesToScroll;
        // Animate slides, then render the new view
        this.animateView(slidesToScroll, false)
            .then(() => {
            this.currentVisibleIndex -= slidesToScroll;
            this.renderView();
            this.isAnimationInProgress = false;
            this.visibleSlideIndexChange.emit(this.currentVisibleIndex);
        });
    }
    /**
     * Handle click on 'next' button
     *
     * @param $event - Event
     */
    onNextButtonClick($event) {
        // Exist early if the carousel is currently transitioning from one state to another
        if (this.isAnimationInProgress) {
            return;
        }
        this.isAnimationInProgress = true;
        // Check if there are enough slides left to scroll the given number of slides
        const availableSlidesToScroll = this.carouselSlideComponents.length - this.getCurrentLastVisibleCarouselSlideIndex() - 1;
        const slidesToScroll = availableSlidesToScroll < this.currentSlidesToScroll
            ? availableSlidesToScroll
            : this.currentSlidesToScroll;
        // Animate slides, then render the new view
        this.animateView(slidesToScroll, true)
            .then(() => {
            this.currentVisibleIndex += slidesToScroll;
            this.renderView();
            this.isAnimationInProgress = false;
            this.visibleSlideIndexChange.emit(this.currentVisibleIndex);
        });
    }
    /**
     * Handle a change on the slide to depict
     *
     * @param slideToItemIndex - Number
     */
    scrollToSlide(slideToItemIndex) {
        // Exist early if the carousel is currently transitioning from one state to another
        if (this.isAnimationInProgress) {
            return;
        }
        this.isAnimationInProgress = true;
        // calculate slide-direction and amount
        let slidesToScroll;
        const scrollForward = this.currentVisibleIndex < slideToItemIndex;
        let fixedSlideToItemIndex = slideToItemIndex;
        // Fix potential 'overscrolling'
        if (slideToItemIndex + this.currentSlidesToShow > this.carouselSlideComponents.length) {
            fixedSlideToItemIndex = slidesToScroll = (this.carouselSlideComponents.length - this.currentSlidesToShow);
        }
        else {
            slidesToScroll = Math.abs(this.currentVisibleIndex - slideToItemIndex);
        }
        // Animate slides, then render the new view
        this.animateView(slidesToScroll, scrollForward)
            .then(() => {
            this.currentVisibleIndex = fixedSlideToItemIndex;
            this.renderView();
            this.isAnimationInProgress = false;
        });
    }
    /**
     * Update scrollable states (left & right)
     */
    updateScrollableStates() {
        this.hasCarouselSlides = this.carouselSlideComponents.length > 0;
        this.isScrollableToLeft = this.currentVisibleIndex > 0;
        this.isScrollableToRight = this.getCurrentLastVisibleCarouselSlideIndex() < this.carouselSlideComponents.length - 1;
    }
    /**
     * Render the view
     */
    renderView() {
        // Update scrollable states first
        this.updateScrollableStates();
        this.carouselSlideComponents.forEach((carouselSlideComponent, index) => {
            this.resetCarouselSlideComponent(carouselSlideComponent);
            // Update visibility flags and apply correct classes
            if (index >= this.currentVisibleIndex && index < this.currentVisibleIndex + this.currentSlidesToShow) {
                carouselSlideComponent.isVisible = true;
                this.renderer.setStyle(carouselSlideComponent.element, 'width', `${100 / this.currentSlidesToShow}%`); // Equally spread width
                this.renderer.addClass(carouselSlideComponent.element, this.visibleModifierClassName);
            }
            else {
                carouselSlideComponent.isVisible = false;
                this.renderer.addClass(carouselSlideComponent.element, this.hiddenModifierClassName);
            }
        });
        // Run change detection
        this.changeDetector.markForCheck();
    }
    /**
     * Animate the view from one state to another (before finally rendering it)
     *
     * @param             slidesToScroll - Number if slides to scroll in an animated manner
     * @param            scrollForward  - Flag, describing whether to scroll left or right (forward or backward)
     * @returns                - Promise, resolved when done
     */
    animateView(slidesToScroll, scrollForward) {
        return new Promise((resolve, reject) => {
            // Find the first visible carousel slide, and get its current width
            const firstVisibleCarouselSlide = this.findFirstVisibleCarouselSlide();
            const calculatedSlideWidth = firstVisibleCarouselSlide.element.offsetWidth;
            // Setup event listener for aniamations
            const transitionEndEventHandler = ($event) => {
                firstVisibleCarouselSlide.element.removeEventListener('transitionend', transitionEndEventHandler);
                resolve(undefined);
            };
            firstVisibleCarouselSlide.element.addEventListener('transitionend', transitionEndEventHandler, false);
            // Animate carousel slides
            this.carouselSlideComponents.forEach((carouselSlideComponent, index) => {
                // Enable animations for changes applied here (CSS transitions, to be particular)
                this.renderer.setStyle(carouselSlideComponent.element, 'transition', ''); // Empty value means the original CSS value
                const currentLastVisibleCarouselSlideIndex = this.getCurrentLastVisibleCarouselSlideIndex();
                const nextLastVisibleCarouselSlideIndex = this.getNextLastVisibleCarouselSlideIndex(scrollForward);
                // Shift existing elements (either to the left or right, probably also partially out of the container)
                if (carouselSlideComponent.isVisible) {
                    const transformValue = scrollForward
                        ? `translateX( -${slidesToScroll * 100}% )`
                        : `translateX( ${slidesToScroll * 100}% )`;
                    this.renderer.setStyle(carouselSlideComponent.element, 'transform', transformValue);
                }
                // Activate and shift incoming slides into the view
                if ((scrollForward && (index > currentLastVisibleCarouselSlideIndex && index <= nextLastVisibleCarouselSlideIndex)) ||
                    (!scrollForward && (index >= nextLastVisibleCarouselSlideIndex && index < this.currentVisibleIndex))) {
                    // Apply classes
                    this.renderer.addClass(carouselSlideComponent.element, this.toBeVisibleModifierClassName);
                    this.renderer.removeClass(carouselSlideComponent.element, this.hiddenModifierClassName);
                    // Apply absolute width (because there is no auto-sizing by flexbox here)
                    const widthValue = `${calculatedSlideWidth}px`;
                    this.renderer.setStyle(carouselSlideComponent.element, 'width', widthValue);
                    // Set current and future position, while transform changes being animated (#perfmatters)
                    let leftValue;
                    let transformValue;
                    if (scrollForward) {
                        leftValue = `calc( 100% + ${(index - currentLastVisibleCarouselSlideIndex - 1) * calculatedSlideWidth}px )`;
                        transformValue = `translateX( -${(slidesToScroll) * 100}% )`;
                    }
                    else {
                        leftValue = `${-(this.currentVisibleIndex - index) * calculatedSlideWidth}px`;
                        transformValue = `translateX( ${(slidesToScroll) * 100}% )`;
                    }
                    this.renderer.setStyle(carouselSlideComponent.element, 'left', leftValue);
                    this.renderer.setStyle(carouselSlideComponent.element, 'transform', transformValue);
                }
            });
        });
    }
    /**
     * Reset a carousel slide component (in particular, remove classes, reset inline styles)
     *
     * @param carouselSlideComponent - Carousel Slide Component
     */
    resetCarouselSlideComponent(carouselSlideComponent) {
        // Disable animations for changes applied here (CSS transitions, to be particular)
        this.renderer.setStyle(carouselSlideComponent.element, 'transition', 'none');
        // Remove all modifier classes
        this.renderer.removeClass(carouselSlideComponent.element, this.visibleModifierClassName);
        this.renderer.removeClass(carouselSlideComponent.element, this.hiddenModifierClassName);
        this.renderer.removeClass(carouselSlideComponent.element, this.toBeVisibleModifierClassName);
        // Reset custom inline styles (probably applied by the animation process)
        this.renderer.setStyle(carouselSlideComponent.element, 'width', ''); // Empty value means the original CSS value
        this.renderer.setStyle(carouselSlideComponent.element, 'left', ''); // Empty value means the original CSS value
        this.renderer.setStyle(carouselSlideComponent.element, 'transform', ''); // Empty value means the original CSS value
    }
    /**
     * Get the index of the currently last visible carousel slide
     *
     * @returns - Index of the currently last visible carousel slide
     */
    getCurrentLastVisibleCarouselSlideIndex() {
        const numberOfCarouselSlideComponent = this.carouselSlideComponents.length;
        if (numberOfCarouselSlideComponent < this.currentSlidesToShow) {
            return this.currentVisibleIndex + numberOfCarouselSlideComponent - 1;
        }
        else {
            return this.currentVisibleIndex + this.currentSlidesToShow - 1;
        }
    }
    /**
     * Get the index of the next last visible carousel slide
     *
     * @param scrollForward - Direction to look for
     * @returns                - Index of the next last visible carousel slide
     */
    getNextLastVisibleCarouselSlideIndex(scrollForward) {
        if (scrollForward) {
            return this.getCurrentLastVisibleCarouselSlideIndex() + this.currentSlidesToScroll;
        }
        else {
            return this.currentVisibleIndex - this.currentSlidesToScroll;
        }
    }
    /**
     * Find the first visible carousel slide within the carousel
     *
     * @returns - First visible carousel clide component
     */
    findFirstVisibleCarouselSlide() {
        return this.carouselSlideComponents.find((carouselSlideComponent) => carouselSlideComponent.isVisible);
    }
    /**
     * Setup properties
     */
    setupProperties() {
        // Set values depending on input type
        if (typeof this.slidesToShow === 'number' || typeof this.slidesToShow === 'string') {
            this.currentSlidesToShow = parseInt(this.slidesToShow.toString(), 10);
            this.currentSlidesToScroll = parseInt(this.slidesToScroll.toString(), 10);
        }
        else {
            // Set values depending on the currently active media query
            switch (this.currentMediaQueryName) {
                case 'widescreen':
                    this.currentSlidesToShow = this.slidesToShow[0];
                    this.currentSlidesToScroll = this.slidesToScroll[0];
                    break;
                case 'desktop':
                    this.currentSlidesToShow = this.slidesToShow[1];
                    this.currentSlidesToScroll = this.slidesToScroll[1];
                    break;
                case 'tablet':
                    this.currentSlidesToShow = this.slidesToShow[2];
                    this.currentSlidesToScroll = this.slidesToScroll[2];
                    break;
                case 'phonelarge':
                    this.currentSlidesToShow = this.slidesToShow[3];
                    this.currentSlidesToScroll = this.slidesToScroll[3];
                    break;
                case 'phone':
                    this.currentSlidesToShow = this.slidesToShow[4];
                    this.currentSlidesToScroll = this.slidesToScroll[4];
                    break;
                default:
                    this.currentSlidesToShow = this.slidesToShow[0];
                    this.currentSlidesToScroll = this.slidesToScroll[0];
                    break;
            }
        }
        // Fix potential 'overscroll' (we would so with the new view)
        if (this.currentVisibleIndex + this.currentSlidesToShow > this.carouselSlideComponents.length) {
            const newVisibleIndex = this.carouselSlideComponents.length - this.currentSlidesToShow;
            this.currentVisibleIndex = newVisibleIndex > 0 ? newVisibleIndex : 0;
        }
    }
    /**
     * Check if some of the (input) properties has changed
     *
     * @param oldValue - Old value
     * @param newValue - New value
     * @returns                         - Result as a boolean value
     */
    hasPropertyChanged(oldValue, newValue) {
        if ((typeof oldValue !== typeof newValue) ||
            ((typeof oldValue === 'number' || typeof oldValue === 'string') && oldValue !== newValue) ||
            (oldValue.length !== newValue.length)) {
            // If the type is different, the number changed, or the array length is different - then the property has changed
            return true;
        }
        else {
            // Else, compare each value in the array
            return !oldValue.every((element, index) => element === newValue[index]);
        }
    }
}
DBCarouselComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: DBViewportService }], target: i0.ɵɵFactoryTarget.Component });
DBCarouselComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBCarouselComponent, selector: "db-carousel", inputs: { slidesToShow: "slidesToShow", slidesToScroll: "slidesToScroll", visibleItem: "visibleItem", showCarouselArrow: "showCarouselArrow" }, outputs: { visibleSlideIndexChange: "visibleSlideIndexChange" }, queries: [{ propertyName: "carouselSlideComponents", predicate: DBCarouselSlideComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<button *ngIf=\"showCarouselArrow\"\r\n  class=\"db-carousel__button db-carousel__button--prev\"\r\n  type=\"button\"\r\n  [class.db-carousel__button--hidden]=\"!hasCarouselSlides\"\r\n  [disabled]=\"!isScrollableToLeft\"\r\n  (click)=\"onPrevButtonClick( $event )\">\r\n  <svg class=\"db-carousel__button-icon\" viewBox=\"0 0 64 64\">\r\n    <path d=\"M36.75 58.7c0 2.15-1.37 4.1-3.45 4.9-2.1.82-4.48.34-6.05-1.2L-.52 35.73c-2.16-2.08-2.16-5.44 0-7.5L27.25 1.55C28.65.2 30.7-.3\r\n      32.6.18c1.9.5 3.4 1.93 3.9 3.76.52 1.83-.03 3.8-1.42 5.13l-23.86 22.9 23.86 22.9c1.07 1 1.67 2.38 1.67 3.82z\">\r\n    </path>\r\n  </svg>\r\n</button>\r\n\r\n<div\r\n  class=\"db-carousel__container\"\r\n  [class.db-carousel__container--empty]=\"!hasCarouselSlides || !showCarouselArrow\">\r\n  <ng-content></ng-content>\r\n</div>\r\n\r\n<button *ngIf=\"showCarouselArrow\"\r\n  class=\"db-carousel__button db-carousel__button--next\"\r\n  type=\"button\"\r\n  [class.db-carousel__button--hidden]=\"!hasCarouselSlides\"\r\n  [disabled]=\"!isScrollableToRight\"\r\n  (click)=\"onNextButtonClick( $event )\">\r\n  <svg class=\"db-carousel__button-icon\" viewBox=\"0 0 64 64\">\r\n    <path d=\"M13.25 58.7c0 2.15 1.37 4.1 3.45 4.9 2.1.82 4.48.34 6.05-1.2l27.77-26.67c2.16-2.08 2.16-5.44 0-7.5L22.75 1.55C21.35.2 19.3-.3\r\n      17.4.18c-1.9.5-3.4 1.93-3.9 3.76-.52 1.83.03 3.8 1.42 5.13l23.86 22.9-23.86 22.9c-1.07 1-1.67 2.38-1.67 3.82z\">\r\n    </path>\r\n  </svg>\r\n</button>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-carousel', template: "<button *ngIf=\"showCarouselArrow\"\r\n  class=\"db-carousel__button db-carousel__button--prev\"\r\n  type=\"button\"\r\n  [class.db-carousel__button--hidden]=\"!hasCarouselSlides\"\r\n  [disabled]=\"!isScrollableToLeft\"\r\n  (click)=\"onPrevButtonClick( $event )\">\r\n  <svg class=\"db-carousel__button-icon\" viewBox=\"0 0 64 64\">\r\n    <path d=\"M36.75 58.7c0 2.15-1.37 4.1-3.45 4.9-2.1.82-4.48.34-6.05-1.2L-.52 35.73c-2.16-2.08-2.16-5.44 0-7.5L27.25 1.55C28.65.2 30.7-.3\r\n      32.6.18c1.9.5 3.4 1.93 3.9 3.76.52 1.83-.03 3.8-1.42 5.13l-23.86 22.9 23.86 22.9c1.07 1 1.67 2.38 1.67 3.82z\">\r\n    </path>\r\n  </svg>\r\n</button>\r\n\r\n<div\r\n  class=\"db-carousel__container\"\r\n  [class.db-carousel__container--empty]=\"!hasCarouselSlides || !showCarouselArrow\">\r\n  <ng-content></ng-content>\r\n</div>\r\n\r\n<button *ngIf=\"showCarouselArrow\"\r\n  class=\"db-carousel__button db-carousel__button--next\"\r\n  type=\"button\"\r\n  [class.db-carousel__button--hidden]=\"!hasCarouselSlides\"\r\n  [disabled]=\"!isScrollableToRight\"\r\n  (click)=\"onNextButtonClick( $event )\">\r\n  <svg class=\"db-carousel__button-icon\" viewBox=\"0 0 64 64\">\r\n    <path d=\"M13.25 58.7c0 2.15 1.37 4.1 3.45 4.9 2.1.82 4.48.34 6.05-1.2l27.77-26.67c2.16-2.08 2.16-5.44 0-7.5L22.75 1.55C21.35.2 19.3-.3\r\n      17.4.18c-1.9.5-3.4 1.93-3.9 3.76-.52 1.83.03 3.8 1.42 5.13l23.86 22.9-23.86 22.9c-1.07 1-1.67 2.38-1.67 3.82z\">\r\n    </path>\r\n  </svg>\r\n</button>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: DBViewportService }]; }, propDecorators: { slidesToShow: [{
                type: Input
            }], slidesToScroll: [{
                type: Input
            }], visibleItem: [{
                type: Input
            }], showCarouselArrow: [{
                type: Input
            }], visibleSlideIndexChange: [{
                type: Output
            }], carouselSlideComponents: [{
                type: ContentChildren,
                args: [DBCarouselSlideComponent, { descendants: true }]
            }] } });

/**
 * Viewport Service Factory
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function viewportServiceFactory(parentViewportService, windowRef, ngZone) {
    return parentViewportService || new DBViewportService(windowRef, ngZone);
}
/**
 * DB Viewport Module
 */
class DBViewportModule {
}
DBViewportModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBViewportModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBViewportModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBViewportModule, imports: [DBWindowRefModule] });
DBViewportModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBViewportModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBViewportService],
                DBWindowRef,
                NgZone,
            ],
            provide: DBViewportService,
            useFactory: viewportServiceFactory,
        },
    ], imports: [DBWindowRefModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBViewportModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DBWindowRefModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBViewportService],
                                DBWindowRef,
                                NgZone,
                            ],
                            provide: DBViewportService,
                            useFactory: viewportServiceFactory,
                        },
                    ],
                }]
        }] });

/**
 * DB Carousel Module
 */
class DBCarouselModule {
}
DBCarouselModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBCarouselModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselModule, declarations: [DBCarouselComponent,
        DBCarouselSlideComponent], imports: [CommonModule,
        DBWindowRefModule,
        DBViewportModule], exports: [DBCarouselComponent,
        DBCarouselSlideComponent] });
DBCarouselModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselModule, imports: [CommonModule,
        DBWindowRefModule,
        DBViewportModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBCarouselComponent,
                        DBCarouselSlideComponent,
                    ],
                    exports: [
                        DBCarouselComponent,
                        DBCarouselSlideComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBWindowRefModule,
                        DBViewportModule,
                    ],
                }]
        }] });

class DBFilterPillComponent {
    constructor() {
        this.label = '';
        this.icon = '';
        this.filterPillClose = new EventEmitter();
    }
    close(event) {
        event.preventDefault();
        this.filterPillClose.emit();
    }
}
DBFilterPillComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFilterPillComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBFilterPillComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBFilterPillComponent, selector: "db-filter-pill", inputs: { label: "label", icon: "icon" }, outputs: { filterPillClose: "filterPillClose" }, ngImport: i0, template: "<div class=\"filter-pill\" tabindex=\"0\" [attr.aria-label]=\"label\">\r\n  <div class=\"filter-pill-row\">\r\n    <db-icon class=\"icon align-self-center\" size=\"sm\" *ngIf=\"icon\">\r\n      {{ icon }}\r\n    </db-icon>\r\n    <div class=\"label align-self-center\">\r\n      <span>\r\n        {{ label }}\r\n      </span>\r\n    </div>\r\n\r\n    <button\r\n      type=\"button\"\r\n      class=\"close ml-2 ml-md-3\"\r\n      aria-label=\"Close\"\r\n      (click)=\"close($event)\"\r\n    >\r\n      <db-icon size=\"sm\">close</db-icon>\r\n    </button>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFilterPillComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-filter-pill', template: "<div class=\"filter-pill\" tabindex=\"0\" [attr.aria-label]=\"label\">\r\n  <div class=\"filter-pill-row\">\r\n    <db-icon class=\"icon align-self-center\" size=\"sm\" *ngIf=\"icon\">\r\n      {{ icon }}\r\n    </db-icon>\r\n    <div class=\"label align-self-center\">\r\n      <span>\r\n        {{ label }}\r\n      </span>\r\n    </div>\r\n\r\n    <button\r\n      type=\"button\"\r\n      class=\"close ml-2 ml-md-3\"\r\n      aria-label=\"Close\"\r\n      (click)=\"close($event)\"\r\n    >\r\n      <db-icon size=\"sm\">close</db-icon>\r\n    </button>\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { label: [{
                type: Input
            }], icon: [{
                type: Input
            }], filterPillClose: [{
                type: Output
            }] } });

class DBFilterPillModule {
}
DBFilterPillModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFilterPillModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBFilterPillModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBFilterPillModule, declarations: [DBFilterPillComponent], imports: [CommonModule, DBIconModule], exports: [DBFilterPillComponent] });
DBFilterPillModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFilterPillModule, imports: [CommonModule, DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBFilterPillModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DBFilterPillComponent],
                    imports: [CommonModule, DBIconModule],
                    exports: [DBFilterPillComponent],
                }]
        }] });

class DBListRowContentComponent {
    constructor() { }
}
DBListRowContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBListRowContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBListRowContentComponent, selector: "db-list-row-content", ngImport: i0, template: "<ng-content></ng-content>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-list-row-content', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content></ng-content>\r\n" }]
        }], ctorParameters: function () { return []; } });

class DBListRowPrefixComponent {
    constructor() { }
}
DBListRowPrefixComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowPrefixComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBListRowPrefixComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBListRowPrefixComponent, selector: "db-list-row-prefix", ngImport: i0, template: "<ng-content></ng-content>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowPrefixComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-list-row-prefix', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content></ng-content>\r\n" }]
        }], ctorParameters: function () { return []; } });

class DBListRowIconComponent {
    constructor() {
        this.icon = '';
        this.showBorder = false;
    }
}
DBListRowIconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBListRowIconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBListRowIconComponent, selector: "db-list-row-icon", inputs: { icon: "icon", showBorder: "showBorder" }, ngImport: i0, template: "<db-list-row-prefix\r\n  *ngIf=\"icon\"\r\n  class=\"icon rounded-circle\"\r\n  [class.bordered]=\"showBorder\"\r\n>\r\n  <db-avatar [icon]=\"icon\" [hideBorder]=\"!showBorder\">\r\n  </db-avatar>\r\n</db-list-row-prefix>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBAvatarComponent, selector: "db-avatar", inputs: ["size", "icon", "name", "src", "disabled", "hideBorder", "transparent", "alt"] }, { kind: "component", type: DBListRowPrefixComponent, selector: "db-list-row-prefix" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-list-row-icon', changeDetection: ChangeDetectionStrategy.OnPush, template: "<db-list-row-prefix\r\n  *ngIf=\"icon\"\r\n  class=\"icon rounded-circle\"\r\n  [class.bordered]=\"showBorder\"\r\n>\r\n  <db-avatar [icon]=\"icon\" [hideBorder]=\"!showBorder\">\r\n  </db-avatar>\r\n</db-list-row-prefix>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { icon: [{
                type: Input
            }], showBorder: [{
                type: Input
            }] } });

class DBMenuItemComponent {
    constructor() {
        this.template = null;
        this.disabled = null;
    }
}
DBMenuItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBMenuItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBMenuItemComponent, selector: "db-menu-item", inputs: { disabled: "disabled" }, viewQueries: [{ propertyName: "template", first: true, predicate: ["content"], descendants: true }], ngImport: i0, template: "<ng-template #content>\r\n  <ng-content></ng-content>\r\n</ng-template>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-menu-item', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-template #content>\r\n  <ng-content></ng-content>\r\n</ng-template>\r\n" }]
        }], propDecorators: { template: [{
                type: ViewChild,
                args: ['content']
            }], disabled: [{
                type: Input
            }] } });

class DBMenuOverlayComponent extends DBOverlayComponentDirective {
    constructor(cdRef, renderer, elementRef) {
        super(cdRef, renderer, elementRef);
        this.cdRef = cdRef;
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.menuItems = [];
    }
    init(triggerElement, menuOverlayPlacement) {
        const position = menuOverlayPlacement === 'left' ? 'bottom-end' : 'bottom-start';
        const pos = position;
        this.initOverlay(triggerElement, { position: pos, size: 'md' });
    }
    show() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.showOverlay();
        });
    }
    hide() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.hideOverlay();
        });
    }
}
DBMenuOverlayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuOverlayComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DBMenuOverlayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBMenuOverlayComponent, selector: "db-menu-overlay", inputs: { menuItems: "menuItems" }, usesInheritance: true, ngImport: i0, template: "<ul *ngIf=\"menuItems\" class=\"db-menu__menu\" role=\"menu\">\r\n  <li\r\n    *ngFor=\"let item of menuItems\"\r\n    class=\"db-menu__menu-item\"\r\n    role=\"menuitem\"\r\n    [attr.disabled]=\"item.disabled\"\r\n    [attr.aria-disabled]=\"item.disabled\"\r\n    [ngClass]=\"{ disabled: item.disabled }\"\r\n  >\r\n    <ng-container *ngTemplateOutlet=\"item.template\"></ng-container>\r\n  </li>\r\n</ul>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuOverlayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-menu-overlay', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ul *ngIf=\"menuItems\" class=\"db-menu__menu\" role=\"menu\">\r\n  <li\r\n    *ngFor=\"let item of menuItems\"\r\n    class=\"db-menu__menu-item\"\r\n    role=\"menuitem\"\r\n    [attr.disabled]=\"item.disabled\"\r\n    [attr.aria-disabled]=\"item.disabled\"\r\n    [ngClass]=\"{ disabled: item.disabled }\"\r\n  >\r\n    <ng-container *ngTemplateOutlet=\"item.template\"></ng-container>\r\n  </li>\r\n</ul>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { menuItems: [{
                type: Input
            }] } });

class DBMenuComponent {
    constructor(elementRef, renderer, renderer2) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.renderer2 = renderer2;
        this.menuOverlayPlacement = 'left';
        this.design = 'primary';
        this.size = 'md';
        this.iconOnly = false;
        this.menuItems = null;
        this.menuItemsContentChildren = null;
        this.labelText = '';
        this.labelTpl = null;
        this.isOpen = false;
        this.overlayRef = null;
    }
    set label(label) {
        if (label instanceof TemplateRef) {
            this.labelTpl = label;
        }
        else {
            this.labelText = label;
        }
    }
    ngOnDestroy() {
        this.hideMenuOverlay();
    }
    toggleOpen() {
        if (this.isOpen) {
            this.hideMenuOverlay();
        }
        else {
            this.showMenuOverlay();
        }
    }
    hideMenuOverlay() {
        if (!this.isOpen) {
            return;
        }
        else {
            this.removeMenuOverlay();
            this.isOpen = false;
        }
    }
    get menuItemsPresent() {
        var _a, _b;
        return ((_a = this.menuItems) === null || _a === void 0 ? void 0 : _a.length) > 0 || ((_b = this.menuItemsContentChildren) === null || _b === void 0 ? void 0 : _b.length) > 0;
    }
    showMenuOverlay() {
        this.createMenuOverlay();
        this.isOpen = true;
    }
    createMenuOverlay() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.overlayRef =
                this.renderer.createAndAppendComponent(DBMenuOverlayComponent);
            // This is a workaround for the (unfortunately still existent) Angular issue that nested transclusion does not work as expected
            // See following issues for example (there are more):
            // https://github.com/angular/angular/issues/12930, https://github.com/dart-lang/angular/issues/195
            // We cannot use the ngProjectAs-workaround, as that does not work with @ContentChildren
            this.overlayRef.instance.menuItems =
                this.menuItems || ((_a = this.menuItemsContentChildren) === null || _a === void 0 ? void 0 : _a.toArray()) || [];
            this.overlayRef.instance.init(this.elementRef, this.menuOverlayPlacement);
            yield this.overlayRef.instance.show();
            this.documentClickListener = this.renderer2.listen('document', 'click', () => {
                this.hideMenuOverlay();
            });
        });
    }
    removeMenuOverlay() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.overlayRef) {
                yield this.overlayRef.instance.hide();
                yield this.renderer.destroyAndRemoveComponent(this.overlayRef);
                this.documentClickListener();
            }
        });
    }
}
DBMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuComponent, deps: [{ token: i0.ElementRef }, { token: DBRenderer }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
DBMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBMenuComponent, selector: "db-menu", inputs: { label: "label", menuOverlayPlacement: "menuOverlayPlacement", design: "design", size: "size", iconOnly: "iconOnly", menuItems: "menuItems" }, queries: [{ propertyName: "menuItemsContentChildren", predicate: DBMenuItemComponent }], ngImport: i0, template: "  <db-button\r\n    *ngIf=\"menuItemsPresent\"\r\n    [design]=\"design\"\r\n    [size]=\"size\"\r\n    [iconOnly]=\"iconOnly\"\r\n    type=\"button\"\r\n    class=\"db-menu__button\"\r\n    (click)=\"toggleOpen()\"\r\n  >\r\n    <ng-template [ngIf]=\"!labelTpl\">{{ labelText }}</ng-template>\r\n    <ng-template *ngTemplateOutlet=\"labelTpl\"></ng-template>\r\n  </db-button>\r\n\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: DBButtonComponent, selector: "db-button", inputs: ["type", "design", "size", "arrow", "loading", "iconOnly"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-menu', changeDetection: ChangeDetectionStrategy.OnPush, template: "  <db-button\r\n    *ngIf=\"menuItemsPresent\"\r\n    [design]=\"design\"\r\n    [size]=\"size\"\r\n    [iconOnly]=\"iconOnly\"\r\n    type=\"button\"\r\n    class=\"db-menu__button\"\r\n    (click)=\"toggleOpen()\"\r\n  >\r\n    <ng-template [ngIf]=\"!labelTpl\">{{ labelText }}</ng-template>\r\n    <ng-template *ngTemplateOutlet=\"labelTpl\"></ng-template>\r\n  </db-button>\r\n\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: DBRenderer }, { type: i0.Renderer2 }]; }, propDecorators: { label: [{
                type: Input
            }], menuOverlayPlacement: [{
                type: Input
            }], design: [{
                type: Input
            }], size: [{
                type: Input
            }], iconOnly: [{
                type: Input
            }], menuItems: [{
                type: Input
            }], menuItemsContentChildren: [{
                type: ContentChildren,
                args: [DBMenuItemComponent]
            }] } });

class DBListRowSuffixComponent {
    constructor() { }
}
DBListRowSuffixComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowSuffixComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBListRowSuffixComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBListRowSuffixComponent, selector: "db-list-row-suffix", ngImport: i0, template: "<ng-content></ng-content>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowSuffixComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-list-row-suffix', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content></ng-content>\r\n" }]
        }], ctorParameters: function () { return []; } });

class DBListRowMenuComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.menuItemsArray = [];
    }
    set menuItems(menuItems) {
        this.menuItemsArray = menuItems.toArray();
        this.changeDetectorRef.markForCheck();
    }
}
DBListRowMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowMenuComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBListRowMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBListRowMenuComponent, selector: "db-list-row-menu", queries: [{ propertyName: "menuItems", predicate: DBMenuItemComponent }], ngImport: i0, template: "<db-list-row-suffix>\r\n  <ng-container *ngIf=\"menuItemsArray.length > 0\">\r\n    <db-menu\r\n      design=\"tertiary\"\r\n      [label]=\"iconLabel\"\r\n      [menuItems]=\"menuItemsArray\"\r\n      [iconOnly]=\"true\"\r\n    >\r\n    </db-menu>\r\n    <ng-template #iconLabel>\r\n      <db-icon class=\"icon\" size=\"md\">more</db-icon>\r\n    </ng-template>\r\n  </ng-container>\r\n</db-list-row-suffix>\r\n", dependencies: [{ kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBMenuComponent, selector: "db-menu", inputs: ["label", "menuOverlayPlacement", "design", "size", "iconOnly", "menuItems"] }, { kind: "component", type: DBListRowSuffixComponent, selector: "db-list-row-suffix" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-list-row-menu', changeDetection: ChangeDetectionStrategy.OnPush, template: "<db-list-row-suffix>\r\n  <ng-container *ngIf=\"menuItemsArray.length > 0\">\r\n    <db-menu\r\n      design=\"tertiary\"\r\n      [label]=\"iconLabel\"\r\n      [menuItems]=\"menuItemsArray\"\r\n      [iconOnly]=\"true\"\r\n    >\r\n    </db-menu>\r\n    <ng-template #iconLabel>\r\n      <db-icon class=\"icon\" size=\"md\">more</db-icon>\r\n    </ng-template>\r\n  </ng-container>\r\n</db-list-row-suffix>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { menuItems: [{
                type: ContentChildren,
                args: [DBMenuItemComponent]
            }] } });

class DBListRowComponent {
    constructor() {
        this.clickable = true;
        this.showBorder = true;
        this.listRowMenu = null;
        this.rowClick = new EventEmitter();
    }
    get isFocusable() {
        if (this.clickable) {
            return 0;
        }
    }
    onRowClick(event) {
        var _a;
        if (this.clickable &&
            !((_a = this.listRowMenu) === null || _a === void 0 ? void 0 : _a.nativeElement.contains(event.target))) {
            this.rowClick.emit();
        }
    }
}
DBListRowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBListRowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBListRowComponent, selector: "db-list-row", inputs: { clickable: "clickable", showBorder: "showBorder" }, outputs: { rowClick: "rowClick" }, host: { listeners: { "click": "onRowClick($event)", "keydown.Enter": "onRowClick($event)" }, properties: { "class.clickable": "this.clickable", "attr.tabindex": "this.isFocusable", "class.bordered": "this.showBorder" } }, queries: [{ propertyName: "listRowMenu", first: true, predicate: DBListRowMenuComponent, descendants: true, read: ElementRef }], ngImport: i0, template: "<ng-content></ng-content>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-list-row', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content></ng-content>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { clickable: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.clickable']
            }], isFocusable: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], showBorder: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.bordered']
            }], listRowMenu: [{
                type: ContentChild,
                args: [DBListRowMenuComponent, { read: ElementRef }]
            }], rowClick: [{
                type: Output
            }], onRowClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.Enter', ['$event']]
            }] } });

class DBMenuModule {
}
DBMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBMenuModule, declarations: [DBMenuComponent, DBMenuItemComponent, DBMenuOverlayComponent], imports: [CommonModule, DBButtonModule, DBRendererModule], exports: [DBMenuComponent, DBMenuItemComponent, DBMenuOverlayComponent] });
DBMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuModule, imports: [CommonModule, DBButtonModule, DBRendererModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DBMenuComponent, DBMenuItemComponent, DBMenuOverlayComponent],
                    imports: [CommonModule, DBButtonModule, DBRendererModule],
                    exports: [DBMenuComponent, DBMenuItemComponent, DBMenuOverlayComponent]
                }]
        }] });

class DBListRowModule {
}
DBListRowModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBListRowModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBListRowModule, declarations: [DBListRowComponent,
        DBListRowPrefixComponent,
        DBListRowContentComponent,
        DBListRowSuffixComponent,
        DBListRowIconComponent,
        DBListRowMenuComponent], imports: [DBIconModule, CommonModule, DBMenuModule, DBAvatarModule], exports: [DBListRowComponent,
        DBListRowPrefixComponent,
        DBListRowContentComponent,
        DBListRowSuffixComponent,
        DBListRowIconComponent,
        DBListRowMenuComponent] });
DBListRowModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowModule, imports: [DBIconModule, CommonModule, DBMenuModule, DBAvatarModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBListRowModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBListRowComponent,
                        DBListRowPrefixComponent,
                        DBListRowContentComponent,
                        DBListRowSuffixComponent,
                        DBListRowIconComponent,
                        DBListRowMenuComponent,
                    ],
                    imports: [DBIconModule, CommonModule, DBMenuModule, DBAvatarModule],
                    exports: [
                        DBListRowComponent,
                        DBListRowPrefixComponent,
                        DBListRowContentComponent,
                        DBListRowSuffixComponent,
                        DBListRowIconComponent,
                        DBListRowMenuComponent,
                    ],
                }]
        }] });

/**
 * DB Loading Overlay Component
 */
class DBLoadingOverlayComponent {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     */
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        this.internalMessage = null;
    }
    /**
     * Loading message
     */
    set message(message) {
        this.internalMessage = message;
        this.changeDetector.detectChanges();
    }
    get message() {
        return this.internalMessage;
    }
}
DBLoadingOverlayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBLoadingOverlayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBLoadingOverlayComponent, selector: "db-loading-overlay", inputs: { message: "message" }, ngImport: i0, template: "<db-spinner class=\"db-loading-overlay__spinner mx-auto\"></db-spinner>\r\n<div class=\"db-loading-overlay__message text-center my-0\" [class.is-empty]=\"!message || message.length === 0\" [innerHTML]=\"message\">\r\n</div>\r\n", dependencies: [{ kind: "component", type: DBSpinnerComponent, selector: "db-spinner", inputs: ["design"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-loading-overlay', template: "<db-spinner class=\"db-loading-overlay__spinner mx-auto\"></db-spinner>\r\n<div class=\"db-loading-overlay__message text-center my-0\" [class.is-empty]=\"!message || message.length === 0\" [innerHTML]=\"message\">\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { message: [{
                type: Input
            }] } });

/**
 * DB Modal Content Default Component
 */
class DBModalContentDefaultComponent {
}
DBModalContentDefaultComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalContentDefaultComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBModalContentDefaultComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBModalContentDefaultComponent, selector: "db-modal-content-default", ngImport: i0, template: "<ng-content></ng-content>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalContentDefaultComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-modal-content-default', template: "<ng-content></ng-content>\r\n" }]
        }] });

/**
 * DB Modal Service
 */
class DBModalService {
    /**
     * Constructor
     */
    constructor() {
        this.modalComponent = null;
        this.changesSource = new BehaviorSubject('closed');
        this.changes = this.changesSource.asObservable();
    }
    /**
     * Check if the modal is opened
     *
     * @returns - Flag, describing whether the modal is closed
     */
    isOpened() {
        return this.modalComponent.isOpen && !this.modalComponent.transitionPhase;
    }
    /**
     * Check if the modal is closed
     *
     * @returns - Flag, describing whether the modal is closed
     */
    isClosed() {
        return !this.modalComponent.isOpen && !this.modalComponent.transitionPhase;
    }
    /**
     * Open the modal, using simple text
     *
     * @param                                         title        - Title
     * @param                                         content      - Content
     * @param] - Modal options
     * @returns              - Promise, resolved when opened
     */
    open(title, content, options) {
        const modalOptions = this.mergeOptions(options);
        this.checkForModalComponent();
        return this.modalComponent.open(title, content, modalOptions);
    }
    /**
     * Open the modal, using a component
     *
     * @param                  component    - Component
     * @param] - Modal options
     * @returns              - Promise, resolved when opened
     */
    openWithComponent(component, options) {
        const modalOptions = this.mergeOptions(options);
        this.checkForModalComponent();
        return this.modalComponent.openWithComponent(component, modalOptions);
    }
    /**
     * Open the modal, using a template reference
     *
     * @param              template     - Template
     * @param] - Modal options
     * @returns              - Promise, resolved when opened
     */
    openWithTemplate(template, options) {
        const modalOptions = this.mergeOptions(options);
        this.checkForModalComponent();
        return this.modalComponent.openWithTemplate(template, modalOptions);
    }
    /**
     * Close the modal
     *
     * @returns - Promise, resolved when closed
     */
    close() {
        this.checkForModalComponent();
        return this.modalComponent.close();
    }
    /**
     * Emit state change
     *
     * @param state - New state
     */
    emitStateChange(state) {
        this.changesSource.next(state);
    }
    /**
     * Merge all the Options for a Modal
     * @param options - Options
     */
    mergeOptions(options = {}) {
        const defaultOptions = {
            closeOnBackdropClickOrEscKey: true, // Best #usability by default
        };
        if (options.trigger && isDevMode()) {
            // istanbul ignore next: Ignored because temporary message
            console.warn('[@db/db-core] The "trigger" option is deprecated and will be removed in one of the upcoming releases.');
        }
        return Object.assign(Object.assign({}, defaultOptions), options);
    }
    /**
     * Register modal component (once!)
     *
     * @param modalComponent - Modal component
     */
    registerModalComponent(modalComponent) {
        if (this.modalComponent === null) {
            this.modalComponent = modalComponent;
        }
        else {
            // istanbul ignore next: Ignored because temporary message
            throw new Error([
                '[DB MODAL]',
                'You can register a DBModalComponent to the DBModalService only once.',
                'Make sure to use the "db-modal" tag only once.',
            ].join(' '));
        }
    }
    /**
     * Check that the modal has been registered, else throw an error
     */
    checkForModalComponent() {
        if (this.modalComponent === null) {
            // istanbul ignore next: Ignored because temporary message
            throw new Error([
                '[DB MODAL]',
                'The DBModalComponent is not defined.',
                'Make sure to place the "db-modal" tag somewhere in your root component.',
            ].join(' '));
        }
    }
}
DBModalService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBModalService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * DB Modal Header Default Component
 */
class DBModalHeaderDefaultComponent {
    /**
     * Constructor
     */
    constructor() {
        this.title = '';
        this.close = new EventEmitter();
    }
    /**
     * Handle click on close button
     */
    onCloseButtonClick() {
        this.close.next();
    }
}
DBModalHeaderDefaultComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalHeaderDefaultComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBModalHeaderDefaultComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBModalHeaderDefaultComponent, selector: "db-modal-header-default", inputs: { title: "title" }, outputs: { close: "close" }, ngImport: i0, template: "<header class=\"db-modal-header-default__container\" [class.has-no-title]=\"title === ''\">\r\n\r\n  <h1 class=\"db-modal-header-default__title\" *ngIf=\"title !== ''\">{{ title }}</h1>\r\n  <ng-content></ng-content>\r\n  <button class=\"db-modal-header-default__button\" type=\"button\" (click)=\"onCloseButtonClick()\">\r\n    <svg class=\"db-modal-header-default__icon\" viewBox=\"0 0 54 54\">\r\n      <path d=\"M29.8,27L53.1,3.8c0.7-0.7,0.7-2.1,0-2.8c-0.4-0.4-0.9-0.6-1.4-0.6c-0.5,0-1.1,0.2-1.4,0.6L27,24.2L3.8,0.9\r\n        C3.4,0.6,2.9,0.4,2.3,0.4c-0.5,0-1.1,0.2-1.4,0.6C0.2,1.7,0.2,3,0.9,3.8L24.2,27L0.9,50.2c-0.7,0.7-0.7,2.1,0,2.8\r\n        c0.4,0.4,0.9,0.6,1.4,0.6c0.5,0,1.1-0.2,1.4-0.6L27,29.8l23.2,23.2c0.4,0.4,0.9,0.6,1.4,0.6c0.5,0,1.1-0.2,1.4-0.6\r\n        c0.7-0.7,0.7-2.1,0-2.8L29.8,27z\">\r\n      </path>\r\n    </svg>\r\n  </button>\r\n\r\n</header>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalHeaderDefaultComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-modal-header-default', template: "<header class=\"db-modal-header-default__container\" [class.has-no-title]=\"title === ''\">\r\n\r\n  <h1 class=\"db-modal-header-default__title\" *ngIf=\"title !== ''\">{{ title }}</h1>\r\n  <ng-content></ng-content>\r\n  <button class=\"db-modal-header-default__button\" type=\"button\" (click)=\"onCloseButtonClick()\">\r\n    <svg class=\"db-modal-header-default__icon\" viewBox=\"0 0 54 54\">\r\n      <path d=\"M29.8,27L53.1,3.8c0.7-0.7,0.7-2.1,0-2.8c-0.4-0.4-0.9-0.6-1.4-0.6c-0.5,0-1.1,0.2-1.4,0.6L27,24.2L3.8,0.9\r\n        C3.4,0.6,2.9,0.4,2.3,0.4c-0.5,0-1.1,0.2-1.4,0.6C0.2,1.7,0.2,3,0.9,3.8L24.2,27L0.9,50.2c-0.7,0.7-0.7,2.1,0,2.8\r\n        c0.4,0.4,0.9,0.6,1.4,0.6c0.5,0,1.1-0.2,1.4-0.6L27,29.8l23.2,23.2c0.4,0.4,0.9,0.6,1.4,0.6c0.5,0,1.1-0.2,1.4-0.6\r\n        c0.7-0.7,0.7-2.1,0-2.8L29.8,27z\">\r\n      </path>\r\n    </svg>\r\n  </button>\r\n\r\n</header>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { title: [{
                type: Input
            }], close: [{
                type: Output
            }] } });

/**
 * DB Modal Default Component
 */
class DBModalDefaultComponent {
    /**
     * Constructor
     *
     * @param modalService - Modal service
     */
    constructor(modalService) {
        this.modalService = modalService;
    }
    /**
     * Hande user wish to close the modal
     */
    onClose() {
        this.modalService.close();
    }
}
DBModalDefaultComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalDefaultComponent, deps: [{ token: DBModalService }], target: i0.ɵɵFactoryTarget.Component });
DBModalDefaultComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBModalDefaultComponent, selector: "db-modal-default", ngImport: i0, template: "<db-modal-header-default\r\n  [title]=\"title\"\r\n  (close)=\"onClose()\">\r\n</db-modal-header-default>\r\n\r\n<db-modal-content-default>\r\n  {{ content }}\r\n</db-modal-content-default>\r\n", dependencies: [{ kind: "component", type: DBModalContentDefaultComponent, selector: "db-modal-content-default" }, { kind: "component", type: DBModalHeaderDefaultComponent, selector: "db-modal-header-default", inputs: ["title"], outputs: ["close"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalDefaultComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-modal-default', template: "<db-modal-header-default\r\n  [title]=\"title\"\r\n  (close)=\"onClose()\">\r\n</db-modal-header-default>\r\n\r\n<db-modal-content-default>\r\n  {{ content }}\r\n</db-modal-content-default>\r\n" }]
        }], ctorParameters: function () { return [{ type: DBModalService }]; } });

/**
 * DB Modal Footer Default Component
 */
class DBModalFooterDefaultComponent {
    /**
     * Constructor
     */
    constructor() {
        this.buttonTitle = '';
        this.close = new EventEmitter();
        this.buttonPosition = 'right';
    }
    /**
     * Handle click on close button
     */
    onCloseButtonClick() {
        this.close.next();
    }
}
DBModalFooterDefaultComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalFooterDefaultComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBModalFooterDefaultComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBModalFooterDefaultComponent, selector: "db-modal-footer-default", inputs: { buttonTitle: "buttonTitle", buttonPosition: "buttonPosition" }, outputs: { close: "close" }, ngImport: i0, template: "<footer class=\"db-modal-footer-default__container d-flex\"\r\n        [ngClass]=\"{'justify-content-start': buttonPosition === 'left',\r\n              'justify-content-center': buttonPosition === 'center',\r\n               'justify-content-end' : buttonPosition ==='right'}\">\r\n\r\n  <db-button design=\"secondary\"\r\n             *ngIf=\"buttonTitle\" (click)=\"onCloseButtonClick()\">\r\n    {{ buttonTitle }}\r\n  </db-button>\r\n  <ng-content></ng-content>\r\n\r\n</footer>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBButtonComponent, selector: "db-button", inputs: ["type", "design", "size", "arrow", "loading", "iconOnly"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalFooterDefaultComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-modal-footer-default', template: "<footer class=\"db-modal-footer-default__container d-flex\"\r\n        [ngClass]=\"{'justify-content-start': buttonPosition === 'left',\r\n              'justify-content-center': buttonPosition === 'center',\r\n               'justify-content-end' : buttonPosition ==='right'}\">\r\n\r\n  <db-button design=\"secondary\"\r\n             *ngIf=\"buttonTitle\" (click)=\"onCloseButtonClick()\">\r\n    {{ buttonTitle }}\r\n  </db-button>\r\n  <ng-content></ng-content>\r\n\r\n</footer>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { buttonTitle: [{
                type: Input
            }], buttonPosition: [{
                type: Input
            }], close: [{
                type: Output
            }] } });

/**
 * DB Modal Component
 */
class DBModalComponent {
    /**
     * Constructor
     */
    constructor(modalService, viewportService, windowRef, componentFactoryResolver, changeDetector, renderer, elementRef) {
        this.isOpen = false;
        this.transitionPhase = null;
        this.options = {};
        this.resolveFn = null;
        this.previouslyFocusedElement = null;
        this.subscription = null;
        this.modalService = modalService;
        this.modalService.registerModalComponent(this);
        this.viewportService = viewportService;
        this.windowRef = windowRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.elementRef = elementRef;
    }
    /**
     * Handle keyup events, and close the modal if ESC was used
     *
     * @param event - Event object
     */
    onWindowKeyup(event) {
        if (this.isOpen && event.key === 'Escape' && this.options.closeOnBackdropClickOrEscKey) {
            this.modalService.close();
        }
    }
    /**
     * Handle click events, and close the modal if the backdrop was clicked
     *
     * @param event - Event object
     */
    on(event) {
        if (event.target === this.elementRef.nativeElement && this.isOpen && this.options.closeOnBackdropClickOrEscKey) {
            this.modalService.close();
        }
    }
    /**
     * Handle transtition end events, signalizing that the modal animations has finished
     *
     * @param event - Event object
     */
    onTransitionEnd(event) {
        // Notify others that the animation has finished
        if (this.transitionPhase && this.resolveFn) {
            if (this.transitionPhase === 'in') {
                this.modalService.emitStateChange('opened');
                this.resolveFn(this.resolveWith);
            }
            else if (this.transitionPhase === 'out') {
                this.modalTemplateViewContainerRef.clear(); // Destroy component
                this.renderer.removeStyle(this.elementRef.nativeElement, 'height');
                this.renderer.removeStyle(this.elementRef.nativeElement, 'padding-top');
                this.renderer.removeStyle(this.elementRef.nativeElement, 'padding-bottom');
                this.renderer.removeStyle(this.elementRef.nativeElement, 'align-items');
                this.modalService.emitStateChange('closed');
                this.resolveFn();
            }
            this.transitionPhase = null;
        }
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        if (this.subscription) { // Cleanup (#perfmatters)
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }
    /**
     * Open the modal, using the default component
     *
     * @param   title   - Modal title
     * @param   content - Modal content
     * @param   options - Modal options
     * @returns         - Promise, resolves with the component reference when opened
     */
    open(title, content, options) {
        if (this.isOpen) {
            return;
        }
        return new Promise((resolve, reject) => {
            // Save currently focused element
            this.previouslyFocusedElement = this.retrieveCurrentlyFocusedElement();
            // Render component
            const componentRef = this.renderModalComponent(DBModalDefaultComponent);
            componentRef.instance.title = title;
            componentRef.instance.content = content;
            this.options = options;
            // Show modal
            this.showModal(resolve, componentRef);
        });
    }
    /**
     * Open the modal, using the given component
     *
     * @param   component - Component
     * @param   options   - Modal options
     * @returns           - Promise, resolves with the component reference when opened
     */
    openWithComponent(component, options) {
        if (this.isOpen) {
            return;
        }
        return new Promise((resolve, reject) => {
            // Save currently focused element
            this.previouslyFocusedElement = this.retrieveCurrentlyFocusedElement();
            // Render component
            const componentRef = this.renderModalComponent(component);
            this.options = options;
            // Show modal
            this.showModal(resolve, componentRef);
        });
    }
    /**
     * Open the modal, using the given template reference
     *
     * @param   template - Template reference
     * @param   options  - Modal options
     * @returns          - Promise, resolved with the embedded view reference when opened
     */
    openWithTemplate(template, options) {
        if (this.isOpen) {
            return;
        }
        return new Promise((resolve, reject) => {
            // Save currently focused element
            this.previouslyFocusedElement = this.retrieveCurrentlyFocusedElement();
            // Render component
            const embeddedViewRef = this.renderModalTemplate(template);
            this.options = options;
            // Show modal
            this.showModal(resolve, embeddedViewRef);
        });
    }
    /**
     * Close the modal
     *
     * @returns - Promise, resolved when closed
     */
    close() {
        return new Promise((resolve, reject) => {
            this.resolveFn = resolve;
            this.modalService.emitStateChange('close');
            this.transitionPhase = 'out';
            this.isOpen = false;
            // Cleanup resize listener
            if (this.subscription) {
                this.subscription.unsubscribe();
                this.subscription = null;
            }
            // Focus the element that was in focus before opening the modal (#accessibility)
            if (this.previouslyFocusedElement) {
                // Try to focus the element
                this.previouslyFocusedElement.focus();
                // Now, if the focus didn't work properly (e.g. the element is not focusable), simply blur, falling back to the body element
                if (!this.windowRef.nativeDocument.activeElement.isSameNode(this.previouslyFocusedElement)) {
                    this.windowRef.nativeDocument.activeElement.blur();
                }
            }
            this.previouslyFocusedElement = null;
        });
    }
    /**
     * Show the modal
     *
     * @param                               resolveFn   - Promise resolve function for the open modal APIs
     * @param resolveWith - Value for the promise resolve function
     */
    showModal(resolveFn, resolveWith) {
        return __awaiter(this, void 0, void 0, function* () {
            // Within iFrame, we only calculate the position once - else, we re-render every time the body size changes
            yield this.setModalPosition();
            if (!this.windowRef.isIframe) {
                this.subscription = this.viewportService
                    .resizeChanges()
                    .pipe(skip(1), // Skip the initial one
                throttleTime(34))
                    .subscribe(() => {
                    this.setModalPosition();
                });
            }
            // Save information for later promise resolving
            this.resolveFn = resolveFn;
            this.resolveWith = resolveWith;
            // Show the modal (animate in)
            this.transitionPhase = 'in';
            this.isOpen = true;
            this.modalService.emitStateChange('open');
            // Wait for the initial rendering to happen, then calculate modal position and set focus (#accessibility)
            this.changeDetector.detectChanges();
            setTimeout(() => {
                this.modalContainerElementRef.nativeElement.focus();
            }, 0);
        });
    }
    /**
     * Calculate and render the modal position, based on whether the app is running within an iFrame
     */
    setModalPosition() {
        return __awaiter(this, void 0, void 0, function* () {
            // Set the modal container height to the window height
            // Note: This is required to fix issues in IE regarding dimension calculations when using flexbox
            const baseHeight = this.windowRef.nativeWindow.innerHeight;
            this.renderer.setStyle(this.elementRef.nativeElement, 'height', `${baseHeight}px`);
            // If we are within an iFrame, we add some space to top and bottom in order to restrain the modal area to the visible screen area
            if (this.windowRef.isIframe) {
                // We use the intersection observer to get to know our available viewport, else we estimate
                // TODO: Intersection observer missing on Window typings (see https://github.com/Microsoft/TypeScript/issues/16255)
                if (this.windowRef.nativeWindow.IntersectionObserver) {
                    // We restrict the modal area to the visible screen area
                    const area = yield this.viewportService.getVisibleArea(this.elementRef);
                    this.renderer.setStyle(this.elementRef.nativeElement, 'padding-top', `${area.intersectionRect.top + 30}px`);
                    this.renderer.setStyle(this.elementRef.nativeElement, 'padding-bottom', `${baseHeight - area.intersectionRect.bottom + 30}px`);
                }
                else {
                    // We calculate the modal position based on the position of the lastly focused element (which is usually the modal trigger)
                    const triggerElementPositionTop = (this.previouslyFocusedElement || this.windowRef.nativeBody).getBoundingClientRect().top;
                    // Plus, we have to prevent modals flowing outside the window (at the bottom of the screen)
                    if (baseHeight - triggerElementPositionTop < 500) {
                        this.renderer.setStyle(this.elementRef.nativeElement, 'align-items', 'flex-end');
                    }
                    else {
                        this.renderer.setStyle(this.elementRef.nativeElement, 'padding-top', `${triggerElementPositionTop + 30}px`);
                        this.renderer.setStyle(this.elementRef.nativeElement, 'align-items', 'flex-start');
                    }
                }
            }
        });
    }
    /**
     * Instantiate the modal content, using the given component
     * See <https://blog.thecodecampus.de/angular-2-dynamically-render-components/> for details
     *
     * @param         component - Component
     * @returns           - Reference to the instantiated component
     */
    renderModalComponent(component) {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
        return this.modalTemplateViewContainerRef.createComponent(componentFactory);
    }
    /**
     * Instantiate the modal content, using the given template reference
     *
     * @param     template - Template reference
     * @returns          - Reference to the instantiated component
     */
    renderModalTemplate(template) {
        return this.modalTemplateViewContainerRef.createEmbeddedView(template);
    }
    /**
     * Retrieve the currently focused element
     *
     * @returns - Currently focused elements
     */
    retrieveCurrentlyFocusedElement() {
        // When running within an iFrame in IE, the 'activeElement' might be invalid - thus we use the body element as a fallback
        // See: https://github.com/vuejs/vue/issues/6157
        return this.windowRef.nativeDocument.activeElement && this.windowRef.nativeDocument.activeElement instanceof HTMLElement
            ? this.windowRef.nativeDocument.activeElement
            : this.windowRef.nativeBody;
    }
}
DBModalComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalComponent, deps: [{ token: DBModalService }, { token: DBViewportService }, { token: DBWindowRef }, { token: i0.ComponentFactoryResolver }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DBModalComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBModalComponent, selector: "db-modal", host: { listeners: { "keyup": "onWindowKeyup($event)", "click": "on($event)", "transitionend": "onTransitionEnd($event)" }, properties: { "class.is-open": "this.isOpen" } }, viewQueries: [{ propertyName: "modalTemplateViewContainerRef", first: true, predicate: ["modalTemplate"], descendants: true, read: ViewContainerRef, static: true }, { propertyName: "modalContainerElementRef", first: true, predicate: ["modalContainer"], descendants: true, static: true }], ngImport: i0, template: "<!--\r\n  Note:\r\n  Due to (really, really) bad browser support, we do not (yet) use the brand-new HTML5 dialog element here (see\r\n  <https://caniuse.com/#feat=dialog>.). Instead, to ensure great accessibility, we rely on various aria attributes.\r\n-->\r\n<div class=\"db-modal__container\" role=\"dialog\" aria-modal=\"true\" tabIndex=\"-1\" #modalContainer>\r\n  <ng-template #modalTemplate></ng-template>\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-modal', template: "<!--\r\n  Note:\r\n  Due to (really, really) bad browser support, we do not (yet) use the brand-new HTML5 dialog element here (see\r\n  <https://caniuse.com/#feat=dialog>.). Instead, to ensure great accessibility, we rely on various aria attributes.\r\n-->\r\n<div class=\"db-modal__container\" role=\"dialog\" aria-modal=\"true\" tabIndex=\"-1\" #modalContainer>\r\n  <ng-template #modalTemplate></ng-template>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: DBModalService }, { type: DBViewportService }, { type: DBWindowRef }, { type: i0.ComponentFactoryResolver }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { isOpen: [{
                type: HostBinding,
                args: ['class.is-open']
            }], modalTemplateViewContainerRef: [{
                type: ViewChild,
                args: ['modalTemplate', { read: ViewContainerRef, static: true }]
            }], modalContainerElementRef: [{
                type: ViewChild,
                args: ['modalContainer', { static: true }]
            }], onWindowKeyup: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], on: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], onTransitionEnd: [{
                type: HostListener,
                args: ['transitionend', ['$event']]
            }] } });

/**
 * DB Modal Module
 */
class DBModalModule {
}
DBModalModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBModalModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBModalModule, declarations: [DBModalComponent,
        DBModalContentDefaultComponent,
        DBModalDefaultComponent,
        DBModalFooterDefaultComponent,
        DBModalHeaderDefaultComponent], imports: [CommonModule,
        DBButtonModule,
        DBViewportModule,
        DBWindowRefModule,
        DBSpinnerModule], exports: [DBModalComponent,
        DBModalContentDefaultComponent,
        DBModalDefaultComponent,
        DBModalFooterDefaultComponent,
        DBModalHeaderDefaultComponent] });
DBModalModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBModalService],
            ],
            provide: DBModalService,
            useFactory: modalServiceFactory,
        },
    ], imports: [CommonModule,
        DBButtonModule,
        DBViewportModule,
        DBWindowRefModule,
        DBSpinnerModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBModalModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBModalComponent,
                        DBModalContentDefaultComponent,
                        DBModalDefaultComponent,
                        DBModalFooterDefaultComponent,
                        DBModalHeaderDefaultComponent,
                    ],
                    exports: [
                        DBModalComponent,
                        DBModalContentDefaultComponent,
                        DBModalDefaultComponent,
                        DBModalFooterDefaultComponent,
                        DBModalHeaderDefaultComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBButtonModule,
                        DBViewportModule,
                        DBWindowRefModule,
                        DBSpinnerModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBModalService],
                            ],
                            provide: DBModalService,
                            useFactory: modalServiceFactory,
                        },
                    ]
                }]
        }] });
/**
 * Modal Service Factory
 *
 * @param parentModalService - Parent heatbeat service (if exists)
 * @returns                           - Modal service instance
 */
function modalServiceFactory(parentModalService) {
    return parentModalService || new DBModalService();
}

/**
 * DB Loading Overlay Service
 */
class DBLoadingOverlayService {
    /**
     * Constructor
     *
     * @param modalService - Modal service
     */
    constructor(modalService) {
        this.modalService = modalService;
    }
    /**
     * Show the loading overlay
     */
    show(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const componentRef = yield this.modalService.openWithComponent(DBLoadingOverlayComponent, {
                closeOnBackdropClickOrEscKey: false,
            });
            if (message) {
                componentRef.instance.message = message;
            }
        });
    }
    /**
     * Hide the loading overlay
     */
    hide() {
        this.modalService.close();
    }
}
DBLoadingOverlayService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayService, deps: [{ token: DBModalService }], target: i0.ɵɵFactoryTarget.Injectable });
DBLoadingOverlayService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DBModalService }]; } });

/**
 * DB Loading Overlay Module
 */
class DBLoadingOverlayModule {
}
DBLoadingOverlayModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBLoadingOverlayModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayModule, declarations: [DBLoadingOverlayComponent], imports: [DBSpinnerModule,
        DBModalModule] });
DBLoadingOverlayModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBLoadingOverlayService],
                DBModalService,
            ],
            provide: DBLoadingOverlayService,
            useFactory: loadingOverlayServiceFactory,
        },
    ], imports: [DBSpinnerModule,
        DBModalModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLoadingOverlayModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBLoadingOverlayComponent,
                    ],
                    imports: [
                        DBSpinnerModule,
                        DBModalModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBLoadingOverlayService],
                                DBModalService,
                            ],
                            provide: DBLoadingOverlayService,
                            useFactory: loadingOverlayServiceFactory,
                        },
                    ]
                }]
        }] });
/**
 * Loading Overlay Service Factory
 *
 * @param   loadingOverlayService - Loading overlay service
 * @param   modalService          - Modal service
 * @returns                       - Loading overlay service
 */
function loadingOverlayServiceFactory(loadingOverlayService, modalService) {
    return loadingOverlayService || new DBLoadingOverlayService(modalService);
}

class DBLinkPillListComponent {
    constructor() {
        this.icon = '';
        this.title = '';
    }
}
DBLinkPillListComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLinkPillListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBLinkPillListComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBLinkPillListComponent, selector: "db-link-pill-list", inputs: { icon: "icon", title: "title" }, ngImport: i0, template: "<div class=\"db-link-pill-list mt-6 mb-5 d-flex align-items-center\">\r\n  <db-icon class=\"db-link-pill-list__icon\" size=\"100%\">\r\n    {{ icon }}\r\n  </db-icon>\r\n  <span class=\"db-heading-4 ml-4\"> {{ title }} </span>\r\n</div>\r\n<ng-content select=\".db-link-pill\"></ng-content>\r\n", dependencies: [{ kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLinkPillListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-link-pill-list', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"db-link-pill-list mt-6 mb-5 d-flex align-items-center\">\r\n  <db-icon class=\"db-link-pill-list__icon\" size=\"100%\">\r\n    {{ icon }}\r\n  </db-icon>\r\n  <span class=\"db-heading-4 ml-4\"> {{ title }} </span>\r\n</div>\r\n<ng-content select=\".db-link-pill\"></ng-content>\r\n" }]
        }], propDecorators: { icon: [{
                type: Input
            }], title: [{
                type: Input
            }] } });

class DBLinkPillModule {
}
DBLinkPillModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLinkPillModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBLinkPillModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBLinkPillModule, declarations: [DBLinkPillListComponent], imports: [CommonModule, DBIconModule], exports: [DBLinkPillListComponent] });
DBLinkPillModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLinkPillModule, imports: [CommonModule, DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBLinkPillModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DBLinkPillListComponent],
                    imports: [CommonModule, DBIconModule],
                    exports: [DBLinkPillListComponent],
                }]
        }] });

/**
 * Abstract Navigation Item Component
 */
class AbstractNavigationItemComponentDirective {
    constructor() {
        this.isActive = false;
        this.isVisited = false;
        this.label = '';
        this.routes = [];
    }
}
AbstractNavigationItemComponentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AbstractNavigationItemComponentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
AbstractNavigationItemComponentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: AbstractNavigationItemComponentDirective, inputs: { label: "label", routes: "routes" }, host: { properties: { "class.is-active": "this.isActive", "class.is-visited": "this.isVisited" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AbstractNavigationItemComponentDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return []; }, propDecorators: { label: [{
                type: Input
            }], routes: [{
                type: Input
            }], isActive: [{
                type: HostBinding,
                args: ['class.is-active']
            }], isVisited: [{
                type: HostBinding,
                args: ['class.is-visited']
            }] } });

/**
 * DB Navigation Item Component
 */
class DBNavigationItemComponent extends AbstractNavigationItemComponentDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Angular change detector
     */
    constructor(changeDetector) {
        super();
        this.changeDetector = changeDetector;
        this.dashedInactive = false;
        this.dashedActive = false;
        this.internalProgress = this.outerCircleCircumference; // Invisible
        this.outerCircleCircumference = 160;
    }
    /**
     * Progress (value has to be between 0 and 100)
     */
    set progress(value) {
        // Calculate the SVG circle progress
        let newInternalProgress = this.outerCircleCircumference; // Zero / invisible by default
        if (this.isActive || this.isVisited) {
            newInternalProgress = this.outerCircleCircumference - (value * this.outerCircleCircumference / 100);
        }
        // Update the internal progress (if it has changed)
        if (newInternalProgress !== this.internalProgress) {
            this.internalProgress = newInternalProgress;
            this.changeDetector.markForCheck();
        }
    }
    get progress() {
        return this.internalProgress;
    }
}
DBNavigationItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationItemComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBNavigationItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBNavigationItemComponent, selector: "db-navigation-item", inputs: { dashedInactive: "dashedInactive", dashedActive: "dashedActive" }, host: { properties: { "class.db-navigation-item--dashed-inactive": "this.dashedInactive", "class.db-navigation-item--dashed-active": "this.dashedActive", "class.has-no-delay": "this.hasNoDelay" } }, usesInheritance: true, ngImport: i0, template: "<svg\r\n    focusable=\"false\"\r\n    class=\"db-navigation-item__outer-circle db-navigation-item__outer-circle--background\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\"></circle>\r\n</svg>\r\n\r\n<svg focusable=\"false\"\r\n     class=\"db-navigation-item__outer-circle db-navigation-item__outer-circle--foreground\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\" [style.stroke-dashoffset]=\"progress + 'px'\"></circle>\r\n</svg>\r\n\r\n<div\r\n    class=\"db-navigation-item__inner-circle\">\r\n\r\n  <svg viewBox=\"0 0 64 64\" class=\"db-navigation-item__icon-visited\">\r\n    <path d=\"M56.5 8.68c-1.33.04-2.6.62-3.52 1.6L22.1 42.46 9.03 28.82c-1.3-1.4-3.2-1.94-4.98-1.45-1.8.48-3.2 1.94-3.66 3.8-.47 1.87.07\r\n    3.85 1.4 5.2l16.7 17.4c.96 1 2.27 1.56 3.63 1.56 1.36 0 2.67-.56 3.63-1.57l34.5-35.93c1.5-1.53 1.96-3.87\r\n    1.14-5.9-.83-2.02-2.76-3.32-4.87-3.25z\"></path>\r\n  </svg>\r\n\r\n  <div class=\"db-navigation-item__icon-default\">\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n\r\n</div>\r\n\r\n<div class=\"db-navigation-item__line\"></div>\r\n\r\n<div [title]=\"label\" class=\"db-navigation-item__label\">\r\n  {{ label }}\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationItemComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-navigation-item', template: "<svg\r\n    focusable=\"false\"\r\n    class=\"db-navigation-item__outer-circle db-navigation-item__outer-circle--background\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\"></circle>\r\n</svg>\r\n\r\n<svg focusable=\"false\"\r\n     class=\"db-navigation-item__outer-circle db-navigation-item__outer-circle--foreground\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\" [style.stroke-dashoffset]=\"progress + 'px'\"></circle>\r\n</svg>\r\n\r\n<div\r\n    class=\"db-navigation-item__inner-circle\">\r\n\r\n  <svg viewBox=\"0 0 64 64\" class=\"db-navigation-item__icon-visited\">\r\n    <path d=\"M56.5 8.68c-1.33.04-2.6.62-3.52 1.6L22.1 42.46 9.03 28.82c-1.3-1.4-3.2-1.94-4.98-1.45-1.8.48-3.2 1.94-3.66 3.8-.47 1.87.07\r\n    3.85 1.4 5.2l16.7 17.4c.96 1 2.27 1.56 3.63 1.56 1.36 0 2.67-.56 3.63-1.57l34.5-35.93c1.5-1.53 1.96-3.87\r\n    1.14-5.9-.83-2.02-2.76-3.32-4.87-3.25z\"></path>\r\n  </svg>\r\n\r\n  <div class=\"db-navigation-item__icon-default\">\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n\r\n</div>\r\n\r\n<div class=\"db-navigation-item__line\"></div>\r\n\r\n<div [title]=\"label\" class=\"db-navigation-item__label\">\r\n  {{ label }}\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { dashedInactive: [{
                type: HostBinding,
                args: ['class.db-navigation-item--dashed-inactive']
            }, {
                type: Input
            }], dashedActive: [{
                type: HostBinding,
                args: ['class.db-navigation-item--dashed-active']
            }, {
                type: Input
            }], hasNoDelay: [{
                type: HostBinding,
                args: ['class.has-no-delay']
            }] } });

/**
 * Abstract Navigation Component
 */
class AbstractNavigationComponentDirective {
    /**
     * Constructor
     *
     * @param router - Angular Router
     * @param changeDetector - Change Detector Reference
     */
    constructor(router, changeDetector) {
        this.navigation = new EventEmitter();
        this.activeLabel = '';
        this.previousRouteUrl = '';
        this.router = router;
        this.changeDetector = changeDetector;
        this.routerSubscription = null;
    }
    /**
     * On Init Lifecycle Hook, setting up the router event listener
     */
    ngOnInit() {
        // Listen for router events
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
            // Skip any updates if the route actually has not changed (#perfmatters)
            if (event.urlAfterRedirects !== this.previousRouteUrl) {
                // Update states of all navigation items
                // We use 'urlAfterRedirects' instead of just 'url' to take automatic routing redirects (e.g. empty routes) into account
                this.updateNavigationItemStates(event.urlAfterRedirects);
                this.previousRouteUrl = event.urlAfterRedirects;
                this.navigation.emit(event.urlAfterRedirects);
                this.changeDetector.markForCheck(); // Re-render the label (mobile view)
            }
        });
    }
    /**
     * After View Init Lifecycle hook, triggering the initial rendering
     */
    ngAfterContentInit() {
        // Initial rendering
        this.previousRouteUrl = this.router.routerState.snapshot.url;
        this.updateNavigationItemStates(this.previousRouteUrl);
    }
    /**
     * On Destroy Lifecycl Hook, cleaning up ressources
     */
    ngOnDestroy() {
        // Cleanup subscriptions
        if (this.routerSubscription !== null) {
            this.routerSubscription.unsubscribe();
            this.routerSubscription = null; // Just to be sure
        }
    }
    /**
     * Hook method for NavigationComponent implementations
     *
     * @param navigationItemComponent - navigationItemComponent
     * @param activeRouteUrlIndex                              - activeRouteUrlIndex
     * @param index                                            - navigation item index
     * @param previousRouteUrl                                 - previousRouteUrl
     */
    doUpdateSpecificNavigationItemState(navigationItemComponent, activeRouteUrlIndex, index, previousRouteUrl) {
        // inherit
    }
    /**
     * Update states of all navigation items
     *
     * @param currentRouteUrl - Current route url
     */
    updateNavigationItemStates(currentRouteUrl) {
        // Skip if there are no navigation items
        if (this.navigationItemComponents !== null) {
            // Check if the navigation routes contain the current url
            const isRouteAvailable = this.navigationItemComponents.some((navigationItemComponent, index) => navigationItemComponent.routes.some((routeUrl) => this.router.routerState.snapshot.url.includes(routeUrl)));
            // Update states of all navigation items
            let reachedActivePage = false;
            this.navigationItemComponents.forEach((navigationItemComponent, index) => {
                // Check if the current url is in some way connected to the page (e.g. page or subpage)
                const activeRouteUrlIndex = navigationItemComponent.routes.findIndex((routeUrl) => this.router.isActive(routeUrl, false) // Ignore parameters of any kind
                );
                // Check whether we have reached the currently active page
                if (activeRouteUrlIndex !== -1) {
                    reachedActivePage = true;
                    this.activeLabel = navigationItemComponent.label;
                }
                // Set the navigation item states
                if (isRouteAvailable) {
                    navigationItemComponent.isVisited = !reachedActivePage;
                }
                navigationItemComponent.isActive = activeRouteUrlIndex !== -1;
                // NavigationComponent implementation hook
                this.doUpdateSpecificNavigationItemState(navigationItemComponent, activeRouteUrlIndex, index, currentRouteUrl);
            });
        }
    }
}
AbstractNavigationComponentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AbstractNavigationComponentDirective, deps: [{ token: i1$2.Router }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
AbstractNavigationComponentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: AbstractNavigationComponentDirective, outputs: { navigation: "navigation" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: AbstractNavigationComponentDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { navigation: [{
                type: Output
            }] } });

/**
 * DB Navigation Component
 */
class DBNavigationComponent extends AbstractNavigationComponentDirective {
    /**
     * Constructor
     *
     * @param router - Angular Router
     */
    constructor(router, changeDetector) {
        super(router, changeDetector);
    }
    doUpdateSpecificNavigationItemState(navigationItemComponent, activeRouteUrlIndex, index, previousRouteUrl) {
        // find index of previous url
        const prevRouteUrlIndex = navigationItemComponent.routes.findIndex((routeUrl) => routeUrl === this.previousRouteUrl);
        navigationItemComponent.hasNoDelay = activeRouteUrlIndex !== -1 && prevRouteUrlIndex !== -1;
        navigationItemComponent.progress = activeRouteUrlIndex === -1
            ? 100
            : ((activeRouteUrlIndex + 1) / navigationItemComponent.routes.length) * 100;
    }
}
DBNavigationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationComponent, deps: [{ token: i1$2.Router }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBNavigationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBNavigationComponent, selector: "db-navigation", queries: [{ propertyName: "navigationItemComponents", predicate: DBNavigationItemComponent }], usesInheritance: true, ngImport: i0, template: "<ng-content></ng-content>\r\n\r\n<div class=\"db-navigation__label\">\r\n  {{ activeLabel }}\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-navigation', template: "<ng-content></ng-content>\r\n\r\n<div class=\"db-navigation__label\">\r\n  {{ activeLabel }}\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { navigationItemComponents: [{
                type: ContentChildren,
                args: [DBNavigationItemComponent]
            }] } });

/**
 * DB Navigation Module
 */
class DBNavigationModule {
}
DBNavigationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBNavigationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationModule, declarations: [DBNavigationComponent,
        DBNavigationItemComponent], imports: [CommonModule,
        RouterModule], exports: [DBNavigationComponent,
        DBNavigationItemComponent] });
DBNavigationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationModule, imports: [CommonModule,
        RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBNavigationComponent,
                        DBNavigationItemComponent,
                    ],
                    exports: [
                        DBNavigationComponent,
                        DBNavigationItemComponent,
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                    ],
                }]
        }] });

/**
 * NB Navigation Item Component
 */
class NBNavigationItemComponent extends AbstractNavigationItemComponentDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Angular change detector
     */
    constructor(changeDetector) {
        super();
        this.changeDetector = changeDetector;
        this.dashedInactive = false;
        this.dashedActive = false;
        this.internalProgress = this.outerCircleCircumference; // Invisible
        this.outerCircleCircumference = 210;
    }
    /**
     * Progress (value has to be between 0 and 100)
     */
    set progress(value) {
        // Calculate the SVG circle progress
        let newInternalProgress = this.outerCircleCircumference; // Zero / invisible by default
        if (this.isActive || this.isVisited) {
            newInternalProgress = this.outerCircleCircumference - (value * this.outerCircleCircumference / 100);
        }
        // Update the internal progress (if it has changed)
        if (newInternalProgress !== this.internalProgress) {
            this.internalProgress = newInternalProgress;
            this.changeDetector.markForCheck();
        }
    }
    get progress() {
        return this.internalProgress;
    }
}
NBNavigationItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationItemComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
NBNavigationItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: NBNavigationItemComponent, selector: "nb-navigation-item", inputs: { dashedInactive: "dashedInactive", dashedActive: "dashedActive" }, host: { properties: { "class.nb-navigation-item--dashed-inactive": "this.dashedInactive", "class.nb-navigation-item--dashed-active": "this.dashedActive", "class.has-no-delay": "this.hasNoDelay" } }, usesInheritance: true, ngImport: i0, template: "<svg\r\n    focusable=\"false\"\r\n    class=\"nb-navigation-item__outer-circle nb-navigation-item__outer-circle--background\">\r\n  <circle cx=\"20\" cy=\"20\" r=\"18\" stroke-linecap=\"round\" stroke-width=\"3\"></circle>\r\n</svg>\r\n\r\n<svg focusable=\"false\"\r\n     class=\"nb-navigation-item__outer-circle nb-navigation-item__outer-circle--foreground\">\r\n  <circle cx=\"20\" cy=\"20\" r=\"18\" stroke-linecap=\"round\" stroke-width=\"3\" [style.stroke-dashoffset]=\"progress + 'px'\"></circle>\r\n</svg>\r\n\r\n<div class=\"nb-navigation-item__inner-circle\">\r\n\r\n  <div class=\"nb-navigation-item__icon-visited\">\r\n    {{ index }}\r\n  </div>\r\n\r\n  <div class=\"nb-navigation-item__icon-default\">\r\n    {{ index }}\r\n  </div>\r\n\r\n</div>\r\n\r\n<div class=\"nb-navigation-item__line\"></div>\r\n\r\n<div class=\"nb-navigation-item__label\">\r\n  {{ label }}\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationItemComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'nb-navigation-item', template: "<svg\r\n    focusable=\"false\"\r\n    class=\"nb-navigation-item__outer-circle nb-navigation-item__outer-circle--background\">\r\n  <circle cx=\"20\" cy=\"20\" r=\"18\" stroke-linecap=\"round\" stroke-width=\"3\"></circle>\r\n</svg>\r\n\r\n<svg focusable=\"false\"\r\n     class=\"nb-navigation-item__outer-circle nb-navigation-item__outer-circle--foreground\">\r\n  <circle cx=\"20\" cy=\"20\" r=\"18\" stroke-linecap=\"round\" stroke-width=\"3\" [style.stroke-dashoffset]=\"progress + 'px'\"></circle>\r\n</svg>\r\n\r\n<div class=\"nb-navigation-item__inner-circle\">\r\n\r\n  <div class=\"nb-navigation-item__icon-visited\">\r\n    {{ index }}\r\n  </div>\r\n\r\n  <div class=\"nb-navigation-item__icon-default\">\r\n    {{ index }}\r\n  </div>\r\n\r\n</div>\r\n\r\n<div class=\"nb-navigation-item__line\"></div>\r\n\r\n<div class=\"nb-navigation-item__label\">\r\n  {{ label }}\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { dashedInactive: [{
                type: HostBinding,
                args: ['class.nb-navigation-item--dashed-inactive']
            }, {
                type: Input
            }], dashedActive: [{
                type: HostBinding,
                args: ['class.nb-navigation-item--dashed-active']
            }, {
                type: Input
            }], hasNoDelay: [{
                type: HostBinding,
                args: ['class.has-no-delay']
            }] } });

/**
 * DB Navigation Component
 */
class NBNavigationComponent extends AbstractNavigationComponentDirective {
    /**
     * Constructor
     *
     * @param router - Angular Router
     * @param changeDetector
     */
    constructor(router, changeDetector) {
        super(router, changeDetector);
    }
    /**
     * Hook method for NavigationComponent implementations
     *
     * @param navigationItemComponent                          - navigationItemComponent
     * @param activeRouteUrlIndex                              - activeRouteUrlIndex
     * @param index                                            - navigation item index
     * @param previousRouteUrl                                 - previousRouteUrl
     */
    doUpdateSpecificNavigationItemState(navigationItemComponent, activeRouteUrlIndex, index, previousRouteUrl) {
        // find index of previous url
        const prevRouteUrlIndex = navigationItemComponent.routes.findIndex((routeUrl) => routeUrl === this.previousRouteUrl);
        navigationItemComponent.hasNoDelay = activeRouteUrlIndex !== -1 && prevRouteUrlIndex !== -1;
        navigationItemComponent.progress = activeRouteUrlIndex === -1
            ? 100
            : ((activeRouteUrlIndex + 1) / navigationItemComponent.routes.length) * 100;
        navigationItemComponent.index = index + 1;
    }
}
NBNavigationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationComponent, deps: [{ token: i1$2.Router }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
NBNavigationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: NBNavigationComponent, selector: "nb-navigation", queries: [{ propertyName: "navigationItemComponents", predicate: NBNavigationItemComponent }], usesInheritance: true, ngImport: i0, template: "<ng-content></ng-content>\r\n\r\n<div class=\"nb-navigation__label\">\r\n  {{ activeLabel }}\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'nb-navigation', template: "<ng-content></ng-content>\r\n\r\n<div class=\"nb-navigation__label\">\r\n  {{ activeLabel }}\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { navigationItemComponents: [{
                type: ContentChildren,
                args: [NBNavigationItemComponent]
            }] } });

/**
 * NB Navigation Module
 */
class NBNavigationModule {
}
NBNavigationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NBNavigationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationModule, declarations: [NBNavigationComponent,
        NBNavigationItemComponent], imports: [CommonModule,
        RouterModule], exports: [NBNavigationComponent,
        NBNavigationItemComponent] });
NBNavigationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationModule, imports: [CommonModule,
        RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NBNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        NBNavigationComponent,
                        NBNavigationItemComponent,
                    ],
                    exports: [
                        NBNavigationComponent,
                        NBNavigationItemComponent,
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                    ],
                }]
        }] });

/**
 * PB Navigation Item Component
 */
class PBNavigationItemComponent extends AbstractNavigationItemComponentDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Angular change detector
     */
    constructor(changeDetector) {
        super();
        this.changeDetector = changeDetector;
        this.dashedInactive = false;
        this.dashedActive = false;
        this.internalProgress = this.outerCircleCircumference; // Invisible
        this.outerCircleCircumference = 160;
    }
    /**
     * Progress (value has to be between 0 and 100)
     */
    set progress(value) {
        // Calculate the SVG circle progress
        let newInternalProgress = this.outerCircleCircumference; // Zero / invisible by default
        if (this.isActive || this.isVisited) {
            newInternalProgress = this.outerCircleCircumference - (value * this.outerCircleCircumference / 100);
        }
        // Update the internal progress (if it has changed)
        if (newInternalProgress !== this.internalProgress) {
            this.internalProgress = newInternalProgress;
            this.changeDetector.markForCheck();
        }
    }
    get progress() {
        return this.internalProgress;
    }
}
PBNavigationItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationItemComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
PBNavigationItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: PBNavigationItemComponent, selector: "pb-navigation-item", inputs: { dashedInactive: "dashedInactive", dashedActive: "dashedActive" }, host: { properties: { "class.pb-navigation-item--dashed-inactive": "this.dashedInactive", "class.pb-navigation-item--dashed-active": "this.dashedActive", "class.has-no-delay": "this.hasNoDelay" } }, usesInheritance: true, ngImport: i0, template: "<svg\r\n    focusable=\"false\"\r\n    class=\"pb-navigation-item__outer-circle pb-navigation-item__outer-circle--background\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\"></circle>\r\n</svg>\r\n\r\n<svg focusable=\"false\"\r\n     class=\"pb-navigation-item__outer-circle pb-navigation-item__outer-circle--foreground\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\" [style.stroke-dashoffset]=\"progress + 'px'\"></circle>\r\n</svg>\r\n\r\n<div\r\n    class=\"pb-navigation-item__inner-circle\">\r\n\r\n  <svg viewBox=\"0 0 64 64\" class=\"pb-navigation-item__icon-visited\">\r\n    <path d=\"M56.5 8.68c-1.33.04-2.6.62-3.52 1.6L22.1 42.46 9.03 28.82c-1.3-1.4-3.2-1.94-4.98-1.45-1.8.48-3.2 1.94-3.66 3.8-.47 1.87.07\r\n    3.85 1.4 5.2l16.7 17.4c.96 1 2.27 1.56 3.63 1.56 1.36 0 2.67-.56 3.63-1.57l34.5-35.93c1.5-1.53 1.96-3.87\r\n    1.14-5.9-.83-2.02-2.76-3.32-4.87-3.25z\"></path>\r\n  </svg>\r\n\r\n  <div class=\"pb-navigation-item__icon-default\">\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n\r\n</div>\r\n\r\n<div class=\"pb-navigation-item__line\"></div>\r\n\r\n<div class=\"pb-navigation-item__label\">\r\n  {{ label }}\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationItemComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'pb-navigation-item', template: "<svg\r\n    focusable=\"false\"\r\n    class=\"pb-navigation-item__outer-circle pb-navigation-item__outer-circle--background\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\"></circle>\r\n</svg>\r\n\r\n<svg focusable=\"false\"\r\n     class=\"pb-navigation-item__outer-circle pb-navigation-item__outer-circle--foreground\">\r\n  <circle cx=\"27\" cy=\"27\" r=\"25.5\" stroke-linecap=\"round\" stroke-width=\"3\" [style.stroke-dashoffset]=\"progress + 'px'\"></circle>\r\n</svg>\r\n\r\n<div\r\n    class=\"pb-navigation-item__inner-circle\">\r\n\r\n  <svg viewBox=\"0 0 64 64\" class=\"pb-navigation-item__icon-visited\">\r\n    <path d=\"M56.5 8.68c-1.33.04-2.6.62-3.52 1.6L22.1 42.46 9.03 28.82c-1.3-1.4-3.2-1.94-4.98-1.45-1.8.48-3.2 1.94-3.66 3.8-.47 1.87.07\r\n    3.85 1.4 5.2l16.7 17.4c.96 1 2.27 1.56 3.63 1.56 1.36 0 2.67-.56 3.63-1.57l34.5-35.93c1.5-1.53 1.96-3.87\r\n    1.14-5.9-.83-2.02-2.76-3.32-4.87-3.25z\"></path>\r\n  </svg>\r\n\r\n  <div class=\"pb-navigation-item__icon-default\">\r\n    <ng-content>\r\n    </ng-content>\r\n  </div>\r\n\r\n</div>\r\n\r\n<div class=\"pb-navigation-item__line\"></div>\r\n\r\n<div class=\"pb-navigation-item__label\">\r\n  {{ label }}\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { dashedInactive: [{
                type: HostBinding,
                args: ['class.pb-navigation-item--dashed-inactive']
            }, {
                type: Input
            }], dashedActive: [{
                type: HostBinding,
                args: ['class.pb-navigation-item--dashed-active']
            }, {
                type: Input
            }], hasNoDelay: [{
                type: HostBinding,
                args: ['class.has-no-delay']
            }] } });

/**
 * PB Navigation Component
 */
class PBNavigationComponent extends AbstractNavigationComponentDirective {
    /**
     * Constructor
     *
     * @param router - Angular Router
     */
    constructor(router, changeDetector) {
        super(router, changeDetector);
    }
    doUpdateSpecificNavigationItemState(navigationItemComponent, activeRouteUrlIndex, index, previousRouteUrl) {
        // find index of previous url
        const prevRouteUrlIndex = navigationItemComponent.routes.findIndex((routeUrl) => routeUrl === this.previousRouteUrl);
        navigationItemComponent.hasNoDelay = activeRouteUrlIndex !== -1 && prevRouteUrlIndex !== -1;
        navigationItemComponent.progress = activeRouteUrlIndex === -1
            ? 100
            : ((activeRouteUrlIndex + 1) / navigationItemComponent.routes.length) * 100;
    }
}
PBNavigationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationComponent, deps: [{ token: i1$2.Router }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
PBNavigationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: PBNavigationComponent, selector: "pb-navigation", queries: [{ propertyName: "navigationItemComponents", predicate: PBNavigationItemComponent }], usesInheritance: true, ngImport: i0, template: "<ng-content></ng-content>\r\n\r\n<div class=\"pb-navigation__label\">\r\n  {{ activeLabel }}\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'pb-navigation', template: "<ng-content></ng-content>\r\n\r\n<div class=\"pb-navigation__label\">\r\n  {{ activeLabel }}\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { navigationItemComponents: [{
                type: ContentChildren,
                args: [PBNavigationItemComponent]
            }] } });

/**
 * PB Navigation Module
 */
class PBNavigationModule {
}
PBNavigationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PBNavigationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationModule, declarations: [PBNavigationComponent,
        PBNavigationItemComponent], imports: [CommonModule,
        RouterModule], exports: [PBNavigationComponent,
        PBNavigationItemComponent] });
PBNavigationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationModule, imports: [CommonModule,
        RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PBNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        PBNavigationComponent,
                        PBNavigationItemComponent,
                    ],
                    exports: [
                        PBNavigationComponent,
                        PBNavigationItemComponent,
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                    ],
                }]
        }] });

/**
 * DB Panel Component
 */
class DBPanelComponent {
}
DBPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBPanelComponent, selector: "db-panel", ngImport: i0, template: "<div class=\"db-panel__wrapper\">\r\n  <ng-content select=\".db-panel__title\"></ng-content>\r\n  <div class=\"db-panel\">\r\n    <ng-content select=\".db-panel__subtitle\"></ng-content>\r\n    <ng-content select=\".db-panel__container-group\"></ng-content>\r\n    <ng-content select=\".db-panel__container\"></ng-content>\r\n  </div>\r\n</div>\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPanelComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-panel', template: "<div class=\"db-panel__wrapper\">\r\n  <ng-content select=\".db-panel__title\"></ng-content>\r\n  <div class=\"db-panel\">\r\n    <ng-content select=\".db-panel__subtitle\"></ng-content>\r\n    <ng-content select=\".db-panel__container-group\"></ng-content>\r\n    <ng-content select=\".db-panel__container\"></ng-content>\r\n  </div>\r\n</div>\r\n" }]
        }] });

/**
 * DB Panel Module
 */
class DBPanelModule {
}
DBPanelModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBPanelModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBPanelModule, declarations: [DBPanelComponent], imports: [CommonModule], exports: [DBPanelComponent] });
DBPanelModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPanelModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPanelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBPanelComponent,
                    ],
                    exports: [
                        DBPanelComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

/**
 * DB Popover Component
 */
class DBPopoverComponent extends DBOverlayComponentDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     * @param renderer       - Renderer
     * @param element        - Element
     * @param windowRef
     * @param viewportService
     */
    constructor(changeDetector, renderer, element, windowRef, viewportService) {
        super(changeDetector, renderer, element);
        this.windowRef = windowRef;
        this.viewportService = viewportService;
        this.content = '';
    }
    /**
     * Initialize the popover
     *
     * @param triggerElement   - Popover trigger element
     * @param triggerDirective - Popover trigger directive
     * @param options          - Popover options
     */
    init(triggerElement, triggerDirective, options) {
        // Setup
        this.triggerDirective = triggerDirective;
        this.title = options.title;
        this.closeButtonLabel = options.closeButtonLabel;
        // Render popover content
        if (options.content instanceof TemplateRef) {
            this.content = '';
            this.renderTemplate(options.content);
        }
        else {
            this.content = options.content;
            this.renderTemplate(this.popoverTextTemplaterRef);
        }
        // Initialize overlay
        this.initOverlay(triggerElement, { position: options.position, size: options.size });
        // detect the device to unsubscribe popover
        this.viewportService.mediaQueryChanges().subscribe((mediaQueryName) => {
            if (mediaQueryName === 'phone' || mediaQueryName === 'phonelarge') {
                this.destroyPopper();
            }
        });
    }
    /**
     * Show the popover
     *
     * @returns - Promise, resolves once the animation finished
     */
    show() {
        return __awaiter(this, void 0, void 0, function* () {
            // Create an element for backDrop
            this.backDropElement = this.renderer.createElement('div');
            this.renderer.addClass(this.backDropElement, 'db-popover__backdrop');
            const refParent = this.element.nativeElement.parentNode;
            const refChild = this.element.nativeElement;
            this.renderer.insertBefore(refParent, this.backDropElement, refChild);
            yield this.showOverlay();
        });
    }
    /**
     * Hide the popover
     *
     * @returns - Promise, resolves once the animation finished
     */
    hide() {
        return __awaiter(this, void 0, void 0, function* () {
            // Remove backdrop
            const refParent = this.element.nativeElement.parentNode;
            this.renderer.removeChild(refParent, this.backDropElement);
            yield this.hideOverlay();
        });
    }
    /**
     * Handle close button click
     *
     * @param event - Mouse event
     */
    onCloseButtonClick(event) {
        event.stopPropagation();
        this.triggerDirective.removePopover();
    }
    /**
     * Render template as popover content
     *
     * @param templateRef - Template reference
     */
    renderTemplate(templateRef) {
        this.popoverTemplateViewContainerRef.createEmbeddedView(templateRef);
    }
}
DBPopoverComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: DBWindowRef }, { token: DBViewportService }], target: i0.ɵɵFactoryTarget.Component });
DBPopoverComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBPopoverComponent, selector: "db-popover", viewQueries: [{ propertyName: "popoverTemplateViewContainerRef", first: true, predicate: ["popoverTemplate"], descendants: true, read: ViewContainerRef, static: true }, { propertyName: "popoverContentElementRef", first: true, predicate: ["popoverContentElement"], descendants: true, static: true }, { propertyName: "popoverTextTemplaterRef", first: true, predicate: ["popoverTextElement"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: "<div [attr.aria-labelledby]=\" title ? title : null\" [attr.aria-describedby]=\" content ? content : null\">\r\n\r\n  <div [ngClass]=\"{'d-flex justify-content-between db-popover__header' : title}\"\r\n      [hidden]=\"!title\">\r\n    {{ title }}\r\n    <button class=\"db-popover__close-button\" type=\"button\" (click)=\"onCloseButtonClick( $event )\">\r\n      <span class=\"sr-only\">Schlie\u00DFen</span>\r\n      <db-icon class=\"db-popover__close-icon\">close</db-icon>\r\n    </button>\r\n  </div>\r\n\r\n  <ng-template #popoverTextElement>\r\n    {{ content }}\r\n  </ng-template>\r\n\r\n  <div class=\"db-popover__content\" [class.db-popover__content--no-title]=\"!title\" #popoverContentElement>\r\n    <div [ngClass]=\"{'d-flex justify-content-between align-items-start': !title}\">\r\n      <div class=\"db-popover__content-text\">\r\n        <ng-template #popoverTemplate>\r\n        </ng-template>\r\n      </div>\r\n\r\n      <button [class.d-none]=\"title\" class=\"db-popover__close-button\" type=\"button\" (click)=\"onCloseButtonClick( $event )\">\r\n        <span class=\"sr-only\">Schlie\u00DFen</span>\r\n        <db-icon class=\"db-popover__close-icon\">close</db-icon>\r\n      </button>\r\n    </div>\r\n\r\n    <div class=\"d-md-none db-popover__close-text\">\r\n      <span class=\"db-popover__close-text--link\" (click)=\"onCloseButtonClick($event)\">\r\n        <db-icon tabindex=\"0\" (keydown.enter)=\"onCloseButtonClick($event)\" class=\"db-popover__close-icon mb-1\">close</db-icon>\r\n        <span class=\"sr-only\">Schlie\u00DFen</span>\r\n        {{ closeButtonLabel }}\r\n      </span>\r\n    </div>\r\n\r\n  </div>\r\n\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-popover', template: "<div [attr.aria-labelledby]=\" title ? title : null\" [attr.aria-describedby]=\" content ? content : null\">\r\n\r\n  <div [ngClass]=\"{'d-flex justify-content-between db-popover__header' : title}\"\r\n      [hidden]=\"!title\">\r\n    {{ title }}\r\n    <button class=\"db-popover__close-button\" type=\"button\" (click)=\"onCloseButtonClick( $event )\">\r\n      <span class=\"sr-only\">Schlie\u00DFen</span>\r\n      <db-icon class=\"db-popover__close-icon\">close</db-icon>\r\n    </button>\r\n  </div>\r\n\r\n  <ng-template #popoverTextElement>\r\n    {{ content }}\r\n  </ng-template>\r\n\r\n  <div class=\"db-popover__content\" [class.db-popover__content--no-title]=\"!title\" #popoverContentElement>\r\n    <div [ngClass]=\"{'d-flex justify-content-between align-items-start': !title}\">\r\n      <div class=\"db-popover__content-text\">\r\n        <ng-template #popoverTemplate>\r\n        </ng-template>\r\n      </div>\r\n\r\n      <button [class.d-none]=\"title\" class=\"db-popover__close-button\" type=\"button\" (click)=\"onCloseButtonClick( $event )\">\r\n        <span class=\"sr-only\">Schlie\u00DFen</span>\r\n        <db-icon class=\"db-popover__close-icon\">close</db-icon>\r\n      </button>\r\n    </div>\r\n\r\n    <div class=\"d-md-none db-popover__close-text\">\r\n      <span class=\"db-popover__close-text--link\" (click)=\"onCloseButtonClick($event)\">\r\n        <db-icon tabindex=\"0\" (keydown.enter)=\"onCloseButtonClick($event)\" class=\"db-popover__close-icon mb-1\">close</db-icon>\r\n        <span class=\"sr-only\">Schlie\u00DFen</span>\r\n        {{ closeButtonLabel }}\r\n      </span>\r\n    </div>\r\n\r\n  </div>\r\n\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: DBWindowRef }, { type: DBViewportService }]; }, propDecorators: { popoverTemplateViewContainerRef: [{
                type: ViewChild,
                args: ['popoverTemplate', { read: ViewContainerRef, static: true }]
            }], popoverContentElementRef: [{
                type: ViewChild,
                args: ['popoverContentElement', { static: true }]
            }], popoverTextTemplaterRef: [{
                type: ViewChild,
                args: ['popoverTextElement', { static: true }]
            }] } });

/**
 * DB Popover Directive
 */
class DBPopoverDirective {
    /**
     * Constructor
     *
     * @param element  - Element
     * @param renderer - Renderer
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        /**
         * Visibility flag
         */
        this.isVisible = false;
        this.dbPopoverTitle = '';
        this.dbPopoverContent = '';
        this.dbPopoverCloseButtonLabel = 'Schließen';
        this.dbPopoverPosition = 'top';
        this.dbPopoverSize = 'sm';
        this.isVisible = false;
        this.currentClickEvent = null;
        this.dbPopoverButtonDesign = 'default';
        this.popovers = [];
    }
    /**
     * Button design component class with child db-icon
     */
    get popoverButtonDesign() {
        switch (this.dbPopoverButtonDesign) {
            case 'smallIcon':
                return 'db-popover-button';
                break;
            case 'smallIconHighlight':
                return 'db-popover-button db-popover-button--highlight';
                break;
            case 'smallText':
                return 'db-popover-button db-popover-button--text';
                break;
            case 'smallHighlightText':
                return 'db-popover-button db-popover-button--text db-popover-button--highlight';
                break;
            default:
                break;
        }
    }
    /**
     * Show popover on click
     *
     * @param event - Mouse event
     */
    showPopover(event) {
        // Skip if the popover is already visible
        if (this.isVisible) {
            return;
        }
        this.currentClickEvent = event;
        // Add popover
        this.addPopover(this.element, {
            content: this.dbPopoverContent,
            position: this.dbPopoverPosition,
            title: this.dbPopoverTitle,
            size: this.dbPopoverSize,
            closeButtonLabel: this.dbPopoverCloseButtonLabel,
        });
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        this.popoverSubscription.unsubscribe();
        if (this.isVisible) {
            this.removePopover();
        }
    }
    /**
     * Hide popover on click outside
     */
    ngAfterViewInit() {
        this.popoverSubscription = fromEvent(document, 'click')
            .subscribe(event => {
            // Skip if the popover is not visible, or the user clicked on / within the popover element
            if (!this.isVisible || this.currentClickEvent === event || this.isElementWithinPopover(event.target)) {
                return;
            }
            this.currentClickEvent = null;
            // Remove popover
            this.removePopover();
        });
    }
    /**
     * Add popover
     *
     * @param   triggerElement - Tooltip trigger element
     * @param   options        - Tooltip options
     * @returns                - Promise, resolved once done (after animation has finished)
     */
    addPopover(triggerElement, options) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isVisible = true;
            // Create component
            const componentRef = this.renderer.createAndAppendComponent(DBPopoverComponent);
            this.popovers.push(componentRef);
            // Show component (with animation)
            componentRef.instance.init(triggerElement, this, options);
            yield componentRef.instance.show();
        });
    }
    /**
     * Remove popover
     *
     * @returns - Promise, resolved once done (after animation has finished)
     */
    removePopover() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isVisible = false;
            // Hide component (with animation)
            const componentRef = this.popovers.shift();
            yield componentRef.instance.hide();
            // Remove component
            this.renderer.destroyAndRemoveComponent(componentRef);
        });
    }
    /**
     * Check if the given element is inside the popover, or the popover itself
     *
     * @param   element - Element
     * @returns         - Flag, describing whether the given element is inside the popover, or the popover itself
     */
    isElementWithinPopover(element) {
        return this.popovers
            .reduce((hasPopoverBeenClicked, componentRef) => componentRef.location.nativeElement.contains(element), false);
    }
}
DBPopoverDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverDirective, deps: [{ token: i0.ElementRef }, { token: DBRenderer }], target: i0.ɵɵFactoryTarget.Directive });
DBPopoverDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBPopoverDirective, selector: "[dbPopover]", inputs: { dbPopoverTitle: "dbPopoverTitle", dbPopoverContent: "dbPopoverContent", dbPopoverCloseButtonLabel: "dbPopoverCloseButtonLabel", dbPopoverPosition: "dbPopoverPosition", dbPopoverSize: "dbPopoverSize", dbPopoverButtonDesign: "dbPopoverButtonDesign" }, host: { listeners: { "click": "showPopover($event)" }, properties: { "class": "this.popoverButtonDesign" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dbPopover]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: DBRenderer }]; }, propDecorators: { dbPopoverTitle: [{
                type: Input
            }], dbPopoverContent: [{
                type: Input
            }], dbPopoverCloseButtonLabel: [{
                type: Input
            }], dbPopoverPosition: [{
                type: Input
            }], dbPopoverSize: [{
                type: Input
            }], dbPopoverButtonDesign: [{
                type: Input
            }], popoverButtonDesign: [{
                type: HostBinding,
                args: ['class']
            }], showPopover: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

/**
 * DB Popover Module
 */
class DBPopoverModule {
}
DBPopoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBPopoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverModule, declarations: [DBPopoverDirective,
        DBPopoverComponent], imports: [CommonModule,
        DBRendererModule,
        DBIconModule], exports: [DBPopoverDirective] });
DBPopoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverModule, imports: [CommonModule,
        DBRendererModule,
        DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBPopoverDirective,
                        DBPopoverComponent,
                    ],
                    exports: [
                        DBPopoverDirective,
                    ],
                    imports: [
                        CommonModule,
                        DBRendererModule,
                        DBIconModule,
                    ]
                }]
        }] });

class DBStatusComponent {
    constructor() {
        this.status = 'none';
        this.labelText = '';
        this.labelWithUnits = null;
    }
    set label(label) {
        if (typeof label === 'string') {
            this.labelText = label;
        }
        else {
            this.labelWithUnits = label;
        }
    }
}
DBStatusComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStatusComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBStatusComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBStatusComponent, selector: "db-status", inputs: { label: "label", status: "status" }, ngImport: i0, template: "<div\r\n  class=\"db-status db-text-6\"\r\n  [ngClass]=\"'db-status--' + status\"\r\n>\r\n  <ng-container *ngIf=\"labelText; else statusWithUnit\">\r\n    <span>{{ labelText }}</span>\r\n  </ng-container>\r\n  <ng-template #statusWithUnit>\r\n    <span\r\n      *ngFor=\"let statusItem of labelWithUnits\"\r\n      [ngClass]=\"'db-status--' + status + '__unit'\"\r\n    >\r\n      <span>{{ statusItem.label }}</span>\r\n      <span [ngClass]=\"{ 'unit-default': !statusItem.unit }\">{{\r\n        statusItem.unit\r\n      }}</span>\r\n    </span>\r\n  </ng-template>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStatusComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-status', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\r\n  class=\"db-status db-text-6\"\r\n  [ngClass]=\"'db-status--' + status\"\r\n>\r\n  <ng-container *ngIf=\"labelText; else statusWithUnit\">\r\n    <span>{{ labelText }}</span>\r\n  </ng-container>\r\n  <ng-template #statusWithUnit>\r\n    <span\r\n      *ngFor=\"let statusItem of labelWithUnits\"\r\n      [ngClass]=\"'db-status--' + status + '__unit'\"\r\n    >\r\n      <span>{{ statusItem.label }}</span>\r\n      <span [ngClass]=\"{ 'unit-default': !statusItem.unit }\">{{\r\n        statusItem.unit\r\n      }}</span>\r\n    </span>\r\n  </ng-template>\r\n</div>\r\n" }]
        }], propDecorators: { label: [{
                type: Input
            }], status: [{
                type: Input
            }] } });

class DBStatusModule {
}
DBStatusModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStatusModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBStatusModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBStatusModule, declarations: [DBStatusComponent], imports: [CommonModule], exports: [DBStatusComponent] });
DBStatusModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStatusModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStatusModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DBStatusComponent],
                    imports: [CommonModule],
                    exports: [DBStatusComponent],
                }]
        }] });

class DBStepperItemComponent {
    constructor() {
        this.stepId = null;
        this.labelChange = new EventEmitter();
        this.label = null;
    }
    set labelSetter(label) {
        this.label = label;
        this.labelChange.emit(label);
    }
}
DBStepperItemComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStepperItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBStepperItemComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBStepperItemComponent, selector: "db-stepper-item", inputs: { stepId: "stepId", labelSetter: ["label", "labelSetter"] }, outputs: { labelChange: "labelChange" }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStepperItemComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'db-stepper-item',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { stepId: [{
                type: Input
            }], labelSetter: [{
                type: Input,
                args: ['label']
            }], labelChange: [{
                type: Output
            }] } });

/**
 * DB Stepper Component
 */
class DBStepperComponent {
    /**
     * Constructor
     *
     * @param changeDetector - Change Detector Reference
     * @param router - Angular Router
     */
    constructor(changeDetector, router) {
        /**
         * Flag to determine whether to use route stepper
         */
        this.useRoutes = false;
        /**
         * Flag to show success message and icon
         */
        this.finished = false;
        /**
         * Flag to show info message and icon
         */
        this.info = false;
        /**
         * Flag to show error/warning message and icon
         */
        this.errorWarning = false;
        /**
         * Default step labels
         */
        this.stepLabels = null;
        /**
         * Stem items of in-page stepper
         */
        this.steps = null;
        /**
         * Active step of in-page stepper
         */
        this.activeStep = null;
        /**
         * Flag to set when final step reached
         */
        this.isFinalStep = false;
        /**
         * Label of the next step
         */
        this.nextStepLabel = '';
        /**
         * Progress, number optimized for SVG circle styling
         */
        this.progressDashOffset = 0;
        /**
         * Current active step position
         */
        this.currentStepPosition = 0;
        /**
         * For Subscrition Cleanup
         */
        this.unsubscribe$ = new Subject();
        /**
         * Subscription for Stepper Item changes
         */
        this.childSubscriptions = new Subscription();
        /**
         * Outer circle cirumference, always as per (2 * Math.PI * radius) calculation
         */
        this.outerCircleCircumference = 145;
        this.activeLabel = '';
        this.previousRouteUrl = '';
        this.router = router;
        this.changeDetector = changeDetector;
        this.routerSubscription = null;
        this.internalProgress = 0; // Invisible
        this.showNextStepInfo = false;
        this.showStepTexts = false;
    }
    /**
     * Set active step for in-page stepper
     */
    set active(step) {
        this.activeStep = step;
        this.updateInPageStepper();
    }
    /**
     * On Init Lifecycle Hook, setting up the router event listener
     */
    ngOnInit() {
        // Listen for router events
        if (this.useRoutes) {
            this.routerSubscription = this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe((event) => {
                if (event.urlAfterRedirects !== this.previousRouteUrl) {
                    // We use 'urlAfterRedirects' instead of just 'url' to take automatic routing redirects (e.g. empty routes) into account
                    this.updateRoutesStepper();
                    this.previousRouteUrl = event.urlAfterRedirects;
                }
            });
        }
    }
    /**
     * After View Init Lifecycle hook, triggering the initial rendering
     */
    ngAfterContentInit() {
        if (this.useRoutes) {
            this.previousRouteUrl = this.router.routerState.snapshot.url;
            this.updateRoutesStepper();
        }
        // Setup subscription for listening to changes in Stepper Item children
        if (this.steps && !this.useRoutes) {
            this.updateInPageStepper();
            this.subscribeToChildChanges();
            this.steps.changes.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
                this.updateInPageStepper();
                this.subscribeToChildChanges();
            });
        }
    }
    /**
     * Subscribe to stepper item component labelEvent to listen to dynamic label changes (usually translation updates)
     */
    subscribeToChildChanges() {
        var _a;
        (_a = this.childSubscriptions) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        if (this.steps) {
            this.childSubscriptions = new Subscription();
            this.steps.forEach((step) => {
                this.childSubscriptions.add(step.labelChange.subscribe(() => {
                    this.updateInPageStepper();
                    this.changeDetector.markForCheck();
                }));
            });
        }
    }
    /**
     * On Destroy Lifecycle Hook, cleaning up resources
     */
    ngOnDestroy() {
        if (this.routerSubscription !== null) {
            this.routerSubscription.unsubscribe();
            this.routerSubscription = null; // Just to be sure
        }
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.childSubscriptions.unsubscribe();
    }
    /**
     * Setter to calculate the progress and update internalProgress
     */
    set progress(progress) {
        // Calculate the SVG circle progress
        const newInternalProgress = progress * 100;
        if (newInternalProgress !== this.internalProgress) {
            this.internalProgress = newInternalProgress;
            this.progressDashOffset = this.outerCircleCircumference - (newInternalProgress * this.outerCircleCircumference / 100);
            this.changeDetector.markForCheck();
        }
    }
    /**
     * Getter for the progress, which returns internalProgress
     */
    get progress() {
        return this.internalProgress;
    }
    /**
     * Update state of the stepper with children (Wizard-like)
     */
    updateInPageStepper() {
        if (this.steps) {
            const currentStepPosition = this.steps
                .map((step) => step.stepId)
                .indexOf(this.activeStep);
            if (currentStepPosition >= 0) {
                this.currentStepPosition = currentStepPosition + 1;
                this.setStepLabels(currentStepPosition, this.steps);
            }
            this.progress =
                (this.currentStepPosition / this.steps.length);
        }
    }
    /**
     * Update state of the route stepper
     */
    updateRoutesStepper() {
        const activeRouteUrlIndex = this.routes.findIndex((routeUrl) => this.router.isActive(routeUrl, false) // Ignore parameters of any kind
        );
        // Check whether we have reached the currently active page
        if (activeRouteUrlIndex !== -1) {
            this.activeLabel = this.labels[activeRouteUrlIndex];
            this.currentStepPosition = activeRouteUrlIndex + 1;
            this.setStepLabels(activeRouteUrlIndex, this.labels);
            this.progress = (this.currentStepPosition / this.routes.length);
        }
    }
    /**
     * Set current and next step label and texts
     */
    setStepLabels(currentPos, steps) {
        const stepsArr = (steps instanceof QueryList) ? steps.toArray() : steps;
        this.activeLabel = (steps instanceof QueryList) ? stepsArr[currentPos].label : stepsArr[currentPos];
        // Set next step labels
        if (stepsArr.length > currentPos + 1) {
            this.isFinalStep = false;
            this.nextStepLabel = (steps instanceof QueryList) ? stepsArr[currentPos + 1].label : stepsArr[currentPos + 1];
        }
        else {
            this.nextStepLabel = null;
            this.isFinalStep = true;
        }
    }
}
DBStepperComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStepperComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i1$2.Router, optional: true }], target: i0.ɵɵFactoryTarget.Component });
DBStepperComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBStepperComponent, selector: "db-stepper", inputs: { labels: "labels", routes: "routes", showNextStepInfo: "showNextStepInfo", showStepTexts: "showStepTexts", useRoutes: "useRoutes", active: "active", finished: "finished", info: "info", errorWarning: "errorWarning", stepLabels: "stepLabels" }, queries: [{ propertyName: "steps", predicate: DBStepperItemComponent }], ngImport: i0, template: "<div class=\"db-stepper\">\r\n  <div class=\"d-flex w-100\">\r\n    <div class=\"db-stepper__circle\">\r\n      <svg height=\"48\" width=\"48\" focusable=\"false\">  \r\n      <circle\r\n        class=\"db-stepper__circle db-stepper__circle--background\"\r\n        stroke-linecap=\"round\"\r\n        transform=\"rotate(-90 27 27)\"\r\n        stroke-width=\"1.5\"\r\n        cx=\"30\"\r\n        cy=\"24\"\r\n        r=\"23\"\r\n      />\r\n      <circle\r\n        class=\"db-stepper__circle db-stepper__circle--foreground\"\r\n        stroke-linecap=\"round\"\r\n        transform=\"rotate(-90 27 27)\"\r\n        stroke-width=\"2.5\"\r\n        cx=\"30\"\r\n        cy=\"24\"\r\n        r=\"23\"\r\n        [ngStyle]=\"{ strokeDashoffset: progressDashOffset }\"\r\n      />\r\n      <text\r\n        class=\"db-stepper__text\"\r\n        x=\"50%\"\r\n        y=\"50%\"\r\n        text-anchor=\"middle\"\r\n        dy=\".3em\"\r\n        *ngIf=\"\r\n        !useRoutes &&\r\n        !info &&\r\n        !errorWarning &&\r\n        !(isFinalStep && finished)\r\n        \"\r\n      >\r\n        {{ currentStepPosition }} / {{ steps.length }}\r\n      </text>\r\n      <text\r\n        data-test=\"route-text\"\r\n        *ngIf=\"\r\n        useRoutes &&\r\n        !info &&\r\n        !errorWarning &&\r\n        !(isFinalStep && finished)\"\r\n        class=\"db-stepper__text\"\r\n        x=\"50%\"\r\n        y=\"50%\"\r\n        text-anchor=\"middle\"\r\n        dy=\".3em\"\r\n      >\r\n      {{ currentStepPosition }} / {{ routes.length }}\r\n      </text>\r\n      <symbol id=\"check\">\r\n        <path\r\n          class=\"object\"\r\n          d=\"M14.7 40c-0.8 0-1.6-0.3-2.1-0.9L2.3 28.9c-0.4-0.4-0.4-1 0-1.4s1-0.4 1.4 0L14 37.7c0.4 0.4 1 0.4 1.4 0\r\n          L44.3 8.8c0.4-0.4 1-0.4 1.4 0s0.4 1 0 1.4L16.8 39.1C16.2 39.7 15.5 40 14.7 40z\"\r\n        />\r\n      </symbol>\r\n      <symbol id=\"info_\">\r\n        <path\r\n          class=\"object\"\r\n          d=\"M12 7C12 7 13 7 13 6 13 6 13 6 13 5 13 5 13 5 13 4 13 4 12 4 12 4 12 4 11 4 11 4 11 5 11 5 11 5 11 6 11\r\n        6 11 6 11 7 12 7 12 7ZM15 20L15 18C15 18 15 18 15 18L13 18 13 18 13 9C13 9 13 9 13 9L10 9C9 9 9 9 9 9L9 11C9\r\n        11 9 11 10 11L11 11 11 11 11 18 10 18C9 18 9 18 9 18L9 20C9 20 9 20 10 20L15 20C15 20 15 20 15 20Z\"\r\n        />\r\n      </symbol>\r\n      <symbol id=\"error-warning_\">\r\n        <path\r\n          class=\"object\"\r\n          d=\"M12 18C13 18 13 18 13 19 13 19 13 20 12 20 11 20 11 19 11 19 11 18 11 18 12 18ZM13 4C13 4 13 4 13 5L13 5\r\n        13 14C13 15 13 15 12 15L12 15 12 15C11 15 11 15 11 14L11 14 11 5C11 4 11 4 11 4L11 4Z\"\r\n        />\r\n      </symbol>\r\n      <use\r\n        *ngIf=\"\r\n          isFinalStep &&\r\n          finished &&\r\n          !info &&\r\n          !errorWarning\r\n        \"\r\n        href=\"#check\"\r\n        stroke=\"#0A3478\"\r\n        stroke-width=\"2\"\r\n        x=\"15\"\r\n        y=\"13\"\r\n        width=\"20\"\r\n        height=\"20\"\r\n      ></use>\r\n      <use\r\n        *ngIf=\"info && !errorWarning\"\r\n        href=\"#info_\"\r\n        stroke=\"#0A3478\"\r\n        stroke-width=\"0\"\r\n        x=\"12\"\r\n        y=\"12\"\r\n        width=\"20\"\r\n        height=\"20\"\r\n      ></use>\r\n      <use\r\n        *ngIf=\"errorWarning\"\r\n        href=\"#error-warning_\"\r\n        stroke=\"#0A3478\"\r\n        stroke-width=\"0\"\r\n        x=\"12\"\r\n        y=\"12\"\r\n        width=\"20\"\r\n        height=\"20\"\r\n      ></use>\r\n      </svg>\r\n    </div>\r\n    <div class=\"db-stepper__content align-items-center\">\r\n      <div class=\"db-stepper__label\">\r\n        <ng-container *ngIf=\"showStepTexts && stepLabels\">\r\n          <div *ngIf=\"errorWarning && stepLabels.errorWarning; else infoTpl\" class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.errorWarning}}</div>\r\n          <ng-template #infoTpl>\r\n            <div *ngIf=\"info && stepLabels.info; else successTpl\"  class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.info}}</div>\r\n          </ng-template>\r\n          <ng-template #successTpl>\r\n            <div *ngIf=\"isFinalStep && finished && stepLabels.success; else titleTpl\"  class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.success}}</div>\r\n          </ng-template>\r\n          <ng-template #titleTpl>\r\n            <div *ngIf=\"stepLabels.step\"  class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.step}} {{ ' ' + currentStepPosition }}</div>\r\n          </ng-template>\r\n        </ng-container>\r\n        <div class=\"db-stepper__label--text\">{{ activeLabel }}</div>\r\n      </div>\r\n      <div class=\"db-stepper__label d-none d-sm-flex text-right\" *ngIf=\"showNextStepInfo\">\r\n        <div *ngIf=\"showStepTexts && stepLabels && !isFinalStep\"  class=\"db-stepper__label--next\">{{stepLabels.next}}</div>\r\n        <div class=\"db-stepper__label--text\">{{ nextStepLabel }}</div>\r\n      </div>\r\n    </div>  \r\n  </div>\r\n  <div class=\"db-stepper__progress-bar d-none d-md-block\" [ngStyle]=\"{ width: progress + '%' }\"></div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStepperComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-stepper', template: "<div class=\"db-stepper\">\r\n  <div class=\"d-flex w-100\">\r\n    <div class=\"db-stepper__circle\">\r\n      <svg height=\"48\" width=\"48\" focusable=\"false\">  \r\n      <circle\r\n        class=\"db-stepper__circle db-stepper__circle--background\"\r\n        stroke-linecap=\"round\"\r\n        transform=\"rotate(-90 27 27)\"\r\n        stroke-width=\"1.5\"\r\n        cx=\"30\"\r\n        cy=\"24\"\r\n        r=\"23\"\r\n      />\r\n      <circle\r\n        class=\"db-stepper__circle db-stepper__circle--foreground\"\r\n        stroke-linecap=\"round\"\r\n        transform=\"rotate(-90 27 27)\"\r\n        stroke-width=\"2.5\"\r\n        cx=\"30\"\r\n        cy=\"24\"\r\n        r=\"23\"\r\n        [ngStyle]=\"{ strokeDashoffset: progressDashOffset }\"\r\n      />\r\n      <text\r\n        class=\"db-stepper__text\"\r\n        x=\"50%\"\r\n        y=\"50%\"\r\n        text-anchor=\"middle\"\r\n        dy=\".3em\"\r\n        *ngIf=\"\r\n        !useRoutes &&\r\n        !info &&\r\n        !errorWarning &&\r\n        !(isFinalStep && finished)\r\n        \"\r\n      >\r\n        {{ currentStepPosition }} / {{ steps.length }}\r\n      </text>\r\n      <text\r\n        data-test=\"route-text\"\r\n        *ngIf=\"\r\n        useRoutes &&\r\n        !info &&\r\n        !errorWarning &&\r\n        !(isFinalStep && finished)\"\r\n        class=\"db-stepper__text\"\r\n        x=\"50%\"\r\n        y=\"50%\"\r\n        text-anchor=\"middle\"\r\n        dy=\".3em\"\r\n      >\r\n      {{ currentStepPosition }} / {{ routes.length }}\r\n      </text>\r\n      <symbol id=\"check\">\r\n        <path\r\n          class=\"object\"\r\n          d=\"M14.7 40c-0.8 0-1.6-0.3-2.1-0.9L2.3 28.9c-0.4-0.4-0.4-1 0-1.4s1-0.4 1.4 0L14 37.7c0.4 0.4 1 0.4 1.4 0\r\n          L44.3 8.8c0.4-0.4 1-0.4 1.4 0s0.4 1 0 1.4L16.8 39.1C16.2 39.7 15.5 40 14.7 40z\"\r\n        />\r\n      </symbol>\r\n      <symbol id=\"info_\">\r\n        <path\r\n          class=\"object\"\r\n          d=\"M12 7C12 7 13 7 13 6 13 6 13 6 13 5 13 5 13 5 13 4 13 4 12 4 12 4 12 4 11 4 11 4 11 5 11 5 11 5 11 6 11\r\n        6 11 6 11 7 12 7 12 7ZM15 20L15 18C15 18 15 18 15 18L13 18 13 18 13 9C13 9 13 9 13 9L10 9C9 9 9 9 9 9L9 11C9\r\n        11 9 11 10 11L11 11 11 11 11 18 10 18C9 18 9 18 9 18L9 20C9 20 9 20 10 20L15 20C15 20 15 20 15 20Z\"\r\n        />\r\n      </symbol>\r\n      <symbol id=\"error-warning_\">\r\n        <path\r\n          class=\"object\"\r\n          d=\"M12 18C13 18 13 18 13 19 13 19 13 20 12 20 11 20 11 19 11 19 11 18 11 18 12 18ZM13 4C13 4 13 4 13 5L13 5\r\n        13 14C13 15 13 15 12 15L12 15 12 15C11 15 11 15 11 14L11 14 11 5C11 4 11 4 11 4L11 4Z\"\r\n        />\r\n      </symbol>\r\n      <use\r\n        *ngIf=\"\r\n          isFinalStep &&\r\n          finished &&\r\n          !info &&\r\n          !errorWarning\r\n        \"\r\n        href=\"#check\"\r\n        stroke=\"#0A3478\"\r\n        stroke-width=\"2\"\r\n        x=\"15\"\r\n        y=\"13\"\r\n        width=\"20\"\r\n        height=\"20\"\r\n      ></use>\r\n      <use\r\n        *ngIf=\"info && !errorWarning\"\r\n        href=\"#info_\"\r\n        stroke=\"#0A3478\"\r\n        stroke-width=\"0\"\r\n        x=\"12\"\r\n        y=\"12\"\r\n        width=\"20\"\r\n        height=\"20\"\r\n      ></use>\r\n      <use\r\n        *ngIf=\"errorWarning\"\r\n        href=\"#error-warning_\"\r\n        stroke=\"#0A3478\"\r\n        stroke-width=\"0\"\r\n        x=\"12\"\r\n        y=\"12\"\r\n        width=\"20\"\r\n        height=\"20\"\r\n      ></use>\r\n      </svg>\r\n    </div>\r\n    <div class=\"db-stepper__content align-items-center\">\r\n      <div class=\"db-stepper__label\">\r\n        <ng-container *ngIf=\"showStepTexts && stepLabels\">\r\n          <div *ngIf=\"errorWarning && stepLabels.errorWarning; else infoTpl\" class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.errorWarning}}</div>\r\n          <ng-template #infoTpl>\r\n            <div *ngIf=\"info && stepLabels.info; else successTpl\"  class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.info}}</div>\r\n          </ng-template>\r\n          <ng-template #successTpl>\r\n            <div *ngIf=\"isFinalStep && finished && stepLabels.success; else titleTpl\"  class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.success}}</div>\r\n          </ng-template>\r\n          <ng-template #titleTpl>\r\n            <div *ngIf=\"stepLabels.step\"  class=\"db-stepper__label--current d-none d-sm-block\">{{stepLabels.step}} {{ ' ' + currentStepPosition }}</div>\r\n          </ng-template>\r\n        </ng-container>\r\n        <div class=\"db-stepper__label--text\">{{ activeLabel }}</div>\r\n      </div>\r\n      <div class=\"db-stepper__label d-none d-sm-flex text-right\" *ngIf=\"showNextStepInfo\">\r\n        <div *ngIf=\"showStepTexts && stepLabels && !isFinalStep\"  class=\"db-stepper__label--next\">{{stepLabels.next}}</div>\r\n        <div class=\"db-stepper__label--text\">{{ nextStepLabel }}</div>\r\n      </div>\r\n    </div>  \r\n  </div>\r\n  <div class=\"db-stepper__progress-bar d-none d-md-block\" [ngStyle]=\"{ width: progress + '%' }\"></div>\r\n</div>\r\n" }]
        }], ctorParameters: function () {
        return [{ type: i0.ChangeDetectorRef }, { type: i1$2.Router, decorators: [{
                        type: Optional
                    }] }];
    }, propDecorators: { labels: [{
                type: Input
            }], routes: [{
                type: Input
            }], showNextStepInfo: [{
                type: Input
            }], showStepTexts: [{
                type: Input
            }], useRoutes: [{
                type: Input
            }], active: [{
                type: Input
            }], finished: [{
                type: Input
            }], info: [{
                type: Input
            }], errorWarning: [{
                type: Input
            }], stepLabels: [{
                type: Input
            }], steps: [{
                type: ContentChildren,
                args: [DBStepperItemComponent]
            }] } });

/**
 * DB Panel Module
 */
class DBStepperModule {
}
DBStepperModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStepperModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBStepperModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBStepperModule, declarations: [DBStepperComponent,
        DBStepperItemComponent], imports: [CommonModule,
        RouterModule], exports: [DBStepperComponent,
        DBStepperItemComponent] });
DBStepperModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStepperModule, imports: [CommonModule,
        RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBStepperModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBStepperComponent,
                        DBStepperItemComponent
                    ],
                    exports: [
                        DBStepperComponent,
                        DBStepperItemComponent
                    ],
                    imports: [
                        CommonModule,
                        RouterModule,
                    ]
                }]
        }] });

/**
 * DB Tab Component
 */
class DBTabComponent {
    /**
     * Constructor
     */
    constructor(changeDetector, renderer, elementRef) {
        this.internalTitle = '';
        this.titleIcon = '';
        this.id = '';
        this.internalIsActive = false;
        this.changeDetector = changeDetector;
        this.renderer = renderer;
        this.elementRef = elementRef;
        this.tabsComponent = null;
    }
    /**
     * Tab title
     */
    set title(title) {
        this.internalTitle = title;
        if (this.tabsComponent !== null) {
            this.tabsComponent.triggerViewUpdate(); // Trigger change detection in parent
        }
    }
    get title() {
        return this.internalTitle;
    }
    /**
     * Tab icon
     */
    set icon(title) {
        this.titleIcon = title;
        if (this.tabsComponent !== null) {
            this.tabsComponent.triggerViewUpdate(); // Trigger change detection in parent
        }
    }
    get icon() {
        return this.titleIcon;
    }
    /**
     * Active flag
     */
    set isActive(isActive) {
        if (this.internalIsActive !== isActive) {
            this.internalIsActive = isActive;
            this.changeDetector.markForCheck(); // Run change detection
        }
    }
    get isActive() {
        return this.internalIsActive;
    }
    /**
     * Shift (animate) the tab content to the center, into the visible area
     */
    shiftToCenter() {
        this.renderer.setStyle(this.elementRef.nativeElement, 'transform', `translateX( 0 )`);
    }
    /**
     * Shift (animate) the tab content to the right, out of the visible area
     */
    shiftToRight() {
        this.renderer.setStyle(this.elementRef.nativeElement, 'transform', `translateX( 100% )`);
    }
    /**
     * Shift (animate) the tab content to the left, out of the visible area
     */
    shiftToLeft() {
        this.renderer.setStyle(this.elementRef.nativeElement, 'transform', `translateX( -100% )`);
    }
}
DBTabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTabComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DBTabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBTabComponent, selector: "db-tab", inputs: { title: "title", icon: "icon", id: "id" }, host: { properties: { "class.is-active": "this.internalIsActive" } }, viewQueries: [{ propertyName: "tabContentElement", first: true, predicate: ["tabContent"], descendants: true }], ngImport: i0, template: "<!--<div class=\"db-tab__content\" #tabContent>-->\r\n  <ng-content></ng-content>\r\n<!--</div>-->\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTabComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-tab', template: "<!--<div class=\"db-tab__content\" #tabContent>-->\r\n  <ng-content></ng-content>\r\n<!--</div>-->\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { title: [{
                type: Input
            }], icon: [{
                type: Input
            }], id: [{
                type: Input
            }], internalIsActive: [{
                type: HostBinding,
                args: ['class.is-active']
            }], tabContentElement: [{
                type: ViewChild,
                args: ['tabContent']
            }] } });

/**
 * DB Tabs Component
 */
class DBTabsComponent {
    /**
     * Constructor
     *
     * @param renderer
     * @param changeDetector
     * @param viewportService
     */
    constructor(renderer, changeDetector, viewportService) {
        /**
         * show scroll arrow left
         */
        this.showArrowLeft = true;
        /**
         * show scroll arrow right
         */
        this.showArrowRight = true;
        this.internalActiveTab = null;
        this.activeTabChange = new EventEmitter();
        this.tabComponents = null;
        this.subscriptions = [];
        this.renderer = renderer;
        this.changeDetector = changeDetector;
        this.viewportService = viewportService;
        this.design = 'default';
    }
    /**
     * Active tab index
     */
    set activeTab(tabId) {
        if (this.tabComponents !== null && this.tabComponents.length > 0) { // Skipif the content hasn't been initialized yet
            this.setActiveTab(tabId);
        }
        else {
            this.internalActiveTab = tabId;
        }
    }
    get activeTab() {
        return this.internalActiveTab;
    }
    /**
     * After Content Init Component Lifecycle Hook
     */
    ngAfterContentInit() {
        // Set active tab initially with a timeout to avoid ExpressionChangedAfterItHasBeenChecked error
        setTimeout(() => {
            if (this.internalActiveTab === null) {
                this.setActiveTab();
            }
            else {
                this.setActiveTab(this.internalActiveTab);
            }
            this.triggerViewUpdate();
        }, 0);
        // Register
        this.tabComponents.forEach((tabComponent) => {
            tabComponent.tabsComponent = this;
        });
        // Handle dynamic tab component changes
        const tabComponentsSubscription = this.tabComponents.changes.subscribe((changes) => {
            // Re-register
            this.tabComponents.forEach((tabComponent) => {
                tabComponent.tabsComponent = this;
            });
            // If, after the change, the currently active tab no longer exists, we reset the active tab to the first available one
            if (!this.doesTabWithIdExist(this.tabComponents.toArray(), this.internalActiveTab)) {
                this.setActiveTab();
            }
        });
        this.subscriptions.push(tabComponentsSubscription);
    }
    /**
     * After View Init Component Lifecycle Hook
     */
    ngAfterViewInit() {
        // update the arrow positon on screen size change
        const mediaQueryChangesSubscription = this.viewportService.resizeChanges(this.tabNavigation.nativeElement)
            .subscribe(() => {
            this.setArrowPosition();
        });
        this.subscriptions.push(mediaQueryChangesSubscription);
    }
    /**
     * On Destroy Component Lifecycle Hook
     */
    ngOnDestroy() {
        // Cleanup to free ressources
        this.subscriptions.forEach((subscription) => {
            subscription.unsubscribe();
        });
        this.subscriptions = null;
    }
    /**
     * Handle clicks on tabs
     *
     * @param tabId - ID of the tab the user has clicked on
     */
    onTabClick(tabId) {
        if (tabId !== this.internalActiveTab) {
            this.setActiveTab(tabId);
            this.activeTabChange.emit(tabId);
        }
    }
    /**
     * Triggers View Update
     *
     */
    triggerViewUpdate() {
        this.changeDetector.markForCheck();
    }
    /**
     * Triggers scroll event to set arrow position
     */
    onScroll() {
        this.setArrowPosition();
    }
    /**
     * scroll pill tabs to left
     *
     */
    scrollTabsLeft() {
        this.tabNavigation.nativeElement.scrollLeft -= 100;
        this.showArrowLeft = this.tabNavigation.nativeElement.scrollLeft !== 0;
        this.showArrowRight = true;
    }
    /**
     * scroll pill tabs to right
     *
     */
    scrollTabsRight() {
        this.tabNavigation.nativeElement.scrollLeft += 100;
        this.showArrowRight = !(this.tabNavigation.nativeElement.scrollLeft + this.tabNavigation.nativeElement.offsetWidth >=
            this.tabNavigation.nativeElement.scrollWidth);
        this.showArrowLeft = true;
    }
    /**
     * Set the active tab
     *
     * @param [tabId] - Unique ID of the tab to activate
     */
    setActiveTab(tabId) {
        // Skip if there are no tab components available
        if (this.tabComponents !== null && this.tabComponents.length > 0) {
            const tabComponentList = this.tabComponents.toArray();
            const newTabId = tabId === undefined ? tabComponentList[0].id : tabId;
            const newTabIndex = this.getTabIndexById(tabComponentList, newTabId);
            const newActiveTabComponent = this.getTabComponentById(tabComponentList, newTabId);
            const oldTabId = this.internalActiveTab === null ? tabComponentList[0].id : this.internalActiveTab;
            const oldTabIndex = this.getTabIndexById(tabComponentList, oldTabId);
            const oldActiveTabComponent = this.getTabComponentById(tabComponentList, oldTabId);
            this.activeTabIndex = newTabIndex;
            //  Stores the width of content left to selected tab
            let sum = 0;
            // Update active tab
            this.internalActiveTab = newTabId;
            // Update active states of all tab components
            this.tabComponents.forEach((tabComponent) => {
                tabComponent.isActive = tabComponent.id === newTabId;
            });
            // Shift the active tab into the center
            newActiveTabComponent.shiftToCenter();
            // Render positions
            this.tabComponents.forEach((tabComponent, tabComponentIndex) => {
                if (tabComponentIndex < newTabIndex) {
                    tabComponent.shiftToLeft();
                    // Stores the width of content left to selected tab
                    if (this.tabNavigation.nativeElement.children[tabComponentIndex]) {
                        sum += this.tabNavigation.nativeElement.children[tabComponentIndex].clientWidth;
                    }
                }
                else {
                    if (tabComponentIndex > newTabIndex) {
                        tabComponent.shiftToRight();
                    }
                }
            });
            // Update selected tab position
            if (this.tabNavigation.nativeElement.children.length > 0) {
                let distance = (sum - (this.tabNavigation.nativeElement.clientWidth -
                    this.tabNavigation.nativeElement.children[newTabIndex].clientWidth) / 2);
                distance = distance > 0 ? distance : 0;
                // Scroll the tab into the view
                this.tabNavigation.nativeElement.scrollLeft = distance;
                this.showArrowLeft = this.tabNavigation.nativeElement.scrollLeft > 0;
                this.showArrowRight = !(this.tabNavigation.nativeElement.scrollLeft + this.tabNavigation.nativeElement.offsetWidth >=
                    this.tabNavigation.nativeElement.scrollWidth);
                this.setArrowPosition();
            }
        }
    }
    /**
     * set the arrow position to the left
     */
    setArrowPosition() {
        // arrow width to calculate center of the selected tab
        if (this.activeTabIndex >= 0) {
            const arrowWidth = 8;
            const selectedTab = this.tabNavigation.nativeElement.children[this.activeTabIndex];
            // calculate the position for arrow
            const arrowPosition = (selectedTab.offsetLeft - this.tabNavigation.nativeElement.scrollLeft +
                ((selectedTab.clientWidth) / 2)) - arrowWidth;
            this.handleValue = arrowPosition;
            this.changeDetector.detectChanges();
        }
    }
    /**
     * Get a tab component by the given tab ID (pure function)
     *
     * @param tabComponents - List of tab components
     * @param                tabId         - ID of the tab
     * @returns                      - Tab component
     */
    getTabComponentById(tabComponents, tabId) {
        return tabComponents.find((tabComponent) => tabComponent.id === tabId);
    }
    /**
     * Get the index of a tab by the given tab ID (pure function)
     *
     * @param tabComponents - List of tab components
     * @param                tabId         - ID of the tab
     * @returns                              - Index of the tab
     */
    getTabIndexById(tabComponents, tabId) {
        return tabComponents.findIndex((tabComponent) => tabComponent.id === tabId);
    }
    /**
     * Check if a tab with the given ID exists (pure function)
     *
     * @param tabComponents - List of tab components
     * @param                tabId         - ID of the tab
     * @returns                             - Flag, describing whether the tab does exist or not
     */
    doesTabWithIdExist(tabComponents, tabId) {
        return this.getTabIndexById(tabComponents, tabId) !== -1;
    }
}
DBTabsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTabsComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: DBViewportService }], target: i0.ɵɵFactoryTarget.Component });
DBTabsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBTabsComponent, selector: "db-tabs", inputs: { activeTab: "activeTab", design: "design" }, outputs: { activeTabChange: "activeTabChange" }, queries: [{ propertyName: "tabComponents", predicate: DBTabComponent, descendants: true }], viewQueries: [{ propertyName: "tabsPositionMarker", first: true, predicate: ["tabsPositionMarker"], descendants: true }, { propertyName: "tabNavigation", first: true, predicate: ["tabNavigation"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<nav class=\"db-tabs__navigation\">\r\n  <div class=\"db-tabs__navigation__left-arrow\" *ngIf=\"design === 'pills'\" [class.d-none]=\"!this.showArrowLeft\">\r\n    <button (click)=\"scrollTabsLeft()\">\r\n      <db-icon class=\"d-none d-sm-block\">arrow-left</db-icon>\r\n    </button>\r\n  </div>\r\n  <ul class=\"db-tabs__list\" [class.db-tabs__list--pills]=\"design === 'pills'\" #tabNavigation (scroll)=\"onScroll()\">\r\n    <li class=\"db-tabs__item\" *ngFor=\"let tabComponent of tabComponents;\" [class.is-active-tab]=\"tabComponent.isActive\">\r\n      <a class=\"db-tabs__link d-flex justify-content-center flex-grow-1 align-items-center\"\r\n         [class.is-active]=\"tabComponent.isActive\"\r\n         [class.is-default]=\"design !== 'pills'\"\r\n         (click)=\"onTabClick( tabComponent.id )\"\r\n         (keydown.enter)=\"onTabClick( tabComponent.id )\"\r\n         tabindex=\"0\"\r\n         >\r\n\r\n        <db-icon *ngIf=\"tabComponent.titleIcon\" size=\"lg\" class=\"db-tabs__link__icon my-3\">{{ tabComponent.titleIcon }}</db-icon>\r\n        <div class=\"db-tabs__link__title d-flex align-items-center justify-content-center flex-grow-1\">\r\n          {{ tabComponent.internalTitle }}\r\n        </div>\r\n      </a>\r\n      <span *ngIf=\"tabComponent.isActive && design !== 'pills'\" [style.left.px]=\"handleValue\" class=\"db-tabs__position-arrow\"></span>\r\n    </li>\r\n  </ul>\r\n  <div class=\"db-tabs__navigation__right-arrow justify-content-end\" *ngIf=\"design === 'pills'\" [class.d-none]=\"!this.showArrowRight\">\r\n    <button (click)=\"scrollTabsRight()\">\r\n      <db-icon class=\"d-none d-sm-block\">arrow-right</db-icon>\r\n    </button>\r\n  </div>\r\n</nav>\r\n\r\n<div class=\"db-tabs__content\">\r\n  <ng-content></ng-content>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTabsComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-tabs', template: "<nav class=\"db-tabs__navigation\">\r\n  <div class=\"db-tabs__navigation__left-arrow\" *ngIf=\"design === 'pills'\" [class.d-none]=\"!this.showArrowLeft\">\r\n    <button (click)=\"scrollTabsLeft()\">\r\n      <db-icon class=\"d-none d-sm-block\">arrow-left</db-icon>\r\n    </button>\r\n  </div>\r\n  <ul class=\"db-tabs__list\" [class.db-tabs__list--pills]=\"design === 'pills'\" #tabNavigation (scroll)=\"onScroll()\">\r\n    <li class=\"db-tabs__item\" *ngFor=\"let tabComponent of tabComponents;\" [class.is-active-tab]=\"tabComponent.isActive\">\r\n      <a class=\"db-tabs__link d-flex justify-content-center flex-grow-1 align-items-center\"\r\n         [class.is-active]=\"tabComponent.isActive\"\r\n         [class.is-default]=\"design !== 'pills'\"\r\n         (click)=\"onTabClick( tabComponent.id )\"\r\n         (keydown.enter)=\"onTabClick( tabComponent.id )\"\r\n         tabindex=\"0\"\r\n         >\r\n\r\n        <db-icon *ngIf=\"tabComponent.titleIcon\" size=\"lg\" class=\"db-tabs__link__icon my-3\">{{ tabComponent.titleIcon }}</db-icon>\r\n        <div class=\"db-tabs__link__title d-flex align-items-center justify-content-center flex-grow-1\">\r\n          {{ tabComponent.internalTitle }}\r\n        </div>\r\n      </a>\r\n      <span *ngIf=\"tabComponent.isActive && design !== 'pills'\" [style.left.px]=\"handleValue\" class=\"db-tabs__position-arrow\"></span>\r\n    </li>\r\n  </ul>\r\n  <div class=\"db-tabs__navigation__right-arrow justify-content-end\" *ngIf=\"design === 'pills'\" [class.d-none]=\"!this.showArrowRight\">\r\n    <button (click)=\"scrollTabsRight()\">\r\n      <db-icon class=\"d-none d-sm-block\">arrow-right</db-icon>\r\n    </button>\r\n  </div>\r\n</nav>\r\n\r\n<div class=\"db-tabs__content\">\r\n  <ng-content></ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: DBViewportService }]; }, propDecorators: { activeTab: [{
                type: Input
            }], design: [{
                type: Input
            }], activeTabChange: [{
                type: Output
            }], tabComponents: [{
                type: ContentChildren,
                args: [DBTabComponent, { descendants: true }]
            }], tabsPositionMarker: [{
                type: ViewChild,
                args: ['tabsPositionMarker']
            }], tabNavigation: [{
                type: ViewChild,
                args: ['tabNavigation', { static: true, read: ElementRef }]
            }] } });

/**
 * DB Tabs Module
 */
class DBTabsModule {
}
DBTabsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBTabsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBTabsModule, declarations: [DBTabComponent,
        DBTabsComponent], imports: [CommonModule,
        DBIconModule,
        DBViewportModule,
        DBButtonModule], exports: [DBTabComponent,
        DBTabsComponent] });
DBTabsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTabsModule, imports: [CommonModule,
        DBIconModule,
        DBViewportModule,
        DBButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBTabComponent,
                        DBTabsComponent,
                    ],
                    exports: [
                        DBTabComponent,
                        DBTabsComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBIconModule,
                        DBViewportModule,
                        DBButtonModule,
                    ],
                }]
        }] });

/**
 * Toggleable state
 */
var ToggleableState;
(function (ToggleableState) {
    ToggleableState[ToggleableState["Opened"] = 0] = "Opened";
    ToggleableState[ToggleableState["Closed"] = 1] = "Closed";
})(ToggleableState || (ToggleableState = {}));

/**
 * DB Toggleable Component
 */
class DBToggleableComponent {
    /**
     * Constructor
     */
    constructor() {
        this.label = '';
        this.closeLinkLabel = 'Ausblenden';
        this.design = 'default';
        this.showCloseLink = false;
        this.open = false;
        this.openChange = new EventEmitter();
    }
    /**
     * Open label, shown while toggle is closed
     */
    set openLabel(value) {
        this.label = value;
    }
    /**
     * Toggle the toggleable content
     */
    toggle() {
        this.open = !this.open;
        const currentState = this.open ? ToggleableState.Opened : ToggleableState.Closed;
        this.openChange.next(currentState);
    }
}
DBToggleableComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBToggleableComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBToggleableComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBToggleableComponent, selector: "db-toggleable", inputs: { label: "label", openLabel: "openLabel", closeLabel: "closeLabel", showCloseLink: "showCloseLink", closeLinkLabel: "closeLinkLabel", open: "open", iconPosition: "iconPosition", design: "design" }, outputs: { openChange: "openChange" }, ngImport: i0, template: "<a class=\"db-toggleable__header\"\r\n   [ngClass]=\"{'is-open': open, 'db-toggleable__header--default': design === 'default'}\"\r\n   (click)=\"toggle()\"\r\n   tabindex=\"0\">\r\n\r\n  <span class=\"db-toggleable__label\"\r\n        [ngClass]=\"{\r\n          'default-position': iconPosition !== 'right',\r\n          'is-right': iconPosition === 'right',\r\n          'db-toggleable__label--default': design === 'default',\r\n          'db-toggleable__label--arrows': design === 'arrows'\r\n        }\">\r\n    {{ open ? closeLabel || label : label }}\r\n  </span>\r\n  <svg class=\"db-toggleable__icon\"\r\n       [ngClass]=\"{'is-right': iconPosition === 'right'}\"\r\n       *ngIf=\"design === 'arrows'\"\r\n       focusable=\"false\"\r\n       viewBox=\"0 0 64 64\">\r\n    <path d=\"M5.3 13.25c-2.15 0-4.1 1.37-4.9 3.45-.82 2.1-.34 4.48 1.2 6.05l26.67 27.77c2.08 2.16 5.44 2.16 7.5 0l26.68-27.77c1.35-1.4\r\n      1.85-3.45 1.37-5.35-.5-1.9-1.93-3.4-3.76-3.9-1.83-.52-3.8.03-5.13 1.42l-22.9 23.86-22.9-23.86c-1-1.07-2.38-1.67-3.82-1.67z\">\r\n    </path>\r\n  </svg>\r\n\r\n  <span *ngIf=\"design === 'default'\">\r\n  <svg class=\"db-toggleable__icon db-toggleable__icon--is-close\"\r\n       [ngClass]=\"{\r\n          'default-position': iconPosition !== 'right',\r\n          'is-right': iconPosition === 'right',\r\n          'db-toggleable__icon--default': design === 'default'\r\n          }\"\r\n       *ngIf=\"open\"\r\n       focusable=\"false\"\r\n       viewBox=\"0 0 64 64\">\r\n    <path class=\"object\" d=\"M36.5,25h-25c-0.6,0-1-0.4-1-1s0.4-1,1-1h25c0.6,0,1,0.4,1,1S37.1,25,36.5,25z\"></path>\r\n  </svg>\r\n\r\n  <svg class=\"db-toggleable__icon\"\r\n       [ngClass]=\"{\r\n          'default-position': iconPosition !== 'right',\r\n          'is-right': iconPosition === 'right',\r\n          'db-toggleable__icon--default': design === 'default'\r\n          }\"\r\n       focusable=\"false\"\r\n       viewBox=\"0 0 64 64\"\r\n       *ngIf=\"!open\">\r\n    <path class=\"object\" d=\"M45,23H25V3c0-0.6-0.4-1-1-1s-1,0.4-1,1v20H3c-0.6,0-1,0.4-1,1s0.4,1,1,1h20v20c0,0.6,0.4,1,1,1s1-0.4,1-1V25\r\n  h20c0.6,0,1-0.4,1-1S45.6,23,45,23z\"> </path>\r\n  </svg>\r\n</span>\r\n</a>\r\n\r\n<div class=\"db-toggleable__content\"\r\n     [class.db-toggleable__content--default]=\"design === 'default'\"\r\n     *ngIf=\"open\">\r\n\r\n  <ng-content></ng-content>\r\n\r\n  <a class=\"db-toggleable__close-link\" *ngIf=\"showCloseLink\" (click)=\"toggle()\" tabindex=\"0\">\r\n    <svg viewBox=\"0 0 64 64\"\r\n         class=\"db-toggleable__close-icon\"\r\n         focusable=\"false\"\r\n         *ngIf=\"design === 'arrows'\">\r\n      <path d=\"M5.3 13.25c-2.15 0-4.1 1.37-4.9 3.45-.82 2.1-.34 4.48 1.2 6.05l26.67 27.77c2.08 2.16 5.44 2.16 7.5 0l26.68-27.77c1.35-1.4\r\n        1.85-3.45 1.37-5.35-.5-1.9-1.93-3.4-3.76-3.9-1.83-.52-3.8.03-5.13 1.42l-22.9 23.86-22.9-23.86c-1-1.07-2.38-1.67-3.82-1.67z\">\r\n      </path>\r\n    </svg>\r\n\r\n    <span *ngIf=\"design === 'default'\">\r\n      <svg class=\"db-toggleable__close-icon\"\r\n           [class.default-position]=\"iconPosition !== 'right'\"\r\n           [class.is-right]=\"iconPosition === 'right'\"\r\n           focusable=\"false\"\r\n           viewBox=\"0 0 64 64\"\r\n           *ngIf=\"open\">\r\n      <path class=\"object\" d=\"M36.5,25h-25c-0.6,0-1-0.4-1-1s0.4-1,1-1h25c0.6,0,1,0.4,1,1S37.1,25,36.5,25z\"></path>\r\n      </svg>\r\n    </span>\r\n    <span class=\"db-toggleable__close-label ml-3\">{{ closeLinkLabel }}</span>\r\n  </a>\r\n\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBToggleableComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-toggleable', template: "<a class=\"db-toggleable__header\"\r\n   [ngClass]=\"{'is-open': open, 'db-toggleable__header--default': design === 'default'}\"\r\n   (click)=\"toggle()\"\r\n   tabindex=\"0\">\r\n\r\n  <span class=\"db-toggleable__label\"\r\n        [ngClass]=\"{\r\n          'default-position': iconPosition !== 'right',\r\n          'is-right': iconPosition === 'right',\r\n          'db-toggleable__label--default': design === 'default',\r\n          'db-toggleable__label--arrows': design === 'arrows'\r\n        }\">\r\n    {{ open ? closeLabel || label : label }}\r\n  </span>\r\n  <svg class=\"db-toggleable__icon\"\r\n       [ngClass]=\"{'is-right': iconPosition === 'right'}\"\r\n       *ngIf=\"design === 'arrows'\"\r\n       focusable=\"false\"\r\n       viewBox=\"0 0 64 64\">\r\n    <path d=\"M5.3 13.25c-2.15 0-4.1 1.37-4.9 3.45-.82 2.1-.34 4.48 1.2 6.05l26.67 27.77c2.08 2.16 5.44 2.16 7.5 0l26.68-27.77c1.35-1.4\r\n      1.85-3.45 1.37-5.35-.5-1.9-1.93-3.4-3.76-3.9-1.83-.52-3.8.03-5.13 1.42l-22.9 23.86-22.9-23.86c-1-1.07-2.38-1.67-3.82-1.67z\">\r\n    </path>\r\n  </svg>\r\n\r\n  <span *ngIf=\"design === 'default'\">\r\n  <svg class=\"db-toggleable__icon db-toggleable__icon--is-close\"\r\n       [ngClass]=\"{\r\n          'default-position': iconPosition !== 'right',\r\n          'is-right': iconPosition === 'right',\r\n          'db-toggleable__icon--default': design === 'default'\r\n          }\"\r\n       *ngIf=\"open\"\r\n       focusable=\"false\"\r\n       viewBox=\"0 0 64 64\">\r\n    <path class=\"object\" d=\"M36.5,25h-25c-0.6,0-1-0.4-1-1s0.4-1,1-1h25c0.6,0,1,0.4,1,1S37.1,25,36.5,25z\"></path>\r\n  </svg>\r\n\r\n  <svg class=\"db-toggleable__icon\"\r\n       [ngClass]=\"{\r\n          'default-position': iconPosition !== 'right',\r\n          'is-right': iconPosition === 'right',\r\n          'db-toggleable__icon--default': design === 'default'\r\n          }\"\r\n       focusable=\"false\"\r\n       viewBox=\"0 0 64 64\"\r\n       *ngIf=\"!open\">\r\n    <path class=\"object\" d=\"M45,23H25V3c0-0.6-0.4-1-1-1s-1,0.4-1,1v20H3c-0.6,0-1,0.4-1,1s0.4,1,1,1h20v20c0,0.6,0.4,1,1,1s1-0.4,1-1V25\r\n  h20c0.6,0,1-0.4,1-1S45.6,23,45,23z\"> </path>\r\n  </svg>\r\n</span>\r\n</a>\r\n\r\n<div class=\"db-toggleable__content\"\r\n     [class.db-toggleable__content--default]=\"design === 'default'\"\r\n     *ngIf=\"open\">\r\n\r\n  <ng-content></ng-content>\r\n\r\n  <a class=\"db-toggleable__close-link\" *ngIf=\"showCloseLink\" (click)=\"toggle()\" tabindex=\"0\">\r\n    <svg viewBox=\"0 0 64 64\"\r\n         class=\"db-toggleable__close-icon\"\r\n         focusable=\"false\"\r\n         *ngIf=\"design === 'arrows'\">\r\n      <path d=\"M5.3 13.25c-2.15 0-4.1 1.37-4.9 3.45-.82 2.1-.34 4.48 1.2 6.05l26.67 27.77c2.08 2.16 5.44 2.16 7.5 0l26.68-27.77c1.35-1.4\r\n        1.85-3.45 1.37-5.35-.5-1.9-1.93-3.4-3.76-3.9-1.83-.52-3.8.03-5.13 1.42l-22.9 23.86-22.9-23.86c-1-1.07-2.38-1.67-3.82-1.67z\">\r\n      </path>\r\n    </svg>\r\n\r\n    <span *ngIf=\"design === 'default'\">\r\n      <svg class=\"db-toggleable__close-icon\"\r\n           [class.default-position]=\"iconPosition !== 'right'\"\r\n           [class.is-right]=\"iconPosition === 'right'\"\r\n           focusable=\"false\"\r\n           viewBox=\"0 0 64 64\"\r\n           *ngIf=\"open\">\r\n      <path class=\"object\" d=\"M36.5,25h-25c-0.6,0-1-0.4-1-1s0.4-1,1-1h25c0.6,0,1,0.4,1,1S37.1,25,36.5,25z\"></path>\r\n      </svg>\r\n    </span>\r\n    <span class=\"db-toggleable__close-label ml-3\">{{ closeLinkLabel }}</span>\r\n  </a>\r\n\r\n</div>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { label: [{
                type: Input
            }], openLabel: [{
                type: Input
            }], closeLabel: [{
                type: Input
            }], showCloseLink: [{
                type: Input
            }], closeLinkLabel: [{
                type: Input
            }], open: [{
                type: Input
            }], iconPosition: [{
                type: Input
            }], design: [{
                type: Input
            }], openChange: [{
                type: Output
            }] } });

/**
 * DB Toggleable Module
 */
class DBToggleableModule {
}
DBToggleableModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBToggleableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBToggleableModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBToggleableModule, declarations: [DBToggleableComponent], imports: [CommonModule], exports: [DBToggleableComponent] });
DBToggleableModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBToggleableModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBToggleableModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBToggleableComponent,
                    ],
                    exports: [
                        DBToggleableComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

/**
 * DB Tooltip Component
 */
class DBTooltipComponent extends DBOverlayComponentDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     * @param renderer       - Renderer
     * @param element        - Element
     */
    constructor(changeDetector, renderer, element) {
        super(changeDetector, renderer, element);
        this.size = 'sm';
    }
    /**
     * Initialize the tooltip
     *
     * @param triggerElement - Tooltip trigger element
     * @param options        - Tooltip options
     *
     * @returns - Promise, resolves once the animation finished
     */
    init(triggerElement, options) {
        this.content = options.content;
        this.initOverlay(triggerElement, { position: options.position, size: this.size });
    }
    /**
     * Show the tooltip
     *
     * @returns - Promise, resolves once the animation finished
     */
    show() {
        return this.showOverlay();
    }
    /**
     * Hide the tooltip
     *
     * @returns - Promise, resolves once the animation finished
     */
    hide() {
        return this.hideOverlay();
    }
}
DBTooltipComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
DBTooltipComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBTooltipComponent, selector: "db-tooltip", usesInheritance: true, ngImport: i0, template: "{{ content }}\r\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-tooltip', template: "{{ content }}\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; } });

/**
 * DB Tooltip Directive
 */
class DBTooltipDirective {
    /**
     * Constructor
     *
     * @param element  - Element
     * @param renderer - Renderer
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor(element, renderer) {
        this.element = element;
        this.renderer = renderer;
        this.dbTooltipContent = '';
        this.dbTooltipPosition = 'top';
        this.isVisible = false;
        this.tooltips = [];
    }
    /**
     * Show tooltip on hover / focus
     */
    showTooltip() {
        // Skip if the tooltip is already visible
        if (this.isVisible) {
            return;
        }
        // Verify if text overflow happened
        if (this.dbTooltipShowOnOverflow) {
            const tooltipElement = this.element.nativeElement;
            const isEllipsisActive = this.dbTooltipShowOnOverflow && (tooltipElement.offsetWidth < tooltipElement.scrollWidth);
            if (!isEllipsisActive) {
                return;
            }
        }
        // Create and render tooltip
        this.createAndRenderTooltip(this.element, {
            content: this.dbTooltipContent,
            position: this.dbTooltipPosition,
        });
    }
    /**
     * Hide tooltip on unhover / blur
     */
    hideTooltip() {
        // Skip if the tooltip is not visible
        if (!this.isVisible) {
            return;
        }
        // Destroy and remove tooltip
        this.destroyAndRemoveTooltip();
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        this.hideTooltip();
    }
    /**
     * Add tooltip
     *
     * @param   triggerElement - Tooltip trigger element
     * @param   options        - Tooltip options
     * @returns                - Promise, resolved once done (after animation has finished)
     */
    createAndRenderTooltip(triggerElement, options) {
        return __awaiter(this, void 0, void 0, function* () {
            this.isVisible = true;
            // Create component
            const componentRef = this.renderer.createAndAppendComponent(DBTooltipComponent);
            this.tooltips.push(componentRef);
            // Show component (with animation)
            componentRef.instance.init(triggerElement, options);
            yield componentRef.instance.show();
        });
    }
    /**
     * Remove tooltip
     *
     * @returns - Promise, resolved once done (after animation has finished)
     */
    destroyAndRemoveTooltip() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isVisible = false;
            // Hide component (with animation)
            const componentRef = this.tooltips.shift();
            yield componentRef.instance.hide();
            // Remove component
            this.renderer.destroyAndRemoveComponent(componentRef);
        });
    }
}
DBTooltipDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipDirective, deps: [{ token: i0.ElementRef }, { token: DBRenderer }], target: i0.ɵɵFactoryTarget.Directive });
DBTooltipDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBTooltipDirective, selector: "[dbTooltip]", inputs: { dbTooltipContent: "dbTooltipContent", dbTooltipPosition: "dbTooltipPosition", dbTooltipShowOnOverflow: "dbTooltipShowOnOverflow" }, host: { listeners: { "focusin": "showTooltip()", "mouseenter": "showTooltip()", "focusout": "hideTooltip()", "mouseleave": "hideTooltip()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[dbTooltip]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: DBRenderer }]; }, propDecorators: { dbTooltipContent: [{
                type: Input
            }], dbTooltipPosition: [{
                type: Input
            }], dbTooltipShowOnOverflow: [{
                type: Input
            }], showTooltip: [{
                type: HostListener,
                args: ['focusin']
            }, {
                type: HostListener,
                args: ['mouseenter']
            }], hideTooltip: [{
                type: HostListener,
                args: ['focusout']
            }, {
                type: HostListener,
                args: ['mouseleave']
            }] } });

/**
 * DB Tooltip Module
 */
class DBTooltipModule {
}
DBTooltipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBTooltipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipModule, declarations: [DBTooltipDirective,
        DBTooltipComponent], imports: [DBRendererModule], exports: [DBTooltipDirective] });
DBTooltipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipModule, imports: [DBRendererModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBTooltipDirective,
                        DBTooltipComponent,
                    ],
                    exports: [
                        DBTooltipDirective,
                    ],
                    imports: [
                        DBRendererModule,
                    ]
                }]
        }] });

/**
 * DB Application Data Store Service
 *
 * The application data store service can be used to save and get the application specific data.
 * The data will be typically saved as a key value pair in each page and saved in this class.
 */
class DBAppDataStoreService {
    constructor() {
        /**
         * Parameter that define under which it will store data in local or session storage
         * this is the default value and can be changed on initialisation of the service to
         * a custom name. This can be use when using multiple applications on the same domain
         * to ensure not to overwrite the storage object for the specific application.
         */
        this.storageName = 'DBAppDataStore';
        /**
         * contains the stored data.
         */
        this.data = {};
        /**
         * (optional) Flags for enabling local or session storage
         */
        this.storageEngineEnabled = false;
        this.storageEngine = sessionStorage;
    }
    /**
     * returns all entries.
     * @returns
     */
    entries() {
        return this.data;
    }
    /**
     * returns corresponding data to given key or null
     * @param key - a key as string to uniquely identify the stored data.
     * @returns - returns the data identified by the key.
     */
    get(key) {
        return this.data[key] || (this.data[key] === false ? false : null);
    }
    /**
     * returns all keys.
     * @returns
     */
    keys() {
        return Object.keys(this.data);
    }
    /**
     * store data which uniquely identified by the key
     * @param key - a key as string to uniquely identify the data.
     * @param value any type of value that you want to store
     */
    set(key, value) {
        this.data[key] = value;
        if (this.storageEngineEnabled) {
            this.storageEngine.setItem(this.storageName, JSON.stringify(this.data));
        }
    }
    /**
     * resets the internal data.
     */
    reset() {
        this.data = {};
        this.storageEngine.removeItem(this.storageName);
    }
    /**
     * This will enable or disbale the use of the local storage as storage engine.
     * It can be disable with enable false that results in clearing the storage object.
     *
     * @param enable boolean Enables or Disables the use
     */
    useLocalStorage(enable = false) {
        this.storageEngineEnabled = enable;
        if (enable) {
            this.storageEngine = localStorage;
            this.retrieveStoredData();
        }
        else {
            localStorage.removeItem(this.storageName);
        }
    }
    /**
     * This will enable or disbale the use of the session storage as storage engine.
     * It can be disable with enable false that results in clearing the storage object.
     *
     * @param enable boolean Enables or Disables the use
     */
    useSessionStorage(enable = false) {
        this.storageEngineEnabled = enable;
        if (enable) {
            this.storageEngine = sessionStorage;
            this.retrieveStoredData();
        }
        else {
            sessionStorage.removeItem(this.storageName);
        }
    }
    /**
     * Aplly stored values to the data store. This will be used on initialization
     * of the storage via storage engine.
     */
    retrieveStoredData() {
        const entry = this.storageEngine.getItem(this.storageName);
        const storedValues = JSON.parse(entry || '{}');
        if (storedValues) {
            Object.keys(storedValues).forEach(key => {
                this.set(key, storedValues[key]);
            });
        }
    }
}
DBAppDataStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAppDataStoreService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBAppDataStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAppDataStoreService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAppDataStoreService, decorators: [{
            type: Injectable
        }] });

/**
 * App Data Store Service Factory
 *
 * @param parentHeatBeatService - Parent app data store service (if exists)
 * @returns                              - App data store service instance
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function appDataStoreServiceFactory(parentHeatBeatService) {
    return parentHeatBeatService || new DBAppDataStoreService();
}
/**
 * DB App Data Store Module
 */
class DBAppDataStoreModule {
}
DBAppDataStoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAppDataStoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBAppDataStoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBAppDataStoreModule });
DBAppDataStoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAppDataStoreModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBAppDataStoreService],
            ],
            provide: DBAppDataStoreService,
            useFactory: appDataStoreServiceFactory,
        },
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAppDataStoreModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBAppDataStoreService],
                            ],
                            provide: DBAppDataStoreService,
                            useFactory: appDataStoreServiceFactory,
                        },
                    ],
                }]
        }] });

var ContentLoaderStatus;
(function (ContentLoaderStatus) {
    ContentLoaderStatus["PENDING"] = "PENDING";
    ContentLoaderStatus["LOADED"] = "LOADED";
    ContentLoaderStatus["FAILED"] = "FAILED";
})(ContentLoaderStatus || (ContentLoaderStatus = {}));

class DBContentLoaderAPIConfigInterface {
}
class DBContentLoaderAPIConfig {
    constructor() {
        this.apiEndpointURL = '';
    }
}
/**
 * Service for Content Loader to fetch data.
 */
class DBContentLoaderService {
    /**
     * Constructor
     *
     * @param                          -basePath
     * @param httpClient               - Http Client service
     * @apiConfig                      -api config
     */
    constructor(basePath, apiConfig, httpClient) {
        this.basePath = basePath;
        this.httpClient = httpClient;
        this.urlFromConfig = apiConfig ? apiConfig.apiEndpointURL : basePath.concat('content/dam/');
    }
    /**
     * Get json data from url
     */
    getContent(contentId) {
        this.contentUrl = this.urlFromConfig + contentId;
        return this.httpClient.get(this.contentUrl);
    }
}
DBContentLoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderService, deps: [{ token: 'BASE_URL' }, { token: DBContentLoaderAPIConfig, optional: true }, { token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
DBContentLoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: undefined, decorators: [{
                        type: Inject,
                        args: ['BASE_URL']
                    }] }, { type: DBContentLoaderAPIConfig, decorators: [{
                        type: Optional
                    }] }, { type: i1$1.HttpClient }];
    } });

/**
 * DB Content Loader Component.
 */
class DBContentLoaderComponent {
    constructor(renderer, contentLoaderService, modalService) {
        this.renderer = renderer;
        this.contentLoaderService = contentLoaderService;
        this.modalService = modalService;
        /**
         * Status of the loading which will be sent to the parent component
         */
        this.status = new EventEmitter();
    }
    /**
     * On change Lifecycle Hook
     * It is called when it receives contentId from the component which is using content loader.
     */
    ngOnChanges() {
        this.status.emit(ContentLoaderStatus.PENDING);
        this.contentLoaderService.getContent(this.contentId)
            .subscribe((res) => {
            this.status.emit(ContentLoaderStatus.LOADED);
            this.createDiv(res.content);
        }, (err) => {
            this.status.emit(ContentLoaderStatus.FAILED);
            if (isDevMode()) {
                console.warn(err);
            }
        });
    }
    /**
     * Popup data will be loaded if an data-modal is recognized. In this case we load the data from remote and open
     * an modal dialog
     * For now we wont throw an exception if content could not be loaded.
     * Instead we update the status of the content to load
     */
    openModal(event) {
        if (event.target.getAttribute('data-modal') != null) {
            this.status.emit(ContentLoaderStatus.PENDING);
            const targetPopup = event.target.getAttribute('data-modal');
            this.contentLoaderService.getContent(targetPopup)
                .subscribe((contentResponse) => {
                this.status.emit(ContentLoaderStatus.LOADED);
                this.activePopup = contentResponse;
                this.modalService.openWithTemplate(this.modalPopUp, { closeOnBackdropClickOrEscKey: true });
            }, (err) => {
                this.status.emit(ContentLoaderStatus.FAILED);
                if (isDevMode()) {
                    console.warn(err);
                }
            });
        }
    }
    /**
     * Event handler to close popup
     */
    onClose() {
        this.modalService.close();
    }
    /**
     * Create and append a div using innerhtml content.
     */
    createDiv(innerHtmlContent) {
        const div = this.renderer.createElement('div');
        div.innerHTML = innerHtmlContent;
        this.renderer.appendChild(this.content.nativeElement, div);
    }
}
DBContentLoaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderComponent, deps: [{ token: i0.Renderer2 }, { token: DBContentLoaderService }, { token: DBModalService }], target: i0.ɵɵFactoryTarget.Component });
DBContentLoaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBContentLoaderComponent, selector: "db-content-loader", inputs: { contentId: "contentId" }, outputs: { status: "status" }, viewQueries: [{ propertyName: "modalPopUp", first: true, predicate: ["modalPopUp"], descendants: true, static: true }, { propertyName: "content", first: true, predicate: ["content"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<ng-template #modalPopUp>\r\n  <div>\r\n    <db-icon class=\"db-content-loader__icon\" (click)=\"onClose()\">close</db-icon>\r\n    <div [innerHTML]=\"activePopup?.content\" class=\"db-content-loader__popup\"></div>\r\n  </div>\r\n</ng-template>\r\n<div #content (click)=\"openModal($event)\"></div>\r\n", styles: [".db-content-loader__popup{padding:10px}.db-content-loader__icon{float:right;margin:20px}\n"], dependencies: [{ kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-content-loader', template: "<ng-template #modalPopUp>\r\n  <div>\r\n    <db-icon class=\"db-content-loader__icon\" (click)=\"onClose()\">close</db-icon>\r\n    <div [innerHTML]=\"activePopup?.content\" class=\"db-content-loader__popup\"></div>\r\n  </div>\r\n</ng-template>\r\n<div #content (click)=\"openModal($event)\"></div>\r\n", styles: [".db-content-loader__popup{padding:10px}.db-content-loader__icon{float:right;margin:20px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: DBContentLoaderService }, { type: DBModalService }]; }, propDecorators: { modalPopUp: [{
                type: ViewChild,
                args: ['modalPopUp', { static: true }]
            }], contentId: [{
                type: Input
            }], content: [{
                type: ViewChild,
                args: ['content', { static: true }]
            }], status: [{
                type: Output
            }] } });

/**
 * DB Content Loader Module
 */
class DBContentLoaderModule {
}
DBContentLoaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBContentLoaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderModule, declarations: [DBContentLoaderComponent], imports: [DBIconModule,
        DBModalModule], exports: [DBContentLoaderComponent] });
DBContentLoaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderModule, providers: [
        DBContentLoaderService,
    ], imports: [DBIconModule,
        DBModalModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBContentLoaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBContentLoaderComponent
                    ],
                    exports: [
                        DBContentLoaderComponent
                    ],
                    imports: [
                        DBIconModule,
                        DBModalModule
                    ],
                    providers: [
                        DBContentLoaderService,
                    ]
                }]
        }] });

/**
 * DB Cookie Service
 */
class DBCookieService {
    /**
     * Constructor
     *
     * @param windowRef - Window Ref
     */
    constructor(windowRef) {
        this.windowRef = windowRef;
    }
    /**
     * Get all cookies
     *
     * @returns - Cookies
     */
    getCookies() {
        return this.parseBrowserCookies();
    }
    /**
     * Get a cookie, by its name
     *
     * @param   name - Cookie name
     * @returns      - Cookie value
     */
    getCookie(name) {
        return this.getCookies()[name] || null;
    }
    /**
     * Set a cookie (either create or update)
     *
     * @param name  - Cookie name
     * @param value - Cookie value
     */
    setCookie(name, value) {
        this.saveBrowserCookies(Object.assign(Object.assign({}, this.parseBrowserCookies()), {
            [name]: value,
        }));
    }
    /**
     * Delete a cookie, by its name
     *
     * @param name - Cookie name
     */
    deleteCookie(name) {
        const _a = this.parseBrowserCookies(), _b = name, removed = _a[_b], cookiesWithout = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
        this.saveBrowserCookies(cookiesWithout);
    }
    /**
     * Parse browser cookies
     *
     * @returns - Cookies as object
     */
    parseBrowserCookies() {
        return this.windowRef.nativeDocument.cookie === ''
            ? {}
            : this.windowRef.nativeDocument.cookie
                .split('; ')
                .reduce((cookies, cookieKeyValue) => {
                const [cookieName, cookieValue] = cookieKeyValue.split('=');
                cookies[decodeURIComponent(cookieName)] = decodeURIComponent(cookieValue);
                return cookies;
            }, {});
    }
    /**
     * Save browser cookies
     *
     * @param   cookies - Cookies
     */
    saveBrowserCookies(cookies) {
        this.windowRef.nativeDocument.cookie = Object
            .keys(cookies)
            .map((cookieName) => `${encodeURIComponent(cookieName)}=${encodeURIComponent(cookies[cookieName])}`)
            .join('; ');
    }
}
DBCookieService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCookieService, deps: [{ token: DBWindowRef }], target: i0.ɵɵFactoryTarget.Injectable });
DBCookieService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCookieService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCookieService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DBWindowRef }]; } });

/**
 * Cookie Service Factory
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function cookieServiceFactory(parentCookieService, windowRef) {
    return parentCookieService || new DBCookieService(windowRef);
}
/**
 * DB Cookie Module
 */
class DBCookieModule {
}
DBCookieModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCookieModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBCookieModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBCookieModule, imports: [DBWindowRefModule] });
DBCookieModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCookieModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBCookieService],
                DBWindowRef,
            ],
            provide: DBCookieService,
            useFactory: cookieServiceFactory,
        },
    ], imports: [DBWindowRefModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBCookieModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DBWindowRefModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBCookieService],
                                DBWindowRef,
                            ],
                            provide: DBCookieService,
                            useFactory: cookieServiceFactory,
                        },
                    ],
                }]
        }] });

class DBHttpClientCache {
    constructor() {
        this.cache = {};
    }
    /**
     * Return cached Request Object if it exists
     *
     * @param string requestUrl
     * @returns HttpResponse<any> | null
     */
    get(requestUrl) {
        return this.cache[requestUrl] || null;
    }
    /**
     * Store Request Object to Cache
     *
     * @param string requestUrl
     * @param HttpResponse<any> res
     */
    set(requestUrl, res) {
        this.cache[requestUrl] = res;
    }
    /**
     * remove specified cached Request
     */
    clear(requestUrl) {
        const _a = this.cache, _b = requestUrl, deletedUrl = _a[_b], cache = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
        this.cache = cache;
    }
    /**
     * remove all cached Requests
     */
    clearAll() {
        this.cache = {};
    }
}
DBHttpClientCache.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCache, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBHttpClientCache.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCache });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCache, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * DB HttpClient Cache Interceptor
 *
 * Scope:
 * - Cache responses from outgoing GET requests
 */
class DBHttpClientCacheInterceptor {
    /**
     * Constructor
     *
     * @param httpClientCache - DBHttpClientCache
     */
    constructor(httpClientCache) {
        this.httpClientCache = httpClientCache;
    }
    /**
     * Request interceptor, returns Response of previous GET Request (if exist)
     *
     * @param   request - Request object
     * @param   next    - Continue handler
     * @returns
     */
    intercept(request, next) {
        // Ignore non-GET requests and the once with a x-refresh=true header
        if (request.method !== 'GET' || (request.headers.has('x-refresh') && request.headers.get('x-refresh') === 'true')) {
            return next.handle(request);
        }
        // Use cached response if available
        const cachedResponse = this.httpClientCache.get(request.urlWithParams);
        if (cachedResponse) {
            if (isDevMode()) {
                // eslint-disable  no-console
                console.groupCollapsed('[HTTPCLIENT CACHE INTERCEPTOR] Returned cached response.');
                console.info('Request URL:', request.urlWithParams);
                console.info('Cached Response:', cachedResponse);
                console.groupEnd();
                // eslint-enable  no-console
            }
            return of(cachedResponse);
        }
        return next.handle(request).pipe(tap((event) => {
            if (event instanceof HttpResponse && event.ok) {
                this.httpClientCache.set(request.urlWithParams, event);
            }
        }));
    }
}
DBHttpClientCacheInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCacheInterceptor, deps: [{ token: DBHttpClientCache }], target: i0.ɵɵFactoryTarget.Injectable });
DBHttpClientCacheInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCacheInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCacheInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DBHttpClientCache }]; } });

/**
 *
 * @param parentHttpClientCache - Parent HttpClientCache (if exists)
 * @returns DBHttpClientCache instance
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function httpClientCacheFactory(parentHttpClientCache) {
    return parentHttpClientCache || new DBHttpClientCache();
}
/**
 * DB HttpClient Cache Module
 */
class DBHttpClientCacheModule {
}
DBHttpClientCacheModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCacheModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBHttpClientCacheModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCacheModule });
DBHttpClientCacheModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCacheModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBHttpClientCache],
            ],
            provide: DBHttpClientCache,
            useFactory: httpClientCacheFactory,
        },
        {
            multi: true,
            provide: HTTP_INTERCEPTORS,
            useClass: DBHttpClientCacheInterceptor,
        },
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHttpClientCacheModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBHttpClientCache],
                            ],
                            provide: DBHttpClientCache,
                            useFactory: httpClientCacheFactory,
                        },
                        {
                            multi: true,
                            provide: HTTP_INTERCEPTORS,
                            useClass: DBHttpClientCacheInterceptor,
                        },
                    ],
                }]
        }] });

/**
 * DB Post Message Service
 *
 * The Post Message Service can be used to receive all kinds of messages coming from iFrames by using an observable-based API while covering
 * various security concerns and browser compatibility issues by default.
 *
 * Security notes: The Post Message Service, by default, takes different security aspects and best practives into account in order to
 * prevent unexpected iFrame attacks. In particular, it
 * - only sets up post message event listeners if at least one adapter is registered
 * - ignores messages sent from a different origin than the one the application runs on
 * - ignores messages if no adapter is registered for it
 * - ignores messages that no part of the application actually listens to.
 */
class DBPostMessageService {
    /**
     * Constructor
     *
     * @param windowRef - Window
     * @param      ngZone    - Angular zone
     */
    constructor(windowRef, ngZone) {
        this.handlePostMessageEventFn = this.handlePostMessageEvent.bind(this);
        this.adapterRegistry = {};
        this.windowRef = windowRef;
        this.angularZone = ngZone;
    }
    /**
     * Register a new post message adapter
     *
     * @param adapter - Post message adapter
     */
    registerAdapter(adapter) {
        // If no adapters are registered, setup the event listener first (#security) (#perfmatters)
        if (Object.keys(this.adapterRegistry).length === 0) {
            this.setupEventListener();
        }
        // Add new adapter to the registry, overwriting previously added adapters with the same name by default
        this.adapterRegistry[adapter.name] = {
            adapter,
            observable: null,
            subject: null,
        };
    }
    /**
     * Unregister an existing post message adapter
     *
     * @param adapterName - Name of the post message adapter to unregister
     */
    unregisterAdapter(adapterName) {
        // Remove the adapter from the registry if it does exist
        if (this.adapterRegistry[adapterName]) {
            delete this.adapterRegistry[adapterName]; // While 'delete' is not that performant, it should be fine here
        }
        // If no adapters are registered, cleanup the event listener (#security) (#perfmatters)
        if (Object.keys(this.adapterRegistry).length === 0) {
            this.cleanupEventListener();
        }
    }
    /**
     * Select the post messages event to listen to, returning an observable which gets updated when new post messages are getting received
     *
     * @param               eventName - Name of the post message event
     * @returns           - Observable, getting updated with the message data, or null if no adapter exists
     */
    select(eventName) {
        // Skip if the adapter does not exist
        if (!this.adapterRegistry[eventName]) {
            return null;
        }
        // Create observable stream (if necessary)
        if (this.adapterRegistry[eventName].subject === null) {
            this.adapterRegistry[eventName].subject = new Subject();
            this.adapterRegistry[eventName].observable = this.adapterRegistry[eventName].subject
                // Convert subject to observable (for better public API, and better API control)
                .asObservable()
                // This runs shortly before the subscription gets disposed (inactive)
                .pipe(finalize(() => {
                // Check if this unsubscribe process removes the last existing observer
                if (this.adapterRegistry[eventName].subject.observers.length === 1) { // 1 right now, after 'unsubscribe' it will be 0
                    this.adapterRegistry[eventName].subject.complete();
                    this.adapterRegistry[eventName].subject = null;
                    this.adapterRegistry[eventName].observable = null;
                }
            }));
        }
        return this.adapterRegistry[eventName].observable;
    }
    /**
     * Send a post message to the parent window
     *
     * @param   eventName          - Name of the event / adapter
     * @param [messageData=null] - Data to be put in the message payload
     */
    sendMessageToParent(eventName, messageData = null) {
        // Skip if there is no parent (if parent and current window are the same), or there is no adapter
        if (this.windowRef.nativeWindow.parent === this.windowRef.nativeWindow.self || this.adapterRegistry[eventName] === undefined) {
            return;
        }
        // Transform message (if necessary)
        let transformedMessageData = messageData;
        if (this.adapterRegistry[eventName].adapter.transformBeforeSending) {
            transformedMessageData = this.adapterRegistry[eventName].adapter.transformBeforeSending(messageData);
        }
        // Parse data (if necessary)
        let parsedMessageData = transformedMessageData;
        if (this.adapterRegistry[eventName].adapter.compatibilityMode === true && transformedMessageData !== null) {
            parsedMessageData = JSON.stringify(messageData); // TODO: Can this throw an exception? Should we try-catch it?
        }
        // Finally, send the post message awaaaaaaaaay ... far far awaaaay ...
        let postMessageTargetOrigin;
        try {
            postMessageTargetOrigin = this.windowRef.nativeWindow.parent.location.origin; // Parent window origin as target (same-origin)
        }
        catch (error) { // E.g. DOMException regading unallowed access of "window"s running on a different origin
            postMessageTargetOrigin = '*'; // Fallback target (cross-origin) -- NOT AS SECURE AS SAME-ORIGIN!
        }
        this.windowRef.nativeWindow.parent.postMessage(parsedMessageData, postMessageTargetOrigin);
    }
    /**
     * Event handler for incoming post messages
     *
     * @param messageEvent - Post message event object
     */
    handlePostMessageEvent(messageEvent) {
        // Run through all the adapters, and process the post message (if possible)
        const adapterRegistryKeys = Object.keys(this.adapterRegistry);
        const numberOfAdapters = adapterRegistryKeys.length;
        for (let i = 0; i < numberOfAdapters; i++) {
            // Parse message data (before applying the filter!)
            // TODO: Performance improvement by caching the first parsing result?
            let parsedMessageData = messageEvent.data ? messageEvent.data : null;
            if (this.adapterRegistry[adapterRegistryKeys[i]].adapter.compatibilityMode === true) {
                parsedMessageData = this.parseMessageData(parsedMessageData);
            }
            if (
            // Skip if no one is even listening for this adapter -- rejecting unwanted messages by default (#security)
            this.adapterRegistry[adapterRegistryKeys[i]].subject !== null &&
                // Skip if the adapter is not responsible for handling this message -- rejecting unwanted messages by default (#security)
                this.adapterRegistry[adapterRegistryKeys[i]].adapter.filter(parsedMessageData)) {
                // Transform message (if necessary)
                let transformedMessageData = parsedMessageData;
                if (this.adapterRegistry[adapterRegistryKeys[i]].adapter.transformAfterReceiving) {
                    transformedMessageData = this.adapterRegistry[adapterRegistryKeys[i]].adapter.transformAfterReceiving(parsedMessageData);
                }
                // Emit result
                this.adapterRegistry[adapterRegistryKeys[i]].subject.next(transformedMessageData);
            }
        }
    }
    /**
     * Parse the post message event data
     *
     * @param messageData - Message event
     * @returns             - Data, which may also be null
     */
    parseMessageData(messageData) {
        /**
         * Conversion only works in case messageData is a string because this is expected by JSON.parse
         * if no conversion can happen simply skip parsing and progress without
         * null results in typeof messageData === 'object'
         * undefined can't happen due to how it is invoked internally
         */
        if (typeof messageData !== 'string') {
            return messageData;
        }
        // Try to convert string into JSON
        let parsedMessageData = null;
        try {
            parsedMessageData = JSON.parse(messageData);
        }
        catch (error) {
            // Simply ignore message content with corrupt JSON (#security)
            if (isDevMode()) {
                console.error('[POST MESSAGE SERVICE] Error while parsing string as JSON.');
            }
        }
        return parsedMessageData;
    }
    /**
     * Register the event listener for receiving post messages (#security) (#perfmatters)
     */
    setupEventListener() {
        this.angularZone.runOutsideAngular(() => {
            this.windowRef.nativeWindow.addEventListener('message', this.handlePostMessageEventFn);
        });
    }
    /**
     * Remove the event listener for receiving post messages (#security) (#perfmatters)
     */
    cleanupEventListener() {
        this.angularZone.runOutsideAngular(() => {
            this.windowRef.nativeWindow.removeEventListener('message', this.handlePostMessageEventFn);
        });
    }
}
DBPostMessageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPostMessageService, deps: [{ token: DBWindowRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
DBPostMessageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPostMessageService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPostMessageService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: DBWindowRef }, { type: i0.NgZone }]; } });

/**
 * Post Message Service Factory
 *
 * @param parentPostMessageService - Parent post message service (if exists)
 * @param                 windowRef                - Window Ref dependency
 * @param                      ngZone                   - Angular zone dependency
 * @returns                                 - Post message service instance
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function postMessageServiceFactory(parentPostMessageService, windowRef, ngZone) {
    return parentPostMessageService || new DBPostMessageService(windowRef, ngZone);
}
/**
 * DB Post Message Module
 */
class DBPostMessageModule {
}
DBPostMessageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPostMessageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBPostMessageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBPostMessageModule, imports: [DBWindowRefModule] });
DBPostMessageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPostMessageModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBPostMessageService],
                DBWindowRef,
                NgZone,
            ],
            provide: DBPostMessageService,
            useFactory: postMessageServiceFactory,
        },
    ], imports: [DBWindowRefModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBPostMessageModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DBWindowRefModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBPostMessageService],
                                DBWindowRef,
                                NgZone,
                            ],
                            provide: DBPostMessageService,
                            useFactory: postMessageServiceFactory,
                        },
                    ],
                }]
        }] });

/**
 * Helper Service to handle scrolling
 */
class DBScrollService {
    /**
     * Constructor
     */
    constructor(router, windowRef) {
        this.scrollPositionChangesSource = new Subject();
        this.scrollPositionChanges = this.scrollPositionChangesSource.asObservable();
        this.windowRef = windowRef;
        this.router = router;
        this.routerSubscription = null;
    }
    /**
     * Scroll to the top of the page on router change
     */
    enableScrollToTopOnRouteChange(scrollOptions = {}) {
        this.routerSubscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
            this.scrollToTop(scrollOptions);
        });
    }
    /**
     * Remove subscription to scroll to top
     */
    disableScrollToTopOnRouteChange() {
        this.routerSubscription.unsubscribe();
        this.routerSubscription = null;
    }
    /**
     * Scroll to the top of the page
     */
    scrollToTop(scrollOptions = {}) {
        this.scroll(null, this.getAllScrollOptions(scrollOptions));
    }
    /**
     * Scroll to the defined Element
     */
    scrollToElement(scrollElement, scrollOptions = {}) {
        this.scroll(scrollElement, this.getAllScrollOptions(scrollOptions));
    }
    /**
     * Scroll to the first invalid form control, and set the focus on its input element
     */
    scrollToFirstInvalidFormControl(scrollOptions = {}) {
        // Get the first invalid form control, based on the order in the DOM
        let element = this.windowRef.nativeBody
            .querySelector('.ng-invalid[formcontrolname], .ng-formcontrol.ng-invalid');
        if (!element) {
            return;
        }
        // If the element is invisible, take the nearest one
        // Inspired by <https://stackoverflow.com/questions/19669786/check-if-element-is-visible-in-dom>; this means we can avoid using
        // getComputedStyle() and thus prevent a (possibly bad) layout flush (#perfmatters)
        if (element.offsetParent === null) {
            element = element.previousElementSibling || element.parentElement;
        }
        // Apply new scroll position
        this.scroll(element, this.getAllScrollOptions(scrollOptions));
        if (!scrollOptions.ignoreFocus) {
            // Set focus to the first input element
            const firstFocusableElement = element.querySelector('input, select, textarea');
            if (!firstFocusableElement) {
                return;
            }
            firstFocusableElement.focus();
        }
    }
    /**
     * Scroll to the given position
     */
    scroll(element, scrollOptions) {
        // Do scroll
        const position = element ? this.getElementOffsetTop(element, scrollOptions.scrollContainer) : 0;
        const actualPosition = position <= scrollOptions.offsetTop ? position : position - scrollOptions.offsetTop;
        scrollOptions.scrollContainer.scrollTop = actualPosition;
        this.scrollPositionChangesSource.next(actualPosition);
    }
    /**
     * Get all scroll options (merge)
     */
    getAllScrollOptions(scrollOptions) {
        return Object.assign({
            offsetTop: 24,
            // By default, the 'document.activeElement' is the scrollable element, aka the <html> tag
            // See <https://www.w3schools.com/jsref/prop_element_scrolltop.asp>
            // inspired by GitHub issue (https://github.com/nuxt/nuxt.js/issues/2512) to fix scrollContainer on safari
            scrollContainer: document.scrollingElement || document.documentElement,
        }, scrollOptions);
    }
    getElementOffsetTop(element, scrollContainerElement) {
        let top = 0;
        let scrollElement = element;
        do {
            top += scrollElement.offsetTop;
            scrollElement = scrollElement.offsetParent; // offsetParent of body returns 'null'
        } while (scrollElement && scrollElement !== scrollContainerElement);
        return top;
    }
}
DBScrollService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBScrollService, deps: [{ token: i1$2.Router }, { token: DBWindowRef }], target: i0.ɵɵFactoryTarget.Injectable });
DBScrollService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBScrollService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBScrollService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: DBWindowRef }]; } });

/**
 *
 * @param parentScrollService - Parent scroll service (if exists)
 * @param router - Angular Router
 * @param windowRef - Window Ref
 * @returns DBScrollService - Scroll service instance ( from parent if exists, otherwise new )
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function scrollServiceFactory(parentScrollService, router, windowRef) {
    return parentScrollService || new DBScrollService(router, windowRef);
}
/**
 * DB System Detection Module
 */
class DBScrollModule {
}
DBScrollModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBScrollModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBScrollModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBScrollModule, imports: [DBWindowRefModule,
        RouterModule], exports: [RouterModule] });
DBScrollModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBScrollModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBScrollService],
                Router,
                DBWindowRef,
            ],
            provide: DBScrollService,
            useFactory: scrollServiceFactory,
        },
    ], imports: [DBWindowRefModule,
        RouterModule, RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBScrollModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        RouterModule,
                    ],
                    imports: [
                        DBWindowRefModule,
                        RouterModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBScrollService],
                                Router,
                                DBWindowRef,
                            ],
                            provide: DBScrollService,
                            useFactory: scrollServiceFactory,
                        },
                    ],
                }]
        }] });

/**
 * DB Session Service
 */
class DBSessionService {
    constructor() {
        const interactionEvents = ['click', 'keydown', 'mousewheel', 'mousemove', 'touchmove'];
        this.combinedEvents = merge(...interactionEvents.map((eventName) => fromEvent(document, eventName)));
    }
    /**
     * @deprecated
     * Provides an observable event on completion of the timeout time,
     * user has not at all interacted in that duration
     * @param timeOutTime - Process Time Out duration in Minutes.
     * @returns Observable when timeout occurs
     */
    timeOut(timeOutTime) {
        if (isDevMode()) {
            console.warn('DBSessionService.timeOut is deprecated! Use onTimeOut instead and pass arguments in milliseconds!');
        }
        timeOutTime *= 60000;
        return this.onTimeOut(timeOutTime);
    }
    /**
     * Initiates the Time Out Method.
     * @param timeOutTime - Process Time Out duration in milli seconds.
     * @returns Observable when timeout occurs
     */
    onTimeOut(timeOutTime) {
        return this.combinedEvents.pipe(debounceTime(timeOutTime));
    }
    /**
     * @deprecated
     * Processes the interactions on the document
     * @param idleTime - ignore the document events for the given time in minutes.
     * @returns Observable of audited time of interactions
     */
    interactions(idleTime) {
        if (isDevMode()) {
            console.warn('DBSessionService.interactions is deprecated! Use onUserActive instead and pass arguments in milliseconds!');
        }
        idleTime *= 60000;
        return this.onUserActive(idleTime);
    }
    /**
     * Processes the User actions on the document and provides an observable at equal intervals of defined time
     * @param idleTime - ignore the document events for the given time in milli seconds.
     * @returns Observable of audited time of interactions
     */
    onUserActive(idleTime) {
        return this.combinedEvents.pipe(auditTime(idleTime));
    }
    /**
     * Provides a warning observable to show that User is idle since a long duration
     * @param warningTime in milli seconds
     * @returns Observable of a warning time
     */
    onWarning(warningTime) {
        return this.combinedEvents.pipe(debounceTime(warningTime));
    }
}
DBSessionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSessionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBSessionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSessionService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSessionService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * Window Ref Service Factory
 *
 * @param parentSessionService - Parent session service (if exists)
 * @returns                             - Session service instance
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function sessionServiceFactory(parentSessionService) {
    return parentSessionService || new DBSessionService();
}
/**
 * DB Session Module
 */
class DBSessionModule {
}
DBSessionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSessionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBSessionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBSessionModule });
DBSessionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSessionModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBSessionService],
            ],
            provide: DBSessionService,
            useFactory: sessionServiceFactory,
        },
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBSessionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBSessionService],
                            ],
                            provide: DBSessionService,
                            useFactory: sessionServiceFactory,
                        },
                    ],
                }]
        }] });

/**
 * DB Handover Service
 */
class DBHandoverService {
    /**
     * Constructor
     */
    constructor(router, location, windowRef) {
        this.router = router;
        this.location = location;
        this.windowRef = windowRef;
        this.data = {};
    }
    /**
     * Extract all query params
     */
    extractQueryParams(queryParamHandovers) {
        // Parse current URL
        const parsedUrl = this.getCurrentUrl();
        // Extract query parameters
        queryParamHandovers
            .forEach((queryParamHandover) => {
            this.extractQueryParam(parsedUrl, queryParamHandover);
        });
        // Update URL
        this.updateCurrentUrl(parsedUrl);
    }
    /**
     * Get current URL
     *
     * @returns - Parsed URL
     */
    getCurrentUrl() {
        // Retrieve current URL (without leading slash)
        // TODO: Add compatibility for non-hash-based routing
        const currentUrl = this.windowRef.nativeWindow.location.hash.substring(1);
        // Parse URL
        return this.router.parseUrl(currentUrl);
    }
    /**
     * Extract query param
     *
     * @param parsedUrl - Parsed URL
     * @param options   - Options
     */
    extractQueryParam(parsedUrl, options) {
        // Read query param
        if (!parsedUrl.queryParams[options.name]) {
            return;
        }
        const queryParamValue = options.parse
            ? options.parse(parsedUrl.queryParams[options.name])
            : parsedUrl.queryParams[options.name];
        // Validate, and save query param value if valid
        if (!options.validate || options.validate(queryParamValue)) {
            this.data[options.name] = queryParamValue;
        }
        // Cleanup query aprams (always, even if invalid)
        if (options.removeFromUrl) {
            delete parsedUrl.queryParams[options.name];
        }
    }
    /**
     * Update current URL
     *
     * @param parsedUrl - Parsed URL
     */
    updateCurrentUrl(parsedUrl) {
        this.location.replaceState(parsedUrl.toString());
    }
}
DBHandoverService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHandoverService, deps: [{ token: i1$2.Router }, { token: i1.Location }, { token: DBWindowRef }], target: i0.ɵɵFactoryTarget.Injectable });
DBHandoverService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHandoverService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHandoverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i1.Location }, { type: DBWindowRef }]; } });

/**
 * DB Handover Options Token
 */
const DB_HANDOVER_OPTIONS = new InjectionToken('DB_HANDOVER_OPTIONS');

/**
 * Handover initializer factory
 *
 * @param   injector - Injector
 * @returns          - App initializer function
 */
const handoverInitializerFactory = (injector) => {
    // Inject dependencies (using the injector to avoid issues with circular dependencies)
    const handoverService = injector.get(DBHandoverService);
    const queryParamHandovers = injector.get(DB_HANDOVER_OPTIONS, []);
    // Extract all query params
    handoverService.extractQueryParams(queryParamHandovers);
};

/**
 * Handover Service Factory
 */
const handoverServiceFactory = (handoverService, router, location, windowRef) => handoverService || new DBHandoverService(router, location, windowRef);

/**
 * DB Handover Module
 */
class DBHandoverModule {
    /**
     * Customer providers
     */
    static withInitializer(handoverOptions) {
        return {
            ngModule: DBHandoverModule,
            providers: [
                {
                    provide: DB_HANDOVER_OPTIONS,
                    useValue: handoverOptions,
                },
                {
                    deps: [
                        Injector,
                    ],
                    multi: true,
                    provide: APP_INITIALIZER,
                    useFactory: handoverInitializerFactory,
                },
            ],
        };
    }
}
DBHandoverModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHandoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBHandoverModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBHandoverModule, imports: [RouterModule,
        DBWindowRefModule] });
DBHandoverModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHandoverModule, providers: [
        {
            deps: [
                [new Optional(), new SkipSelf(), DBHandoverService],
                Router,
                Location,
                DBWindowRef,
            ],
            provide: DBHandoverService,
            useFactory: handoverServiceFactory,
        },
    ], imports: [RouterModule,
        DBWindowRefModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHandoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        RouterModule,
                        DBWindowRefModule,
                    ],
                    providers: [
                        {
                            deps: [
                                [new Optional(), new SkipSelf(), DBHandoverService],
                                Router,
                                Location,
                                DBWindowRef,
                            ],
                            provide: DBHandoverService,
                            useFactory: handoverServiceFactory,
                        },
                    ],
                }]
        }] });

// FORM COMPONENTS BARREL

/**
 * DB Validatble Directive
 */
class DBValidatableDirective extends DBCoreFormBaseDirective {
    /**
     * Constructor
     *
     * @param changeDetector - Change detector
     */
    constructor(changeDetector) {
        super({
            checkNoChanges: () => {
                // Do nothing (as there is nothing really to do here)
            },
            detach: () => {
                // Do nothing (as there is nothing really to do here)
            },
            detectChanges: () => {
                // Do nothing (as there is nothing really to do here)
            },
            markForCheck: () => {
                // Do nothing (as there is nothing really to do here)
            },
            reattach: () => {
                // Do nothing (as there is nothing really to do here)
            },
        });
    }
}
DBValidatableDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidatableDirective, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
DBValidatableDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBValidatableDirective, selector: "[dbValidatable]", providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DBValidatableDirective),
        },
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidatableDirective, decorators: [{
            type: Directive,
            args: [{
                    providers: [
                        {
                            multi: true,
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DBValidatableDirective),
                        },
                    ],
                    selector: '[dbValidatable]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; } });

/**
 * Abstract Validation Status Component
 */
class DBAbstractValidationStatusComponentDirective {
    /**
     * Constructor
     *
     * @param formGroup       - Parent form group directive
     * @param  changeDetector - Change detector
     */
    constructor(parentFormGroup, changeDetector) {
        this.parentFormGroup = parentFormGroup;
        this.changeDetector = changeDetector;
        this.internalFor = null;
        this.isVisible = false;
        this.subscription = null;
    }
    /**
     * Name / path to the form control to show the validation error messages for
     */
    set for(formControlName) {
        this.internalFor = formControlName;
        if (this.parentFormGroup) {
            this.wireUpControl(); // Repeated setup on changes
        }
    }
    get for() {
        return this.internalFor || '';
    }
    /**
     * (optional) parentForm is necessary if the parent is a FormArray.
     * In this case the FormGroupDirective does not show the correct parent form control.
     */
    set parentForm(parent) {
        this.internalParentForm = parent;
        if (this.parentFormGroup) {
            this.wireUpControl(); // Repeated setup on changes
        }
    }
    get parentForm() {
        return this.internalParentForm;
    }
    /**
     * On Init
     */
    ngOnInit() {
        this.wireUpControl(); // Initial setup
    }
    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.cleanUp();
    }
    /**
     * Clean up (#perfmatters)
     */
    cleanUp() {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }
}
DBAbstractValidationStatusComponentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAbstractValidationStatusComponentDirective, deps: [{ token: i1$3.FormGroupDirective }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
DBAbstractValidationStatusComponentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBAbstractValidationStatusComponentDirective, inputs: { for: "for", parentForm: "parentForm" }, host: { properties: { "class.is-visible": "this.isVisible" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBAbstractValidationStatusComponentDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i1$3.FormGroupDirective }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { for: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], isVisible: [{
                type: HostBinding,
                args: ['class.is-visible']
            }] } });

/**
 * DB Validation Errors Component
 */
class DBValidationErrorsComponent extends DBAbstractValidationStatusComponentDirective {
    /**
     * Constructor
     *
     * @param formGroup      - Parent form group directive
     * @param  changeDetector - Change detector
     */
    constructor(formGroup, changeDetector) {
        super(formGroup, changeDetector);
        this.messages = [];
        this.currentError = null;
    }
    /**
     * Check if the given message should be visible in the UI, based on the current validation errors
     *
     * @param message - Validation error message object
     */
    isMessageVisible(message) {
        // return this.currentError !== null ? message.rule === this.currentError : false;
        return this.currentError === null ? false : message.rule === this.currentError;
    }
    /**
     * Message identifier (used as trackby function) (#perfmatters)
     *
     * @param                    index - Index of the element
     * @param item  - Element itself
     * @returns                          - Unique value, in this case the name of the validation rule
     */
    identifyMessage(index, item) {
        return item.rule;
    }
    /**
     * Wire up this validation errors component to the corresponding form control
     */
    wireUpControl() {
        if (!this.parentFormGroup || !this.parentFormGroup.control) {
            return;
        }
        // Get control
        if (this.parentForm) {
            // parentForm is necessary if the parent is a FormArray.
            // In this case the FormGroupDirective does not show the correct parent form control.
            this.control = this.parentForm.get(this.for);
        }
        else {
            this.control = this.parentFormGroup.control.get(this.for);
        }
        if (this.control) {
            // Set initial visibility state and current error
            this.currentError = this.getControlValidationError();
            this.isVisible = this.control.touched;
            // Continue to update the current error
            this.cleanUp();
            this.subscription = this.control.statusChanges
                .subscribe((status) => {
                this.currentError = this.getControlValidationError();
                this.changeDetector.detectChanges();
            });
            // Continue to update the visibility state
            // Here, we monkey-patch Angular-specific form control functions because an observable for touch state changes does not exist (yet)
            // Inspired by <https://github.com/angular/angular/issues/17736#issuecomment-310812368>
            // eslint-disable  prefer-arrow/prefer-arrow-functions, @typescript-eslint/no-this-alias
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const thisReference = this;
            const originalMarkAsTouched = this.control.markAsTouched;
            this.control.markAsTouched = function () {
                originalMarkAsTouched.apply(this, arguments);
                thisReference.isVisible = true;
                thisReference.changeDetector.markForCheck();
            };
            const originalMarkAsUntouched = this.control.markAsUntouched;
            this.control.markAsUntouched = function () {
                originalMarkAsUntouched.apply(this, arguments);
                thisReference.isVisible = false;
                thisReference.changeDetector.markForCheck();
            };
            // eslint-enable  prefer-arrow/prefer-arrow-functions, @typescript-eslint/no-this-alias
        }
    }
    /**
     * Get validation error of current control
     *
     * @returns - Validation rule producing the error, or null if none exists
     */
    getControlValidationError() {
        return this.control.status === 'INVALID' ? Object.keys(this.control.errors)[0] : null;
    }
}
DBValidationErrorsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidationErrorsComponent, deps: [{ token: i1$3.FormGroupDirective, skipSelf: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBValidationErrorsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBValidationErrorsComponent, selector: "db-validation-errors", inputs: { messages: "messages" }, usesInheritance: true, ngImport: i0, template: "<div class=\"db-validation-errors__message\"\r\n    *ngFor=\"let message of messages; trackBy: identifyMessage\"\r\n    [class.is-visible]=\"isMessageVisible( message )\"\r\n    [attr.aria-hidden]=\"!isMessageVisible( message )\">\r\n  {{ message.message }}\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidationErrorsComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-validation-errors', template: "<div class=\"db-validation-errors__message\"\r\n    *ngFor=\"let message of messages; trackBy: identifyMessage\"\r\n    [class.is-visible]=\"isMessageVisible( message )\"\r\n    [attr.aria-hidden]=\"!isMessageVisible( message )\">\r\n  {{ message.message }}\r\n</div>\r\n" }]
        }], ctorParameters: function () {
        return [{ type: i1$3.FormGroupDirective, decorators: [{
                        type: SkipSelf
                    }] }, { type: i0.ChangeDetectorRef }];
    }, propDecorators: { messages: [{
                type: Input
            }] } });

/**
 * DB Validation Success Component
 */
class DBValidationSuccessComponent extends DBAbstractValidationStatusComponentDirective {
    /**
     * Constructor
     *
     * @param formGroup       - Parent form group directive
     * @param  changeDetector - Change detector
     */
    constructor(formGroup, changeDetector) {
        super(formGroup, changeDetector);
        /**
         * Success message
         */
        this.message = null;
        this.message = '';
    }
    /**
     * Wire up this validation success component to the corresponding form control
     */
    wireUpControl() {
        if (!this.parentFormGroup || !this.parentFormGroup.control) {
            return;
        }
        // Get control
        this.control = this.parentFormGroup.control.get(this.for);
        if (this.control) {
            // Set initial visibility state
            this.isVisible = this.control.valid;
            // Continue to update the current success
            this.cleanUp();
            this.subscription = this.control.statusChanges.subscribe((status) => {
                this.isVisible = this.control.valid;
                this.changeDetector.markForCheck();
            });
        }
    }
}
DBValidationSuccessComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidationSuccessComponent, deps: [{ token: i1$3.FormGroupDirective, skipSelf: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBValidationSuccessComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBValidationSuccessComponent, selector: "db-validation-success", inputs: { message: "message", srMessage: "srMessage" }, usesInheritance: true, ngImport: i0, template: "<div [attr.aria-hidden]=\"!isVisible\">\r\n  <db-icon class=\"db-validation-success__icon\" size=\"20px\" color=\"#5DA564\" aria-hidden=\"true\">check</db-icon>\r\n  <span *ngIf=\"srMessage\" class=\"sr-only\">{{ srMessage }}</span>\r\n  <div class=\"db-validation-success__message\"\r\n      *ngIf=\"message\">\r\n    {{ message }}\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidationSuccessComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-validation-success', template: "<div [attr.aria-hidden]=\"!isVisible\">\r\n  <db-icon class=\"db-validation-success__icon\" size=\"20px\" color=\"#5DA564\" aria-hidden=\"true\">check</db-icon>\r\n  <span *ngIf=\"srMessage\" class=\"sr-only\">{{ srMessage }}</span>\r\n  <div class=\"db-validation-success__message\"\r\n      *ngIf=\"message\">\r\n    {{ message }}\r\n  </div>\r\n</div>\r\n" }]
        }], ctorParameters: function () {
        return [{ type: i1$3.FormGroupDirective, decorators: [{
                        type: SkipSelf
                    }] }, { type: i0.ChangeDetectorRef }];
    }, propDecorators: { message: [{
                type: Input
            }], srMessage: [{
                type: Input
            }] } });

/**
 * DB Validation Module
 */
class DBValidationModule {
}
DBValidationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBValidationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBValidationModule, declarations: [DBValidatableDirective,
        DBValidationErrorsComponent,
        DBValidationSuccessComponent], imports: [CommonModule,
        DBIconModule], exports: [DBValidatableDirective,
        DBValidationErrorsComponent,
        DBValidationSuccessComponent] });
DBValidationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidationModule, imports: [CommonModule,
        DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBValidationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBValidatableDirective,
                        DBValidationErrorsComponent,
                        DBValidationSuccessComponent,
                    ],
                    exports: [
                        DBValidatableDirective,
                        DBValidationErrorsComponent,
                        DBValidationSuccessComponent,
                    ],
                    imports: [
                        CommonModule,
                        DBIconModule,
                    ],
                }]
        }] });

/**
 * A collection of utilities for various validation purposes
 * @dynamic
 */
class DBValidatorsUtil {
    /**
     * Chain multiple validators / validation rules. The behaviour is pretty similar to the one of Validator.compose(), yet the chain is
     * failing fast (quick) on validation errors. (pure function)
     *
     * @param validators - List of validator functions
     * @returns                   - Composed (chained) validator function
     */
    static chain(validators) {
        // Composed validator function
        return (formControl) => {
            // Call each validator, fail fast on validation error
            const numberOfValidators = validators.length;
            let finalValidationResult = null;
            for (let i = 0; i < numberOfValidators; i++) {
                const validationResult = validators[i](formControl);
                if (validationResult !== null) {
                    finalValidationResult = validationResult;
                    break; // Fail fast (#perfmatters)
                }
            }
            return finalValidationResult;
        };
    }
    /**
     * Check whether at least one of the given validators runs green
     *
     * @param   validators - List of validator functions
     * @returns            - Validator function
     */
    static or(validators) {
        // Composed validator function
        return (formControl) => {
            // Call each validator, fail fast on validation error
            const numberOfValidators = validators.length;
            let finalValidationResult = null;
            for (let i = 0; i < numberOfValidators; i++) {
                const validationResult = validators[i](formControl);
                if (validationResult === null) {
                    finalValidationResult = validationResult;
                    break; // Fail fast (#perfmatters)
                }
                if (validationResult !== null && finalValidationResult === null) {
                    finalValidationResult = validationResult;
                }
            }
            return finalValidationResult;
        };
    }
    /**
     * Mark a whole form (and all its children) as dirty. (pure, recursive function)
     * Implementation inspired by <https://github.com/angular/angular/issues/11774>.
     * Optionally the parameter onlyDirty can be provided to mark only fields already containing a value.
     * This can be used to enable validation messages when re-entering a partly filled page including saved invalid inputs.
     *
     * @param AbstractControl abstractControl - Abstract control, probably always a FormGroup
     * @param boolean onlyDirty - if only fields containing a value should be marked.
     */
    static markFormAsDirtyAndTouched(abstractControl, onlyDirty) {
        if (!onlyDirty
            || !((typeof abstractControl.value === 'undefined' || abstractControl.value === null)
                || (typeof abstractControl.value === 'string' && abstractControl.value.length === 0))) {
            // Mark as dirty & touched
            abstractControl.markAsDirty({
                onlySelf: true,
            });
            abstractControl.markAsTouched({
                onlySelf: true,
            });
        }
        // Also apply state changes to all children (recursively)
        if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
            Object.keys(abstractControl.controls).forEach((key) => {
                DBValidatorsUtil.markFormAsDirtyAndTouched(abstractControl.controls[key], onlyDirty);
            });
        }
    }
    /**
     * Mark a whole form (and all its children) as pristine and untouched. (pure, recursive function)
     *
     * @param AbstractControl abstractControl - Abstract control (probably always a FormGroup)
     */
    static markFormAsPristineAndUntouched(abstractControl) {
        // Mark as pristine & untouched
        abstractControl.markAsPristine({
            onlySelf: true,
        });
        abstractControl.markAsUntouched({
            onlySelf: true,
        });
        // Also apply state changes to all children (recursively)
        if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
            Object.keys(abstractControl.controls).forEach((key) => {
                DBValidatorsUtil.markFormAsPristineAndUntouched(abstractControl.controls[key]);
            });
        }
    }
}

/**
 * CheckboxGroup Required validator
 * @dynamic
 */
class DBCheckboxGroupRequiredValidator {
    /**
     * Validates a number of Checkboxes to be checked
     *
     * @param expectedNumberOfItemsToBeChecked: Number of items which should be checked
     */
    static validate(expectedNumberOfItemsToBeChecked) {
        return (formControl) => {
            const validCheckboxes = formControl.value.filter(item => item.model === true);
            if (validCheckboxes !== undefined && expectedNumberOfItemsToBeChecked <= validCheckboxes.length
                && expectedNumberOfItemsToBeChecked <= formControl.value.length) {
                return null;
            }
            else {
                return {
                    checkboxgrouprequired: {
                        actualValue: formControl.value,
                        expectedValue: true,
                    },
                };
            }
        };
    }
}

/**
 * Exact Length Validator
 * @dynamic
 */
class DBExactLengthValidator {
    static validate(length) {
        return (formControl) => {
            // Skip if the value is empty (implementation inspired by the internal Angular one)
            if (formControl.value === null || formControl.value === undefined || formControl.value.toString().length === 0) {
                return null;
            }
            else if (formControl.value.toString().length === length) {
                return null;
            }
            else {
                return {
                    exactlength: {
                        actualLength: formControl.value.toString().length,
                        requiredLength: length,
                    },
                };
            }
        };
    }
}

/**
 * Maximum Length Validator
 * @dynamic
 */
class DBMaxLengthValidator {
    static validate(length) {
        return Validators.maxLength(length); // Use the Angular validation implementation
    }
}

/**
 * DB Max Number Validator
 * @dynamic
 */
class DBMaxNumberValidator {
    static validate(maxNumber) {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value === '') {
                return null;
            }
            if (Number(formControl.value) <= maxNumber) {
                return null;
            }
            else {
                return {
                    maxnumber: {
                        actualNumber: formControl.value,
                        maxNumber,
                    },
                };
            }
        };
    }
}

/**
 * Minimum Length Validator
 * @dynamic
 */
class DBMinLengthValidator {
    static validate(length) {
        return Validators.minLength(length); // Use the Angular validation implementation
    }
}

/**
 * DB Min Number Validator
 * @dynamic
 */
class DBMinNumberValidator {
    static validate(minNumber) {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value === '') {
                return null;
            }
            // Replace comma with dots to account for german number input "xx,xx" -> "xx.xx"
            const parsedValue = `${formControl.value}`.replace(',', '.');
            if (Number(parsedValue) >= minNumber) {
                return null;
            }
            else {
                return {
                    minnumber: {
                        actualNumber: formControl.value,
                        minNumber,
                    },
                };
            }
        };
    }
}

/**
 * Number Range validator
 * @dynamic
 */
class DBNumberRangeValidator {
    static validate(minNumber, maxNumber) {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value === '') {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBMinNumberValidator.validate(minNumber),
                DBMaxNumberValidator.validate(maxNumber),
            ])(formControl);
        };
    }
}

/**
 * Number Step validator
 * @dynamic
 */
class DBNumberStepValidator {
    static validate(step) {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value === '') {
                return null;
            }
            const isValid = formControl.value % 1 ?
                ((formControl.value * 100) % (step * 100)) === 0 : formControl.value % step === 0; // To support bigDecimal
            if (isValid) {
                return null;
            }
            else {
                return {
                    numberstep: {
                        actualNumber: formControl.value,
                        step
                    }
                };
            }
        };
    }
}

/**
 * Pattern Validator
 * @dynamic
 */
class DBPatternValidator {
    static validate(pattern) {
        return Validators.pattern(pattern); // Use the Angular validation implementation
    }
}

/**
 * Required validator, optionally with a value to compare against
 * @dynamic
 */
class DBRequiredValidator {
    static validate(expectedValue) {
        if (expectedValue) {
            return (formControl) => {
                if (formControl.value === expectedValue) {
                    return null;
                }
                else {
                    return {
                        required: {
                            actualValue: formControl.value,
                            expectedValue,
                        },
                    };
                }
            };
        }
        else {
            return Validators.required; // Use the Angular validation implementation
        }
    }
}

/**
 * Number validator
 * @dynamic
 */
class DBNumberValidator {
    static validate(withNumberFormatter = false, withNDecimalPlaces = false) {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value === '') {
                return null;
            }
            let regex = new RegExp(/^-?\d+(?:[\.\,]\d*)?$/); // default positive or negative number with or without decimal places
            if (withNumberFormatter && withNDecimalPlaces) {
                // number with at least 1 decimal place to use with numberFormatter
                regex = new RegExp(/^-?\d+[\,\.]{1}(\d)+?$/);
            }
            else if (!withNumberFormatter && withNDecimalPlaces) {
                // Require min. 1 decimal place
                regex = new RegExp(/^-?\d+[\.\,]{1}\d*?$/);
            }
            else if (withNumberFormatter) {
                // number with exact 2 decimal when decimal places are entered
                regex = new RegExp(/^-?\d+(?:[\.\,]\d{2})?$/);
            }
            if (regex.test(formControl.value)) {
                return null;
            }
            else {
                return {
                    number: {
                        reason: 'This field must contain a valid number.',
                    },
                };
            }
        };
    }
}

/**
 * Account number validator
 * @dynamic
 */
class DBAccountNumberValidator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBNumberValidator.validate(),
                DBExactLengthValidator.validate(7),
                DBAccountNumberValidator.validateAccountNumber()
            ])(formControl);
        };
    }
    /**
     * Validate Account number
     */
    static validateAccountNumber() {
        return (formControl) => {
            const customerNumber = formControl.value;
            const a1 = customerNumber.charAt(0) * 1;
            const a2 = customerNumber.charAt(1) * 2 > 9 ? customerNumber.charAt(1) * 2 - 9 : customerNumber.charAt(1) * 2;
            const a3 = customerNumber.charAt(2) * 1;
            const a4 = customerNumber.charAt(3) * 2 > 9 ? customerNumber.charAt(3) * 2 - 9 : customerNumber.charAt(3) * 2;
            const a5 = customerNumber.charAt(4) * 1;
            const a6 = customerNumber.charAt(5) * 2 > 9 ? customerNumber.charAt(5) * 2 - 9 : customerNumber.charAt(5) * 2;
            const a7 = customerNumber.charAt(6) * 1;
            const valid1 = a1 + a2 + a3 + a4 + a5 + a6 + a7;
            let valid2;
            if (Math.round((a1 + a2 + a3 + a4 + a5 + a6) / 10) * 10 < (a1 + a2 + a3 + a4 + a5 + a6)) {
                valid2 = Math.round((a1 + a2 + a3 + a4 + a5 + a6) / 10) * 10 + 10;
            }
            else {
                valid2 = Math.round((a1 + a2 + a3 + a4 + a5 + a6) / 10) * 10;
            }
            // Return validation result
            if (valid1 === valid2) {
                return null;
            }
            else {
                return {
                    accountnumber: {
                        reason: 'The account number is invalid.'
                    },
                };
            }
        };
    }
}

/**
 * Max Age validator
 * @dynamic
 */
class DBMaxAgeValidator {
    static validate(maxAge) {
        return (formControl) => {
            const dateOfBirth = formControl.value;
            // Skip if the date is not entered or is empty
            if (dateOfBirth === null || dateOfBirth === undefined || dateOfBirth.length === 0) {
                return null;
            }
            const formatStr = 'dd.MM.yyyy'; // date format allowed
            try {
                const date = parse(dateOfBirth, formatStr, new Date());
                const age = differenceInYears(new Date(), date);
                if (age <= maxAge) {
                    return null;
                }
                else {
                    return {
                        maxage: {
                            maxAge,
                            reason: `Age must be equals to or less than ${maxAge}.`,
                        },
                    };
                }
            }
            catch (_a) {
                return {
                    maxage: {
                        reason: `${formControl.value} is not a valid date, or does not have the format "${formatStr}".`,
                    },
                };
            }
        };
    }
}

/**
 * Min Age validator
 * @dynamic
 */
class DBMinAgeValidator {
    static validate(minAge) {
        return (formControl) => {
            const dateOfBirth = formControl.value;
            // Skip if the date is not entered or is empty
            if (dateOfBirth === null || dateOfBirth === undefined || dateOfBirth.length === 0) {
                return null;
            }
            const formatStr = 'dd.MM.yyyy'; // date format allowed
            try {
                const date = parse(dateOfBirth, formatStr, new Date());
                const age = differenceInYears(new Date(), date);
                if (age >= minAge) {
                    return null;
                }
                else {
                    return {
                        minage: {
                            minAge,
                            reason: `Age must be equals to or greater than ${minAge}.`,
                        },
                    };
                }
            }
            catch (_a) {
                return {
                    minage: {
                        reason: `${formControl.value} is not a valid date, or does not have the format "${formatStr}".`,
                    },
                };
            }
        };
    }
}

/**
 * Age Range Validator
 * @dynamic
 */
class DBAgeRangeValidator {
    static validate(minAge, maxAge) {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBMinAgeValidator.validate(minAge),
                DBMaxAgeValidator.validate(maxAge),
            ])(formControl);
        };
    }
}

/**
 * DB Bank Code Validator
 * @dynamic
 */
class DBBankCodeValidator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBNumberValidator.validate(),
                DBExactLengthValidator.validate(8)
            ])(formControl);
        };
    }
}

/**
 * Branch number validator
 * @dynamic
 */
class DBBranchNumberValidator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBNumberValidator.validate(),
                DBExactLengthValidator.validate(3)
            ])(formControl);
        };
    }
}

/**
 * City validator
 * @dynamic
 */
class DBCityValidator {
    static validate() {
        return (formControl) => DBValidatorsUtil.chain([
            DBMaxLengthValidator.validate(28),
            DBPatternValidator.validate(/^(?!.*[ .\-/]$)[A-Za-z\u00C0-\u017F \-.]*(\s?[/][A-Za-z\u00C0-\u017F \-.]+)?(\s?\([A-Za-z\u00C0-\u017F \-.]+\))?$/),
        ])(formControl);
    }
}

/**
 * Corporate tax number validator is developed to validate corporate tax number based upon modified 11s, 2s and 11s procedure
 * algorithms logics.
 * A valid corporate tax number(German tax number) structure should be a combination of below field:
 * four-digit federal tax office number (often only 2 digits are given on official documents)
 * District number (all states except Nordrhein-Westfalen: 3 digits, Nordrhein-Westfalen: 4 digits)
 * Distinguishing number (all states except Nordrhein-Westfalen: 4 digits, Nordrhein-Westfalen: 3 digits)
 * one-digit check digit
 * @dynamic
 */
class DBCorporateTaxNumberValidator {
    static validate(minLength, maxLength) {
        return (formControl) => DBValidatorsUtil.chain([
            DBMinLengthValidator.validate(minLength ? minLength : 10),
            DBMaxLengthValidator.validate(maxLength ? maxLength : 13),
            DBPatternValidator.validate('[0-9 ]+'),
            DBCorporateTaxNumberValidator.validateProcedures()
        ])(formControl);
    }
    /**
     * ValidatesingleDigit function is created to extract Single-digit multiplication result
     * (If multiplication result has two digits, then last digit + 1).
     */
    static validateSingleDigit(val) {
        if (val > 9) {
            const lastDigit = val % 10;
            return lastDigit + 1;
        }
        else {
            return (val - 1) % 9 + 1;
        }
    }
    /**
     * extractSingleDigit function is created to extract Single-digit post multiplication result of corporate
     * tax number and summand (If multiplication result has two digits, then last digit) else return single digit as it is.
     */
    static extractSingleDigit(val) {
        if (val > 9) {
            const lastDigit = val % 10;
            return lastDigit;
        }
        else {
            return val;
        }
    }
    /**
     * crossSum function is created to extract single digit post crossSum until it is a single digit.
     */
    static crossSum(val) {
        return (val - 1) % 9 + 1;
    }
    /*
    * In the modified 11s procedure, the individual digits are first multiplied by the country-specific factors.
    For the result of each multiplication, a check is now performed to determine whether the multiplication result has two digits.
    If this is the case, only the last digit of the multiplication result is used for the subsequent calculation and one is added to it.
    The cross sum is formed from the individual - now always one-digit - multiplication results.
    The check digit is the difference between the cross sum and the next highest number divisible by 10.
    * Bundsland(federal state) follows modified 11s procedure is Rheinland-Pfalz
    */
    static modified11sProcedure(formControl) {
        let valid = false;
        formControl.value = formControl.value.replace(/\s/g, '');
        const taxNumberLength = formControl.value.length;
        const procedure = 'modified11sProcedure';
        const taxNumberArray = [...(formControl.value.slice(0, -1))];
        const factorModified11s = this.getProcedureFactors(taxNumberArray, procedure);
        const sumOneDigitMultiplication = taxNumberArray.reduce((sum, val, i) => sum +
            this.validateSingleDigit(val * factorModified11s[0][i]), 0);
        const nearestRounded = Math.ceil(sumOneDigitMultiplication / 10) * 10;
        const diff = nearestRounded - sumOneDigitMultiplication;
        const lastDigit = formControl.value.substr(taxNumberLength - 1);
        if (diff === Number(lastDigit)) {
            valid = true;
            return valid;
        }
        else {
            return valid;
        }
    }
    /*
    * In the modified 2s procedure, First, a summand is added according to the digit of the tax number in the German tax number format.
      If the sum has more than one digit, only the last digit is taken over.
      The individual sums are then multiplied by a factor corresponding to the digit.
      The cross sum is now drawn from the respective products until it is a single digit.
      The individual one-digit cross sums are added together.
      The difference between the sum of the one-digit checksums and the next higher number divisible by 10 is the check digit.
  
    * Bundsland(federal state) follows modified 2s procedure is (Baden-Württemberg; Hessen; Schleswig-Holstein)
    */
    static twoSProcedure(formControl) {
        let valid = false;
        formControl.value = formControl.value.replace(/\s/g, '');
        const taxNumberLength = formControl.value.length;
        const procedure = '2sProcedure';
        const taxNumberArray = [...(formControl.value.slice(0, -1))];
        const factorsAndSummand = this.getProcedureFactors(taxNumberArray, procedure);
        const multArray = [];
        for (let factor = 0; factor < Math.min(taxNumberArray.length, factorsAndSummand[1].length, factorsAndSummand[0].length); factor++) {
            multArray[factor] = this.extractSingleDigit(Number(taxNumberArray[factor]) + factorsAndSummand[1][factor]);
            multArray[factor] = this.crossSum(multArray[factor] * factorsAndSummand[0][factor]);
        }
        const sumMultArray = multArray.reduce((acc, current) => acc + current, 0);
        const nearestRounded = Math.ceil(sumMultArray / 10) * 10;
        const diff = Math.abs(nearestRounded - sumMultArray);
        const lastDigit = formControl.value.substr(taxNumberLength - 1);
        if (diff === Number(lastDigit)) {
            valid = true;
            return valid;
        }
        else {
            return valid;
        }
    }
    /**
     *  getProcedureFactors function is used to get associated factors and summand defined against bundsland (federal states)
     *  with appended zero at the beginning of the factor and summand array based upon user entered corporate tax number length.
     */
    static getProcedureFactors(corporateTaxNumber, procedure) {
        const finalFactor = [];
        switch (procedure) {
            case ('11sProcedure'):
                const factorOne11sProcedure = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
                const factorTwo11sProcedure = [4, 3, 2, 7, 6, 5, 4, 3, 2];
                const factorThree11sProcedure = [2, 9, 8, 7, 6, 5, 4, 3, 2];
                const factorFour11sProcedure = [3, 2, 1, 7, 6, 5, 4, 3, 2, 1];
                const factorFive11sProcedure = [7, 6, 5, 8, 4, 3, 2];
                if ((corporateTaxNumber.length > factorOne11sProcedure.length) || (corporateTaxNumber.length > factorTwo11sProcedure.length) ||
                    (corporateTaxNumber.length > factorThree11sProcedure.length) || (corporateTaxNumber.length > factorFour11sProcedure.length) ||
                    (corporateTaxNumber.length > factorFive11sProcedure.length)) {
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(factorOne11sProcedure, corporateTaxNumber.length));
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(factorTwo11sProcedure, corporateTaxNumber.length));
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(factorThree11sProcedure, corporateTaxNumber.length));
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(factorFour11sProcedure, corporateTaxNumber.length));
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(factorFive11sProcedure, corporateTaxNumber.length));
                }
                break;
            case ('2sProcedure'):
                const factor2sProcedure = [512, 256, 128, 64, 32, 16, 8, 4, 2];
                const summand2sProcedure = [9, 8, 7, 6, 5, 4, 3, 2, 1];
                if (corporateTaxNumber.length > factor2sProcedure.length) {
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(factor2sProcedure, corporateTaxNumber.length));
                }
                else {
                    finalFactor.push(factor2sProcedure);
                }
                if (corporateTaxNumber.length > summand2sProcedure.length) {
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(summand2sProcedure, corporateTaxNumber.length));
                }
                else {
                    finalFactor.push(summand2sProcedure);
                }
                break;
            default:
                const modified11sFactor = [1, 2, 1, 2, 1, 2, 1, 2, 1];
                if (corporateTaxNumber.length > modified11sFactor.length) {
                    finalFactor.push(this.prefillZerosBasedOnTaxNumber(modified11sFactor, corporateTaxNumber.length));
                }
                else {
                    finalFactor.push(modified11sFactor);
                }
        }
        return finalFactor;
    }
    /**
     *  prefillZerosBasedOnTaxNumber function is used to append zero at the beginning of the factor array &
     *  Summand array(i.e. modified 11s, 2s and 11s procedure) based upon corporate tax number length.
     */
    static prefillZerosBasedOnTaxNumber(factor, num) {
        const zerosToAdd = num - factor.length;
        for (let iFactor = 0; iFactor < zerosToAdd; iFactor++) {
            factor.unshift(0);
        }
        return factor;
    }
    /*
    * In the 11s procedure, First, a multiplication with a country-specific factor according to the place of the tax number
    * in the German tax number format takes place.
      * The individual products are summed up.
    * Case 1:	For all states participating in the 11 procedure with the exception of Nordrhein-Westfalen:
      * Unless the sum of the products is already divisible by 11 (in which case the check digit is 0), the check digit
    * is the difference between the sum
    * of the products and the next number divisible by 11.
    * Case 2:	For Nordrhein-Westfalen:
      * The sum of the products is divided by 11.
      * The remainder, which cannot be divided by 11, is the check digit.
  
    * Bundsland(federal state) follows 11s procedure is (Bayern, Berlin, Brandenburg, Bremen, Hamburg, Mecklenburg-Vorpommern,
      Niedersachsen, Nordrhein-Westfalen, Saarland, Sachsen, Sachsen-Anhalt, Thüringen)
    */
    static elevenSProcedure(formControl) {
        let valid = false;
        formControl.value = formControl.value.replace(/\s/g, '');
        const taxNumberLength = formControl.value.length;
        const taxNumberArray = [...(formControl.value.slice(0, -1))];
        const procedure = '11sProcedure';
        const factorsList = this.getProcedureFactors(taxNumberArray, procedure);
        const finalStatus = [];
        for (const factor in factorsList) {
            if (factorsList.hasOwnProperty(factor)) {
                const multArray = factorsList[factor].map((e, iteFactor) => e * taxNumberArray[iteFactor])
                    .reduce((val, sum) => {
                    const finalSum = (val + sum);
                    return (finalSum);
                }, 0);
                const nextRounded = 11 * Math.ceil(multArray / 11);
                const lastRounded = 11 * Math.floor(multArray / 11);
                const diffNextRounded = Math.abs(nextRounded - multArray);
                const diffLastRounded = Math.abs(lastRounded - multArray);
                const lastDigit = formControl.value.substr(taxNumberLength - 1);
                if ((diffNextRounded === Number(lastDigit)) || (diffLastRounded === Number(lastDigit))) {
                    valid = true;
                    finalStatus.push(valid);
                }
                else {
                    valid = false;
                    finalStatus.push(valid);
                }
            }
        }
        if (finalStatus.some(found => found === true)) {
            return valid = true;
        }
        else {
            return valid = false;
        }
    }
    /*
    * ValidateProcedures function is created to validate corporate tax number(german tax number) against all 3 algorithms defined if either
    * of the algorithms return true that means corporate tax number is valid in this case null will be retuned else a validation message
    * invalid corporate tax number will be displayed.
    */
    static validateProcedures() {
        let modified11sresult = false;
        let twoSresult = false;
        let elevenSresult = false;
        return (formControl) => {
            modified11sresult = this.modified11sProcedure(formControl);
            twoSresult = this.twoSProcedure(formControl);
            elevenSresult = this.elevenSProcedure(formControl);
            if (modified11sresult || twoSresult || elevenSresult) {
                return null;
            }
            else {
                return {
                    taxnumber: {
                        reason: 'The corporate tax number is invalid.'
                    },
                };
            }
        };
    }
}

/**
 * Credit card Validator
 * @dynamic
 */
class DBCreditCardNumberValidator {
    /***
     * Validate credit card number
     */
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBNumberValidator.validate(),
                DBExactLengthValidator.validate(16),
                DBCreditCardNumberValidator.validateChecksum()
            ])(formControl);
        };
    }
    /**
     * Validate the credit card number by checksum
     *
     * Inspired by: https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s20.html
     */
    static validateChecksum() {
        return (formControl) => {
            const cardNumberLength = formControl.value.length;
            const checksum = formControl.value
                .split('')
                // Map to array of numbers to run luhn algorithm
                .map((digit) => +digit)
                // Run Luhn algorithm to validate the credit card number
                .reduce((acc, value, index) => {
                // This vastly abstracts the luhn algorithm. Check the above link for better understanding
                const digit = (index + (cardNumberLength % 2 ? 0 : 1)) % 2
                    ? (value * 2) - (value > 4 ? 9 : 0)
                    : value;
                return (acc += digit);
            }, 0);
            // Check the result
            if (checksum % 10 === 0) {
                return null;
            }
            else {
                return {
                    creditcard: {
                        reason: 'The credit card number is invalid.',
                    },
                };
            }
        };
    }
}

/**
 * Converts momentjs tokens to Unicode standard.
 */
const momentToUnicodeFormat = (format) => format.replace(/D/g, 'd').replace(/Y/g, 'y');

/**
 * Future Date Validator
 *
 * @dynamic
 */
class DBFutureDateValidator {
    /**
     * Validate whether the given date is in the future (excludes the current date)
     *
     * @param   formatStr - Date format
     * @returns        - Validator function
     */
    static validate(formatStr = 'dd.MM.yyyy') {
        return (formControl) => {
            // Skip if the date is not entered or is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            // Keep compatibility to moment, which was used before date-fns
            formatStr = momentToUnicodeFormat(formatStr);
            try {
                let date = parse(formControl.value, formatStr, new Date());
                // When no day parameter was specified, also accept current month as valid input
                if (formatStr.indexOf('d') === -1) {
                    date = addMonths(date, 1);
                }
                if (isFuture(date)) {
                    return null;
                }
                else {
                    return {
                        futuredate: {
                            reason: `${formControl.value} is a past date.`,
                        },
                    };
                }
            }
            catch (_a) {
                return {
                    futuredate: {
                        reason: `${formControl.value} is not a valid date, or does not have the format "${formatStr}".`,
                    },
                };
            }
        };
    }
}

/**
 * Past Date validator
 *
 * @dynamic
 */
class DBPastDateValidator {
    static validate(formatStr = 'dd.MM.yyyy', pastDateLimit) {
        return (formControl) => {
            // Skip if the date is not entered or is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            // Keep compatibility to moment, which was used before date-fns
            formatStr = momentToUnicodeFormat(formatStr);
            try {
                // Only compare date, but not time
                const date = parse(formControl.value, formatStr, new Date().setHours(0, 0, 0, 0));
                const isValidPastDate = isBefore(date, new Date().setHours(0, 0, 0, 0));
                if (isValidPastDate) {
                    if (pastDateLimit) {
                        // Check if the date is not less than or equal to given date limit (if provided)
                        const limitDate = parse(pastDateLimit, formatStr, new Date().setHours(0, 0, 0, 0));
                        const isValidLimitDate = isAfter(date, limitDate);
                        if (!isValidLimitDate) {
                            return {
                                pastdatelimit: {
                                    reason: `${formControl.value} should be after the provided date limit ${pastDateLimit}.`,
                                },
                            };
                        }
                    }
                    return null;
                }
                else {
                    return {
                        pastdate: {
                            reason: `${formControl.value} is a future date.`,
                        },
                    };
                }
            }
            catch (_a) {
                return {
                    pastdate: {
                        reason: `${formControl.value} is not a valid date, or does not have the format "${formatStr}".`,
                    },
                };
            }
        };
    }
}

/**
 * Same Date Validator
 *
 * @dynamic
 */
class DBSameDateValidator {
    /**
     * Validate whether the given date is today
     *
     * @param   formatStr - Date format
     * @returns        - Validator function
     */
    static validate(formatStr = 'dd.MM.yyyy') {
        return (formControl) => {
            // Skip if the date is not entered or is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            // Keep compatibility to moment, which was used before date-fns
            formatStr = momentToUnicodeFormat(formatStr);
            try {
                const date = parse(formControl.value, formatStr, new Date());
                let isSame = false;
                // Day parameter can be omitted, then we only check for same month
                if (formatStr.indexOf('d') > -1) {
                    isSame = isSameDay(new Date(), date);
                }
                else {
                    isSame = isSameMonth(new Date(), date);
                }
                // Return validity
                if (isSame) {
                    return null;
                }
                else {
                    return {
                        samedate: {
                            reason: `${formControl.value} must be the current date.`,
                        },
                    };
                }
            }
            catch (_a) {
                return {
                    samedate: {
                        reason: `${formControl.value} is not a valid date, or does not have the format "${formatStr}".`,
                    },
                };
            }
        };
    }
}

/**
 * Date Range Validator
 *
 * @dynamic
 */
class DBDateRangeValidator {
    /**
     * Validate whether the date is in the given date range
     *
     * @param   allowPast   - Allow date to be in the past
     * @param   allowToday  - Allow date to be today
     * @param   allowFuture - Allow date to be in the future
     * @returns             - Validator function
     */
    static validate(allowPast, allowToday, allowFuture) {
        return (formControl) => {
            // Skip if the date is not entered or is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            // Check validity
            return DBValidatorsUtil.or([
                ...(allowPast ? [DBPastDateValidator.validate()] : []),
                ...(allowToday ? [DBSameDateValidator.validate()] : []),
                ...(allowFuture ? [DBFutureDateValidator.validate()] : []),
            ])(formControl);
        };
    }
}

/**
 * Date validator
 * @dynamic
 */
class DBDateValidator {
    static validate(formatStr = 'dd.MM.yyyy') {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            // Keep compatibility to moment, which was used before date-fns
            formatStr = momentToUnicodeFormat(formatStr);
            try {
                const date = parse(formControl.value, formatStr, new Date());
                const formattedDate = format(date, formatStr);
                if (formControl.value === formattedDate) {
                    return null;
                }
                else {
                    return {
                        date: {
                            reason: `${formControl.value} does not have the format "${formatStr}".`,
                        },
                    };
                }
            }
            catch (_a) {
                return {
                    date: {
                        reason: `${formControl.value} is not a valid date, or does not have the format "${formatStr}".`,
                    },
                };
            }
        };
    }
}

/**
 * E-Mail validator
 * @dynamic
 */
class DBEmailValidator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            // We split the regex to make it easier to read & understand -- while also making the linter happy ;)
            const regex = new RegExp([
                '^[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*',
                '@',
                '(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+(?:[a-zA-Z-]*[a-zA-Z]){2,}?$',
            ].join(''));
            return DBValidatorsUtil.chain([
                DBPatternValidator.validate(regex)
            ])(formControl);
        };
    }
}

/**
 * DB IBAN Bank Identifier Validator
 * @dynamic
 */
class DBIbanBankIdentifierValidator {
    /**
     * Validate Bank Identifier
     * @param bankIdentifiers
     */
    static validate(bankIdentifiers) {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            const bankIdentifierInIban = formControl.value.substr(4); // exclude country code and check digit
            // checks value at specified offset position, if found then returns true
            const result = bankIdentifiers.filter((bankIdentifier) => {
                const bankId = bankIdentifier.bankId; // value to match
                const fromOffset = bankIdentifier.offset; // index / position
                if (bankId.length > 1) { // if bank identifier passed
                    const toOffset = bankId.length + fromOffset;
                    return bankIdentifierInIban.substring(fromOffset, toOffset) === bankId;
                }
                else {
                    return bankIdentifierInIban.charAt(fromOffset) === bankId; // check if match found offset
                }
            }).length > 0;
            if (result) { // if bank identifier is found
                return null;
            }
            else {
                return {
                    ibanbankidentifier: {
                        reason: 'Bank Identifier does not match.'
                    }
                };
            }
        };
    }
}

/**
 * DB IBAN Country Code Validator
 * @dynamic
 */
class DBIbanCountryCodeValidator {
    /**
     * Validates if IBAN belongs to one of the specified countries
     */
    static validate(countryCodes) {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            // get first two characters from entered IBAN ie. CountryCode
            const country = formControl.value.substr(0, 2);
            // check if the country code is present in IBAN
            const result = countryCodes.filter((countryCode) => countryCode === country).length === 1;
            if (result) { // if country is found
                return null;
            }
            else {
                return {
                    ibancountrycode: {
                        reason: 'The IBAN Country Code is not valid.',
                    },
                };
            }
        };
    }
}

/**
 * IBAN Validator
 * @dynamic
 */
/* eslint-disable */
class DBIbanValidator {
    /* eslint-enable */
    /***
     * Validate International Bank Number ( IBAN )
     */
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBMinLengthValidator.validate(15),
                DBMaxLengthValidator.validate(34),
                DBPatternValidator.validate(/^[A-Z]{2}[0-9A-Z]*$/),
                DBIbanValidator.validateIBANCheckDigit()
            ])(formControl);
        };
    }
    /**
     * Validate IBAN Check Digit
     * 3rd and 4th digits are check digit in IBAN
     */
    static validateIBANCheckDigit() {
        return (formControl) => {
            // Step 1: Rearrange (move country code and check digit to the end)
            const rearrangedIban = `${formControl.value.substr(4)}${formControl.value.substr(0, 4)}`;
            // Step 2: Convert characters into numbers (A = 10, B = 11, ...)
            const numericIban = rearrangedIban
                .split('')
                .map((ibanChar) => isNaN(parseInt(ibanChar, 10))
                ? (ibanChar.charCodeAt(0) - 55).toString()
                : ibanChar)
                .join('');
            // Step 3: Verify checksum
            let checksum = numericIban;
            while (checksum.length > 2) {
                checksum = `${parseInt(checksum.substring(0, 10), 10) % 97}${checksum.substring(10)}`;
            }
            const hasCountryCode = this.IBAN_CODE_LENGTH.hasOwnProperty(formControl.value.substr(0, 2));
            const countryIbanLength = this.IBAN_CODE_LENGTH[formControl.value.substr(0, 2)];
            const isIbanValid = hasCountryCode ? parseInt(checksum, 10) % 97 === 1 &&
                (formControl.value.length === countryIbanLength) : false;
            // Return validation result
            if (isIbanValid) {
                return null;
            }
            else {
                return {
                    iban: {
                        reason: 'The IBAN is invalid.',
                    },
                };
            }
        };
    }
}
// IBAN country code and length
DBIbanValidator.IBAN_CODE_LENGTH = {
    AL: 28,
    AD: 24,
    AT: 20,
    AZ: 28,
    BH: 22,
    BY: 28,
    BE: 16,
    BA: 20,
    BR: 29,
    BG: 22,
    CR: 22,
    HR: 21,
    CY: 28,
    CZ: 24,
    DK: 18,
    DO: 28,
    EG: 29,
    SV: 28,
    EE: 20,
    FO: 18,
    FI: 18,
    FR: 27,
    GE: 22,
    DE: 22,
    GI: 23,
    GR: 27,
    GL: 18,
    GT: 28,
    VA: 22,
    HU: 28,
    IS: 26,
    IQ: 23,
    IE: 22,
    IL: 23,
    IT: 27,
    JO: 30,
    KZ: 20,
    XK: 20,
    KW: 30,
    LV: 21,
    LB: 28,
    LY: 25,
    LI: 21,
    LT: 20,
    LU: 20,
    MT: 31,
    MR: 27,
    MU: 30,
    MD: 24,
    MC: 27,
    ME: 22,
    NL: 18,
    MK: 19,
    NO: 15,
    PK: 24,
    PS: 29,
    PL: 28,
    PT: 25,
    QA: 29,
    RO: 24,
    LC: 32,
    SM: 27,
    ST: 25,
    SA: 24,
    RS: 22,
    SC: 31,
    SK: 24,
    SI: 19,
    ES: 24,
    SD: 18,
    SE: 24,
    CH: 21,
    TL: 23,
    TN: 24,
    TR: 26,
    UA: 29,
    AE: 23,
    GB: 22,
    VG: 24
};

/**
 * ISO 8859-1 validator
 * @dynamic
 */
class DBIso88591Validator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            const regex = new RegExp('^[\u0020-\u007e\u00a0-\u00ff]+$');
            return DBValidatorsUtil.chain([
                DBPatternValidator.validate(regex)
            ])(formControl);
        };
    }
}

/**
 * Person name validator
 * @dynamic
 */
class DBPersonNameValidator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBMinLengthValidator.validate(2),
                DBMaxLengthValidator.validate(35),
                DBPatternValidator.validate(/^[A-Za-z\u00C0-\u017F \-.']+$/),
            ])(formControl);
        };
    }
}

/**
 * Phone validator
 * @dynamic
 */
class DBPhoneValidator {
    static validate(minLength, maxLength) {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBMinLengthValidator.validate(minLength ? minLength : 9),
                DBMaxLengthValidator.validate(maxLength ? maxLength : 23),
                DBPatternValidator.validate(/^[0-9 /()+-]*$/),
            ])(formControl);
        };
    }
    static validatePhoneWithCountryCode(countryCodes, minLength, maxLength) {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBPatternValidator.validate(/^\+?\d+$/),
                ...(countryCodes ?
                    [
                        DBPhoneValidator.validateCountryCode(countryCodes),
                        DBPhoneValidator.validateDigitAfterCountryCode(countryCodes)
                    ] :
                    [
                        DBPhoneValidator.validateUniversalExitCode()
                    ]),
                DBMinLengthValidator.validate(minLength ? minLength : 12),
                DBMaxLengthValidator.validate(maxLength ? maxLength : 19)
            ])(formControl);
        };
    }
    /**
     * Checks if value contains the valid country code
     * @param codes
     */
    static validateCountryCode(codes) {
        return (formControl) => {
            const countryCodeValid = codes.some(value => formControl.value.startsWith(value));
            if (countryCodeValid) {
                return null;
            }
            else {
                return {
                    countrycode: {
                        reason: 'The Country Code is invalid.'
                    }
                };
            }
        };
    }
    /**
     * Checks if value has no zero following the given valid country code.
     * @param codes
     */
    static validateDigitAfterCountryCode(codes) {
        return (formControl) => {
            const hasAZeroAfterCountryCode = codes.some(value => formControl.value.indexOf(value) !== -1
                && formControl.value.length > formControl.value.indexOf(value) + value.length
                && formControl.value.charAt(formControl.value.indexOf(value) + value.length) === '0');
            // Return validation result
            if (!hasAZeroAfterCountryCode) {
                return null;
            }
            else {
                return {
                    countryCodeZero: {
                        reason: 'There shouldn\'t be a zero after the country code.'
                    }
                };
            }
        };
    }
    /**
     * Checks if value has a universal exit code
     */
    static validateUniversalExitCode() {
        return (formControl) => {
            if (!formControl.value.startsWith('+')) {
                return {
                    exitcode: {
                        reason: 'Invalid phone number exit code.',
                    }
                };
            }
            else {
                return null;
            }
        };
    }
}

/**
 * Validates the PIN
 *
 * Requirements:
 *   1. PIN  must be of length of 5
 *   2. Only numbers and characters are allowed
 *   3. Should not allow ascending/descending number/character order
 *   4. should not allow same number or character
 * @dynamic
 */
class DBPinValidator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBExactLengthValidator.validate(5),
                DBPatternValidator.validate(/^[a-zA-Z0-9]*$/),
                DBPinValidator.validatePinSequence()
            ])(formControl);
        };
    }
    /**
     * PIN validator
     * @dynamic
     */
    static validatePinSequence() {
        return (formControl) => {
            // ascending or descending sequence
            if (DBPinValidator.validateNoSequentialCharacters(formControl.value)) {
                return {
                    complexity: {
                        reason: 'The PIN is invalid.',
                    },
                };
            }
            return null;
        };
    }
    /**
     * This method is to check number or characters are in ascending or descending order
     * @param string rawInput
     * @returns boolean
     */
    static validateNoSequentialCharacters(rawInput) {
        // get string array of pin
        const stringArray = rawInput.split('');
        // get char codes of each entered char in PIN
        const charCodeArray = stringArray.map((item) => item.charCodeAt(0));
        // store the first diff so you know ascending or descending order to check
        const diffDirection = charCodeArray[1] - charCodeArray[0];
        // returns true if all the characters are same
        return new Set(charCodeArray).size === 1 ||
            // if all characters are same, it forms a sequence
            // if there are two numbers with a difference other than 'diffDirection', then not in sequence(eg.11112)
            // also if there are two numbers with diff > 1, i.e. not consecutive, hence not in sequence
            !charCodeArray.some((item, index) => {
                const diff = charCodeArray[index + 1] - item;
                return charCodeArray[index + 1] === undefined ? false : Math.abs(diff) > 1 ? true : diff !== diffDirection;
            });
    }
}

/**
 * Validate street with House Number validator
 * @dynamic
 */
class DBStreetWithHouseNumberValidator {
    static validate() {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value === '') {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBMaxLengthValidator.validate(54),
                DBPatternValidator.validate(/^(?!.*[ .\-/]$)[\u00C0-\u017Fa-zA-Z0-9. \-]+ ([0-9]+\s?[a-zA-Z]?)([/]{0,2}\s?[\u00C0-\u017Fa-zA-Z]+\s)?([\-/]?[0-9]*\s?[a-zA-Z]?)?$/),
            ])(formControl);
        };
    }
}

/**
 * DB Sub Account Number Validator
 * @dynamic
 */
class DBSubAccountNumberValidator {
    static validate() {
        return (formControl) => {
            // Skip if the value is empty
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBNumberValidator.validate(),
                DBExactLengthValidator.validate(2)
            ])(formControl);
        };
    }
}

/**
 * Validates the German Tax Identification Number
 *
 * Requirements:
 *   1. First digit must not be a zero
 *   2. The pattern must be valid
 *   3. The check digit must be valid
 * @dynamic
 */
class DBTinValidator {
    static validate(foreignCountry = false) {
        return (formControl) => {
            const taxId = formControl.value;
            // Skip if the value is empty
            if (taxId === null || taxId === undefined || taxId.length === 0) {
                return null;
            }
            if (foreignCountry) { // validate for foreign tax identification number
                return DBValidatorsUtil.chain([
                    DBMaxLengthValidator.validate(20),
                    DBPatternValidator.validate(/^[a-z0-9A-Z- /]+$/),
                ])(formControl);
            }
            else {
                // Validate length
                if (taxId.length !== 11) {
                    return DBExactLengthValidator.validate(11)(formControl);
                }
                // Validate first digit
                const hasValidFirstDigit = parseInt(taxId.substr(0, 1), 10) !== 0;
                if (hasValidFirstDigit &&
                    taxId.match(/^\d+$/) &&
                    DBTinValidator.validateTINConsecutivePattern(taxId) &&
                    DBTinValidator.validateTINPattern(taxId) &&
                    DBTinValidator.validateTINCheckDigit(taxId)) {
                    return null;
                }
                else {
                    return {
                        tin: {
                            reason: 'The TIN is invalid.',
                        },
                    };
                }
            }
        };
    }
    /**
     * Validate the check digit of the TIN
     *
     * @param   tin - Tax Identification Number
     * @returns     - Flag, describing whether the check digit is valid
     */
    static validateTINCheckDigit(tin) {
        // Get checksum digit
        const currentChecksumDigit = parseInt(tin.substr(10, 1), 10); // 11th digit is check digit
        // Calculate checksum
        const expectedChecksumDigit = (11 - DBTinValidator.convertTaxIdIntoNumberArray(tin)
            .reduce((checksumDigit, currentDigit) => ((((currentDigit + checksumDigit) % 10) || 10) * 2) % 11, 10)) % 10;
        return currentChecksumDigit === expectedChecksumDigit;
    }
    /**
     * Validate the pattern of the TIN
     *
     * Requirements:
     *   1. No same digit should be in consecutive sequence
     *   2. There must be exactly one duplicate digit (appearing either twice or three times)
     *
     * @param   tin - Tax Identification Number
     * @returns     - Flag, describing whether the pattern is valid
     */
    static validateTINPattern(tin) {
        const tinDigits = DBTinValidator.convertTaxIdIntoNumberArray(tin);
        // Map each digit to the number of its occurrences
        const digitCountMapping = {};
        tinDigits.forEach((digit) => {
            if (digitCountMapping[digit]) {
                digitCountMapping[digit]++;
            }
            else {
                digitCountMapping[digit] = 1;
            }
        });
        // Get the repeated digits
        // Casting needed cause Object.keys() always returns an array of strings instead of numbers
        const repeatedDigits = Object.keys(digitCountMapping)
            .filter((digit) => digitCountMapping[digit] > 1);
        const validRepeatedDigits = repeatedDigits
            .filter((digit) => digitCountMapping[digit] <= 3);
        return repeatedDigits.length === 1 && validRepeatedDigits.length === 1;
    }
    /**
     *  Validate the sequence of TIN digits
     *
     *  IF digits are appearing three times, they must not appear after each other (at least one digit in between)
     *
     * @param  tin  - Tax Identification Number
     * @returns  - Flag, describing digits are consecutive or not
     */
    static validateTINConsecutivePattern(tin) {
        // Pattern to check that if a digit exists three times they're not consecutive
        const consecutiveRegexp = /(\d)\1{2}/g;
        return !(consecutiveRegexp.test(tin.slice(0, -1)));
    }
    /**
     * Converts the TIN into an array of numbers (without the check digit)
     *
     * @param   tin - Tax Identification number
     * @returns     - Array of TIN digits, without the check digit
     */
    static convertTaxIdIntoNumberArray(tin) {
        return tin
            // Remove checksum digit
            .slice(0, -1)
            // Convert string into array
            .split('')
            // Convert digit strings into numbers
            .map((digit) => parseInt(digit, 10));
    }
}

/**
 * VAT-ID validator
 * @dynamic
 */
class DBVatIdValidator {
    static validate() {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            return DBValidatorsUtil.chain([
                DBMinLengthValidator.validate(7),
                DBMaxLengthValidator.validate(14),
                DBPatternValidator.validate(/^[A-Z]{2}[0-9]+$/),
                DBVatIdValidator.validateVatIdCountries()
            ])(formControl);
        };
    }
    /**
     * Validator function to reflect errors for VAT-ID allowed countries.
     */
    static validateVatIdCountries() {
        const allowedVatIdCountries = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES', 'FI', 'FR', 'HR', 'HU', 'IE', 'IT',
            'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'XI'];
        return (formControl) => {
            // Skip if the value is empty or already true
            if (formControl.value === null || formControl.value === undefined || formControl.value.length === 0) {
                return null;
            }
            if (allowedVatIdCountries.indexOf(formControl.value.substring(0, 2)) === -1) {
                return {
                    vatIdCountries: {
                        reason: 'Not allowed VAT-ID country code error.',
                    }
                };
            }
            return null;
        };
    }
}

/**
 * WIN validator
 * @dynamic
 */
class DBWinValidator {
    static validate() {
        return (formControl) => {
            if (formControl.value === null || formControl.value === undefined || formControl.value === '') {
                return null;
            }
            const regex = new RegExp(/^(DE|de|De|dE)[0-9]{9}$/);
            if (regex.test(formControl.value)) {
                return null;
            }
            else {
                return {
                    pattern: {
                        reason: `The valid WIN must start with DE followed by 9 digits.`,
                    },
                };
            }
        };
    }
}

/**
 * Zip code validator
 * @dynamic
 */
/* eslint-disable */
class DBZipValidator {
    static validate(country) {
        return (formControl) => {
            // By default, when country is Germany and user doesn't change the country
            if (country === null || country === undefined || country.length === 0) {
                return DBValidatorsUtil.chain([
                    DBNumberValidator.validate(),
                    DBExactLengthValidator.validate(5)
                ])(formControl);
            }
            else {
                const selectedCountry = this.ZIP_CODE_VALIDATION[country];
                // If the chosen country is European and available in ZIP_CODE_VALIDATION
                if (selectedCountry) {
                    return DBValidatorsUtil.chain([
                        selectedCountry.regex ?
                            DBPatternValidator.validate(selectedCountry.regex) :
                            DBNumberValidator.validate(),
                        DBExactLengthValidator.validate(selectedCountry.length)
                    ])(formControl);
                }
                else {
                    // If the chosen country is non-european and is not available in ZIP_CODE_VALIDATION
                    return DBValidatorsUtil.chain([
                        DBNumberValidator.validate(),
                        DBPatternValidator.validate(/^(?!0+$).{1,10}$/),
                        DBMinLengthValidator.validate(3)
                    ])(formControl);
                }
                // TODO: Add all countries (non-european) in the above stated countries list
            }
        };
    }
}
DBZipValidator.ZIP_CODE_VALIDATION = {
    '003_AL': {
        length: 4
    },
    '006_AD': {
        length: 5,
        regex: /^AD[0-9]{3}$/
    },
    '022_BE': {
        length: 4
    },
    '028_BA': {
        length: 5
    },
    '034_BG': {
        length: 4
    },
    '108_HR': {
        length: 5
    },
    '216_CZ': {
        length: 5
    },
    '242_CY': {
        length: 4
    },
    '042_DK': {
        length: 4
    },
    '051_EE': {
        length: 5
    },
    '055_FI': {
        length: 5
    },
    '056_FR': {
        length: 5
    },
    '044_DE': {
        length: 5
    },
    '069_GB': {
        length: 8,
        regex: /^[A-Za-z0-9\s]{5,8}$/
    },
    '067_GR': {
        length: 5
    },
    '224_HU': {
        length: 4
    },
    '086_IS': {
        length: 3
    },
    '085_IE': {
        length: 8,
        regex: /^[a-z0-9A-Z]{3}\s{1}[a-z0-9A-Z]{4}$/
    },
    '088_IT': {
        length: 5
    },
    '113_LV': {
        length: 4
    },
    '117_LI': {
        length: 4
    },
    '118_LT': {
        length: 5
    },
    '119_LU': {
        length: 4
    },
    '133_MK': {
        length: 4
    },
    '126_MT': {
        length: 8,
        regex: /^[a-zA-Z]{3}\s{1}[0-9]{4}$/
    },
    '138_MC': {
        length: 5,
        regex: /^980[0-9]{2}$/
    },
    '137_MD': {
        length: 7,
        regex: /^MD-[0-9]{4}$/,
    },
    '149_NL': {
        length: 7,
        regex: /^[0-9]{4}\s{1}[a-zA-Z]{2}$/
    },
    '157_NO': {
        length: 4
    },
    '159_AT': {
        length: 4
    },
    '169_PL': {
        length: 5
    },
    '170_PT': {
        length: 7
    },
    '174_RO': {
        length: 6
    },
    '175_RU': {
        length: 6
    },
    '179_SM': {
        length: 5
    },
    '185_RS': {
        length: 5
    },
    '189_SK': {
        length: 5
    },
    '190_SI': {
        length: 4
    },
    '192_ES': {
        length: 5
    },
    '182_SE': {
        length: 5
    },
    '183_CH': {
        length: 4
    },
    '217_TN': {
        length: 4
    },
    '218_TR': {
        length: 5
    },
    '223_UA': {
        length: 5
    },
    '229_VA': {
        length: 5
    },
    '238_BY': {
        length: 6
    },
};

/**
 * Technical validators
 */
/**
 * DB Technical Validators
 * @dynamic
 */
class DBValidators {
}
/**
 * Technical validators
 */
DBValidators.date = DBDateValidator.validate;
DBValidators.dateRange = DBDateRangeValidator.validate;
DBValidators.exactLength = DBExactLengthValidator.validate;
DBValidators.futureDate = DBFutureDateValidator.validate;
DBValidators.maxLength = DBMaxLengthValidator.validate;
DBValidators.maxNumber = DBMaxNumberValidator.validate;
DBValidators.minLength = DBMinLengthValidator.validate;
DBValidators.minNumber = DBMinNumberValidator.validate;
DBValidators.number = DBNumberValidator.validate;
DBValidators.numberRange = DBNumberRangeValidator.validate;
DBValidators.pattern = DBPatternValidator.validate;
DBValidators.required = DBRequiredValidator.validate;
DBValidators.checkboxGroupRequired = DBCheckboxGroupRequiredValidator.validate;
DBValidators.sameDate = DBSameDateValidator.validate;
DBValidators.numberStep = DBNumberStepValidator.validate;
/**
 * Domain validators
 */
DBValidators.accountNumber = DBAccountNumberValidator.validate;
DBValidators.ageRange = DBAgeRangeValidator.validate;
DBValidators.bankCode = DBBankCodeValidator.validate;
DBValidators.branchNumber = DBBranchNumberValidator.validate;
DBValidators.city = DBCityValidator.validate;
DBValidators.creditCard = DBCreditCardNumberValidator.validate;
DBValidators.email = DBEmailValidator.validate;
DBValidators.iban = DBIbanValidator.validate;
DBValidators.ibanCountyCode = DBIbanCountryCodeValidator.validate;
DBValidators.ibanBankIdentifier = DBIbanBankIdentifierValidator.validate;
DBValidators.iso_8859_1 = DBIso88591Validator.validate;
DBValidators.maxAge = DBMaxAgeValidator.validate;
DBValidators.minAge = DBMinAgeValidator.validate;
DBValidators.pastDate = DBPastDateValidator.validate;
DBValidators.personName = DBPersonNameValidator.validate;
DBValidators.phone = DBPhoneValidator.validate;
DBValidators.phoneCountryCode = DBPhoneValidator.validatePhoneWithCountryCode;
DBValidators.pin = DBPinValidator.validate;
DBValidators.streetWithHouseNumber = DBStreetWithHouseNumberValidator.validate;
DBValidators.subAccountNumber = DBSubAccountNumberValidator.validate;
DBValidators.tin = DBTinValidator.validate;
DBValidators.win = DBWinValidator.validate;
DBValidators.zip = DBZipValidator.validate;
DBValidators.vatId = DBVatIdValidator.validate;
DBValidators.corporateTaxNumberValidator = DBCorporateTaxNumberValidator.validate;

/**
 * DB Heading Component
 *
 * Implementaition inspired by <https://github.com/ThePacielloGroup/html5-h>.
 */
class DBHeadingComponent {
    /**
     * Constructor
     *
     * @param elementRef - Element Ref
     * @param renderer   - Renderer
     */
    constructor(elementRef, renderer) {
        this.level = null;
        this.elementRef = elementRef;
        this.renderer = renderer;
    }
    /**
     * On Init
     */
    ngOnInit() {
        if (!this.level) {
            this.level = this.getImplicitHeadingLevel();
        }
        else if (DBHeadingComponent.validHeadingLevels.indexOf(parseInt(this.level.toString(), 10)) === -1) {
            this.level = 0;
        }
    }
    /**
     * Get implicit heading level
     */
    getImplicitHeadingLevel() {
        // Walk up the DOM, counting the level changes
        let currentLevel = 1; // Start with 'h1'
        let currentElement = this.elementRef.nativeElement;
        while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
            if (DBHeadingComponent.sectionElements.indexOf(currentElement.nodeName.toLowerCase()) !== -1) {
                currentLevel++;
            }
            currentElement = currentElement.parentNode;
        }
        // Limit maximum heading level
        if (currentLevel > 6) {
            currentLevel = 0;
        }
        return currentLevel;
    }
}
/**
 * Elements which create a new HTML section, thus affecting heading levels
 * See <https://developer.mozilla.org/de/docs/Web/Guide/HTML/Sections_and_Outlines_of_an_HTML5_document>.
 */
DBHeadingComponent.sectionElements = ['article', 'aside', 'nav', 'section'];
/**
 * Valid heading levels
 */
DBHeadingComponent.validHeadingLevels = [1, 2, 3, 4, 5, 6];
DBHeadingComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHeadingComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
DBHeadingComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBHeadingComponent, selector: "db-heading", inputs: { level: "level" }, ngImport: i0, template: "<!-- Heading text via transclusion, wrapped in a re-usable template -->\r\n<ng-template #ngContentTemplate><ng-content></ng-content></ng-template>\r\n\r\n<!-- Correct template instantiation, based on heading level -->\r\n<ng-container [ngSwitch]=\"level\">\r\n  <p *ngSwitchCase=\"0\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></p>\r\n  <h1 *ngSwitchCase=\"1\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h1>\r\n  <h2 *ngSwitchCase=\"2\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h2>\r\n  <h3 *ngSwitchCase=\"3\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h3>\r\n  <h4 *ngSwitchCase=\"4\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h4>\r\n  <h5 *ngSwitchCase=\"5\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h5>\r\n  <h6 *ngSwitchCase=\"6\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h6>\r\n</ng-container>\r\n", dependencies: [{ kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { kind: "directive", type: i1.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHeadingComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-heading', template: "<!-- Heading text via transclusion, wrapped in a re-usable template -->\r\n<ng-template #ngContentTemplate><ng-content></ng-content></ng-template>\r\n\r\n<!-- Correct template instantiation, based on heading level -->\r\n<ng-container [ngSwitch]=\"level\">\r\n  <p *ngSwitchCase=\"0\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></p>\r\n  <h1 *ngSwitchCase=\"1\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h1>\r\n  <h2 *ngSwitchCase=\"2\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h2>\r\n  <h3 *ngSwitchCase=\"3\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h3>\r\n  <h4 *ngSwitchCase=\"4\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h4>\r\n  <h5 *ngSwitchCase=\"5\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h5>\r\n  <h6 *ngSwitchCase=\"6\"><ng-container *ngTemplateOutlet=\"ngContentTemplate\"></ng-container></h6>\r\n</ng-container>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { level: [{
                type: Input
            }] } });

/**
 * DB Heading Module
 */
class DBHeadingModule {
}
DBHeadingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHeadingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBHeadingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBHeadingModule, declarations: [DBHeadingComponent], imports: [CommonModule], exports: [DBHeadingComponent] });
DBHeadingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHeadingModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBHeadingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        DBHeadingComponent,
                    ],
                    exports: [
                        DBHeadingComponent,
                    ],
                    imports: [
                        CommonModule,
                    ],
                }]
        }] });

class DBDocsPrettyprintPipe {
    transform(value) {
        if (!value) {
            return value;
        }
        return window.PR.prettyPrintOne(value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;'));
    }
}
DBDocsPrettyprintPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPrettyprintPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBDocsPrettyprintPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPrettyprintPipe, name: "dbDocsPrettyprint" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPrettyprintPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbDocsPrettyprint',
                    pure: true,
                }]
        }] });

/**
 * DB Docs Code Component
 */
class DBDocsCodeComponent {
    constructor() {
        this.code = '';
        this.canCopy = true;
    }
    onCopyButtonClick() {
        this.codeSourceElement.nativeElement.select();
        try {
            document.execCommand('copy');
        }
        catch (error) {
            // Do nothing
        }
        window.getSelection().removeAllRanges();
        this.copyButtonElement.nativeElement.focus();
    }
}
DBDocsCodeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsCodeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBDocsCodeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDocsCodeComponent, selector: "db-docs-code", inputs: { code: "code", canCopy: "canCopy" }, viewQueries: [{ propertyName: "codeSourceElement", first: true, predicate: ["codeSource"], descendants: true, read: ElementRef, static: true }, { propertyName: "copyButtonElement", first: true, predicate: ["copyButton"], descendants: true, read: ElementRef }], ngImport: i0, template: "<!-- Single line! -->\r\n<pre class=\"db-docs-code__code\" [innerHTML]=\"code | dbDocsPrettyprint\"></pre>\r\n<db-button *ngIf=\"canCopy\" class=\"db-docs-code__button\" type=\"button\" title=\"Copy to clipboard\" (click)=\"onCopyButtonClick()\" #copyButton>\r\n  <svg focusable=\"false\" viewBox=\"0 0 48 48\">\r\n    <path class=\"object\" d=\"M19,31H8c-0.6,0-1-0.4-1-1s0.4-1,1-1h11c0.6,0,1,0.4,1,1S19.6,31,19,31z\" />\r\n    <path class=\"object\" d=\"M28,24H8c-0.6,0-1-0.4-1-1s0.4-1,1-1h20c0.6,0,1,0.4,1,1S28.6,24,28,24z\" />\r\n    <path class=\"object\" d=\"M28,17H8c-0.6,0-1-0.4-1-1s0.4-1,1-1h20c0.6,0,1,0.4,1,1S28.6,17,28,17z\" />\r\n    <path class=\"object\" d=\"M42,2H18c-1.7,0-3,1.3-3,3v2H6c-1.7,0-3,1.3-3,3v33c0,1.7,1.3,3,3,3h24c1.7,0,3-1.3,3-3v-2h9c1.7,0,3-1.3,3-3\r\n    V5C45,3.3,43.7,2,42,2z M31,43c0,0.6-0.4,1-1,1H6c-0.6,0-1-0.4-1-1V10c0-0.6,0.4-1,1-1h24c0.6,0,1,0.4,1,1V43z M43,38\r\n   c0,0.6-0.4,1-1,1h-9V19h7c0.6,0,1-0.4,1-1s-0.4-1-1-1h-7v-5h7c0.6,0,1-0.4,1-1s-0.4-1-1-1h-7c0-1.7-1.3-3-3-3H17V5c0-0.6,0.4-1,1-1\r\n    h24c0.6,0,1,0.4,1,1V38z\" />\r\n  </svg>\r\n</db-button>\r\n<textarea class=\"db-docs-code__source\" [value]=\"code\" tabindex=\"-1\" #codeSource></textarea>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBButtonComponent, selector: "db-button", inputs: ["type", "design", "size", "arrow", "loading", "iconOnly"] }, { kind: "pipe", type: DBDocsPrettyprintPipe, name: "dbDocsPrettyprint" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsCodeComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-docs-code', template: "<!-- Single line! -->\r\n<pre class=\"db-docs-code__code\" [innerHTML]=\"code | dbDocsPrettyprint\"></pre>\r\n<db-button *ngIf=\"canCopy\" class=\"db-docs-code__button\" type=\"button\" title=\"Copy to clipboard\" (click)=\"onCopyButtonClick()\" #copyButton>\r\n  <svg focusable=\"false\" viewBox=\"0 0 48 48\">\r\n    <path class=\"object\" d=\"M19,31H8c-0.6,0-1-0.4-1-1s0.4-1,1-1h11c0.6,0,1,0.4,1,1S19.6,31,19,31z\" />\r\n    <path class=\"object\" d=\"M28,24H8c-0.6,0-1-0.4-1-1s0.4-1,1-1h20c0.6,0,1,0.4,1,1S28.6,24,28,24z\" />\r\n    <path class=\"object\" d=\"M28,17H8c-0.6,0-1-0.4-1-1s0.4-1,1-1h20c0.6,0,1,0.4,1,1S28.6,17,28,17z\" />\r\n    <path class=\"object\" d=\"M42,2H18c-1.7,0-3,1.3-3,3v2H6c-1.7,0-3,1.3-3,3v33c0,1.7,1.3,3,3,3h24c1.7,0,3-1.3,3-3v-2h9c1.7,0,3-1.3,3-3\r\n    V5C45,3.3,43.7,2,42,2z M31,43c0,0.6-0.4,1-1,1H6c-0.6,0-1-0.4-1-1V10c0-0.6,0.4-1,1-1h24c0.6,0,1,0.4,1,1V43z M43,38\r\n   c0,0.6-0.4,1-1,1h-9V19h7c0.6,0,1-0.4,1-1s-0.4-1-1-1h-7v-5h7c0.6,0,1-0.4,1-1s-0.4-1-1-1h-7c0-1.7-1.3-3-3-3H17V5c0-0.6,0.4-1,1-1\r\n    h24c0.6,0,1,0.4,1,1V38z\" />\r\n  </svg>\r\n</db-button>\r\n<textarea class=\"db-docs-code__source\" [value]=\"code\" tabindex=\"-1\" #codeSource></textarea>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { code: [{
                type: Input
            }], canCopy: [{
                type: Input
            }], codeSourceElement: [{
                type: ViewChild,
                args: ['codeSource', {
                        read: ElementRef, static: true
                    }]
            }], copyButtonElement: [{
                type: ViewChild,
                args: ['copyButton', {
                        read: ElementRef, static: false
                    }]
            }] } });

/* This module contains and exports the db-docs-code component
*  This module has no dependencies and can directly be imported
*/
class DBDocsCodeModule {
}
DBDocsCodeModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsCodeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsCodeModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsCodeModule, declarations: [DBDocsCodeComponent,
        DBDocsPrettyprintPipe], imports: [CommonModule,
        DBButtonModule], exports: [DBDocsCodeComponent,
        DBDocsPrettyprintPipe] });
DBDocsCodeModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsCodeModule, imports: [CommonModule,
        DBButtonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsCodeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        DBButtonModule,
                    ],
                    exports: [
                        DBDocsCodeComponent,
                        DBDocsPrettyprintPipe,
                    ],
                    declarations: [
                        DBDocsCodeComponent,
                        DBDocsPrettyprintPipe,
                    ],
                }]
        }] });

/**
 * DB Docs Example Component
 */
class DBDocsExampleComponent {
    /**
     * Constructor
     */
    constructor() {
        this.title = '';
        this.codeHtml = '';
        this.codeTs = '';
        this.codeScss = '';
        this.codeJson = '';
    }
}
DBDocsExampleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsExampleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBDocsExampleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDocsExampleComponent, selector: "db-docs-example", inputs: { title: "title", codeHtml: "codeHtml", codeTs: "codeTs", codeScss: "codeScss", codeJson: "codeJson" }, ngImport: i0, template: "<div class=\"db-docs-example__header\">\r\n  <h3 class=\"db-docs-example__title\">Example{{ title !== '' ? ': ' + title : '' }}</h3>\r\n</div>\r\n<db-tabs class=\"db-docs-example__tabs\" activeTab=\"example\">\r\n\r\n  <db-tab id=\"example\" title=\"Demo\">\r\n    <div class=\"db-docs-example__demo\">\r\n      <ng-content>\r\n      </ng-content>\r\n    </div>\r\n  </db-tab>\r\n\r\n  <db-tab id=\"code\" class=\"db-docs-example__code-tab\" title=\"Code\">\r\n    <section *ngIf=\"codeHtml !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">HTML</h4>\r\n      <db-docs-code [code]=\"codeHtml\"></db-docs-code>\r\n    </section>\r\n    <section *ngIf=\"codeTs !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">TypeScript</h4>\r\n      <db-docs-code [code]=\"codeTs\"></db-docs-code>\r\n    </section>\r\n    <section *ngIf=\"codeScss !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">SASS</h4>\r\n      <db-docs-code [code]=\"codeScss\"></db-docs-code>\r\n    </section>\r\n    <section *ngIf=\"codeJson !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">JSON</h4>\r\n      <db-docs-code [code]=\"codeJson | json\"></db-docs-code>\r\n    </section>\r\n  </db-tab>\r\n\r\n</db-tabs>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBDocsCodeComponent, selector: "db-docs-code", inputs: ["code", "canCopy"] }, { kind: "component", type: DBTabComponent, selector: "db-tab", inputs: ["title", "icon", "id"] }, { kind: "component", type: DBTabsComponent, selector: "db-tabs", inputs: ["activeTab", "design"], outputs: ["activeTabChange"] }, { kind: "pipe", type: i1.JsonPipe, name: "json" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsExampleComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-docs-example', template: "<div class=\"db-docs-example__header\">\r\n  <h3 class=\"db-docs-example__title\">Example{{ title !== '' ? ': ' + title : '' }}</h3>\r\n</div>\r\n<db-tabs class=\"db-docs-example__tabs\" activeTab=\"example\">\r\n\r\n  <db-tab id=\"example\" title=\"Demo\">\r\n    <div class=\"db-docs-example__demo\">\r\n      <ng-content>\r\n      </ng-content>\r\n    </div>\r\n  </db-tab>\r\n\r\n  <db-tab id=\"code\" class=\"db-docs-example__code-tab\" title=\"Code\">\r\n    <section *ngIf=\"codeHtml !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">HTML</h4>\r\n      <db-docs-code [code]=\"codeHtml\"></db-docs-code>\r\n    </section>\r\n    <section *ngIf=\"codeTs !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">TypeScript</h4>\r\n      <db-docs-code [code]=\"codeTs\"></db-docs-code>\r\n    </section>\r\n    <section *ngIf=\"codeScss !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">SASS</h4>\r\n      <db-docs-code [code]=\"codeScss\"></db-docs-code>\r\n    </section>\r\n    <section *ngIf=\"codeJson !== ''\">\r\n      <h4 class=\"db-docs-font font-weight-bold\">JSON</h4>\r\n      <db-docs-code [code]=\"codeJson | json\"></db-docs-code>\r\n    </section>\r\n  </db-tab>\r\n\r\n</db-tabs>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { title: [{
                type: Input
            }], codeHtml: [{
                type: Input
            }], codeTs: [{
                type: Input
            }], codeScss: [{
                type: Input
            }], codeJson: [{
                type: Input
            }] } });

/* This module contains and exports the db-docs-example component
*  This module has no dependencies and can directly be imported
*/
class DBDocsExampleModule {
}
DBDocsExampleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsExampleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsExampleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsExampleModule, declarations: [DBDocsExampleComponent], imports: [CommonModule,
        DBDocsCodeModule,
        DBTabsModule], exports: [DBDocsExampleComponent] });
DBDocsExampleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsExampleModule, imports: [CommonModule,
        DBDocsCodeModule,
        DBTabsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsExampleModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        DBDocsCodeModule,
                        DBTabsModule,
                    ],
                    exports: [
                        DBDocsExampleComponent,
                    ],
                    declarations: [
                        DBDocsExampleComponent,
                    ],
                }]
        }] });

/**
 * DB Docs Header Component
 */
class DBDocsHeaderNavigationComponent {
    constructor(http) {
        this.http = http;
        this.routes = [];
        this.routesUrl = '';
        this.active = '';
    }
    /**
     * On Init Lifecycle Hook
     */
    ngOnInit() {
        if (this.routes.length < 1 && this.routesUrl.length > 0) {
            this.getRoutes();
        }
    }
    /**
     * After View Init Lifecycle Hook
     */
    ngAfterViewInit() { }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
    }
    /**
     * Fetch routes from remove file
     */
    getRoutes() {
        this.http.get(this.routesUrl).subscribe(data => {
            this.routes = data['routes'];
        });
    }
}
DBDocsHeaderNavigationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderNavigationComponent, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Component });
DBDocsHeaderNavigationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDocsHeaderNavigationComponent, selector: "db-docs-header-navigation", inputs: { routes: "routes", routesUrl: "routesUrl", active: "active" }, ngImport: i0, template: "<div class=\"db-docs-header-navigation\">\r\n  <ul class=\"db-docs-header-navigation__list\">\r\n    <li class=\"db-docs-header-navigation__list__element\"><a href=\"/\"><db-icon>dashboard</db-icon></a></li>\r\n    <li class=\"db-docs-header-navigation__list__element\" *ngFor=\"let route of routes\">\r\n      <a [class.is-active]=\"route.name === active\" [href]=\"route.url\" [target]=\"route.external === true ? '_blank' : '_self'\">{{route.name}}</a>\r\n    </li>\r\n  </ul>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderNavigationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-docs-header-navigation', template: "<div class=\"db-docs-header-navigation\">\r\n  <ul class=\"db-docs-header-navigation__list\">\r\n    <li class=\"db-docs-header-navigation__list__element\"><a href=\"/\"><db-icon>dashboard</db-icon></a></li>\r\n    <li class=\"db-docs-header-navigation__list__element\" *ngFor=\"let route of routes\">\r\n      <a [class.is-active]=\"route.name === active\" [href]=\"route.url\" [target]=\"route.external === true ? '_blank' : '_self'\">{{route.name}}</a>\r\n    </li>\r\n  </ul>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }]; }, propDecorators: { routes: [{
                type: Input
            }], routesUrl: [{
                type: Input
            }], active: [{
                type: Input
            }] } });

/* This module contains and exports the db-docs-header component
*  This module has no dependencies and can directly be imported
*/
class DBDocsHeaderNavigationModule {
}
DBDocsHeaderNavigationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsHeaderNavigationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderNavigationModule, declarations: [DBDocsHeaderNavigationComponent], imports: [CommonModule,
        HttpClientModule,
        DBIconModule], exports: [DBDocsHeaderNavigationComponent] });
DBDocsHeaderNavigationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderNavigationModule, imports: [CommonModule,
        HttpClientModule,
        DBIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        HttpClientModule,
                        DBIconModule
                    ],
                    exports: [
                        DBDocsHeaderNavigationComponent
                    ],
                    declarations: [
                        DBDocsHeaderNavigationComponent
                    ],
                }]
        }] });

class DBDocsService {
    constructor() {
        this.themeSubject = new BehaviorSubject(this.activeTheme);
        this.repositoryUrl = '';
    }
    /**
     * emits active theme changes
     * @param string option - selected option value
     */
    setActiveTheme(option) {
        this.activeTheme = option;
        this.themeSubject.next(option);
    }
    /**
     *
     * @returns BehaviorSubject<string>
     */
    getActiveTheme() {
        return this.themeSubject;
    }
    /**
     * returns the prefix of the active theme
     *
     * @returns string
     */
    getActiveThemePrefix() {
        return this.activeTheme.replace('-theme', '').toUpperCase();
    }
}
DBDocsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DBDocsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return []; } });

/**
 * DB Docs Search Pipe
 */
class DBDocsSearchPipe {
    transform(elements, searchQuery) {
        // Skip if search term is empty
        if (!searchQuery || searchQuery === '') {
            return [];
        }
        // Get search terms
        const searchTerms = searchQuery
            .toLowerCase() // Ignore casing
            .trim() // Remove whitespaces
            .split(/\s+/); // Split query into separate search terms
        // Find
        return elements.filter((element) => searchTerms.every((searchTerm) => element.keywords.find((keyword) => keyword.startsWith(searchTerm))));
    }
}
DBDocsSearchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBDocsSearchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSearchPipe, name: "dbDocsSearch" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSearchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbDocsSearch',
                    pure: true,
                }]
        }] });

/**
 * DB Docs Header Component
 */
class DBDocsHeaderComponent {
    /**
     * Constructor
     */
    constructor(router, changeDetector, viewportService, windowRef, themeService) {
        this.selectedTheme = '';
        this.scope = '';
        this.name = '';
        this.version = '';
        this.elements = [];
        this.isSearching = false;
        this.searchQuery = '';
        this.selectedSearchResultIndex = 0;
        this.internalThemes = [];
        this.navigationToggle = new EventEmitter();
        this.isNavigationVisible = false;
        this.shiftKeyCounter = 0;
        this.doBlockMouseenter = false;
        this.subscriptions = [];
        this.showScrollBar = false;
        this.searchOverlayHeight = '0px';
        this.router = router;
        this.changeDetector = changeDetector;
        this.windowRef = windowRef;
        this.themeService = themeService;
        this.viewportService = viewportService;
        this.isSelectingProduct = false;
        this.frontendLibraries = [];
        this.selectedTheme = (localStorage.getItem('selectedTheme') || '');
    }
    /**
     * Themes
     */
    get themes() {
        return this.internalThemes;
    }
    set themes(themes) {
        this.internalThemes = themes;
        this.onSelectTheme(this.selectedTheme || this.internalThemes[0].value);
    }
    /**
     * Handle global key event
     *
     * @param key - Name of the key
     */
    onWindowKeyup(key) {
        if (key === 'Shift') {
            this.shiftKeyCounter++;
            if (this.shiftKeyCounter === 2) {
                this.searchInputElement.nativeElement.focus();
            }
            setTimeout(() => {
                this.shiftKeyCounter = 0;
            }, 400);
        }
    }
    /**
     * On Init Lifecycle Hook
     */
    ngOnInit() {
        // Hear for router events
        this.subscriptions.push(this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
            // Hide the navigation menu (on mobile)
            this.isNavigationVisible = false;
            this.changeDetector.markForCheck();
        }));
        // Flatten navigation structure for search functionality
        this.elements = [
            ...this.structure.elements,
            ...this.structure.sections.reduce((allElements, section) => [...allElements, ...section.elements], []),
        ];
    }
    /**
     * After View Init Lifecycle Hook
     */
    ngAfterViewInit() {
        this.subscriptions.push(this.searchResultElements.changes.pipe(merge$1(this.viewportService.resizeChanges()))
            .subscribe(() => {
            // Calculate search overlay height
            const wantedHeight = this.searchQuery.length > 0 && this.searchResultElements.length === 0
                ? 180
                : this.searchResultElements.length * 68;
            // ToDo: Remove hardcoded values
            const allowedHeight = window.innerHeight - 86;
            this.showScrollBar = wantedHeight > allowedHeight;
            this.searchOverlayHeight = `${Math.min(wantedHeight, allowedHeight)}px`;
            if (this.searchQuery.length > 0 && this.searchResultElements.length === 0) {
                this.randomElement = this.getRandomElement();
            }
            this.changeDetector.detectChanges();
        }));
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        // Cleanup
        this.subscriptions.map((subscription) => {
            subscription.unsubscribe();
            return null;
        });
        this.subscriptions = null;
    }
    /**
     * Handle search input
     *
     * @param value - Search input value
     */
    onSearchInput(value) {
        this.searchQuery = value;
        this.isSearching = value.length > 0;
        this.selectedSearchResultIndex = 0;
        this.doBlockMouseenter = true;
    }
    /**
     * Handle searhc keydown
     *
     * @param event - Event object
     */
    onSearchKeydown(event) {
        // Navigate through the list of search results
        if (this.searchResultElements.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            // Prevent the arrow down & up keys from moving the input cursor / caret around
            event.preventDefault();
            // Navigate
            if (event.key === 'ArrowDown') {
                this.selectedSearchResultIndex = this.selectedSearchResultIndex + 1 >= this.searchResultElements.length
                    ? 0
                    : this.selectedSearchResultIndex + 1;
            }
            else {
                if (event.key === 'ArrowUp') {
                    this.selectedSearchResultIndex = this.selectedSearchResultIndex === 0
                        ? this.searchResultElements.length - 1
                        : this.selectedSearchResultIndex - 1;
                }
            }
            // Scroll to the selected item so that it is within the visible area
            this.searchResultElements.toArray()[this.selectedSearchResultIndex].nativeElement.scrollIntoViewIfNeeded();
            // Get away ...
        }
        else {
            if (event.key === 'Escape' || event.key === 'Tab') {
                this.onSearchInput('');
                this.searchInputElement.nativeElement.blur();
                // Select (if not focused anyway)
            }
            else if (this.searchResultElements.length > 0 && event.key === 'Enter') {
                this.searchResultElements.toArray()[this.selectedSearchResultIndex].nativeElement.click();
                this.searchResultElements.toArray()[this.selectedSearchResultIndex].nativeElement.blur();
                this.searchInputElement.nativeElement.blur();
            }
        }
    }
    /**
     * Handle search mouseenter
     *
     * @param elementIndex - Index of the currently hovered element
     */
    onSearchMouseenter(elementIndex) {
        if (elementIndex !== this.selectedSearchResultIndex && !this.doBlockMouseenter) {
            this.selectedSearchResultIndex = elementIndex;
        }
        this.doBlockMouseenter = false;
    }
    /**
     * Handle search click
     */
    onSearchClick() {
        this.onSearchInput('');
    }
    /**
     * Handle click on backdrop
     */
    onBackdropClick() {
        this.onSearchInput('');
        this.isSelectingProduct = false;
    }
    /**
     * Handle click on clear button
     */
    onClearButtonClick() {
        this.onSearchInput('');
    }
    /**
     * Check if there are search results
     */
    hasNoSearchResults() {
        return this.searchQuery.length > 0 && (this.searchResultElements || []).length === 0;
    }
    /**
     * Handle click on product link
     */
    onProductClick() {
        this.router.navigateByUrl('');
        // this.isSelectingProduct = !this.isSelectingProduct;
    }
    /**
     * Check if themes are provided
     *
     * @returns - Flag, describing whether themes are provided
     */
    hasThemes() {
        return this.themes.length > 1;
    }
    /**
     * Select a new theme
     *
     * @param option - Selected option (theme)
     */
    onSelectTheme(option) {
        this.selectedTheme = option;
        localStorage.setItem('selectedTheme', option);
        this.themeService.setActiveTheme(option);
        this.themes.forEach((theme) => {
            this.windowRef.nativeWindow.document.documentElement.classList.remove(theme.value);
        });
        this.windowRef.nativeWindow.document.documentElement.classList.add(option);
        this.windowRef.nativeWindow.dispatchEvent(new CustomEvent('resize')); // Force recalculation
    }
    /**
     * Handle click on toggle navigation button
     */
    onToggleNavigationClick() {
        this.isNavigationVisible = !this.isNavigationVisible;
        this.navigationToggle.emit();
    }
    getRandomElement() {
        return this.elements[Math.floor(Math.random() * this.elements.length)];
    }
}
DBDocsHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderComponent, deps: [{ token: i1$2.Router }, { token: i0.ChangeDetectorRef }, { token: DBViewportService }, { token: DBWindowRef }, { token: DBDocsService }], target: i0.ɵɵFactoryTarget.Component });
DBDocsHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDocsHeaderComponent, selector: "db-docs-header", inputs: { scope: "scope", name: "name", version: "version", structure: "structure", themes: "themes" }, outputs: { navigationToggle: "navigationToggle" }, host: { listeners: { "window:keyup": "onWindowKeyup($event.key)" } }, viewQueries: [{ propertyName: "searchInputElement", first: true, predicate: ["searchInput"], descendants: true, read: ElementRef, static: true }, { propertyName: "searchResultElements", predicate: ["searchResultElement"], descendants: true, read: ElementRef }], ngImport: i0, template: "<div class=\"db-docs-header__search-backdrop\" [class.is-visible]=\"isSearching || isSelectingProduct\" (click)=\"onBackdropClick()\"></div>\r\n\r\n<header class=\"db-docs-header__header d-flex flex-row justify-content-between\" [class.is-expanded]=\"isSelectingProduct\">\r\n\r\n  <!-- Mobile Navigation Toggle Button (Burger) -->\r\n  <button type=\"button\" class=\"db-docs-header__burger\" [class.is-open]=\"isNavigationVisible\" (click)=\"onToggleNavigationClick()\">\r\n    <svg width=\"32\" height=\"32\" viewBox=\"0 0 32 32\" class=\"db-docs-header__burger-icon\">\r\n      <g transform=\"translate(0, -6)\">\r\n        <line x1=\"6\" y1=\"16\" x2=\"26\" y2=\"16\" stroke=\"#333\" stroke-width=\"2\" class=\"db-docs-header__burger-icon--top\"></line>\r\n      </g>\r\n      <line x1=\"6\" y1=\"16\" x2=\"26\" y2=\"16\" stroke=\"#333\" stroke-width=\"2\" class=\"db-docs-header__burger-icon--middle\"></line>\r\n      <g transform=\"translate(0, 6)\">\r\n        <line x1=\"6\" y1=\"16\" x2=\"26\" y2=\"16\" stroke=\"#333\" stroke-width=\"2\" class=\"db-docs-header__burger-icon--bottom\"></line>\r\n      </g>\r\n    </svg>\r\n  </button>\r\n\r\n  <!-- Title -->\r\n  <button class=\"db-docs-header__product mr-auto\" (click)=\"onProductClick()\">\r\n    <h1 class=\"db-docs-header__product-title\">\r\n      <span class=\"db-docs-header__product-brand\">\r\n        <svg class=\"db-docs-header__brand\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\"\r\n          x=\"0px\" y=\"0px\" viewBox=\"0 0 125 15\" xml:space=\"preserve\" preserveAspectRatio=\"xMinYMin meet\">\r\n          <path class=\"db-docs-header__brand-path\" d=\"M116.7,1h-1.6v13.3h1.6V1z M123.5,14.3L118.7,9l4.4-4H121l-4.3,4.1l4.6,5.2H123.5z\r\n          M112.5,14.3V8.5 c0-2.3-0.9-3.6-3.4-3.6c-1.4,0-2.3,0.5-3.1,1.6L105.9,5h-1.6c0,0.5,0.1,1.6,0.1,2.4v6.9h1.6V9.7c0-1.7,0.6-3.5,2.6-3.5\r\n          c2,0,2.2,1.4,2.2,3v5.1H112.5z M100.3,9.7c0,1.9-0.2,3.8-2.6,3.8c-1.1,0-2.1-0.6-2.1-1.7c0-1.9,2.4-2.1,3.9-2.1\r\n          C99.7,9.6,99.9,9.6,100.3,9.7 M102,14.3c-0.1-0.4-0.3-1.5-0.3-2.7V8.2c0-2.4-1.2-3.3-3.6-3.3c-1.8,0-3.4,0.7-3.5,2.7h1.6\r\n          c0.1-1.2,0.8-1.6,2-1.6c1.6,0,2.1,0.8,2.1,2.2c0,0.2,0,0.5,0,0.5c-0.3,0-0.6,0-0.9,0c-2.3,0-5.4,0.3-5.4,3.1c0,1.9,1.6,2.8,3.3,2.8\r\n          c1.4,0,2.4-0.4,3.2-1.5c0.1,0.4,0.1,0.9,0.2,1.3H102z M90.7,10.5c0,2.2-1.9,2.4-3.7,2.4h-2.3V8.1h2.9C89.3,8.1,90.7,8.6,90.7,10.5\r\n          M90.2,4.5c0,1.7-1.3,2.3-2.9,2.3h-2.6V2.4h2.9C89,2.4,90.2,2.9,90.2,4.5 M92.6,10.6c0-2-1.2-2.9-3.1-3.3C91.1,6.9,92,6,92,4.3\r\n          C92,1.9,89.8,1,87.7,1H83v13.3h4.5C90,14.3,92.6,13.6,92.6,10.6 M76.2,8.9h-5.2c0.2-1.5,1-2.9,2.8-2.9C75.4,5.9,76.2,7.4,76.2,8.9\r\n          M77.8,10c0-2.7-0.9-5.1-4.1-5.1c-3.1,0-4.5,2.2-4.5,5c0,2.8,1.4,4.7,4.4,4.7c2.2,0,3.8-1,4.1-3.2h-1.6c-0.2,1.3-1.1,2.1-2.4,2.1\r\n          c-1.9,0-2.8-1.6-2.8-3.3V10H77.8z M67.3,14.3V8.5c0-2.3-0.9-3.6-3.4-3.6c-1.3,0-2.3,0.5-3.1,1.6V1h-1.6v13.3h1.6V9.4\r\n          c0-1.7,0.7-3.3,2.6-3.3c2,0,2.2,1.4,2.2,3v5.1H67.3z M57.5,11.2L57.5,11.2l-1.6,0c-0.1,1.3-1,2.2-2.4,2.2c-2.1,0-2.9-2-2.9-3.7\r\n          c0-1.9,0.7-3.8,2.9-3.8c1.3,0,2.2,0.8,2.2,2.1h1.6c-0.2-2.2-1.7-3.3-3.9-3.3c-3.1,0-4.7,2.1-4.7,5c0,2.8,1.6,4.7,4.6,4.7\r\n          C55.7,14.5,57.3,13.4,57.5,11.2 M47.6,11.6c0-1.7-1.4-2.2-2.8-2.6c-0.6-0.2-1.3-0.3-1.9-0.6C42.3,8.2,42,7.8,42,7.3\r\n          c0-1,1.1-1.4,1.9-1.4c1.1,0,1.8,0.6,1.9,1.7h1.6c-0.1-2-1.6-2.8-3.5-2.8c-1.7,0-3.6,0.7-3.6,2.6c0,1.7,1.4,2.2,2.8,2.6\r\n          c0.6,0.1,1.3,0.3,1.9,0.6c0.5,0.3,0.9,0.6,0.9,1.2c0,1.1-1.1,1.6-2.1,1.6c-1.3,0-2-0.6-2.1-1.9h-1.6c0,2.2,1.7,3,3.7,3\r\n          C45.6,14.5,47.6,13.7,47.6,11.6 M39.2,14.3V13c-0.3,0-0.6,0.1-0.9,0.1c-1.2,0-1.3-0.5-1.3-1.5V6.1h2.1V5H37V2.5l-1.6,0.7V5h-1.9v1.1\r\n          h1.9v6.1c0,1.5,0.8,2.1,2.3,2.1C38.2,14.4,38.6,14.4,39.2,14.3 M32.2,14.3c-0.1-1.6,0-3.2,0-4.8V5h-1.6v4.9c0,1.7-0.6,3.3-2.6,3.3\r\n          c-2,0-2.2-1.4-2.2-3V5H24v5.8c0,2.3,0.9,3.6,3.4,3.6c1.4,0,2.3-0.5,3.1-1.6l0.1,1.5H32.2z M20.5,8.9h-5.2c0.1-1.5,1-2.9,2.8-2.9\r\n          C19.7,5.9,20.5,7.4,20.5,8.9 M22.1,10c0-2.7-0.9-5.1-4.1-5.1c-3.1,0-4.5,2.2-4.5,5c0,2.8,1.4,4.7,4.4,4.7c2.2,0,3.8-1,4.1-3.2h-1.6\r\n          c-0.2,1.3-1.1,2.1-2.4,2.1c-1.9,0-2.8-1.6-2.8-3.3V10H22.1z M10.3,7.6c0,3.5-1.4,5.4-5.1,5.4H2.8V2.3h2.1c1.2,0,2.5,0.1,3.5,0.8\r\n          C9.9,4.2,10.3,5.9,10.3,7.6 M12.1,7.6c0-2.7-0.8-5-3.4-6.1C7.6,1.1,6.1,1,5,1H1v13.3h3.3c0.7,0,1.5,0,2.3-0.1\r\n          C10.4,13.8,12.1,11.2,12.1,7.6\"></path>\r\n        </svg>\r\n      </span>\r\n      <span class=\"db-docs-header__product-scope\">{{ scope }}</span><span class=\"db-docs-header__product-name\">{{ name }}</span>\r\n      <span class=\"db-docs-header__product-version\">{{ version }}</span>\r\n    </h1>\r\n    <!-- <db-icon class=\"db-docs-header__product-icon\">arrow-down</db-icon> -->\r\n  </button>\r\n\r\n  <!-- Search -->\r\n  <div class=\"db-docs-header__search\" (keydown)=\"onSearchKeydown( $event )\">\r\n\r\n    <input\r\n        type=\"text\"\r\n        class=\"db-docs-header__search-input\"\r\n        placeholder=\"Search ...\"\r\n        [value]=\"searchQuery\"\r\n        (input)=\"onSearchInput( $event.target.value )\"\r\n        #searchInput>\r\n    <div class=\"db-docs-header__search-details\">\r\n      <db-icon class=\"db-docs-header__search-input-icon\">search</db-icon>\r\n      <span class=\"db-docs-header__search-shortcut\" [class.is-visible]=\"!isSearching\">Double Shift</span>\r\n      <button class=\"db-docs-header__search-clear\" type=\"button\" title=\"Close search [Esc]\" (click)=\"onClearButtonClick()\" [class.is-visible]=\"isSearching\">\r\n        <svg id=\"close\" viewBox=\"0 0 48 48\" width=\"100%\" height=\"100%\">\r\n          <path class=\"object\" d=\"M25.4,24L42.7,6.7c0.4-0.4,0.4-1,0-1.4s-1-0.4-1.4,0L24,22.6L6.7,5.3c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4 L22.6,24L5.3,41.3c-0.4,0.4-0.4,1,0,1.4C5.5,42.9,5.7,43,6,43s0.5-0.1,0.7-0.3L24,25.4l17.3,17.3c0.2,0.2,0.5,0.3,0.7,0.3 s0.5-0.1,0.7-0.3c0.4-0.4,0.4-1,0-1.4L25.4,24z\"></path>\r\n        </svg>\r\n      </button>\r\n    </div>\r\n\r\n    <div class=\"db-docs-header__search-overlay\" [class.is-expanded]=\"isSearching\">\r\n\r\n      <div class=\"db-docs-header__search-empty\" *ngIf=\"hasNoSearchResults()\">\r\n        <db-icon class=\"db-docs-header__search-empty-icon\">search</db-icon>\r\n        <p class=\"db-docs-header__search-empty-title\">No search results found.</p>\r\n        <p class=\"db-docs-header__search-empty-recommendation\">\r\n          But have you visited the <a [routerLink]=\"randomElement?.link\" (click)=\"onSearchClick()\">{{ randomElement?.name }}</a> page yet?\r\n        </p>\r\n      </div>\r\n\r\n      <ul\r\n          class=\"db-docs-header__search-results\"\r\n          [class.is-scrolling]=\"showScrollBar\"\r\n          [style.maxHeight]=\"searchOverlayHeight\"\r\n          [style.minHeight]=\"searchOverlayHeight\">\r\n        <li class=\"db-docs-header__search-results__result\" *ngFor=\"let element of ( elements | dbDocsSearch: searchQuery ); let i = index;\">\r\n          <a\r\n              class=\"db-docs-header__search-link\"\r\n              [routerLink]=\"element.link\"\r\n              [class.is-selected]=\"i === selectedSearchResultIndex\"\r\n              (mouseenter)=\"onSearchMouseenter( i )\"\r\n              (click)=\"onSearchClick()\"\r\n              #searchResultElement>\r\n            {{ element.name }}\r\n            <span\r\n                class=\"db-docs-header__search-label db-docs-header__search-label--{{ label }}\"\r\n                *ngFor=\"let label of element.labels\">{{ label }}</span>\r\n            <span class=\"db-docs-header__search-route\">{{ element.link }}</span>\r\n          </a>\r\n        <li>\r\n      </ul>\r\n    </div>\r\n\r\n  </div>\r\n\r\n  <!-- Theme Switch -->\r\n  <div>\r\n    <db-select class=\"db-docs-header__theme\" label=\"UI Theme\" *ngIf=\"hasThemes()\" [options]=\"themes\" [model]=\"selectedTheme\" (modelChange)=\"onSelectTheme( $event )\">\r\n    </db-select>\r\n  </div>\r\n\r\n  <!-- Deutsche Bank Logo -->\r\n  <svg class=\"db-docs-header__logo\" viewBox=\"0 0 70 70\">\r\n    <path d=\"M10.5 60h50V10h-50v50zM.5 0h70v70H.5V0zm15 53l26.462-36H54.5L28.038 53H15.5z\"></path>\r\n  </svg>\r\n\r\n</header>\r\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }, { kind: "component", type: DBSelectComponent, selector: "db-select", inputs: ["options", "label", "loading", "loadingLabel", "placeholder"] }, { kind: "pipe", type: DBDocsSearchPipe, name: "dbDocsSearch" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-docs-header', template: "<div class=\"db-docs-header__search-backdrop\" [class.is-visible]=\"isSearching || isSelectingProduct\" (click)=\"onBackdropClick()\"></div>\r\n\r\n<header class=\"db-docs-header__header d-flex flex-row justify-content-between\" [class.is-expanded]=\"isSelectingProduct\">\r\n\r\n  <!-- Mobile Navigation Toggle Button (Burger) -->\r\n  <button type=\"button\" class=\"db-docs-header__burger\" [class.is-open]=\"isNavigationVisible\" (click)=\"onToggleNavigationClick()\">\r\n    <svg width=\"32\" height=\"32\" viewBox=\"0 0 32 32\" class=\"db-docs-header__burger-icon\">\r\n      <g transform=\"translate(0, -6)\">\r\n        <line x1=\"6\" y1=\"16\" x2=\"26\" y2=\"16\" stroke=\"#333\" stroke-width=\"2\" class=\"db-docs-header__burger-icon--top\"></line>\r\n      </g>\r\n      <line x1=\"6\" y1=\"16\" x2=\"26\" y2=\"16\" stroke=\"#333\" stroke-width=\"2\" class=\"db-docs-header__burger-icon--middle\"></line>\r\n      <g transform=\"translate(0, 6)\">\r\n        <line x1=\"6\" y1=\"16\" x2=\"26\" y2=\"16\" stroke=\"#333\" stroke-width=\"2\" class=\"db-docs-header__burger-icon--bottom\"></line>\r\n      </g>\r\n    </svg>\r\n  </button>\r\n\r\n  <!-- Title -->\r\n  <button class=\"db-docs-header__product mr-auto\" (click)=\"onProductClick()\">\r\n    <h1 class=\"db-docs-header__product-title\">\r\n      <span class=\"db-docs-header__product-brand\">\r\n        <svg class=\"db-docs-header__brand\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\"\r\n          x=\"0px\" y=\"0px\" viewBox=\"0 0 125 15\" xml:space=\"preserve\" preserveAspectRatio=\"xMinYMin meet\">\r\n          <path class=\"db-docs-header__brand-path\" d=\"M116.7,1h-1.6v13.3h1.6V1z M123.5,14.3L118.7,9l4.4-4H121l-4.3,4.1l4.6,5.2H123.5z\r\n          M112.5,14.3V8.5 c0-2.3-0.9-3.6-3.4-3.6c-1.4,0-2.3,0.5-3.1,1.6L105.9,5h-1.6c0,0.5,0.1,1.6,0.1,2.4v6.9h1.6V9.7c0-1.7,0.6-3.5,2.6-3.5\r\n          c2,0,2.2,1.4,2.2,3v5.1H112.5z M100.3,9.7c0,1.9-0.2,3.8-2.6,3.8c-1.1,0-2.1-0.6-2.1-1.7c0-1.9,2.4-2.1,3.9-2.1\r\n          C99.7,9.6,99.9,9.6,100.3,9.7 M102,14.3c-0.1-0.4-0.3-1.5-0.3-2.7V8.2c0-2.4-1.2-3.3-3.6-3.3c-1.8,0-3.4,0.7-3.5,2.7h1.6\r\n          c0.1-1.2,0.8-1.6,2-1.6c1.6,0,2.1,0.8,2.1,2.2c0,0.2,0,0.5,0,0.5c-0.3,0-0.6,0-0.9,0c-2.3,0-5.4,0.3-5.4,3.1c0,1.9,1.6,2.8,3.3,2.8\r\n          c1.4,0,2.4-0.4,3.2-1.5c0.1,0.4,0.1,0.9,0.2,1.3H102z M90.7,10.5c0,2.2-1.9,2.4-3.7,2.4h-2.3V8.1h2.9C89.3,8.1,90.7,8.6,90.7,10.5\r\n          M90.2,4.5c0,1.7-1.3,2.3-2.9,2.3h-2.6V2.4h2.9C89,2.4,90.2,2.9,90.2,4.5 M92.6,10.6c0-2-1.2-2.9-3.1-3.3C91.1,6.9,92,6,92,4.3\r\n          C92,1.9,89.8,1,87.7,1H83v13.3h4.5C90,14.3,92.6,13.6,92.6,10.6 M76.2,8.9h-5.2c0.2-1.5,1-2.9,2.8-2.9C75.4,5.9,76.2,7.4,76.2,8.9\r\n          M77.8,10c0-2.7-0.9-5.1-4.1-5.1c-3.1,0-4.5,2.2-4.5,5c0,2.8,1.4,4.7,4.4,4.7c2.2,0,3.8-1,4.1-3.2h-1.6c-0.2,1.3-1.1,2.1-2.4,2.1\r\n          c-1.9,0-2.8-1.6-2.8-3.3V10H77.8z M67.3,14.3V8.5c0-2.3-0.9-3.6-3.4-3.6c-1.3,0-2.3,0.5-3.1,1.6V1h-1.6v13.3h1.6V9.4\r\n          c0-1.7,0.7-3.3,2.6-3.3c2,0,2.2,1.4,2.2,3v5.1H67.3z M57.5,11.2L57.5,11.2l-1.6,0c-0.1,1.3-1,2.2-2.4,2.2c-2.1,0-2.9-2-2.9-3.7\r\n          c0-1.9,0.7-3.8,2.9-3.8c1.3,0,2.2,0.8,2.2,2.1h1.6c-0.2-2.2-1.7-3.3-3.9-3.3c-3.1,0-4.7,2.1-4.7,5c0,2.8,1.6,4.7,4.6,4.7\r\n          C55.7,14.5,57.3,13.4,57.5,11.2 M47.6,11.6c0-1.7-1.4-2.2-2.8-2.6c-0.6-0.2-1.3-0.3-1.9-0.6C42.3,8.2,42,7.8,42,7.3\r\n          c0-1,1.1-1.4,1.9-1.4c1.1,0,1.8,0.6,1.9,1.7h1.6c-0.1-2-1.6-2.8-3.5-2.8c-1.7,0-3.6,0.7-3.6,2.6c0,1.7,1.4,2.2,2.8,2.6\r\n          c0.6,0.1,1.3,0.3,1.9,0.6c0.5,0.3,0.9,0.6,0.9,1.2c0,1.1-1.1,1.6-2.1,1.6c-1.3,0-2-0.6-2.1-1.9h-1.6c0,2.2,1.7,3,3.7,3\r\n          C45.6,14.5,47.6,13.7,47.6,11.6 M39.2,14.3V13c-0.3,0-0.6,0.1-0.9,0.1c-1.2,0-1.3-0.5-1.3-1.5V6.1h2.1V5H37V2.5l-1.6,0.7V5h-1.9v1.1\r\n          h1.9v6.1c0,1.5,0.8,2.1,2.3,2.1C38.2,14.4,38.6,14.4,39.2,14.3 M32.2,14.3c-0.1-1.6,0-3.2,0-4.8V5h-1.6v4.9c0,1.7-0.6,3.3-2.6,3.3\r\n          c-2,0-2.2-1.4-2.2-3V5H24v5.8c0,2.3,0.9,3.6,3.4,3.6c1.4,0,2.3-0.5,3.1-1.6l0.1,1.5H32.2z M20.5,8.9h-5.2c0.1-1.5,1-2.9,2.8-2.9\r\n          C19.7,5.9,20.5,7.4,20.5,8.9 M22.1,10c0-2.7-0.9-5.1-4.1-5.1c-3.1,0-4.5,2.2-4.5,5c0,2.8,1.4,4.7,4.4,4.7c2.2,0,3.8-1,4.1-3.2h-1.6\r\n          c-0.2,1.3-1.1,2.1-2.4,2.1c-1.9,0-2.8-1.6-2.8-3.3V10H22.1z M10.3,7.6c0,3.5-1.4,5.4-5.1,5.4H2.8V2.3h2.1c1.2,0,2.5,0.1,3.5,0.8\r\n          C9.9,4.2,10.3,5.9,10.3,7.6 M12.1,7.6c0-2.7-0.8-5-3.4-6.1C7.6,1.1,6.1,1,5,1H1v13.3h3.3c0.7,0,1.5,0,2.3-0.1\r\n          C10.4,13.8,12.1,11.2,12.1,7.6\"></path>\r\n        </svg>\r\n      </span>\r\n      <span class=\"db-docs-header__product-scope\">{{ scope }}</span><span class=\"db-docs-header__product-name\">{{ name }}</span>\r\n      <span class=\"db-docs-header__product-version\">{{ version }}</span>\r\n    </h1>\r\n    <!-- <db-icon class=\"db-docs-header__product-icon\">arrow-down</db-icon> -->\r\n  </button>\r\n\r\n  <!-- Search -->\r\n  <div class=\"db-docs-header__search\" (keydown)=\"onSearchKeydown( $event )\">\r\n\r\n    <input\r\n        type=\"text\"\r\n        class=\"db-docs-header__search-input\"\r\n        placeholder=\"Search ...\"\r\n        [value]=\"searchQuery\"\r\n        (input)=\"onSearchInput( $event.target.value )\"\r\n        #searchInput>\r\n    <div class=\"db-docs-header__search-details\">\r\n      <db-icon class=\"db-docs-header__search-input-icon\">search</db-icon>\r\n      <span class=\"db-docs-header__search-shortcut\" [class.is-visible]=\"!isSearching\">Double Shift</span>\r\n      <button class=\"db-docs-header__search-clear\" type=\"button\" title=\"Close search [Esc]\" (click)=\"onClearButtonClick()\" [class.is-visible]=\"isSearching\">\r\n        <svg id=\"close\" viewBox=\"0 0 48 48\" width=\"100%\" height=\"100%\">\r\n          <path class=\"object\" d=\"M25.4,24L42.7,6.7c0.4-0.4,0.4-1,0-1.4s-1-0.4-1.4,0L24,22.6L6.7,5.3c-0.4-0.4-1-0.4-1.4,0s-0.4,1,0,1.4 L22.6,24L5.3,41.3c-0.4,0.4-0.4,1,0,1.4C5.5,42.9,5.7,43,6,43s0.5-0.1,0.7-0.3L24,25.4l17.3,17.3c0.2,0.2,0.5,0.3,0.7,0.3 s0.5-0.1,0.7-0.3c0.4-0.4,0.4-1,0-1.4L25.4,24z\"></path>\r\n        </svg>\r\n      </button>\r\n    </div>\r\n\r\n    <div class=\"db-docs-header__search-overlay\" [class.is-expanded]=\"isSearching\">\r\n\r\n      <div class=\"db-docs-header__search-empty\" *ngIf=\"hasNoSearchResults()\">\r\n        <db-icon class=\"db-docs-header__search-empty-icon\">search</db-icon>\r\n        <p class=\"db-docs-header__search-empty-title\">No search results found.</p>\r\n        <p class=\"db-docs-header__search-empty-recommendation\">\r\n          But have you visited the <a [routerLink]=\"randomElement?.link\" (click)=\"onSearchClick()\">{{ randomElement?.name }}</a> page yet?\r\n        </p>\r\n      </div>\r\n\r\n      <ul\r\n          class=\"db-docs-header__search-results\"\r\n          [class.is-scrolling]=\"showScrollBar\"\r\n          [style.maxHeight]=\"searchOverlayHeight\"\r\n          [style.minHeight]=\"searchOverlayHeight\">\r\n        <li class=\"db-docs-header__search-results__result\" *ngFor=\"let element of ( elements | dbDocsSearch: searchQuery ); let i = index;\">\r\n          <a\r\n              class=\"db-docs-header__search-link\"\r\n              [routerLink]=\"element.link\"\r\n              [class.is-selected]=\"i === selectedSearchResultIndex\"\r\n              (mouseenter)=\"onSearchMouseenter( i )\"\r\n              (click)=\"onSearchClick()\"\r\n              #searchResultElement>\r\n            {{ element.name }}\r\n            <span\r\n                class=\"db-docs-header__search-label db-docs-header__search-label--{{ label }}\"\r\n                *ngFor=\"let label of element.labels\">{{ label }}</span>\r\n            <span class=\"db-docs-header__search-route\">{{ element.link }}</span>\r\n          </a>\r\n        <li>\r\n      </ul>\r\n    </div>\r\n\r\n  </div>\r\n\r\n  <!-- Theme Switch -->\r\n  <div>\r\n    <db-select class=\"db-docs-header__theme\" label=\"UI Theme\" *ngIf=\"hasThemes()\" [options]=\"themes\" [model]=\"selectedTheme\" (modelChange)=\"onSelectTheme( $event )\">\r\n    </db-select>\r\n  </div>\r\n\r\n  <!-- Deutsche Bank Logo -->\r\n  <svg class=\"db-docs-header__logo\" viewBox=\"0 0 70 70\">\r\n    <path d=\"M10.5 60h50V10h-50v50zM.5 0h70v70H.5V0zm15 53l26.462-36H54.5L28.038 53H15.5z\"></path>\r\n  </svg>\r\n\r\n</header>\r\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: i0.ChangeDetectorRef }, { type: DBViewportService }, { type: DBWindowRef }, { type: DBDocsService }]; }, propDecorators: { scope: [{
                type: Input
            }], name: [{
                type: Input
            }], version: [{
                type: Input
            }], structure: [{
                type: Input
            }], themes: [{
                type: Input
            }], navigationToggle: [{
                type: Output
            }], searchResultElements: [{
                type: ViewChildren,
                args: ['searchResultElement', {
                        read: ElementRef
                    }]
            }], searchInputElement: [{
                type: ViewChild,
                args: ['searchInput', {
                        read: ElementRef, static: true,
                    }]
            }], onWindowKeyup: [{
                type: HostListener,
                args: ['window:keyup', ['$event.key']]
            }] } });

/* This module contains and exports the db-docs-header component
*  This module has no dependencies and can directly be imported
*/
class DBDocsHeaderModule {
}
DBDocsHeaderModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsHeaderModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderModule, declarations: [DBDocsHeaderComponent,
        DBDocsSearchPipe], imports: [CommonModule,
        RouterModule,
        DBIconModule,
        DBSelectModule], exports: [DBDocsHeaderComponent,
        DBDocsSearchPipe] });
DBDocsHeaderModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderModule, imports: [CommonModule,
        RouterModule,
        DBIconModule,
        DBSelectModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        DBIconModule,
                        DBSelectModule,
                    ],
                    exports: [
                        DBDocsHeaderComponent,
                        DBDocsSearchPipe,
                    ],
                    declarations: [
                        DBDocsHeaderComponent,
                        DBDocsSearchPipe,
                    ],
                }]
        }] });

function Deprecated(component, alternative, version) {
    return (Base) => class DeprecatedDecorator extends Base {
        constructor(...args) {
            super(...args);
            let removal = '';
            if (version) {
                removal = ' and will be removed in version ' + version;
            }
            if (isDevMode()) {
                console.warn('The Component "' + component + '" is deprecated' + removal + '. Please use ' + alternative + ' instead.');
            }
        }
    };
}

/**
 * @deprecated
 */
let DBIntroPreviewDirective = class DBIntroPreviewDirective {
};
DBIntroPreviewDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIntroPreviewDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
DBIntroPreviewDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: DBIntroPreviewDirective, selector: "db-intro-preview", ngImport: i0 });
DBIntroPreviewDirective = __decorate([
    Deprecated('db-intro-preview', 'db-docs-preview')
], DBIntroPreviewDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIntroPreviewDirective, decorators: [{
            type: Directive,
            args: [{
                    /* eslint-disable-next-line @angular-eslint/directive-selector */
                    selector: 'db-intro-preview'
                }]
        }] });

/**
 * DB Intro Component
 */
class DBIntroComponent {
    /**
     * Constructor
     */
    constructor(docsService) {
        this.docsService = docsService;
        this.linkToSourceCode = null;
        this.linkToTestCoverage = null;
        this.linkToDocs = null;
        this.noPrefix = false;
        this.title = null;
    }
}
DBIntroComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIntroComponent, deps: [{ token: DBDocsService }], target: i0.ɵɵFactoryTarget.Component });
DBIntroComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBIntroComponent, selector: "db-docs-intro", inputs: { linkToSourceCode: "linkToSourceCode", linkToTestCoverage: "linkToTestCoverage", linkToDocs: "linkToDocs", noPrefix: "noPrefix", title: "title" }, ngImport: i0, template: "<header class=\"db-docs-title\">\r\n  <section class=\"container px-0 d-flex justify-content-between\">\r\n  <h2 class=\"db-docs-title__title\">\r\n    {{title}}\r\n  </h2>\r\n\r\n  <ul class=\"db-docs-title__list\">\r\n    <li class=\"db-docs-title__list-item\" *ngIf=\"linkToDocs !== null\">\r\n      <a class=\"db-docs-title__list-link\" target=\"_blank\" [href]=\"linkToDocs\">\r\n        API Documentation\r\n      </a>\r\n    </li>\r\n    <li class=\"db-docs-title__list-item\" *ngIf=\"linkToTestCoverage !== null\">\r\n      <a class=\"db-docs-title__list-link\" target=\"_blank\" [href]=\"linkToTestCoverage\">\r\n        Test Coverage\r\n      </a>\r\n    </li>\r\n    <li class=\"db-docs-title__list-item\" *ngIf=\"linkToSourceCode !== null\">\r\n      <a class=\"db-docs-title__list-link\" target=\"_blank\" href=\"{{docsService.repositoryUrl}}{{ linkToSourceCode }}\">\r\n        Source Code\r\n      </a>\r\n    </li>\r\n  </ul>\r\n  </section>\r\n</header>\r\n\r\n<div class=\"db-docs-description__content\">\r\n  <ng-content>\r\n  </ng-content>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBIntroComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-docs-intro', template: "<header class=\"db-docs-title\">\r\n  <section class=\"container px-0 d-flex justify-content-between\">\r\n  <h2 class=\"db-docs-title__title\">\r\n    {{title}}\r\n  </h2>\r\n\r\n  <ul class=\"db-docs-title__list\">\r\n    <li class=\"db-docs-title__list-item\" *ngIf=\"linkToDocs !== null\">\r\n      <a class=\"db-docs-title__list-link\" target=\"_blank\" [href]=\"linkToDocs\">\r\n        API Documentation\r\n      </a>\r\n    </li>\r\n    <li class=\"db-docs-title__list-item\" *ngIf=\"linkToTestCoverage !== null\">\r\n      <a class=\"db-docs-title__list-link\" target=\"_blank\" [href]=\"linkToTestCoverage\">\r\n        Test Coverage\r\n      </a>\r\n    </li>\r\n    <li class=\"db-docs-title__list-item\" *ngIf=\"linkToSourceCode !== null\">\r\n      <a class=\"db-docs-title__list-link\" target=\"_blank\" href=\"{{docsService.repositoryUrl}}{{ linkToSourceCode }}\">\r\n        Source Code\r\n      </a>\r\n    </li>\r\n  </ul>\r\n  </section>\r\n</header>\r\n\r\n<div class=\"db-docs-description__content\">\r\n  <ng-content>\r\n  </ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return [{ type: DBDocsService }]; }, propDecorators: { linkToSourceCode: [{
                type: Input
            }], linkToTestCoverage: [{
                type: Input
            }], linkToDocs: [{
                type: Input
            }], noPrefix: [{
                type: Input
            }], title: [{
                type: Input
            }] } });

/* This module contains and exports the db-docs-title component
*  This module has no dependencies and can directly be imported
*/
class DBDocsIntroModule {
}
DBDocsIntroModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsIntroModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsIntroModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsIntroModule, declarations: [DBIntroComponent,
        DBIntroPreviewDirective], imports: [CommonModule], exports: [DBIntroComponent,
        DBIntroPreviewDirective] });
DBDocsIntroModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsIntroModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsIntroModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        DBIntroComponent,
                        DBIntroPreviewDirective,
                    ],
                    declarations: [
                        DBIntroComponent,
                        DBIntroPreviewDirective,
                    ],
                }]
        }] });

/**
 * DB Docs Navigation Search Pipe
 */
class DBDocsNavigationSearchPipe {
    transform(sectionElements, searchTerm) {
        // Skip if search term is empty
        if (!searchTerm || searchTerm === '') {
            return sectionElements;
        }
        // Get search terms
        const normalizedSearchTerms = searchTerm
            .toLowerCase()
            .trim()
            .split(/\s+/);
        // Find
        return sectionElements.filter((sectionElement) => normalizedSearchTerms.every((normalizedSearchTerm) => sectionElement.keywords.find((keyword) => keyword.startsWith(normalizedSearchTerm))));
    }
}
DBDocsNavigationSearchPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationSearchPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
DBDocsNavigationSearchPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationSearchPipe, name: "dbDocsNavigationSearch" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationSearchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'dbDocsNavigationSearch',
                    pure: true,
                }]
        }] });

/**
 * DB Docs Navigation Component
 */
class DBDocsNavigationComponent {
    /**
     * Constructor
     *
     * @param Router            router         - Router
     * @param DBWindowRef       windowRef      - Window Ref
     * @param ChangeDetectorRef changeDetector - Change detector
     */
    constructor(router, windowRef, changeDetector) {
        this.activeNavigationGroup = '';
        this.currentYear = (new Date()).getFullYear().toString();
        this.subscription = null;
        this.router = router;
        this.windowRef = windowRef;
        this.changeDetector = changeDetector;
        this.structure = {};
    }
    /**
     * On Init Lifecycle Hook
     */
    ngOnInit() {
        // Hear for router events
        this.subscription = this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
            // Activate correct navigation group
            this.activeNavigationGroup = event.url.split('/')[1];
            this.changeDetector.detectChanges();
            // Scroll active navigation into the view
            setTimeout(() => {
                this.links
                    .toArray()
                    .find((link) => link.nativeElement.classList.contains('is-active'))
                    .nativeElement.scrollIntoViewIfNeeded();
            }, 0);
        });
    }
    /**
     * On Destroy Lifecycle Hook
     */
    ngOnDestroy() {
        // Cleanup
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }
    /**
     * Check if given navigation group is the currently active one
     *
     * @param   string  navigationGroup - Navigation group
     * @returns boolean                 - Result
     */
    isActiveNavigationGroup(navigationGroup) {
        return this.activeNavigationGroup === navigationGroup;
    }
    /**
     * Handle click on navigation group
     *
     * @param string navigationGroup - Navigation group
     */
    onClickNavigationGroup(navigationGroup) {
        this.activeNavigationGroup = this.activeNavigationGroup !== navigationGroup ? navigationGroup : ''; // Toggle
    }
}
DBDocsNavigationComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationComponent, deps: [{ token: i1$2.Router }, { token: DBWindowRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
DBDocsNavigationComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDocsNavigationComponent, selector: "db-docs-navigation", inputs: { structure: "structure" }, viewQueries: [{ propertyName: "links", predicate: ["link"], descendants: true, read: ElementRef }], ngImport: i0, template: "<nav class=\"db-docs-navigation__navigation\">\r\n\r\n  <div class=\"db-docs-navigation__content\">\r\n\r\n    <ul class=\"db-docs-navigation__list db-docs-navigation__list--always-active\">\r\n      <li class=\"db-docs-navigation__item\" *ngFor=\"let element of structure.elements\">\r\n\r\n        <a class=\"db-docs-navigation__link\" *ngIf=\"!element.externalLink\" [routerLink]=\"element.link\" routerLinkActive=\"is-active\" #link>\r\n          {{ element.name }}\r\n        </a>\r\n        <a class=\"db-docs-navigation__link db-docs-navigation__link db-docs-navigation__link--external\" *ngIf=\"element.externalLink\" [href]=\"element.link\" target=\"_blank\" #link>\r\n          {{ element.name }}\r\n        </a>\r\n\r\n      </li>\r\n    </ul>\r\n\r\n    <div *ngFor=\"let section of structure.sections; let isLastSection = last\">\r\n\r\n      <div\r\n        class=\"db-docs-navigation__label\"\r\n        [class.is-active]=\"isActiveNavigationGroup( section.link )\"\r\n        [class.db-docs-navigation__label--last]=\"isLastSection\"\r\n        (click)=\"onClickNavigationGroup( section.link )\">\r\n        <span class=\"db-docs-navigation__label-text\">{{ section.name }}</span>\r\n        <db-icon class=\"db-docs-navigation__label-icon\" size=\"14px\">arrow-down</db-icon>\r\n      </div>\r\n\r\n      <ul\r\n        class=\"db-docs-navigation__list\"\r\n        [class.is-active]=\"isActiveNavigationGroup( section.link )\"\r\n        style.maxHeight=\"{{ isActiveNavigationGroup( section.link ) ? 4.8 * section.elements.length : 0 }}rem\">\r\n        <li class=\"db-docs-navigation__item\" *ngFor=\"let sectionElement of section.elements\">\r\n\r\n          <a class=\"db-docs-navigation__link\" *ngIf=\"!sectionElement.externalLink\" [routerLink]=\"sectionElement.link\" routerLinkActive=\"is-active\" #link>\r\n            {{ sectionElement.name }}\r\n          </a>\r\n          <a class=\"db-docs-navigation__link db-docs-navigation__link db-docs-navigation__link--external\" *ngIf=\"sectionElement.externalLink\" [href]=\"sectionElement.link\" target=\"_blank\" #link>\r\n            {{ sectionElement.name }}\r\n          </a>\r\n\r\n        </li>\r\n      </ul>\r\n\r\n    </div>\r\n\r\n  </div>\r\n\r\n  <p class=\"db-docs-navigation__copyright\">&copy; 2016-{{ currentYear }} Deutsche Bank</p>\r\n\r\n</nav>\r\n", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DBIconComponent, selector: "db-icon", inputs: ["color", "size"] }, { kind: "directive", type: i1$2.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i1$2.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-docs-navigation', template: "<nav class=\"db-docs-navigation__navigation\">\r\n\r\n  <div class=\"db-docs-navigation__content\">\r\n\r\n    <ul class=\"db-docs-navigation__list db-docs-navigation__list--always-active\">\r\n      <li class=\"db-docs-navigation__item\" *ngFor=\"let element of structure.elements\">\r\n\r\n        <a class=\"db-docs-navigation__link\" *ngIf=\"!element.externalLink\" [routerLink]=\"element.link\" routerLinkActive=\"is-active\" #link>\r\n          {{ element.name }}\r\n        </a>\r\n        <a class=\"db-docs-navigation__link db-docs-navigation__link db-docs-navigation__link--external\" *ngIf=\"element.externalLink\" [href]=\"element.link\" target=\"_blank\" #link>\r\n          {{ element.name }}\r\n        </a>\r\n\r\n      </li>\r\n    </ul>\r\n\r\n    <div *ngFor=\"let section of structure.sections; let isLastSection = last\">\r\n\r\n      <div\r\n        class=\"db-docs-navigation__label\"\r\n        [class.is-active]=\"isActiveNavigationGroup( section.link )\"\r\n        [class.db-docs-navigation__label--last]=\"isLastSection\"\r\n        (click)=\"onClickNavigationGroup( section.link )\">\r\n        <span class=\"db-docs-navigation__label-text\">{{ section.name }}</span>\r\n        <db-icon class=\"db-docs-navigation__label-icon\" size=\"14px\">arrow-down</db-icon>\r\n      </div>\r\n\r\n      <ul\r\n        class=\"db-docs-navigation__list\"\r\n        [class.is-active]=\"isActiveNavigationGroup( section.link )\"\r\n        style.maxHeight=\"{{ isActiveNavigationGroup( section.link ) ? 4.8 * section.elements.length : 0 }}rem\">\r\n        <li class=\"db-docs-navigation__item\" *ngFor=\"let sectionElement of section.elements\">\r\n\r\n          <a class=\"db-docs-navigation__link\" *ngIf=\"!sectionElement.externalLink\" [routerLink]=\"sectionElement.link\" routerLinkActive=\"is-active\" #link>\r\n            {{ sectionElement.name }}\r\n          </a>\r\n          <a class=\"db-docs-navigation__link db-docs-navigation__link db-docs-navigation__link--external\" *ngIf=\"sectionElement.externalLink\" [href]=\"sectionElement.link\" target=\"_blank\" #link>\r\n            {{ sectionElement.name }}\r\n          </a>\r\n\r\n        </li>\r\n      </ul>\r\n\r\n    </div>\r\n\r\n  </div>\r\n\r\n  <p class=\"db-docs-navigation__copyright\">&copy; 2016-{{ currentYear }} Deutsche Bank</p>\r\n\r\n</nav>\r\n" }]
        }], ctorParameters: function () { return [{ type: i1$2.Router }, { type: DBWindowRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { structure: [{
                type: Input
            }], links: [{
                type: ViewChildren,
                args: ['link', {
                        read: ElementRef,
                    }]
            }] } });

/* This module contains and exports the db-docs-navigation component
*  This module has no dependencies and can directly be imported
*/
class DBDocsNavigationModule {
}
DBDocsNavigationModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsNavigationModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationModule, declarations: [DBDocsNavigationComponent,
        DBDocsNavigationSearchPipe], imports: [CommonModule,
        DBIconModule,
        RouterModule], exports: [DBDocsNavigationComponent,
        DBDocsNavigationSearchPipe] });
DBDocsNavigationModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationModule, imports: [CommonModule,
        DBIconModule,
        RouterModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsNavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        DBIconModule,
                        RouterModule,
                    ],
                    exports: [
                        DBDocsNavigationComponent,
                        DBDocsNavigationSearchPipe,
                    ],
                    declarations: [
                        DBDocsNavigationComponent,
                        DBDocsNavigationSearchPipe,
                    ],
                }]
        }] });

class DBDocsPreviewComponent {
    constructor() {
        this.activeClass = '';
        this.filterButtons = [
            { filter: '', isClicked: true },
            { filter: 'white', isClicked: false },
            { filter: 'dark', isClicked: false },
            { filter: 'checkered', isClicked: false },
        ];
    }
    ngOnInit() {
    }
    setActive(button) {
        for (const but of this.filterButtons) {
            but.isClicked = false;
        }
        this.backgroundColor = '';
        this.activeClass = button.filter;
        button.isClicked = true;
    }
    setColor(newColor) {
        for (const but of this.filterButtons) {
            but.isClicked = false;
        }
        this.backgroundColor = newColor.target.value;
        this.activeClass = '';
    }
}
DBDocsPreviewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPreviewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBDocsPreviewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDocsPreviewComponent, selector: "db-docs-preview", ngImport: i0, template: "<div class=\"d-flex flex-row align-items-center\" style=\"background-color: #fff\">\r\n  <h4 class=\"db-docs-preview__title\">Preview</h4>\r\n  <div class=\"flex-grow-1\"></div>\r\n  <db-button class=\"db-docs-preview__color-picker ml-3\" *ngFor=\"let button of filterButtons; let first = first\" [class.clicked]=\"button.isClicked\" (click)=\"setActive(button)\">\r\n    <div class=\"db-docs-preview__color-picker__color-preview\" [class]=\"first ? 'first' : button.filter\"></div>\r\n  </db-button>\r\n  <input type=\"color\" class=\"db-docs-preview__color-picker__color-preview__input ml-3\" (change)=\"setColor($event)\" (click)=\"setColor($event)\" [class.clicked]=\"backgroundColor\">\r\n</div>\r\n\r\n<div class=\"db-docs-preview\" [ngStyle]=\"backgroundColor && {'background-color': backgroundColor}\" [class]=\"activeClass\">\r\n  <div class=\"db-docs-preview__content\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n", styles: ["db-docs-preview{position:relative;display:block;padding:0!important;max-width:1140px}db-docs-preview .db-docs-preview{display:block;border:1px solid #dcdcdc}db-docs-preview .db-docs-preview__title{font-size:1.6rem;line-height:3.4rem;font-weight:700;padding-bottom:2px;color:#0c2340;margin-top:0;margin-bottom:0}db-docs-preview .db-docs-preview__content{padding:1.6rem 2rem}.db-docs-preview__color-picker{display:inline-block;white-space:nowrap}.db-docs-preview__color-picker__color-preview{height:20px;width:20px;border-radius:50%}.db-docs-preview__color-picker__color-preview__input{height:24px;width:24px;border:2px solid #cdcdcd;border-radius:50px;cursor:pointer}.db-docs-preview__color-picker .db-button--secondary{background-color:#dcdcdc;border-radius:50%;border:2px solid #dcdcdc;padding:0;min-width:auto;height:inherit;width:auto}.db-docs-preview__color-picker .db-button--secondary:hover{background-color:#dcdcdc}.checkered{background-color:#f9fafc;background-image:-o-linear-gradient(45deg,#fff 25%,transparent 25%),-o-linear-gradient(135deg,#fff 25%,transparent 25%),-o-linear-gradient(45deg,transparent 75%,#fff 75%),-o-linear-gradient(135deg,transparent 75%,#fff 75%);background-image:linear-gradient(45deg,#fff 25%,transparent 25%),linear-gradient(-45deg,#fff 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#fff 75%),linear-gradient(-45deg,transparent 75%,#fff 75%);background-size:10px 10px;background-position:0 0,0 5px,5px -5px,-5px 0px}.clicked,.clicked .db-button{border-color:#2282c1}.dark{background-color:#292929;border:none}.white{background-color:#fff;border:none}.first{border-radius:0;border-right:1px solid red;-webkit-transform:rotate(50deg);transform:rotate(50deg);-webkit-transform-origin:75% 0%;transform-origin:75% 0%}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: DBButtonComponent, selector: "db-button", inputs: ["type", "design", "size", "arrow", "loading", "iconOnly"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPreviewComponent, decorators: [{
            type: Component,
            args: [{ changeDetection: ChangeDetectionStrategy.OnPush, selector: 'db-docs-preview', template: "<div class=\"d-flex flex-row align-items-center\" style=\"background-color: #fff\">\r\n  <h4 class=\"db-docs-preview__title\">Preview</h4>\r\n  <div class=\"flex-grow-1\"></div>\r\n  <db-button class=\"db-docs-preview__color-picker ml-3\" *ngFor=\"let button of filterButtons; let first = first\" [class.clicked]=\"button.isClicked\" (click)=\"setActive(button)\">\r\n    <div class=\"db-docs-preview__color-picker__color-preview\" [class]=\"first ? 'first' : button.filter\"></div>\r\n  </db-button>\r\n  <input type=\"color\" class=\"db-docs-preview__color-picker__color-preview__input ml-3\" (change)=\"setColor($event)\" (click)=\"setColor($event)\" [class.clicked]=\"backgroundColor\">\r\n</div>\r\n\r\n<div class=\"db-docs-preview\" [ngStyle]=\"backgroundColor && {'background-color': backgroundColor}\" [class]=\"activeClass\">\r\n  <div class=\"db-docs-preview__content\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n", styles: ["db-docs-preview{position:relative;display:block;padding:0!important;max-width:1140px}db-docs-preview .db-docs-preview{display:block;border:1px solid #dcdcdc}db-docs-preview .db-docs-preview__title{font-size:1.6rem;line-height:3.4rem;font-weight:700;padding-bottom:2px;color:#0c2340;margin-top:0;margin-bottom:0}db-docs-preview .db-docs-preview__content{padding:1.6rem 2rem}.db-docs-preview__color-picker{display:inline-block;white-space:nowrap}.db-docs-preview__color-picker__color-preview{height:20px;width:20px;border-radius:50%}.db-docs-preview__color-picker__color-preview__input{height:24px;width:24px;border:2px solid #cdcdcd;border-radius:50px;cursor:pointer}.db-docs-preview__color-picker .db-button--secondary{background-color:#dcdcdc;border-radius:50%;border:2px solid #dcdcdc;padding:0;min-width:auto;height:inherit;width:auto}.db-docs-preview__color-picker .db-button--secondary:hover{background-color:#dcdcdc}.checkered{background-color:#f9fafc;background-image:-o-linear-gradient(45deg,#fff 25%,transparent 25%),-o-linear-gradient(135deg,#fff 25%,transparent 25%),-o-linear-gradient(45deg,transparent 75%,#fff 75%),-o-linear-gradient(135deg,transparent 75%,#fff 75%);background-image:linear-gradient(45deg,#fff 25%,transparent 25%),linear-gradient(-45deg,#fff 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#fff 75%),linear-gradient(-45deg,transparent 75%,#fff 75%);background-size:10px 10px;background-position:0 0,0 5px,5px -5px,-5px 0px}.clicked,.clicked .db-button{border-color:#2282c1}.dark{background-color:#292929;border:none}.white{background-color:#fff;border:none}.first{border-radius:0;border-right:1px solid red;-webkit-transform:rotate(50deg);transform:rotate(50deg);-webkit-transform-origin:75% 0%;transform-origin:75% 0%}\n"] }]
        }], ctorParameters: function () { return []; } });

class DBDocsPreviewModule {
}
DBDocsPreviewModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPreviewModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsPreviewModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPreviewModule, declarations: [DBDocsPreviewComponent], imports: [CommonModule,
        DBButtonModule,
        DBInputModule,
        DBToggleableModule], exports: [DBDocsPreviewComponent] });
DBDocsPreviewModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPreviewModule, imports: [CommonModule,
        DBButtonModule,
        DBInputModule,
        DBToggleableModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsPreviewModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        DBButtonModule,
                        DBInputModule,
                        DBToggleableModule
                    ],
                    exports: [
                        DBDocsPreviewComponent,
                    ],
                    declarations: [
                        DBDocsPreviewComponent,
                    ],
                }]
        }] });

/**
 * DB Docs Section Component
 */
class DBDocsSectionComponent {
    /**
     * Constructor
     */
    constructor() {
        this.componentClass = true;
        this.title = this.title || 'How to use';
    }
}
DBDocsSectionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSectionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
DBDocsSectionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: DBDocsSectionComponent, selector: "db-docs-section", inputs: { title: "title" }, host: { properties: { "class.db-docs-section": "this.componentClass" } }, ngImport: i0, template: "<h3 class=\"db-docs-section__title\">{{title}}</h3>\r\n<div class=\"db-docs-section__content\">\r\n  <ng-content>\r\n  </ng-content>\r\n</div>\r\n" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSectionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'db-docs-section', template: "<h3 class=\"db-docs-section__title\">{{title}}</h3>\r\n<div class=\"db-docs-section__content\">\r\n  <ng-content>\r\n  </ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: function () { return []; }, propDecorators: { title: [{
                type: Input
            }], componentClass: [{
                type: HostBinding,
                args: ['class.db-docs-section']
            }] } });

/* This module contains and exports the db-docs-description component
*  This module has no dependencies and can directly be imported
*/
class DBDocsSectionModule {
}
DBDocsSectionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSectionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsSectionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSectionModule, declarations: [DBDocsSectionComponent], exports: [DBDocsSectionComponent] });
DBDocsSectionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSectionModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsSectionModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        DBDocsSectionComponent,
                    ],
                    declarations: [
                        DBDocsSectionComponent,
                    ],
                }]
        }] });

/**
 * Dependencies
 */
const docsDependencies = [
    DBDocsCodeModule,
    DBDocsExampleModule,
    DBDocsHeaderModule,
    DBDocsHeaderNavigationModule,
    DBDocsIntroModule,
    DBDocsNavigationModule,
    DBDocsPreviewModule,
    DBDocsSectionModule,
];
/* This module contains and exports the db-docs components
*  This module has no dependencies and can directly be imported
*/
class DBDocsModule {
}
DBDocsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DBDocsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: DBDocsModule, imports: [DBDocsCodeModule,
        DBDocsExampleModule,
        DBDocsHeaderModule,
        DBDocsHeaderNavigationModule,
        DBDocsIntroModule,
        DBDocsNavigationModule,
        DBDocsPreviewModule,
        DBDocsSectionModule], exports: [DBDocsCodeModule,
        DBDocsExampleModule,
        DBDocsHeaderModule,
        DBDocsHeaderNavigationModule,
        DBDocsIntroModule,
        DBDocsNavigationModule,
        DBDocsPreviewModule,
        DBDocsSectionModule] });
DBDocsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsModule, imports: [docsDependencies, DBDocsCodeModule,
        DBDocsExampleModule,
        DBDocsHeaderModule,
        DBDocsHeaderNavigationModule,
        DBDocsIntroModule,
        DBDocsNavigationModule,
        DBDocsPreviewModule,
        DBDocsSectionModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DBDocsModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        ...docsDependencies,
                    ],
                    imports: [
                        ...docsDependencies,
                    ]
                }]
        }] });

/*
 * Public API Surface of db-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ContentLoaderStatus, DBAccountNumberValidator, DBAgeRangeValidator, DBAlertComponent, DBAlertModule, DBAppDataStoreModule, DBAppDataStoreService, DBAvatarComponent, DBAvatarModule, DBBadgeComponent, DBBadgeModule, DBBadgedContentComponent, DBBadgedContentTriggerDirective, DBBankCodeValidator, DBBranchNumberValidator, DBButtonComponent, DBButtonModule, DBCarouselComponent, DBCarouselModule, DBCarouselSlideComponent, DBCheckboxComponent, DBCheckboxGroupComponent, DBCheckboxGroupRequiredValidator, DBCheckboxModule, DBCityValidator, DBContentLoaderAPIConfig, DBContentLoaderAPIConfigInterface, DBContentLoaderComponent, DBContentLoaderModule, DBContentLoaderService, DBCookieModule, DBCookieService, DBCoreBaseDirective, DBCoreFormBaseDirective, DBCoreModule, DBCreditCardNumberPipe, DBCreditCardNumberValidator, DBCreditCardPipe, DBDatePipe, DBDateRangeValidator, DBDateValidator, DBDetectClickOutsideDirective, DBDocsCodeComponent, DBDocsCodeModule, DBDocsExampleComponent, DBDocsExampleModule, DBDocsHeaderComponent, DBDocsHeaderModule, DBDocsHeaderNavigationComponent, DBDocsHeaderNavigationModule, DBDocsIntroModule, DBDocsModule, DBDocsNavigationComponent, DBDocsNavigationModule, DBDocsNavigationSearchPipe, DBDocsPrettyprintPipe, DBDocsPreviewComponent, DBDocsPreviewModule, DBDocsSearchPipe, DBDocsSectionComponent, DBDocsSectionModule, DBDocsService, DBDownloadButtonComponent, DBDownloadButtonModule, DBEmailValidator, DBExactLengthValidator, DBFilterPillComponent, DBFilterPillModule, DBFormatters, DBFormattingModule, DBFutureDateValidator, DBHandoverModule, DBHandoverService, DBHeadingComponent, DBHeadingModule, DBHttpClientCache, DBHttpClientCacheInterceptor, DBHttpClientCacheModule, DBIbanPipe, DBIbanValidator, DBIconComponent, DBIconModule, DBIconService, DBInputComponent, DBInputDateComponent, DBInputDateDirective, DBInputDateModule, DBInputFormatDirective, DBInputModule, DBIntroComponent, DBIntroPreviewDirective, DBIso88591Validator, DBLinkPillListComponent, DBLinkPillModule, DBListRowComponent, DBListRowContentComponent, DBListRowIconComponent, DBListRowMenuComponent, DBListRowModule, DBListRowPrefixComponent, DBListRowSuffixComponent, DBLoadingOverlayComponent, DBLoadingOverlayModule, DBLoadingOverlayService, DBMaxAgeValidator, DBMaxLengthValidator, DBMaxNumberValidator, DBMenuComponent, DBMenuItemComponent, DBMenuModule, DBMenuOverlayComponent, DBMinAgeValidator, DBMinLengthValidator, DBMinNumberValidator, DBModalComponent, DBModalContentDefaultComponent, DBModalDefaultComponent, DBModalFooterDefaultComponent, DBModalHeaderDefaultComponent, DBModalModule, DBModalService, DBMonthYearPipe, DBNavigationComponent, DBNavigationItemComponent, DBNavigationModule, DBNumberPipe, DBNumberRangeValidator, DBNumberValidator, DBOverlayComponentDirective, DBPanelComponent, DBPanelModule, DBPastDateValidator, DBPatternValidator, DBPersonNameValidator, DBPhoneNumberPipe, DBPhoneValidator, DBPinValidator, DBPopoverComponent, DBPopoverDirective, DBPopoverModule, DBPostMessageModule, DBPostMessageService, DBRadioButtonComponent, DBRadioButtonModule, DBRadioGroupComponent, DBRenderer, DBRendererModule, DBRequiredValidator, DBSameDateValidator, DBScrollModule, DBScrollService, DBSelectComponent, DBSelectModule, DBSessionModule, DBSessionService, DBSignatureComponent, DBSignatureModule, DBSliderComponent, DBSliderModule, DBSliderService, DBSpinnerComponent, DBSpinnerModule, DBStatusComponent, DBStatusModule, DBStepperComponent, DBStepperItemComponent, DBStepperModule, DBStreetWithHouseNumberValidator, DBSubAccountNumberValidator, DBSwitchComponent, DBSwitchModule, DBSystemDetectionModule, DBSystemDetectionService, DBTabComponent, DBTabsComponent, DBTabsModule, DBTextareaComponent, DBTextareaModule, DBTinValidator, DBToggleableComponent, DBToggleableModule, DBTooltipComponent, DBTooltipDirective, DBTooltipModule, DBTypeAheadComponent, DBTypeAheadDataService, DBTypeAheadModule, DBValidatableDirective, DBValidationErrorsComponent, DBValidationModule, DBValidationSuccessComponent, DBValidators, DBValidatorsUtil, DBViewportModule, DBViewportService, DBVolumeSliderComponent, DBVolumeSliderModule, DBWinValidator, DBWindowRef, DBWindowRefModule, DBZipValidator, DeepmergeService, NBNavigationComponent, NBNavigationItemComponent, NBNavigationModule, PBNavigationComponent, PBNavigationItemComponent, PBNavigationModule, ToggleableState, appDataStoreServiceFactory, cookieServiceFactory, creditCardFormatter, dateFormatter, debitCardFormatter, getLabelByValue, handoverInitializerFactory, handoverServiceFactory, httpClientCacheFactory, ibanFormatter, integerFormatter, loadingOverlayServiceFactory, modalServiceFactory, monthYearFormatter, numberFormatter, phoneNumberFormatter, postMessageServiceFactory, rendererFactory, scrollServiceFactory, sealOneIdFormatter, sessionServiceFactory, systemDetectionServiceFactory, viewportServiceFactory, windowRefServiceFactory };
